import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from "react";
import { bsGamma, gammaProfile, flowSummary } from "./lib/optionsMath";
import IDag from "./views/IDag";
import Watchlist from "./views/Watchlist";
import JournalStats from "./views/JournalStats";
import Levels from "./views/Levels";
import { calcVolumeProfile, computeLevels, anchoredVwaps } from "./lib/levels";
import { calcAVWAP, calcRVOL, detectInstitutionalPatterns } from "./lib/patterns";
import { calcBacktest, reliability } from "./lib/backtest";
import { verdictOf, VERDICT_STYLE } from "./lib/patternVerdicts";
import { createChart, AreaSeries, LineSeries, HistogramSeries, CandlestickSeries, createSeriesMarkers, LineStyle } from "lightweight-charts";

const YAHOO_PROXY = "https://corsproxy.io/?url=https://query1.finance.yahoo.com/v8/finance/chart/";

// Yahoo Finance uses different symbols for cash-settled indices
const YAHOO_TICKER_MAP = { SPX: "^GSPC", XSP: "^XSP", VIX: "^VIX", RUT: "^RUT", NDX: "^NDX", DJX: "^DJI" };
const toYahooTicker = t => YAHOO_TICKER_MAP[t] || t;

const DEFAULT_STOCKS = {
  "Chips & Semiconductors": [
    { ticker: "NVDA", name: "NVIDIA" }, { ticker: "AMD", name: "AMD" },
    { ticker: "AVGO", name: "Broadcom" }, { ticker: "MRVL", name: "Marvell" },
    { ticker: "QCOM", name: "Qualcomm" }, { ticker: "INTC", name: "Intel" },
  ],
  "Cloud & Data Centers": [
    { ticker: "MSFT", name: "Microsoft" }, { ticker: "AMZN", name: "Amazon" },
    { ticker: "GOOGL", name: "Alphabet" }, { ticker: "META", name: "Meta" },
    { ticker: "ORCL", name: "Oracle" }, { ticker: "CRWV", name: "CoreWeave" },
  ],
  "Pure AI / Software": [
    { ticker: "PLTR", name: "Palantir" }, { ticker: "AI", name: "C3.ai" },
    { ticker: "SOUN", name: "SoundHound AI" }, { ticker: "BBAI", name: "BigBear.ai" },
    { ticker: "NOW", name: "ServiceNow" }, { ticker: "PATH", name: "UiPath" },
  ],
  "Quantum Computing": [
    { ticker: "IONQ", name: "IonQ" }, { ticker: "RGTI", name: "Rigetti Computing" },
    { ticker: "QBTS", name: "D-Wave Quantum" },
  ],
  "Robotics & Defense AI": [
    { ticker: "KTOS", name: "Kratos Defense" }, { ticker: "RR", name: "Richtech Robotics" },
  ],
  "Energy for AI": [
    { ticker: "VST", name: "Vistra Energy" }, { ticker: "CEG", name: "Constellation Energy" },
    { ticker: "ETN", name: "Eaton Corp" }, { ticker: "NRG", name: "NRG Energy" },
    { ticker: "IREN", name: "Iris Energy" }, { ticker: "OKLO", name: "Oklo" },
    { ticker: "SMR", name: "NuScale Power" }, { ticker: "ETR", name: "Entergy" },
  ],
  "Cooling & Infrastructure": [
    { ticker: "VRT", name: "Vertiv" }, { ticker: "SMCI", name: "Super Micro" },
    { ticker: "EQIX", name: "Equinix" }, { ticker: "DLR", name: "Digital Realty" },
  ],
  "Memory & Storage": [
    { ticker: "MU", name: "Micron" }, { ticker: "WDC", name: "Western Digital" },
    { ticker: "PSTG", name: "Pure Storage" }, { ticker: "STX", name: "Seagate" },
  ],
  "Networking & Hardware": [
    { ticker: "ANET", name: "Arista Networks" }, { ticker: "DELL", name: "Dell Technologies" },
    { ticker: "HPE", name: "HP Enterprise" }, { ticker: "CSCO", name: "Cisco" },
    { ticker: "COHR", name: "Coherent" }, { ticker: "LITE", name: "Lumentum" },
  ],
  "Security AI": [
    { ticker: "CRWD", name: "CrowdStrike" },
  ],
  "Krypto / Bitcoin": [
    { ticker: "MSTR", name: "MicroStrategy" },
  ],
  "Chip Foundry": [
    { ticker: "TSM", name: "TSMC" }, { ticker: "ASML", name: "ASML" },
    { ticker: "AMAT", name: "Applied Materials" }, { ticker: "LRCX", name: "Lam Research" },
  ],
  "Råvarer & Metaller": [
    { ticker: "FCX", name: "Freeport-McMoRan" }, { ticker: "MP", name: "MP Materials" },
    { ticker: "UUUU", name: "Energy Fuels" },
  ],
};

const AI_CHAIN = [
  { id:"apps",        label:"AI Applikasjoner",  icon:"🤖", cx:390, cy:45,
    status:"ETTERSPURT",   sc:"#f97316", demand:5, opp:3,
    tickers:["PLTR","AI","BBAI","PATH"],
    desc:"Sluttprodukter drevet av AI. Etterspørselen vokser eksponentielt, men mange selskaper er kraftig overpriset uten lønnsomhet.",
    next:"Fokus skifter fra vekst til lønnsomhet. Palantir er lønnsomt og reelt — de fleste andre er hype." },
  { id:"cloud",       label:"Skytjenester",       icon:"☁️", cx:390, cy:142,
    status:"DOMINERER",    sc:"#3b82f6", demand:5, opp:2,
    tickers:["MSFT","AMZN","GOOGL","META"],
    desc:"AWS, Azure og GCP bruker 100+ milliarder per år på AI-infrastruktur. Vinnerne er allerede kjent og priset inn av markedet.",
    next:"Stabil vekst, begrenset oppside. Mer interessant å se hva de kjøper — det peker mot neste mulighet." },
  { id:"ai_plat",     label:"AI Plattform",       icon:"⚡", cx:128, cy:238,
    status:"MONOPOL",      sc:"#3b82f6", demand:5, opp:2,
    tickers:["NVDA"],
    desc:"NVIDIA CUDA er de facto standard for AI-trening og inference. 90%+ markedsandel og et nesten umulig økosystem å erstatte.",
    next:"AMD og Intel prøver å ta markedsandeler — men CUDA-vollen holder. Hold NVDA, men risikoen er stor på nedsiden." },
  { id:"datacenter",  label:"Datasentre",         icon:"🏢", cx:390, cy:238,
    status:"VOKSER",       sc:"#10b981", demand:4, opp:3,
    tickers:["EQIX","DLR","SMCI","IREN"],
    desc:"Bygges på høygir over hele verden. Ventetid 2-4 år for nye anlegg. Supermicro leverer AI-optimerte servere direkte til hyperscalers.",
    next:"Flaskehalsen er ikke plass — det er strøm og kjøling. Se på energi-kategorien." },
  { id:"energy",      label:"Energi & Kraft",     icon:"🔌", cx:652, cy:190,
    status:"FLASKEHALS",   sc:"#f59e0b", demand:5, opp:5,
    tickers:["VST","CEG","ETR","OKLO","SMR"],
    desc:"NESTE STORE MULIGHET. AI-datasentre bruker nå mer strøm enn mange land. Tradisjonelt strømnett holder ikke — atomkraft og gass er eneste skalérbare løsning.",
    next:"Markedet undervurderer strøm-mangelen dramatisk. Vistra (VST) og Constellation (CEG) er de stille vinnerne ingen snakker om." },
  { id:"cooling",     label:"Kjøling",            icon:"❄️", cx:652, cy:295,
    status:"UNDERVURDERT", sc:"#a78bfa", demand:4, opp:4,
    tickers:["VRT","DELL","CSCO"],
    desc:"Hvert H100/H200 GPU-rack bruker 10-40 kW — like mye som 3-10 vanlige hus. Tradisjonell luftkjøling virker ikke. Flytende kjøling er eneste løsning i stor skala.",
    next:"Vertiv (VRT) er den store vinneren. Ekstremt lite fulgt av AI-investorer — stor mulighet akkurat nå." },
  { id:"networking",  label:"Nettverk & Fiber",   icon:"🌐", cx:390, cy:345,
    status:"ETTERSPURT",   sc:"#f97316", demand:4, opp:4,
    tickers:["AVGO","MRVL","COHR","LITE"],
    desc:"AI-klynger krever ekstrem intern båndbredde mellom tusenvis av GPU-er. InfiniBand (NVIDIA) vs Ethernet-krigen pågår akkurat nå.",
    next:"Coherent (COHR) og Marvell (MRVL) er posisjonert perfekt. Fiberoptikk er blodbanen — og kapasiteten må 10-dobles." },
  { id:"gpu",         label:"GPU / Akseleratorer", icon:"🔲", cx:212, cy:462,
    status:"PRISET INN",   sc:"#475569", demand:5, opp:2,
    tickers:["NVDA","AMD","INTC"],
    desc:"Alle vet dette. NVIDIA er priset for tiår med perfekt vekst. Blackwell-generasjonen allerede utsolgt til 2026. Enormt nedsiderisiko hvis vekst skuffer.",
    next:"Store oppside begrenset. Men noen selger heller ikke gode NVDA-aksjer. Hold eller ta profitt gradvis ved all-time highs." },
  { id:"memory",      label:"Minne / HBM",        icon:"💾", cx:568, cy:462,
    status:"FLASKEHALS",   sc:"#f59e0b", demand:5, opp:5,
    tickers:["MU","WDC","STX"],
    desc:"HBM3E (High Bandwidth Memory) er GPU-ens blod — uten nok minne stopper hele AI-kjeden. SK Hynix og Micron kan ikke produsere nok. Dette er nå.",
    next:"Sandisk/WD opp 1000%+. Micron (MU) har fortsatt potensiale. Spørsmålet: når er det levert nok? Da snur det fort." },
  { id:"foundry",     label:"Chip Foundry",       icon:"🏭", cx:390, cy:568,
    status:"STABIL",       sc:"#64748b", demand:4, opp:3,
    tickers:["TSM","ASML","AMAT","LRCX"],
    desc:"TSMC kontrollerer 90%+ av avansert chip-produksjon (3nm, 2nm). Ny kapasitet tar 3-5 år å bygge og koster 20+ milliarder dollar.",
    next:"ASML lager maskinene som lager maskinene — unikt EUV-monopol. Ingen AI uten ASML. Kjøp og glem." },
  { id:"materials",   label:"Råvarer & Metaller", icon:"⛏️", cx:390, cy:662,
    status:"UNDERVURDERT", sc:"#a78bfa", demand:3, opp:4,
    tickers:["FCX","MP","UUUU"],
    desc:"Kobber, silisium, sjeldne jordarter og uran. Ekstremt lite fulgt av AI-investorer, men hele kjeden er umulig uten disse materialene.",
    next:"Kobber er neste store gap. Hvert datasenter bruker kilometersvis med kabler. Freeport-McMoRan (FCX) er det enkle spillet." },
];

const CHAIN_EDGES = [
  ["apps","cloud"],
  ["cloud","ai_plat"],["cloud","datacenter"],
  ["ai_plat","gpu"],
  ["datacenter","energy"],["datacenter","cooling"],["datacenter","networking"],
  ["networking","gpu"],["networking","memory"],
  ["gpu","foundry"],["memory","foundry"],
  ["foundry","materials"],
];

const HOME_DOTS = Array.from({length:32},(_,i)=>({
  angle:(i/32)*Math.PI*2 + [0.15,-0.1,0.25,0,-0.2,0.1,0.3,-0.05][i%8],
  radius:115+(i%5)*30+(i%3)*18,
  size:1.4+(i%4)*0.55,
  delay:(i*0.38)%4.5,
  dur:2.8+(i%4)*0.65,
  op:0.1+(i%6)*0.07,
}));

const CAT_COLORS = {
  "Chips & Semiconductors": "#f59e0b",
  "Cloud & Data Centers": "#3b82f6",
  "Pure AI / Software": "#a78bfa",
  "Energy for AI": "#10b981",
  "Cooling & Infrastructure": "#06b6d4",
  "Memory & Storage": "#f43f5e",
  "Networking & Hardware": "#fb923c",
  "Egendefinert": "#e879f9",
  "Security AI": "#ef4444",
  "Krypto / Bitcoin": "#f7931a",
  "Quantum Computing": "#7c3aed",
  "Robotics & Defense AI": "#0ea5e9",
  "Chip Foundry": "#64748b",
  "Råvarer & Metaller": "#b45309",
};

const PERIODS = [
  { label: "1D", range: "1d", interval: "5m" },
  { label: "1U", range: "5d", interval: "1d" },
  { label: "1M", range: "1mo", interval: "1d" },
  { label: "3M", range: "3mo", interval: "1d" },
  { label: "6M", range: "6mo", interval: "1wk" },
  { label: "1Å", range: "1y", interval: "1wk" },
];

const STOCK_INTEL = {
  NVDA: { revenueGrowth: 78, grossMargin: 75, forwardPE: 35, beatRate: 90, aiExposure: "Meget høy", earningsSurprise: 12, nextEarnings: "2026-08-27", expectedMove: 8.2, avgMove: 9.1, pf: { earningsRisk: "HIGH", gammaRegime: "POSITIVE", gammaFlip: 118, ivPct: 72, optionsActivity: "VERY HIGH", callPutRatio: 2.8 }, desc: "Dominerende AI-chip leverandør. Blackwell-arkitekturen driver massiv etterspørsel fra hyperscale data centers globalt." },
  AMD: { revenueGrowth: 24, grossMargin: 52, forwardPE: 27, beatRate: 78, aiExposure: "Høy", earningsSurprise: 4, nextEarnings: "2026-10-27", expectedMove: 7.1, avgMove: 8.3, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 155, ivPct: 58, optionsActivity: "HIGH", callPutRatio: 1.9 }, desc: "Sterk konkurrent til NVIDIA innen AI-akseleratorer med MI-serien. Vokser men taper markedsandel på GPU-siden." },
  MSFT: { revenueGrowth: 17, grossMargin: 70, forwardPE: 31, beatRate: 85, aiExposure: "Meget høy", earningsSurprise: 6, nextEarnings: "2026-10-22", expectedMove: 4.1, avgMove: 4.8, pf: { earningsRisk: "LOW", gammaRegime: "POSITIVE", gammaFlip: 385, ivPct: 42, optionsActivity: "HIGH", callPutRatio: 2.1 }, desc: "Azure AI og OpenAI-integrasjon gir sterk og stabil AI-eksponering. Copilot driver produktivitetsvekst på tvers av Microsoft 365." },
  GOOGL: { revenueGrowth: 14, grossMargin: 58, forwardPE: 22, beatRate: 80, aiExposure: "Høy", earningsSurprise: 7, nextEarnings: "2026-10-27", expectedMove: 5.2, avgMove: 6.1, pf: { earningsRisk: "LOW", gammaRegime: "POSITIVE", gammaFlip: 158, ivPct: 44, optionsActivity: "MEDIUM", callPutRatio: 1.7 }, desc: "Gemini-modellene og Google Cloud AI driver vekst, men søkebasert annonseinntekt er under press fra AI-konkurrenter." },
  META: { revenueGrowth: 21, grossMargin: 82, forwardPE: 24, beatRate: 88, aiExposure: "Høy", earningsSurprise: 9, nextEarnings: "2026-10-28", expectedMove: 5.8, avgMove: 7.2, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 478, ivPct: 51, optionsActivity: "HIGH", callPutRatio: 2.0 }, desc: "AI-drevet annonsering og open-source Llama-modellene gjør Meta til en sterk og lønnsom AI-aktør." },
  AMZN: { revenueGrowth: 13, grossMargin: 48, forwardPE: 35, beatRate: 82, aiExposure: "Høy", earningsSurprise: 5, nextEarnings: "2026-10-29", expectedMove: 4.9, avgMove: 5.7, pf: { earningsRisk: "LOW", gammaRegime: "POSITIVE", gammaFlip: 182, ivPct: 39, optionsActivity: "MEDIUM", callPutRatio: 1.8 }, desc: "AWS er verdens største cloud-plattform. Bedrock og Trainium-chips styrker AI-posisjonen." },
  PLTR: { revenueGrowth: 39, grossMargin: 80, forwardPE: 118, beatRate: 85, aiExposure: "Meget høy", earningsSurprise: 8, nextEarnings: "2026-11-03", expectedMove: 11.2, avgMove: 13.1, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 38, ivPct: 88, optionsActivity: "VERY HIGH", callPutRatio: 3.1 }, desc: "AI-plattform for forsvar og enterprise. Høy vekst men svært høy verdsettelse gjør aksjen volatil." },
  MU: { revenueGrowth: 58, grossMargin: 38, forwardPE: 14, beatRate: 76, aiExposure: "Høy", earningsSurprise: 11, nextEarnings: "2026-09-24", expectedMove: 9.1, avgMove: 10.4, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 112, ivPct: 76, optionsActivity: "HIGH", callPutRatio: 1.6 }, desc: "HBM-minne er kritisk for AI-GPU-er. Micron er en av få globale leverandører og nyter godt av AI-boom." },
  VRT: { revenueGrowth: 42, grossMargin: 35, forwardPE: 38, beatRate: 83, aiExposure: "Høy", earningsSurprise: 7, nextEarnings: "2026-10-22", expectedMove: 8.9, avgMove: 9.8, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 82, ivPct: 65, optionsActivity: "HIGH", callPutRatio: 2.2 }, desc: "Kjøling og strømstyring for data centers. Direkte benefisiant av AI-infrastruktur-boom." },
  AVGO: { revenueGrowth: 44, grossMargin: 64, forwardPE: 28, beatRate: 87, aiExposure: "Meget høy", earningsSurprise: 6, nextEarnings: "2026-09-03", expectedMove: 5.4, avgMove: 6.2, pf: { earningsRisk: "LOW", gammaRegime: "POSITIVE", gammaFlip: 195, ivPct: 48, optionsActivity: "MEDIUM", callPutRatio: 2.0 }, desc: "AI-nettverkschips og custom ASIC-er for hyperscalers. Sterk og diversifisert posisjon i AI-infrastruktur." },
  ANET: { revenueGrowth: 23, grossMargin: 64, forwardPE: 38, beatRate: 84, aiExposure: "Høy", earningsSurprise: 6, nextEarnings: "2026-10-27", expectedMove: 6.8, avgMove: 7.9, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 298, ivPct: 55, optionsActivity: "MEDIUM", callPutRatio: 1.8 }, desc: "Nettverksinfrastruktur for de største AI-data-centerene. Sterk vekst drevet av hyperscaler-etterspørsel." },
  CEG: { revenueGrowth: 18, grossMargin: 42, forwardPE: 26, beatRate: 79, aiExposure: "Medium", earningsSurprise: 5, nextEarnings: "2026-11-05", expectedMove: 5.1, avgMove: 5.8, pf: { earningsRisk: "LOW", gammaRegime: "POSITIVE", gammaFlip: 165, ivPct: 44, optionsActivity: "MEDIUM", callPutRatio: 1.9 }, desc: "Kjernekraft-leverandør til data centers. Microsoft og Google er blant kundene — stabil AI-energi-play." },
  VST: { revenueGrowth: 22, grossMargin: 38, forwardPE: 21, beatRate: 78, aiExposure: "Medium", earningsSurprise: 4, nextEarnings: "2026-11-04", expectedMove: 5.8, avgMove: 6.4, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 82, ivPct: 52, optionsActivity: "MEDIUM", callPutRatio: 1.7 }, desc: "Strømleverandør med stor eksponering mot data center-vekst. Diversifisert energiportefølje." },
  AI: { revenueGrowth: 20, grossMargin: 60, forwardPE: 85, beatRate: 70, aiExposure: "Høy", earningsSurprise: 3, nextEarnings: "2026-09-02", expectedMove: 12.0, avgMove: 14.5, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 28, ivPct: 82, optionsActivity: "HIGH", callPutRatio: 1.4 }, desc: "Enterprise AI-plattform for store selskaper og offentlig sektor. Høy vekst men fortsatt ikke lønnsomt — svært volatil aksje." },
  ORCL: { revenueGrowth: 12, grossMargin: 72, forwardPE: 24, beatRate: 82, aiExposure: "Høy", earningsSurprise: 5, nextEarnings: "2026-09-10", expectedMove: 6.5, avgMove: 7.8, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 158, ivPct: 54, optionsActivity: "MEDIUM", callPutRatio: 1.8 }, desc: "Oracle Cloud Infrastructure (OCI) vokser raskt som alternativ til AWS/Azure. Sterk posisjon innen AI-databaseløsninger og GPU-klynger." },
  CRWV: { revenueGrowth: 420, grossMargin: 70, forwardPE: 999, beatRate: 72, aiExposure: "Meget høy", earningsSurprise: 7, nextEarnings: "2026-11-12", expectedMove: 15.0, avgMove: 18.0, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 12, ivPct: 84, optionsActivity: "HIGH", callPutRatio: 2.1 }, desc: "AI GPU-sky-infrastruktur — bygger massive GPU-klynger leid ut til hyperscalers og AI-laboratorier. Microsoft er største kunde (~62% av inntektene). IPO mars 2025, støttet av NVIDIA. Ikke lønnsomt ennå men eksploderende vekst." },
  CRWD: { revenueGrowth: 25, grossMargin: 76, forwardPE: 58, beatRate: 88, aiExposure: "Høy", earningsSurprise: 6, nextEarnings: "2026-09-02", expectedMove: 8.5, avgMove: 10.2, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 365, ivPct: 62, optionsActivity: "HIGH", callPutRatio: 2.0 }, desc: "Ledende AI-drevet cybersikkerhetsplattform. Falcon-plattformen bruker maskinlæring for trusseljakt og endepunktbeskyttelse i sanntid." },
  MSTR: { revenueGrowth: 7, grossMargin: 72, forwardPE: 999, beatRate: 58, aiExposure: "Lav", earningsSurprise: 2, nextEarnings: "2026-10-28", expectedMove: 18.0, avgMove: 22.0, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 320, ivPct: 91, optionsActivity: "VERY HIGH", callPutRatio: 2.5 }, desc: "Primært et Bitcoin-treasury-selskap forkledd som programvareselskap. Svært høy korrelasjon med BTC-prisen — ekstrem volatilitet." },
  IREN: { revenueGrowth: 180, grossMargin: 38, forwardPE: 22, beatRate: 70, aiExposure: "Medium", earningsSurprise: 5, nextEarnings: "2026-08-20", expectedMove: 14.0, avgMove: 16.5, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 9, ivPct: 85, optionsActivity: "HIGH", callPutRatio: 1.8 }, desc: "Bitcoin-miner som bruker fornybar energi. Ekspanderer nå inn i AI GPU-hosting — kombinasjonen av krypto og AI-infrastruktur gir høy vekst men også høy volatilitet." },
  NOW: { revenueGrowth: 22, grossMargin: 79, forwardPE: 45, beatRate: 88, aiExposure: "Høy", earningsSurprise: 6, nextEarnings: "2026-10-22", expectedMove: 5.8, avgMove: 6.9, pf: { earningsRisk: "LOW", gammaRegime: "POSITIVE", gammaFlip: 885, ivPct: 46, optionsActivity: "MEDIUM", callPutRatio: 2.1 }, desc: "Enterprise AI-plattform for workflow-automatisering. Now AI-agenter integreres i IT, HR og finans hos Fortune 500-selskaper — sterk og forutsigbar vekst." },
  IONQ: { revenueGrowth: 95, grossMargin: 48, forwardPE: 999, beatRate: 72, aiExposure: "Meget høy", earningsSurprise: 5, nextEarnings: "2026-11-05", expectedMove: 16.0, avgMove: 19.0, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 28, ivPct: 87, optionsActivity: "HIGH", callPutRatio: 1.6 }, desc: "Ledende quantum computing-selskap basert på trapped-ion teknologi. Samarbeider med Microsoft Azure og Amazon Braket — best posisjonert for quantum advantage innen 2-3 år." },
  RGTI: { revenueGrowth: 42, grossMargin: 22, forwardPE: 999, beatRate: 60, aiExposure: "Meget høy", earningsSurprise: 3, nextEarnings: "2026-11-10", expectedMove: 22.0, avgMove: 28.0, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 8, ivPct: 92, optionsActivity: "HIGH", callPutRatio: 1.4 }, desc: "Superconducting qubit quantum computing. Svært spekulativ og volatil — liten omsetning men bred partnerskapsportefølje. Høyrisiko/høybelønning quantum-play." },
  QBTS: { revenueGrowth: 28, grossMargin: 18, forwardPE: 999, beatRate: 58, aiExposure: "Meget høy", earningsSurprise: 2, nextEarnings: "2026-11-10", expectedMove: 20.0, avgMove: 26.0, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 5, ivPct: 89, optionsActivity: "MEDIUM", callPutRatio: 1.3 }, desc: "D-Wave bruker quantum annealing — en annen tilnærming enn gate-based quantum. Har betalende enterprise-kunder innen logistikk og finans. Mer moden enn RGTI men fortsatt spekulativ." },
  KTOS: { revenueGrowth: 16, grossMargin: 27, forwardPE: 38, beatRate: 78, aiExposure: "Høy", earningsSurprise: 5, nextEarnings: "2026-10-29", expectedMove: 7.5, avgMove: 8.8, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 28, ivPct: 56, optionsActivity: "MEDIUM", callPutRatio: 1.7 }, desc: "Produsent av autonome droner og AI-drevne forsvarssystemer. Valkyrie-dronen og Gremlins-programmet er kjerneprodukter — sterk posisjon i det voksende markedet for billige autonome våpen." },
  RR: { revenueGrowth: 55, grossMargin: 38, forwardPE: 999, beatRate: 62, aiExposure: "Høy", earningsSurprise: 4, nextEarnings: "2026-11-12", expectedMove: 24.0, avgMove: 30.0, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 2, ivPct: 88, optionsActivity: "LOW", callPutRatio: 1.2 }, desc: "Liten men raskt voksende robotikkselskap fokusert på kommersielle roboter for gjestfrihet og serviceindustrien. Adam-roboten brukes i hoteller og restauranter — tidlig fase med høy vekst men fortsatt ikke lønnsomt." },
  PATH: { revenueGrowth: 11, grossMargin: 84, forwardPE: 32, beatRate: 78, aiExposure: "Høy", earningsSurprise: 5, nextEarnings: "2026-09-10", expectedMove: 9.5, avgMove: 11.2, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 22, ivPct: 62, optionsActivity: "MEDIUM", callPutRatio: 1.6 }, desc: "Ledende RPA (Robotic Process Automation) plattform som integrerer AI-agenter i forretningsprosesser. Over 10 000 enterprise-kunder bruker UiPath til å automatisere repetitive oppgaver på tvers av IT, finans og HR." },
  TSM: { revenueGrowth: 34, grossMargin: 56, forwardPE: 22, beatRate: 88, aiExposure: "Meget høy", earningsSurprise: 8, nextEarnings: "2026-10-15", expectedMove: 5.8, avgMove: 6.4, pf: { earningsRisk: "LOW", gammaRegime: "POSITIVE", gammaFlip: 185, ivPct: 42, optionsActivity: "HIGH", callPutRatio: 2.2 }, desc: "Verden eneste produsent av 3nm og 2nm chips — 90%+ markedsandel. Ingen AI-chip eksisterer uten TSMC. Geopolitisk risiko mot Taiwan er eneste reelle nedside. Kjøp og gjem." },
  ASML: { revenueGrowth: 24, grossMargin: 52, forwardPE: 35, beatRate: 85, aiExposure: "Meget høy", earningsSurprise: 6, nextEarnings: "2026-10-15", expectedMove: 5.2, avgMove: 6.0, pf: { earningsRisk: "LOW", gammaRegime: "POSITIVE", gammaFlip: 820, ivPct: 38, optionsActivity: "MEDIUM", callPutRatio: 2.0 }, desc: "Monopol på EUV-lithografimaskinene som lager avanserte chips. Ingen substitutter finnes — ASML er bokstavelig talt umulig å erstatte. En maskin koster 350 millioner dollar og tar 2 år å bygge." },
  AMAT: { revenueGrowth: 7, grossMargin: 48, forwardPE: 18, beatRate: 82, aiExposure: "Høy", earningsSurprise: 5, nextEarnings: "2026-11-12", expectedMove: 6.2, avgMove: 7.1, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 168, ivPct: 48, optionsActivity: "MEDIUM", callPutRatio: 1.8 }, desc: "Ledende leverandør av utstyr til halvlederproduksjon — CVD, PVD og CMP-maskiner. Hver ny chip-fabrikk krever milliarder i Applied Materials-utstyr. Profiterer direkte på TSMC og Samsung-ekspansjon." },
  LRCX: { revenueGrowth: 12, grossMargin: 47, forwardPE: 19, beatRate: 83, aiExposure: "Høy", earningsSurprise: 6, nextEarnings: "2026-10-22", expectedMove: 6.8, avgMove: 7.8, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 82, ivPct: 46, optionsActivity: "MEDIUM", callPutRatio: 1.9 }, desc: "Spesialist på etch- og deposisjonsutstyr — kritiske trinn i chipfabrikasjon. Sterkt posisjonert for 3D NAND og EUV-generasjonen av chips som AI-boom driver frem." },
  COHR: { revenueGrowth: 18, grossMargin: 44, forwardPE: 24, beatRate: 76, aiExposure: "Høy", earningsSurprise: 5, nextEarnings: "2026-11-05", expectedMove: 8.5, avgMove: 10.0, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 62, ivPct: 58, optionsActivity: "MEDIUM", callPutRatio: 1.7 }, desc: "Produsent av optiske transceivere og fiberkomponenter for datasentre og AI-klynger. Etterspørselen etter 400G/800G optikk eksploderer i takt med AI-infrastruktur-byggingen globalt." },
  LITE: { revenueGrowth: 14, grossMargin: 46, forwardPE: 22, beatRate: 74, aiExposure: "Høy", earningsSurprise: 4, nextEarnings: "2026-08-19", expectedMove: 9.2, avgMove: 10.8, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 55, ivPct: 56, optionsActivity: "MEDIUM", callPutRatio: 1.6 }, desc: "Ledende optisk teknologiselskap innen telecom og datasentre. 3D-sensing, laserkomponenter og fiberoptikk for neste generasjons AI-nettverksinfrastruktur — direkte benefisiant av GPU-klynge-ekspansjon." },
  OKLO: { revenueGrowth: 999, grossMargin: 0, forwardPE: 999, beatRate: 50, aiExposure: "Medium", earningsSurprise: 0, nextEarnings: "2026-11-05", expectedMove: 18.0, avgMove: 22.0, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 18, ivPct: 88, optionsActivity: "HIGH", callPutRatio: 2.1 }, desc: "Neste generasjons mikro-kjernekraftverk (Aurora). Sam Altman (OpenAI) er styreleder. Data centers fra Amazon og Microsoft har signert intensjonsavtaler. Kommersiell drift tidligst 2027-2028 — pre-revenue spekulasjon." },
  SMR: { revenueGrowth: 999, grossMargin: 0, forwardPE: 999, beatRate: 48, aiExposure: "Medium", earningsSurprise: 0, nextEarnings: "2026-11-05", expectedMove: 22.0, avgMove: 28.0, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 8, ivPct: 90, optionsActivity: "MEDIUM", callPutRatio: 1.8 }, desc: "Liten modulær reaktor (SMR) teknologi med NuScale VOYGR-design. Tett knyttet til US DoE og nordamerikansk energiinfrastruktur. Høy regulatorisk risiko men enorm potensiell oppside hvis SMR-teknologi slår gjennom." },
  ETR: { revenueGrowth: 6, grossMargin: 34, forwardPE: 17, beatRate: 76, aiExposure: "Lav", earningsSurprise: 3, nextEarnings: "2026-10-29", expectedMove: 3.8, avgMove: 4.2, pf: { earningsRisk: "LOW", gammaRegime: "POSITIVE", gammaFlip: 82, ivPct: 28, optionsActivity: "LOW", callPutRatio: 1.4 }, desc: "Stor amerikansk kraftleverandør med sterk kjerneraft-portefølje i sørstatene. AI-datasentre krever pålitelig baseload-strøm — Entergy er en av leverandørene som profiterer på industrialiseringen av AI-strøm." },
  FCX: { revenueGrowth: 8, grossMargin: 42, forwardPE: 14, beatRate: 74, aiExposure: "Lav", earningsSurprise: 4, nextEarnings: "2026-10-22", expectedMove: 5.2, avgMove: 6.0, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 38, ivPct: 44, optionsActivity: "MEDIUM", callPutRatio: 1.5 }, desc: "Verdens største børsnoterte kobberprodusent. Hvert AI-datasenter bruker enormt med kobber i kabler, kjøling og strømsystemer. Kobber er blodet i AI-infrastruktur-boom — FCX er det enkleste spillet." },
  MP: { revenueGrowth: 22, grossMargin: 28, forwardPE: 38, beatRate: 70, aiExposure: "Lav", earningsSurprise: 3, nextEarnings: "2026-10-29", expectedMove: 12.0, avgMove: 14.5, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 15, ivPct: 72, optionsActivity: "MEDIUM", callPutRatio: 1.6 }, desc: "Eneste betydelige produsent av sjeldne jordarter (REE) i USA. Magnet-materialer fra MP er kritiske for EV-motorer, vindturbiner og forsvarssystemer. USA reduserer kinesisk avhengighet — MP er strategisk viktig." },
  UUUU: { revenueGrowth: 45, grossMargin: 32, forwardPE: 22, beatRate: 68, aiExposure: "Lav", earningsSurprise: 4, nextEarnings: "2026-11-04", expectedMove: 16.0, avgMove: 20.0, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 5, ivPct: 78, optionsActivity: "MEDIUM", callPutRatio: 1.7 }, desc: "Uranprodusent og produsent av sjeldne jordarter i USA. Kjernekraft-renessansen driver uranpriser. Datasentre som signerer kjernekraftavtaler trenger uran — UUUU profiterer direkte på atom-AI-koblingen." },
  QCOM: { revenueGrowth: 11, grossMargin: 56, forwardPE: 16, beatRate: 82, aiExposure: "Høy", earningsSurprise: 5, nextEarnings: "2026-10-29", expectedMove: 6.5, avgMove: 7.8, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 158, ivPct: 42, optionsActivity: "MEDIUM", callPutRatio: 1.5 }, desc: "Qualcomm dominerer mobilprosessorer (Snapdragon) og eier fundamental 5G-patentportefølje — alle produsenter betaler royalties per enhet. Apple-modem-tap er priset inn etter fall fra $250. Vekstdrivere: Snapdragon X Elite for AI-PC, Snapdragon Digital Chassis for bil (BMW, Mercedes, VW), og on-device AI." },
  INTC: { revenueGrowth: -4, grossMargin: 38, forwardPE: 22, beatRate: 62, aiExposure: "Medium", earningsSurprise: -2, nextEarnings: "2026-10-23", expectedMove: 8.2, avgMove: 9.5, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 21, ivPct: 58, optionsActivity: "HIGH", callPutRatio: 1.4 }, desc: "Intel er i en kritisk turnaround-fase under ny ledelse. Gaudi AI-akselerator og Intel 18A-fabrikasjonsprosess er nøkkelkatalytatorer. Foundry-virksomheten konkurrerer med TSMC. Høy risiko men potensiell stor belønning om 18A-prosessen lykkes i 2026-2027." },
  MRVL: { revenueGrowth: 61, grossMargin: 60, forwardPE: 38, beatRate: 84, aiExposure: "Meget høy", earningsSurprise: 7, nextEarnings: "2026-09-03", expectedMove: 8.8, avgMove: 10.2, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 68, ivPct: 62, optionsActivity: "HIGH", callPutRatio: 2.0 }, desc: "Marvell lager custom AI-akseleratorer (XPUs) for Google og Amazon, pluss optiske DSP-er for AI-datasentre. Sterk posisjon i to av de raskest voksende AI-infrastruktur-segmentene. Dyrere enn QCOM men vekstprofilen rettferdiggjør premiumet." },
  DELL: { revenueGrowth: 9, grossMargin: 22, forwardPE: 14, beatRate: 78, aiExposure: "Høy", earningsSurprise: 5, nextEarnings: "2026-11-25", expectedMove: 7.5, avgMove: 8.8, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 115, ivPct: 48, optionsActivity: "MEDIUM", callPutRatio: 1.7 }, desc: "Dell er den største leverandøren av AI-servere (PowerEdge XE-serien med NVIDIA H100/H200). ISG-divisjonen (Infrastructure Solutions Group) vokser eksplosivt med AI-bestillinger fra bedrifter. Lav margin men høy volum og sterk posisjon i enterprise AI-hardware." },
  SMCI: { revenueGrowth: 38, grossMargin: 14, forwardPE: 16, beatRate: 70, aiExposure: "Høy", earningsSurprise: 4, nextEarnings: "2026-11-05", expectedMove: 12.0, avgMove: 14.5, pf: { earningsRisk: "HIGH", gammaRegime: "NEGATIVE", gammaFlip: 38, ivPct: 72, optionsActivity: "HIGH", callPutRatio: 1.5 }, desc: "Super Micro Computer leverer høy-tetthet GPU-servere og kjølesystemer for AI-datasentre. Direkte konkurrent til Dell på AI-infrastruktur. Høy vekst men kompensert av lav margin, regnskaps-uro og delisting-frykt i 2024. Volatil og kontroversiell — høyrisiko/høybelønning." },
  WDC: { revenueGrowth: 42, grossMargin: 34, forwardPE: 12, beatRate: 74, aiExposure: "Medium", earningsSurprise: 6, nextEarnings: "2026-10-29", expectedMove: 8.5, avgMove: 10.0, pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: 52, ivPct: 54, optionsActivity: "MEDIUM", callPutRatio: 1.6 }, desc: "Western Digital produserer harddisker og NAND flash. AI-datasentre bruker massive mengder lagring — både HDD for kald lagring og NAND for hurtigbuffer. Syklisk bransje men AI-supersyklusen gir uvanlig lang opptursperiode." },
};

const EPS_GROWTH = {
  NVDA:55, AMD:35, MSFT:15, GOOGL:15, META:25, AMZN:30, PLTR:65,
  MU:100, VRT:45, AVGO:18, ANET:22, CEG:12, VST:18, ORCL:12,
  CRWD:38, NOW:22, KTOS:20, PATH:15, TSM:30, ASML:20, AMAT:8,
  LRCX:12, COHR:20, LITE:12, ETR:5, FCX:8, MP:25, UUUU:40,
};

function getIntel(ticker) {
  const base = STOCK_INTEL[ticker] || {
    revenueGrowth: 15, grossMargin: 45, forwardPE: 25, beatRate: 75, aiExposure: "Medium",
    earningsSurprise: 4, nextEarnings: "Ikke satt", expectedMove: 6.0, avgMove: 7.0,
    pf: { earningsRisk: "MEDIUM", gammaRegime: "POSITIVE", gammaFlip: null, ivPct: 50, optionsActivity: "MEDIUM", callPutRatio: 1.5 },
    desc: "Detaljert intelligence-data ikke tilgjengelig for denne aksjen ennå.",
  };
  const epsGrowth = EPS_GROWTH[ticker] ?? null;
  const peg = (epsGrowth && base.forwardPE < 200) ? Math.round(base.forwardPE / epsGrowth * 100) / 100 : null;
  return { ...base, epsGrowth, peg };
}

function scorePEG(v) {
  if (v === null) return { dot: "gray", label: "N/A", txt: "Ikke beregnet — selskapet er ikke lønnsomt ennå" };
  if (v < 1)    return { dot: "green", label: `${v}x — Undervurdert`, txt: "PEG under 1 betyr at du betaler mindre enn vekstraten — Peter Lynch kalte dette ideelt" };
  if (v < 1.5)  return { dot: "green", label: `${v}x — Rettferdig priset`, txt: "PEG 1–1.5 er normalt akseptert som fair verdsettelse for vekstselskaperv" };
  if (v < 2.5)  return { dot: "yellow", label: `${v}x — Noe dyrt`, txt: "PEG over 1.5 betyr at markedet betaler en premium — rettferdiggjøres av kvalitet eller monopol" };
  return { dot: "red", label: `${v}x — Overpriset`, txt: "PEG over 2.5 krever ekstraordinær langsiktig vekst for å rettferdiggjøres — høy risiko" };
}

function scoreRevGrowth(v) {
  if (v >= 50) return { dot: "green", label: "Eksepsjonelt", txt: "Topp 5% av S&P500 — sjelden vekstrate" };
  if (v >= 25) return { dot: "green", label: "Sterkt", txt: "Godt over markedsgjennomsnittet" };
  if (v >= 10) return { dot: "yellow", label: "OK", txt: "På linje med markedsgjennomsnittet" };
  return { dot: "red", label: "Svakt", txt: "Under markedsgjennomsnittet — vær obs" };
}
function scoreMargin(v) {
  if (v >= 70) return { dot: "green", label: "Utmerket", txt: "Svært høy marginkvalitet — betyr prismakt" };
  if (v >= 50) return { dot: "green", label: "Bra", txt: "Over bransjegjennomsnittet" };
  if (v >= 35) return { dot: "yellow", label: "Middels", txt: "Rundt bransjegjennomsnittet" };
  return { dot: "red", label: "Lavt", txt: "Under gjennomsnittet — marginsqueeze-risiko" };
}
function scorePE(v) {
  if (v <= 20) return { dot: "green", label: "Rimelig", txt: "Attraktiv prising relativt til vekst" };
  if (v <= 35) return { dot: "yellow", label: "Fair", txt: "Priset inn moderat vekst — ok nivå" };
  if (v <= 60) return { dot: "yellow", label: "Dyrt", txt: "Høy verdsettelse — krever sterk vekst" };
  return { dot: "red", label: "Veldig dyrt", txt: "Priset for perfeksjon — liten feilmargin" };
}
function scoreBeat(v) {
  if (v >= 85) return { dot: "green", label: "Svært pålitelig", txt: "Slår estimater konsekvent — trygg havn" };
  if (v >= 75) return { dot: "green", label: "Pålitelig", txt: "Godt track record på earnings" };
  if (v >= 65) return { dot: "yellow", label: "Variabelt", txt: "Noe usikkert — sjekk siste kvartaler" };
  return { dot: "red", label: "Upålitelig", txt: "Skuffer ofte — høy earnings-risiko" };
}
function scoreIV(v) {
  if (v >= 80) return { dot: "red", label: "Svært høy", txt: "Opsjoner er dyre — markedet priser inn stor usikkerhet" };
  if (v >= 60) return { dot: "yellow", label: "Høy", txt: "Forhøyet volatilitetsforventning" };
  if (v >= 40) return { dot: "yellow", label: "Normal", txt: "Volatilitet rundt historisk snitt" };
  return { dot: "green", label: "Lav", txt: "Rolig marked — lavt stress-nivå" };
}
function scoreGamma(regime) {
  if (regime === "POSITIVE" || regime === "POSITIV") return { dot: "green", label: "Positiv Gamma", txt: "Market makers demper volatilitet — markedet er mer stabilt" };
  return { dot: "red", label: "Negativ Gamma", txt: "Market makers forsterker bevegelser — volatilitet kan eksplodere" };
}
function scoreActivity(v) {
  if (v === "VERY HIGH") return { dot: "red", label: "Svært høy", txt: "Uvanlig høy opsjonsaktivitet — noe er i gjære" };
  if (v === "HIGH") return { dot: "yellow", label: "Høy", txt: "Over normalt aktivitetsnivå" };
  return { dot: "green", label: "Normal", txt: "Rolig opsjonsmarked" };
}
function scoreCPR(v) {
  if (v >= 2.5) return { dot: "green", label: "Svært bullish", txt: `${v}x — opsjonsmarkedet er ekstremt optimistisk` };
  if (v >= 1.5) return { dot: "green", label: "Bullish", txt: `${v}x — flest calls, markedet er positivt` };
  if (v >= 1.0) return { dot: "yellow", label: "Nøytralt", txt: `${v}x — balansert mellom bulls og bears` };
  return { dot: "red", label: "Bearish", txt: `${v}x — flest puts, markedet er defensivt` };
}
function scoreERisk(v) {
  if (v === "HIGH") return { dot: "red", label: "Høy risiko", txt: "Stor potensiell kursreaksjon på earnings" };
  if (v === "MEDIUM") return { dot: "yellow", label: "Moderat risiko", txt: "Normal earnings-usikkerhet" };
  return { dot: "green", label: "Lav risiko", txt: "Historisk rolig rundt earnings" };
}
function scoreShortFloat(v) {
  if (v === null || v === undefined) return { dot: "gray", label: "Ukjent", txt: "Ingen short-data tilgjengelig" };
  const pct = v * 100;
  if (pct >= 20) return { dot: "red",    label: `${pct.toFixed(1)}%`, txt: `${pct.toFixed(1)}% av float er shortet — ekstremt press, men også squeeze-kandidat` };
  if (pct >= 10) return { dot: "orange", label: `${pct.toFixed(1)}%`, txt: `${pct.toFixed(1)}% av float er shortet — høyt bearish sentiment` };
  if (pct >= 5)  return { dot: "yellow", label: `${pct.toFixed(1)}%`, txt: `${pct.toFixed(1)}% av float er shortet — moderat press fra shorts` };
  return { dot: "green", label: `${pct.toFixed(1)}%`, txt: `${pct.toFixed(1)}% av float er shortet — lavt short-press, liten motvind` };
}
function scoreShortRatio(v) {
  if (v === null || v === undefined) return { dot: "gray", label: "Ukjent", txt: "Ingen days-to-cover data" };
  if (v >= 7) return { dot: "red",    label: `${v.toFixed(1)} d`, txt: `${v.toFixed(1)} dager å dekke — shorts sitter fast, høy squeeze-risiko` };
  if (v >= 3) return { dot: "yellow", label: `${v.toFixed(1)} d`, txt: `${v.toFixed(1)} dager å dekke — moderat squeeze-potensial` };
  return { dot: "green", label: `${v.toFixed(1)} d`, txt: `${v.toFixed(1)} dager å dekke — shorts kan raskt ut, lavt squeeze-potensial` };
}

function calcRSI(prices, period = 14) {
  if (!prices || prices.length < period + 1) return null;
  // Wilder's smoothed RSI (industry standard used by TradingView, Yahoo, Google)
  let avgGain = 0, avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const diff = prices[i] - prices[i - 1];
    if (diff > 0) avgGain += diff; else avgLoss -= diff;
  }
  avgGain /= period;
  avgLoss /= period;
  for (let i = period + 1; i < prices.length; i++) {
    const diff = prices[i] - prices[i - 1];
    avgGain = (avgGain * (period - 1) + (diff > 0 ? diff : 0)) / period;
    avgLoss = (avgLoss * (period - 1) + (diff < 0 ? -diff : 0)) / period;
  }
  if (avgLoss === 0) return 100;
  return Math.round(100 - 100 / (1 + avgGain / avgLoss));
}

function calcBollinger(prices, period = 20) {
  if (!prices || prices.length < period) return null;
  const slice = prices.slice(-period);
  const avg = slice.reduce((a, b) => a + b, 0) / period;
  const std = Math.sqrt(slice.reduce((s, p) => s + Math.pow(p - avg, 2), 0) / period);
  const bandWidth = avg > 0 ? (std * 4 / avg) * 100 : 0;
  const price = prices[prices.length - 1];
  const upper = avg + 2 * std, lower = avg - 2 * std;
  const squeeze = bandWidth < 8;
  const position = upper > lower ? ((price - lower) / (upper - lower)) * 100 : 50;
  return { avg: Math.round(avg * 100) / 100, bandWidth: Math.round(bandWidth * 10) / 10, squeeze, upper, lower, position: Math.round(position) };
}

function calcMA(prices, period) {
  if (!prices || prices.length < period) return null;
  return prices.slice(-period).reduce((a, b) => a + b, 0) / period;
}
function calcMALine(prices, period) {
  return prices.map((_, i) => {
    if (i < period - 1) return null;
    const win = prices.slice(i - period + 1, i + 1);
    if (win.some(v => v == null)) return null;
    return win.reduce((a, b) => a + b, 0) / period;
  });
}
function calcEMALine(prices, period) {
  const out = new Array(prices.length).fill(null);
  const seedVals = [];
  let ema = null;
  for (let i = 0; i < prices.length; i++) {
    if (prices[i] == null) continue;
    if (ema === null) {
      seedVals.push(prices[i]);
      if (seedVals.length === period) {
        ema = seedVals.reduce((a, b) => a + b, 0) / period;
        out[i] = ema;
      }
    } else {
      ema = prices[i] * (2 / (period + 1)) + ema * (1 - 2 / (period + 1));
      out[i] = ema;
    }
  }
  return out;
}


function bsDelta(S, K, T, sigma, isCall = true, r = 0.05) {
  if (T <= 0 || sigma <= 0 || S <= 0 || K <= 0) return null;
  const d1 = (Math.log(S / K) + (r + sigma * sigma / 2) * T) / (sigma * Math.sqrt(T));
  const t = 1 / (1 + 0.2316419 * Math.abs(d1));
  const poly = t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  const n = 1 - (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-d1 * d1 / 2) * poly;
  const nd1 = d1 >= 0 ? n : 1 - n;
  return isCall ? nd1 : nd1 - 1;
}

// Compute live gamma flip from CBOE options data
// Uses all expirations (od.contracts) when available, falls back to nearest expiry
// Returns { gammaFlip: number|null, gammaRegime: "POSITIV"|"NEGATIV"|null }
function calcGammaFlip(od) {
  if (!od?.ok || !od.currentPrice) return { gammaFlip: null, gammaRegime: null };
  const cp = od.currentPrice;
  const now = new Date();
  const gexMap = {};

  // Build unified contract list: prefer all-expiry contracts, fall back to nearest expiry
  const allContracts = od.contracts?.length
    ? od.contracts
    : [
        ...(od.calls || []).map(c => ({ ...c, type: "C", expiration: od.expirationDate })),
        ...(od.puts  || []).map(p => ({ ...p, type: "P", expiration: od.expirationDate })),
      ];

  if (!allContracts.length) return { gammaFlip: null, gammaRegime: null };

  allContracts.forEach(o => {
    if (!(o.openInterest > 0) || !(o.impliedVolatility > 0) || !o.expiration) return;
    const dL = Math.max(1, Math.round((new Date(o.expiration + "T12:00:00") - now) / (1000 * 60 * 60 * 24)));
    const Tv = dL / 365;
    const g = bsGamma(cp, o.strike, Tv, o.impliedVolatility);
    if (!isFinite(g)) return;
    const gex = o.openInterest * g * cp * cp * 0.01;
    gexMap[o.strike] = (gexMap[o.strike] || 0) + (o.type === "C" ? gex : -gex);
  });

  if (!Object.keys(gexMap).length) return { gammaFlip: null, gammaRegime: null };

  const totalGex = Object.values(gexMap).reduce((a, b) => a + b, 0);
  const gammaRegime = totalGex >= 0 ? "POSITIV" : "NEGATIV";
  let gammaFlip = null;
  const sortedS = Object.keys(gexMap).map(Number).sort((a, b) => b - a);
  let cum = 0, prevS = null, prevC = null;
  for (const s of sortedS) {
    const cur = cum + (gexMap[s] || 0);
    if (prevC !== null && ((prevC > 0 && cur <= 0) || (prevC < 0 && cur >= 0))) {
      const ratio = Math.abs(prevC) / (Math.abs(prevC) + Math.abs(cur));
      gammaFlip = +(prevS - ratio * (prevS - s)).toFixed(2);
      break;
    }
    prevS = s; prevC = cur; cum = cur;
  }
  return { gammaFlip, gammaRegime };
}

// Detect gamma squeeze setup from live options data
// Returns null | { strength: "STERK"|"MULIG", conditions: string[], topCall: obj|null }
function detectGammaSqueeze(od) {
  if (!od?.ok || !od.currentPrice) return null;
  const cp = od.currentPrice;
  const calls = od.calls || [];
  const puts  = od.puts  || [];

  const { gammaRegime } = calcGammaFlip(od);
  const negGamma = gammaRegime === "NEGATIV";

  const totalCallVol = calls.reduce((s, c) => s + (c.volume || 0), 0);
  const totalPutVol  = puts.reduce((s,  p) => s + (p.volume  || 0), 0);
  const cpRatio = totalPutVol > 0 ? totalCallVol / totalPutVol : null;
  const bullishCPR = cpRatio !== null && cpRatio > 1.5;

  // Unusual call activity within 15 % of current price
  const nearbyUnusual = calls
    .filter(c => Math.abs(c.strike - cp) / cp < 0.15
              && (c.volume || 0) > 0
              && (c.openInterest || 0) > 0
              && c.volume > c.openInterest * 1.5)
    .sort((a, b) => b.volume - a.volume);
  const hasUnusualCalls = nearbyUnusual.length > 0;

  const flags = [negGamma, bullishCPR, hasUnusualCalls];
  const met   = flags.filter(Boolean).length;

  // Need at least unusual calls + one other condition
  if (!hasUnusualCalls || met < 2) return null;

  const conditions = [];
  if (negGamma)       conditions.push("Negativ gamma regime");
  if (bullishCPR)     conditions.push(`C/P ratio ${cpRatio.toFixed(2)}`);
  if (hasUnusualCalls) conditions.push(`Uvanlig call-kjøp nær ATM`);

  return {
    strength:  met === 3 ? "STERK" : "MULIG",
    conditions,
    topCall:   nearbyUnusual[0] || null,
    cpRatio,
  };
}

function calcVolumeSignal(volumes) {
  if (!volumes || volumes.length < 21) return null;
  const valid = volumes.filter(v => v != null && v > 0);
  if (valid.length < 21) return null;
  const today = valid[valid.length - 1];
  const avg20 = valid.slice(-21, -1).reduce((a, b) => a + b, 0) / 20;
  if (avg20 === 0) return null;
  return { today, avg20, ratio: today / avg20 };
}

function getOptionsStrategy(intel, liveD) {
  const prices = liveD?.history || [];
  const volumes = liveD?.volumes || [];
  const rsi = calcRSI(prices);
  const bb = calcBollinger(prices);
  const volSig = calcVolumeSignal(volumes);
  const ema9arr = calcEMALine(prices, 9); const ema9 = ema9arr[ema9arr.length-1] ?? null;
  const ma50 = calcMA(prices, 50);
  const ma200 = calcMA(prices, 200);
  const price = liveD?.price;
  const iv = intel.pf.ivPct;
  const gamma = intel.pf.gammaRegime;
  const earningsDate = liveD?.nextEarnings || (intel.nextEarnings >= nyDay() ? intel.nextEarnings : null);
  const daysToEarnings = earningsDate ? Math.round((new Date(earningsDate) - new Date()) / 86400000) : null;

  const signals = [];
  let strategy = null, strategyColor = "#94a3b8", strategyDesc = "";

  if (rsi !== null) {
    if (rsi < 30) signals.push({ text: `RSI ${rsi} — Oversold`, c: "#10b981" });
    else if (rsi > 70) signals.push({ text: `RSI ${rsi} — Overbought`, c: "#f43f5e" });
    else signals.push({ text: `RSI ${rsi} — Nøytral`, c: "#f59e0b" });
  }
  if (bb) {
    if (bb.squeeze) signals.push({ text: `BB Squeeze — Stor bevegelse nær`, c: "#a78bfa" });
    else signals.push({ text: `BB Width ${bb.bandWidth}%`, c: "#64748b" });
  }
  if (ema9 !== null) {
    const ema20arr2 = calcEMALine(prices, 20); const ema20cur = ema20arr2[ema20arr2.length-1] ?? null;
    if (ema20cur !== null) {
      if (ema9 > ema20cur) signals.push({ text: `EMA9 > EMA20 ▲ Kortsiktig momentum`, c: "#10b981" });
      else signals.push({ text: `EMA9 < EMA20 ▼ Kortsiktig svakhet`, c: "#f43f5e" });
    }
  }
  if (ma50 && ma200 && price) {
    if (ma50 > ma200) signals.push({ text: `Golden Cross ▲`, c: "#10b981" });
    else signals.push({ text: `Death Cross ▼`, c: "#f43f5e" });
  }
  if (iv <= 40) signals.push({ text: `IV ${iv} — Billige opsjoner`, c: "#10b981" });
  else if (iv >= 75) signals.push({ text: `IV ${iv} — Dyre opsjoner`, c: "#f43f5e" });
  if (daysToEarnings !== null && daysToEarnings >= 0 && daysToEarnings <= 21)
    signals.push({ text: `Earnings om ${daysToEarnings}d`, c: "#f59e0b" });
  if (volSig) {
    if (volSig.ratio >= 3) signals.push({ text: `Volum ${volSig.ratio.toFixed(1)}x snitt — Ekstrem spike 🔥`, c: "#f59e0b" });
    else if (volSig.ratio >= 2) signals.push({ text: `Volum ${volSig.ratio.toFixed(1)}x snitt — Uvanlig høyt`, c: "#f59e0b" });
    else if (volSig.ratio >= 1.5) signals.push({ text: `Volum ${volSig.ratio.toFixed(1)}x snitt — Forhøyet`, c: "#94a3b8" });
    else if (volSig.ratio < 0.5) signals.push({ text: `Volum ${volSig.ratio.toFixed(1)}x snitt — Veldig lavt`, c: "#64748b" });
  }

  if (bb?.squeeze && daysToEarnings !== null && daysToEarnings <= 21 && daysToEarnings >= 0) {
    strategy = "BUY STRADDLE"; strategyColor = "#a78bfa";
    strategyDesc = "Squeeze + earnings nær = forvent stor bevegelse. Kjøp både call og put (ATM straddle).";
  } else if (rsi !== null && rsi < 30 && iv <= 55 && gamma === "POSITIVE") {
    strategy = "BUY CALL"; strategyColor = "#10b981";
    const volBoost = volSig && volSig.ratio >= 2 ? ` Volumspike ${volSig.ratio.toFixed(1)}x bekrefter interessen — høy overbevisning.` : "";
    strategyDesc = `Oversold + lav IV + positiv gamma = gunstig for å kjøpe calls. Velg strike 5-10% OTM med 30-60 DTE.${volBoost}`;
  } else if (rsi !== null && rsi > 70 && iv <= 55) {
    strategy = "BUY PUT"; strategyColor = "#f43f5e";
    strategyDesc = "Overbought + lav IV = gunstig for å kjøpe puts. Vær obs på gamma-regime før entry.";
  } else if (iv >= 70 && (daysToEarnings === null || daysToEarnings < 0 || daysToEarnings > 21)) {
    strategy = "SELL PREMIUM"; strategyColor = "#f59e0b";
    strategyDesc = "Høy IV uten nær earnings = selg premium. Iron condor eller cash-secured put kan lønne seg.";
  } else if (bb?.squeeze && iv <= 50) {
    strategy = "BUY OPTIONS"; strategyColor = "#06b6d4";
    strategyDesc = "Bollinger squeeze + lav IV = potensiell eksplosjon. Vurder straddle eller retningsbestemt opsjon.";
  } else if (ma50 && ma200 && ma50 > ma200 && rsi !== null && rsi < 60 && iv <= 55) {
    strategy = "BULL CALL SPREAD"; strategyColor = "#3b82f6";
    strategyDesc = "Bullish trend (golden cross) + moderat IV. Kjøp ATM call, selg OTM call for å redusere kostnad.";
  } else {
    strategy = "VENT"; strategyColor = "#475569";
    strategyDesc = "Ingen klare signaler akkurat nå. Avvent bedre setup eller sjekk kortere/lengre tidsperiode.";
  }

  return { rsi, bb, ema9, ma50, ma200, signals, strategy, strategyColor, strategyDesc, daysToEarnings };
}

function classifySetup(intel, liveD) {
  const prices = liveD?.history || [];
  const rsi = calcRSI(prices);
  const bb = calcBollinger(prices);
  const ma20 = calcMA(prices, 20);
  const ma50 = calcMA(prices, 50);
  const ma200 = calcMA(prices, 200);
  const price = liveD?.price;
  const iv = intel.pf.ivPct;
  const gamma = intel.pf.gammaRegime;
  const earningsDate = liveD?.nextEarnings || (intel.nextEarnings >= nyDay() ? intel.nextEarnings : null);
  const dte = earningsDate ? Math.round((new Date(earningsDate) - new Date()) / 86400000) : null;
  const fromHigh = liveD?.weekHigh52 && price ? ((price - liveD.weekHigh52) / liveD.weekHigh52) * 100 : null;
  const volRatio = liveD?.volume && liveD?.avgVolume && liveD.avgVolume > 0
    ? liveD.volume / liveD.avgVolume : null;

  if (bb?.squeeze && dte !== null && dte >= 5 && dte <= 21 && iv < 62)
    return { type:"EARNINGS PLAY", color:"#f59e0b", icon:"📅", desc:`Squeeze + earnings om ${dte}d + lav IV — kjøp opsjoner nå før IV crush`, score: 95 };
  if (bb?.squeeze && rsi !== null && rsi >= 38 && rsi <= 63 && iv < 58)
    return { type:"SQUEEZE PLAY", color:"#a78bfa", icon:"⚡", desc:"Bollinger squeeze + nøytral RSI + lav IV — stor bevegelse på vei", score: 90 };
  if (volRatio !== null && volRatio >= 2.5 && rsi !== null && rsi >= 45 && rsi <= 70 && liveD?.change > 1.5)
    return { type:"VOLUME BREAKOUT", color:"#f97316", icon:"📊", desc:`Volum ${volRatio.toFixed(1)}x snitt + pris opp ${liveD.change.toFixed(1)}% — institusjoner er inne`, score: 88 };
  if (rsi !== null && rsi < 33 && gamma === "POSITIVE" && iv < 65)
    return { type:"OVERSOLD BOUNCE", color:"#06b6d4", icon:"🎯", desc:`RSI ${rsi} oversold + positiv gamma${volRatio && volRatio >= 2 ? ` + volum ${volRatio.toFixed(1)}x` : ""} — potensiell reversal`, score: volRatio && volRatio >= 2 ? 88 : 85 };
  if (dte !== null && dte >= 3 && dte <= 14 && iv < 52)
    return { type:"EARNINGS SETUP", color:"#f59e0b", icon:"📊", desc:`Earnings om ${dte}d + lav IV — kjøp billig før rapporten`, score: 80 };
  if (rsi !== null && rsi >= 52 && rsi <= 68 && ma20 && ma50 && ma20 > ma50 && price && price > ma50 && gamma === "POSITIVE")
    return { type:"MOMENTUM", color:"#10b981", icon:"🚀", desc:`MA20 (${ma20.toFixed(0)}) > MA50 (${ma50.toFixed(0)}) + RSI ${rsi} + over MA50 + positiv gamma — trendcontinuation`, score: 75 };
  if (fromHigh !== null && fromHigh < -25 && rsi !== null && rsi < 45 && iv < 65)
    return { type:"DEEP VALUE", color:"#3b82f6", icon:"💎", desc:`${Math.abs(fromHigh).toFixed(0)}% under 52u-topp + RSI ${rsi} — potensiell gjeninnhenting`, score: 65 };
  if (rsi !== null && rsi > 72 && gamma === "NEGATIVE")
    return { type:"REVERSAL WATCH", color:"#f43f5e", icon:"⚠️", desc:`RSI ${rsi} overbought + negativ gamma — vurder puts eller ta gevinst`, score: 60 };
  return null;
}

function calcBullishScore(intel, liveD) {
  let s = 0;
  const pf = intel.pf;
  s += pf.gammaRegime === "POSITIVE" ? 20 : 0;
  s += pf.callPutRatio >= 2.5 ? 20 : pf.callPutRatio >= 2.0 ? 15 : pf.callPutRatio >= 1.5 ? 10 : pf.callPutRatio >= 1.0 ? 5 : 0;
  s += pf.ivPct <= 40 ? 15 : pf.ivPct <= 60 ? 10 : pf.ivPct <= 75 ? 5 : 0;
  s += intel.revenueGrowth >= 50 ? 20 : intel.revenueGrowth >= 25 ? 15 : intel.revenueGrowth >= 10 ? 8 : 0;
  s += intel.beatRate >= 85 ? 15 : intel.beatRate >= 75 ? 10 : intel.beatRate >= 65 ? 5 : 0;
  if (liveD?.history?.length > 1) {
    const pct = (liveD.history[liveD.history.length-1] / liveD.history[0] - 1) * 100;
    s += pct > 10 ? 10 : pct > 3 ? 6 : pct > 0 ? 3 : 0;
  }
  return Math.min(s, 100);
}

function getBullishReasons(intel, liveD) {
  const r = [];
  if (intel.pf.gammaRegime === "POSITIVE") r.push({ text: "Positiv gamma", c: "#10b981" });
  if (intel.pf.callPutRatio >= 2.0) r.push({ text: `C/P ${intel.pf.callPutRatio}x`, c: "#10b981" });
  else if (intel.pf.callPutRatio >= 1.5) r.push({ text: `C/P ${intel.pf.callPutRatio}x`, c: "#f59e0b" });
  if (intel.pf.ivPct <= 50) r.push({ text: `IV ${intel.pf.ivPct} lav`, c: "#10b981" });
  if (intel.revenueGrowth >= 25) r.push({ text: `Rev +${intel.revenueGrowth}%`, c: "#3b82f6" });
  if (intel.beatRate >= 85) r.push({ text: `Beat ${intel.beatRate}%`, c: "#3b82f6" });
  if (liveD?.history?.length > 1) {
    const pct = (liveD.history[liveD.history.length-1] / liveD.history[0] - 1) * 100;
    if (pct > 0) r.push({ text: `▲${pct.toFixed(1)}% momentum`, c: "#a78bfa" });
  }
  return r;
}

function getPressureReasons(intel) {
  const r = [];
  if (intel.pf.earningsRisk === "HIGH") r.push({ text: "Earnings risiko HØY", c: "#f43f5e" });
  if (intel.pf.gammaRegime === "NEGATIVE") r.push({ text: "Negativ gamma", c: "#f43f5e" });
  if (intel.pf.ivPct >= 75) r.push({ text: `IV ${intel.pf.ivPct} svært høy`, c: "#f43f5e" });
  if (intel.pf.optionsActivity === "VERY HIGH") r.push({ text: "Opsjoner VERY HIGH", c: "#f43f5e" });
  if (intel.pf.callPutRatio < 1.0) r.push({ text: `C/P ${intel.pf.callPutRatio}x bearish`, c: "#f43f5e" });
  if (intel.pf.ivPct >= 60 && intel.pf.ivPct < 75) r.push({ text: `IV ${intel.pf.ivPct} forhøyet`, c: "#f59e0b" });
  return r;
}

function calcPressure(intel) {
  let s = 0;
  const pf = intel.pf;
  s += pf.earningsRisk === "HIGH" ? 25 : pf.earningsRisk === "MEDIUM" ? 15 : 5;
  s += pf.gammaRegime === "NEGATIVE" ? 20 : 5;
  s += pf.ivPct > 75 ? 20 : pf.ivPct > 50 ? 12 : 5;
  s += pf.optionsActivity === "VERY HIGH" ? 20 : pf.optionsActivity === "HIGH" ? 12 : 5;
  s += pf.callPutRatio > 2.5 ? 5 : pf.callPutRatio < 1.0 ? 20 : 10;
  return Math.min(s, 100);
}

function Dot({ color }) {
  const c = color === "green" ? "#10b981" : color === "yellow" ? "#f59e0b" : "#f43f5e";
  return <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: c, flexShrink: 0, boxShadow: `0 0 5px ${c}90`, marginTop: 1 }} />;
}

function MRow({ label, value, score, hint }) {
  const isMobile = useIsMobile();
  const c = score.dot === "green" ? "#10b981" : score.dot === "yellow" ? "#f59e0b" : "#f43f5e";
  return (
    <div style={{ padding: "9px 14px", borderBottom: "1px solid #0d1e30", display: "grid", gridTemplateColumns: isMobile ? "minmax(0,100px) 1fr auto" : "140px 1fr auto", gap: isMobile ? 6 : 10, alignItems: "start" }}>
      <div style={{ fontSize: isMobile ? 11 : 12, color: "#94a3b8", paddingTop: 1, overflow:"hidden" }}>{label}</div>
      <div style={{minWidth:0}}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0", marginBottom: 2 }}>{value}</div>
        <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.4 }}>{hint}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 5, paddingTop: 2 }}>
        <Dot color={score.dot} />
        <span style={{ fontSize: isMobile ? 10 : 11, color: c, whiteSpace: "nowrap" }}>{score.label}</span>
      </div>
    </div>
  );
}

function PressureGauge({ score }) {
  const color = score >= 70 ? "#f43f5e" : score >= 45 ? "#f59e0b" : "#10b981";
  const label = score >= 70 ? "HØY SPENNING" : score >= 45 ? "MODERAT" : "LAV SPENNING";
  const desc = score >= 70
    ? "Markedet priser inn stor usikkerhet. Vær forberedt på volatilitet og store kurssvingninger."
    : score >= 45
    ? "Noe forhøyet markedspress. Følg med på nyheter og viktige datapunkter."
    : "Rolig markedsmiljø akkurat nå. Lavt stress-nivå i opsjonsmarkedet.";
  return (
    <div style={{ background: "#060f1c", border: `1px solid ${color}30`, borderRadius: 8, padding: 16, textAlign: "center" }}>
      <div style={{ fontSize: 9, color: "#475569", letterSpacing: 1, marginBottom: 8 }}>🔥 PRESSURE SCORE</div>
      <div style={{ fontSize: 52, fontWeight: 700, color, lineHeight: 1 }}>{score}</div>
      <div style={{ fontSize: 10, color: "#334155", marginBottom: 10 }}>/100</div>
      <div style={{ height: 6, background: "#0d1e30", borderRadius: 3, marginBottom: 10, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${score}%`, background: `linear-gradient(90deg,#10b981,${color})`, borderRadius: 3 }} />
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 5, letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 10, color: "#64748b", lineHeight: 1.5 }}>{desc}</div>
    </div>
  );
}

function SixCards({ ticker, intel, liveD, liveGammaFlip, liveGammaRegime }) {
  const isMobile = useIsMobile();
  const pressure = calcPressure(intel);
  const priceColor = (liveD?.change || 0) >= 0 ? "#10b981" : "#f43f5e";
  const aiColors = { "Meget høy": "#a78bfa", "Høy": "#3b82f6", "Medium": "#f59e0b", "Lav": "#64748b" };
  const displayFlip = liveGammaFlip ?? intel.pf.gammaFlip;
  const isLive = !!liveGammaFlip;
  const livePrice = liveD?.price;
  // Effektivt regime: pris vs flip er mer handlingsbart enn total GEX
  const effectiveRegime = (displayFlip && livePrice)
    ? (livePrice > displayFlip ? "POSITIV" : "NEGATIV")
    : (liveGammaRegime || intel.pf.gammaRegime);
  const displayRegime = effectiveRegime;
  const gColor = (displayRegime === "POSITIVE" || displayRegime === "POSITIV") ? "#10b981" : "#f43f5e";
  const flipRelation = displayFlip && livePrice
    ? (livePrice > displayFlip ? "Over" : "Under")
    : null;
  const pColor = pressure >= 70 ? "#f43f5e" : pressure >= 45 ? "#f59e0b" : "#10b981";

  const earningsDate = liveD?.nextEarnings || (intel.nextEarnings >= nyDay() ? intel.nextEarnings : null);
  const daysToEarnings = () => {
    if (!earningsDate) return "—";
    const diff = Math.round((new Date(earningsDate) - new Date()) / 86400000);
    if (diff < 0) return "Passert";
    if (diff === 0) return "I DAG!";
    return `${diff}d`;
  };

  const cards = [
    { label: "PRIS", val: liveD ? `$${liveD.price < 10 ? liveD.price.toFixed(2) : liveD.price.toFixed(1)}` : "…", sub: liveD ? `${liveD.change >= 0 ? "+" : ""}${liveD.change.toFixed(2)}% i dag` : "henter…", color: priceColor },
    { label: "AI SCORE", val: intel.aiExposure, sub: "AI-eksponering", color: aiColors[intel.aiExposure] || "#64748b" },
    { label: "NESTE EVENT", val: daysToEarnings(), sub: earningsDate ? `Earnings ${earningsDate}${liveD?.nextEarnings ? " ↻" : ""}` : "Dato ikke satt", color: "#f59e0b" },
    { label: "EXPECTED MOVE", val: `±${intel.expectedMove}%`, sub: `Snitt: ±${intel.avgMove}%`, color: "#06b6d4" },
    { label: "GAMMA REGIME", val: displayRegime, sub: flipRelation ? `${flipRelation} flip $${displayFlip}${isLive?" ↻":""}` : `Flip $${displayFlip||"—"}${isLive?" ↻":""}`, color: gColor },
    { label: "PRESSURE", val: `${pressure}/100`, sub: pressure >= 70 ? "Høy spenning" : pressure >= 45 ? "Moderat" : "Rolig", color: pColor },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile?"repeat(2,1fr)":"repeat(6,1fr)", gap: 8, marginBottom: 20 }}>
      {cards.map((c, i) => (
        <div key={i} style={{ background: "#060f1c", border: `1px solid ${c.color}20`, borderTop: `2px solid ${c.color}`, borderRadius: "0 0 6px 6px", padding: "10px 10px 8px" }}>
          <div style={{ fontSize: 8, color: "#334155", letterSpacing: 1, marginBottom: 5 }}>{c.label}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.color, marginBottom: 3 }}>{c.val}</div>
          <div style={{ fontSize: 9, color: "#475569", lineHeight: 1.3 }}>{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

// ── INSTITUSJONELLE MØNSTER ──────────────────────────────────────────────────


function calcSignalScore(patterns) {
  if (!patterns?.length) return 0;
  const cutoff = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const recent = patterns.filter(p => p.date >= cutoff && p.date <= today);
  const uniqueTypes = new Set(recent.map(p => p.type)).size;
  if (uniqueTypes === 0) return 0;
  if (uniqueTypes === 1) return 2;
  if (uniqueTypes === 2) return 3;
  if (uniqueTypes === 3) return 4;
  return 5;
}

function detectGoldenSetups(patterns, closes) {
  if (!patterns?.length || !closes?.length) return [];
  const cutoff = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const recent = patterns.filter(p => p.date >= cutoff && p.date <= today);
  const types = new Set(recent.map(p => p.type));
  const n = closes.length;
  const lastClose = closes[n - 1];
  const ma50 = n >= 50 ? closes.slice(-50).reduce((s, x) => s + x, 0) / 50 : null;
  const ma200 = n >= 200 ? closes.slice(-200).reduce((s, x) => s + x, 0) / 200 : null;
  const nearMA50 = ma50 && Math.abs(lastClose - ma50) / ma50 < 0.04;
  const nearMA200 = ma200 && Math.abs(lastClose - ma200) / ma200 < 0.05;
  const hasStrongRvolUp = recent.some(p => p.type === "HIGH_RVOL_UP" && p.rvol >= 2.0);
  const golden = [];

  if (types.has("LIQUIDITY_SWEEP") && types.has("HIGH_RVOL_UP") && nearMA50)
    golden.push({ name:"Institutional Sweep", emoji:"🎯", color:"#c084fc", signals:["LIKVIDITETSJAKT","RVOL↑","Nær MA50"], desc:"Algoritmer jaget stop-losses under MA50 mens institusjoner kjøpte. Høyeste treffsikkerhet.", confidence:"high" });

  if (types.has("SHOOTING_STAR") && (types.has("HIGH_RVOL_DOWN") || types.has("HIGH_RVOL_UP")) && (nearMA200 || (ma200 && lastClose > ma200)))
    golden.push({ name:"Distribution Reversal", emoji:"⭐", color:"#f43f5e", signals:["SHOOTING STAR", types.has("HIGH_RVOL_DOWN")?"RVOL↓":"RVOL↑", nearMA200?"Nær MA200":"Over MA200"], desc:"Selgerne knuste kjøpernes rally ved motstand på høyt volum. Put/short-signal.", confidence:"high" });

  if (types.has("ACCUMULATION") && hasStrongRvolUp)
    golden.push({ name:"Wyckoff Launchpad", emoji:"🚀", color:"#f59e0b", signals:["AKKUMULERING","RVOL↑ ≥2.0x"], desc:"15+ dager med sidelengs konsolidering etterfulgt av breakout på høy RVOL. Optimalt R/R.", confidence:"high" });

  if (types.has("BULL_TRAP") && (nearMA50 || nearMA200))
    golden.push({ name:"Fakeout Warning", emoji:"⚠️", color:"#f97316", signals:["BULL TRAP", nearMA50?"Nær MA50":"Nær MA200"], desc:"Pris steg svakt på lav RVOL inn i MA-motstand. Unngå FOMO-kjøp — vurder dekket call.", confidence:"moderate" });

  return golden;
}


// ─────────────────────────────────────────────────────────────────────────────

const SIGNAL_INFO = {
  SHOOTING_STAR:    { name:"Shooting Star ⭐", bias:"bearish", color:"#f43f5e", summary:"Prisen steg kraftig men falt tilbake til åpning. Selgerne tok over.", detail:"Advarsel om kortsiktig reversal. Sterkest nær motstand (MA50/200) og på høyt volum. Bekreft med rød dag etterpå.", timeframe:"1–5 dager", action:"Vurder å ta gevinst, selge calls, eller kjøpe puts for sikring." },
  LIQUIDITY_SWEEP:  { name:"Likviditetsjakt 🎯", bias:"bullish", color:"#c084fc", summary:"Institusjonelle aktører slo ut stop-losses under støtte, deretter kjøpte aggressivt.", detail:"Prisen faller under støttesone, utløser stop-losses, og reverserer raskt. Smart money samler aksjer billig.", timeframe:"2–10 dager", action:"Bullish setup. Bekreft med grønn dag og volum. Entry nær støttesonen." },
  REJECTION_WICK:   { name:"Rejection Wick ↩", bias:"bullish", color:"#38bdf8", summary:"Lang nedre hale viser sterk kjøpsinteresse ved støttenivå.", detail:"Selgerne prøvde å presse prisen ned, men kjøperne tok over kraftig. Dannes gjerne ved MA-er.", timeframe:"1–5 dager", action:"Bullish signal ved støtte. Se etter grønn dag og volum som bekreftelse." },
  HIGH_RVOL_UP:     { name:"Høyt RVOL Opp", bias:"bullish", color:"#f59e0b", summary:"Uvanlig høyt volum på en oppgangsdag — institusjonell interesse.", detail:"Volum 1.5×+ over 20-dagers snitt på grønn dag. Indikerer at store aktører kjøper.", timeframe:"3–10 dager", action:"Momentum-signal. Følg trenden, sett stop under dagens lavpunkt." },
  HIGH_RVOL_DOWN:   { name:"Høyt RVOL Ned", bias:"bearish", color:"#f43f5e", summary:"Uvanlig høyt volum på en nedgangsdag — distribusjon.", detail:"Volum 1.5×+ over snitt på rød dag. Store aktører selger. Potensielt trendskifte.", timeframe:"3–10 dager", action:"Bearish signal. Vurder å redusere eksponering eller kjøpe puts." },
  MA_BOUNCE_EMA20:  { name:"EMA20-sprett", bias:"bullish", color:"#34d399", summary:"Aksjen sprettet opp fra EMA20 — kortsiktig støtte holder.", detail:"EMA20 fungerer som dynamisk støtte i uptrend. Bounce herfra er typisk entry-punkt for momentum-tradere.", timeframe:"3–10 dager", action:"Bullish i uptrend. Entry ved EMA20, stop under." },
  MA_BOUNCE_MA50:   { name:"MA50-sprett", bias:"bullish", color:"#10b981", summary:"Aksjen sprettet fra MA50 — viktig mellomlang støtte holder.", detail:"MA50 er en kritisk støttelinje. Bounce herfra med volum er et sterkere signal enn EMA20.", timeframe:"5–20 dager", action:"Bullish. God risk/reward ved entry her med MA50 som gulv." },
  ACCUMULATION:     { name:"Akkumulering 📦", bias:"bullish", color:"#fb923c", summary:"Lav volatilitet + høyt volum — institusjonell oppsamling.", detail:"Wyckoff-konsept: store aktører kjøper stille uten å drive prisen opp. Typisk forløper til breakout.", timeframe:"1–4 uker", action:"Sett alert på breakout over motstand. Vurder early entry med liten posisjon." },
  HAMMER:           { name:"Hammer 🔨", bias:"bullish", color:"#10b981", summary:"Lang nedre hale etter nedgang — reversal-signal.", detail:"Prisen falt kraftig under åpning men kjøperne tok over. Bullish reversal, spesielt etter en downtrend.", timeframe:"1–5 dager", action:"Bullish reversal. Bekreft med grønn dag etterpå." },
  BULL_TRAP:        { name:"Bull Trap ⚠️", bias:"bearish", color:"#f97316", summary:"Falsk breakout over motstand — prisen falt raskt tilbake.", detail:"Aksjen brøt over MA-motstand men lukket under. Kjøpere som gikk inn på breakout sitter fast.", timeframe:"1–5 dager", action:"Bearish. Unngå kjøp. Potensielt put-setup eller short." },
  BREAKOUT_20D:     { name:"20-dagers breakout", bias:"bullish", color:"#22d3ee", summary:"Ny 20-dagers høy med høyt volum — momentum-breakout.", detail:"Prisen brøt ut over 20-dagers high med RVOL ≥ 1.5×. Klassisk Turtle Trading-signal fra Richard Dennis.", timeframe:"1–4 uker", action:"Momentum-kjøp. Stop under breakout-nivå." },
  RSI_DIV_BULL:     { name:"RSI-divergens ↗", bias:"bullish", color:"#f472b6", summary:"Prisen lagde lavere bunn men RSI lagde høyere bunn — skjult styrke.", detail:"Bullish divergens: selgerne presser prisen ned men kjøperkraften øker. Ofte forløper til reversal.", timeframe:"3–15 dager", action:"Bullish reversal-signal. Bekreft med grønn dag og RSI over 40." },
};

function LWDeepChart({ closes, timestamps, volumes, opens, highs, lows, ticker, isMobile, trades = [], prediction = null, predStats = null }) {
  const containerRef = useRef();
  const chartRef = useRef(null);
  const markersPluginRef = useRef(null);
  const candleMarkersPluginRef = useRef(null);
  const priceLineRefs = useRef([]);
  const predLineRefs = useRef([]);
  const volTooltipRef = useRef();
  const volDataRef = useRef(new Map());
  const chartHeight = isMobile ? Math.round(window.innerHeight * 0.45) : 500;
  const [period, setPeriod] = useState("3M");
  const [chartType, setChartType] = useState("area"); // "area" | "candle"
  const [showPatterns, setShowPatterns] = useState(false);
  const [showSR, setShowSR] = useState(false);
  const [showMA, setShowMA] = useState({ ema9:true, ema20:true, ma50:true, ma200:false, vol:true });
  const [intraDayData, setIntraDayData] = useState(null);
  const [intraDayLoading, setIntraDayLoading] = useState(false);
  const [subInterval, setSubInterval] = useState(null); // "15m" | "1h" | null
  const [subIntervalData, setSubIntervalData] = useState(null);
  const [subIntervalLoading, setSubIntervalLoading] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const patternsRef = useRef([]);
  const [showAVWAP, setShowAVWAP] = useState(false);
  const [avwapAnchorDate, setAvwapAnchorDate] = useState(null); // null = auto (highest vol day)
  const showAVWAPRef = useRef(false);
  showAVWAPRef.current = showAVWAP;
  const [showVP, setShowVP] = useState(false);
  const [vpInfo, setVpInfo] = useState(null);
  const vpCanvasRef = useRef(null);
  const vpDataRef = useRef(null);
  const vpLineRefs = useRef([]);
  const showVPRef = useRef(false);
  showVPRef.current = showVP;
  const drawVPFnRef = useRef(() => {});
  const [riskAccount, setRiskAccount] = useState(() => { try { return localStorage.getItem("riskAccount") || "100000"; } catch { return "100000"; } });
  const [riskPct, setRiskPct] = useState(() => { try { return localStorage.getItem("riskPct") || "1"; } catch { return "1"; } });
  const [riskEntry, setRiskEntry] = useState("");
  const [riskStop, setRiskStop] = useState("");
  const [riskTarget, setRiskTarget] = useState("");

  const PERIODS = [
    {label:"D",days:0},{label:"1U",days:5},{label:"1M",days:21},{label:"3M",days:63},
    {label:"6M",days:126},{label:"1Å",days:252},
  ];
  const MA_CFG = [
    ["ema9","EMA 9","#c084fc"],["ema20","EMA 20","#38bdf8"],
    ["ma50","MA 50","#f59e0b"],["ma200","MA 200","#facc15"],
    ["vol","Volum","#64748b"],
  ];

  useEffect(() => { try { localStorage.setItem("riskAccount", riskAccount); } catch {} }, [riskAccount]);
  useEffect(() => { try { localStorage.setItem("riskPct", riskPct); } catch {} }, [riskPct]);
  useEffect(() => {
    const last = closes?.[closes.length - 1];
    if (last != null) setRiskEntry(last.toFixed(2));
  }, [closes, ticker]);

  const riskCalc = (() => {
    const entry = parseFloat(riskEntry);
    const stop = parseFloat(riskStop);
    const target = parseFloat(riskTarget);
    const acc = parseFloat(riskAccount) || 0;
    const pct = parseFloat(riskPct) || 0;
    if (!entry || !stop || entry <= 0 || stop <= 0 || Math.abs(entry - stop) < 0.0001) return null;
    const dollarRisk = acc * pct / 100;
    const riskPerShare = Math.abs(entry - stop);
    const shares = Math.floor(dollarRisk / riskPerShare);
    if (shares <= 0) return null;
    const posSize = shares * entry;
    const rr = !isNaN(target) && target > 0 && Math.abs(target - entry) > 0.0001
      ? Math.abs(target - entry) / riskPerShare : null;
    return { dollarRisk, shares, posSize, rr, riskPerShare };
  })();

  // VP draw — reads from refs so safe to call from subscriptions
  const doDrawVP = () => {
    const canvas = vpCanvasRef.current;
    const vp = vpDataRef.current;
    if (!canvas || !showVPRef.current) {
      canvas?.getContext("2d")?.clearRect(0, 0, canvas?.width || 0, canvas?.height || 0);
      return;
    }
    if (!vp || !chartRef.current) return;
    const { area } = chartRef.current;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const maxBarW = W - 1;
    vp.buckets.forEach(({ price, vol }) => {
      // Hver stolpe dekker hele prisintervallet sitt, så profilen henger sammen uansett zoom
      const yTop = area.priceToCoordinate(price + vp.bucketSize / 2), yBot = area.priceToCoordinate(price - vp.bucketSize / 2);
      if (yTop == null || yBot == null || yBot < 0 || yTop > H) return;
      const barW = (vol / vp.maxVol) * maxBarW;
      const isPOC = Math.abs(price - vp.poc) <= vp.bucketSize * 0.6;
      const isVA = price >= vp.val && price <= vp.vah;
      ctx.fillStyle = isPOC ? "#fbbf24dd" : isVA ? "#3b82f650" : "#94a3b828";
      ctx.fillRect(W - barW, yTop, barW, Math.max(1, yBot - yTop - 0.5));
    });
  };
  drawVPFnRef.current = doDrawVP;

  // Hent intraday (5m) data når D-perioden er valgt
  useEffect(() => {
    if (period !== "D" || !ticker) return;
    setIntraDayData(null);
    setIntraDayLoading(true);
    const isProxy = window.location.hostname !== "localhost";
    const yUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${toYahooTicker(ticker)}?range=1d&interval=5m`;
    const proxies = isProxy
      ? [`/yahoo?url=${encodeURIComponent(yUrl)}`]
      : [`https://corsproxy.io/?url=${encodeURIComponent(yUrl)}`, `https://api.allorigins.win/raw?url=${encodeURIComponent(yUrl)}`];
    let cancelled = false;
    (async () => {
      for (const proxy of proxies) {
        try {
          const res = await fetch(proxy, { signal: AbortSignal.timeout(8000) });
          if (!res.ok) continue;
          const data = await res.json();
          const r = data?.chart?.result?.[0];
          if (!r || cancelled) break;
          const q = r.indicators?.quote?.[0] || {};
          setIntraDayData({
            closes: q.close?.map(v => v ?? null) || [],
            opens:  q.open?.map(v => v ?? null)  || [],
            highs:  q.high?.map(v => v ?? null)  || [],
            lows:   q.low?.map(v => v ?? null)   || [],
            volumes: q.volume || [],
            timestamps: r.timestamp || [],
          });
          break;
        } catch(_) {}
      }
      if (!cancelled) setIntraDayLoading(false);
    })();
    return () => { cancelled = true; };
  }, [period, ticker]);

  // Hent sub-interval data (15m eller 1h) for 1U/1M perioder
  useEffect(() => {
    if (!subInterval || !ticker) { setSubIntervalData(null); return; }
    setSubIntervalLoading(true);
    const periodCfg = {
      "D":  { "15m":{ range:"2d",  interval:"15m" }, "1h":{ range:"5d",  interval:"60m" } },
      "1U": { "15m":{ range:"5d",  interval:"15m" }, "1h":{ range:"5d",  interval:"60m" } },
      "1M": { "15m":{ range:"1mo", interval:"30m" }, "1h":{ range:"1mo", interval:"60m" } },
      "3M": { "1h":{ range:"3mo", interval:"60m" } },
      "6M": { "1h":{ range:"6mo", interval:"60m" } },
      "1Å": { "1h":{ range:"1y",  interval:"60m" } },
    };
    const cfg = periodCfg[period]?.[subInterval];
    if (!cfg) return;
    const isProxy = window.location.hostname !== "localhost";
    const yUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${toYahooTicker(ticker)}?range=${cfg.range}&interval=${cfg.interval}`;
    const proxies = isProxy
      ? [`/yahoo?url=${encodeURIComponent(yUrl)}`]
      : [`https://corsproxy.io/?url=${encodeURIComponent(yUrl)}`, `https://api.allorigins.win/raw?url=${encodeURIComponent(yUrl)}`];
    let cancelled = false;
    (async () => {
      for (const proxy of proxies) {
        try {
          const res = await fetch(proxy, { signal: AbortSignal.timeout(10000) });
          if (!res.ok) continue;
          const data = await res.json();
          const r = data?.chart?.result?.[0];
          if (!r || cancelled) break;
          const q = r.indicators?.quote?.[0] || {};
          setSubIntervalData({
            closes: q.close?.map(v => v ?? null) || [],
            opens:  q.open?.map(v => v ?? null)  || [],
            highs:  q.high?.map(v => v ?? null)  || [],
            lows:   q.low?.map(v => v ?? null)   || [],
            volumes: q.volume || [],
            timestamps: r.timestamp || [],
          });
          break;
        } catch(_) {}
      }
      if (!cancelled) setSubIntervalLoading(false);
    })();
    return () => { cancelled = true; };
  }, [subInterval, ticker]);

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout:{ background:{color:"#060f1c"}, textColor:"#94a3b8" },
      grid:{ vertLines:{color:"#1e293b"}, horzLines:{color:"#1e293b"} },
      crosshair:{ mode:1 },
      localization:{ timeFormatter: ts => {
        if (typeof ts === "object" && ts !== null && "year" in ts)
          return new Date(ts.year, ts.month-1, ts.day).toLocaleDateString("nb-NO", {day:"numeric",month:"short",year:"numeric"});
        if (typeof ts === "string")
          return new Date(ts + "T12:00:00").toLocaleDateString("nb-NO", {day:"numeric",month:"short",year:"numeric"});
        const d = new Date(ts * 1000);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleTimeString("nb-NO", {hour:"2-digit",minute:"2-digit"});
      }},
      timeScale:{ borderColor:"#1e293b", timeVisible:false },
      rightPriceScale:{ borderColor:"#1e293b", scaleMargins:{ top:0.05, bottom:0.25 } },
      width: containerRef.current.clientWidth,
      height: chartHeight,
      handleScroll:{ mouseWheel:false },
      handleScale:{ mouseWheel:false, pinch:false },
    });
    const area  = chart.addSeries(AreaSeries, { lineColor:"#10b981", topColor:"#10b98120", bottomColor:"transparent", lineWidth:2, priceLineVisible:false, lastValueVisible:true });
    const candle = chart.addSeries(CandlestickSeries, { upColor:"#10b981", downColor:"#f43f5e", borderUpColor:"#10b981", borderDownColor:"#f43f5e", wickUpColor:"#10b981", wickDownColor:"#f43f5e", priceLineVisible:false, lastValueVisible:true, visible:false });
    const ema9s  = chart.addSeries(LineSeries, { color:"#c084fc", lineWidth:1, priceLineVisible:false, lastValueVisible:false });
    const ema20s = chart.addSeries(LineSeries, { color:"#38bdf8", lineWidth:1, priceLineVisible:false, lastValueVisible:false });
    const ma50s  = chart.addSeries(LineSeries, { color:"#f59e0b", lineWidth:1, priceLineVisible:false, lastValueVisible:false });
    const ma200s = chart.addSeries(LineSeries, { color:"#facc15", lineWidth:1, priceLineVisible:false, lastValueVisible:false });
    // egen VWAP (klikk på en dato) + faste ankere: årets bunn, årets topp, siste store kurshopp
    const avwapS = chart.addSeries(LineSeries, { color:"#c084fc", lineWidth:2, lineStyle:LineStyle.Dotted, priceLineVisible:false, lastValueVisible:true, visible:false, title:"VWAP egen" });
    const vwapFixed = ["low","high","gap"].map(key => ({ key, s: chart.addSeries(LineSeries, {
      color: key==="low"?"#10b981":key==="high"?"#f43f5e":"#fb923c", lineWidth: key==="low"?2:1, lineStyle: key==="gap"?LineStyle.Dashed:LineStyle.Solid,
      priceLineVisible:false, lastValueVisible:true, visible:false, title: key==="low"?"VWAP bunn":key==="high"?"VWAP topp":"VWAP hopp" }) }));
    const volS   = chart.addSeries(HistogramSeries, { priceFormat:{type:"volume"}, priceScaleId:"vol", lastValueVisible:false, priceLineVisible:false });
    chart.priceScale("vol").applyOptions({ scaleMargins:{ top:0.8, bottom:0 }, visible:false });
    const projS  = chart.addSeries(LineSeries, { color:"#a78bfa", lineWidth:2, lineStyle:LineStyle.Dashed, priceLineVisible:false, lastValueVisible:true, visible:false, title:"Pred." });
    markersPluginRef.current = createSeriesMarkers(area, []);
    candleMarkersPluginRef.current = createSeriesMarkers(candle, []);
    chartRef.current = { chart, area, candle, ema9s, ema20s, ma50s, ma200s, avwapS, vwapFixed, volS, projS };
    chart.subscribeCrosshairMove(param => {
      const el = volTooltipRef.current;
      if (!el) return;
      if (!param.time) { el.textContent = ""; return; }
      // 1) Scan seriesData for histogram entry (has .color, others don't)
      let v = 0;
      try {
        for (const [, d] of (param.seriesData || [])) {
          if (d && d.value > 0 && "color" in d) { v = d.value; break; }
        }
      } catch(_) {}
      // 2) Fallback: direct Map lookup by date string
      if (!v) {
        const t = param.time;
        const ts = typeof t === "string" ? t
          : typeof t === "number" ? new Date(t * 1000).toISOString().slice(0, 10)
          : (t && typeof t === "object") ? `${t.year}-${String(t.month).padStart(2,"0")}-${String(t.day).padStart(2,"0")}` : "";
        if (ts) v = volDataRef.current.get(ts) ?? 0;
      }
      el.textContent = v > 0
        ? `Vol: ${v >= 1e9 ? (v/1e9).toFixed(2)+"B" : v >= 1e6 ? (v/1e6).toFixed(2)+"M" : v >= 1e3 ? (v/1e3).toFixed(0)+"K" : v.toFixed(0)}`
        : "";
    });
    chart.subscribeClick(param => {
      if (!param.time) { setSelectedPattern(null); return; }
      const t = param.time;
      const dateStr = typeof t === "string" ? t
        : typeof t === "number" ? new Date(t*1000).toISOString().slice(0,10)
        : (t && typeof t === "object") ? `${t.year}-${String(t.month).padStart(2,"0")}-${String(t.day).padStart(2,"0")}` : "";
      if (!dateStr) return;
      if (showAVWAPRef.current) {
        setAvwapAnchorDate(dateStr);
        return;
      }
      const hits = patternsRef.current.filter(p => p.date === dateStr);
      setSelectedPattern(hits.length > 0 ? hits : null);
    });
    const ro = new ResizeObserver(e => { if(e[0]) { chart.applyOptions({width:e[0].contentRect.width}); drawVPFnRef.current(); } });
    ro.observe(containerRef.current);
    chart.timeScale().subscribeVisibleLogicalRangeChange(() => { drawVPFnRef.current(); });
    containerRef.current.addEventListener("pointermove", () => { drawVPFnRef.current(); });
    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; };
  }, []);

  useEffect(() => {
    const isIntraDay = period === "D";
    const hasSubInterval = !!subInterval && !!subIntervalData;
    const showingIntraday = isIntraDay || hasSubInterval;
    const src = isIntraDay ? intraDayData
              : hasSubInterval ? subIntervalData
              : (closes?.length ? { closes, timestamps, volumes, opens, highs, lows } : null);
    if (!chartRef.current || !src?.closes?.length || !src?.timestamps?.length) return;
    const { chart, area, candle, ema9s, ema20s, ma50s, ma200s, volS } = chartRef.current;

    // Intraday/sub-interval: vis klokkeslett på x-aksen
    const fmtDate = ts => {
      if (typeof ts === "object" && ts !== null && "year" in ts)
        return new Date(ts.year, ts.month-1, ts.day).toLocaleDateString("nb-NO", {day:"numeric",month:"short",year:"numeric"});
      if (typeof ts === "string")
        return new Date(ts + "T12:00:00").toLocaleDateString("nb-NO", {day:"numeric",month:"short",year:"numeric"});
      return new Date(ts * 1000).toLocaleDateString("nb-NO", {day:"numeric",month:"short",year:"numeric"});
    };
    chart.applyOptions({
      timeScale: { timeVisible: showingIntraday, secondsVisible: false, borderColor:"#1e293b" },
      localization: { timeFormatter: showingIntraday
        ? ts => new Date(ts * 1000).toLocaleTimeString("nb-NO", {hour:"2-digit",minute:"2-digit"})
        : fmtDate
      },
    });

    const srcCloses = src.closes, srcTs = src.timestamps, srcVols = src.volumes || [];
    const srcOpens = src.opens || [], srcHighs = src.highs || [], srcLows = src.lows || [];

    const dp = PERIODS.find(p=>p.label===period)||PERIODS[2];
    const n = showingIntraday ? srcCloses.length : Math.min(dp.days, srcCloses.length);
    const startIdx = srcCloses.length - n;
    const sliceTs = srcTs.slice(-n);
    const sliceC  = srcCloses.slice(-n);
    const sliceO  = srcOpens.slice(-n);
    const sliceH  = srcHighs.slice(-n);
    const sliceL  = srcLows.slice(-n);
    const sliceV  = srcVols.slice(-n);

    const toDate = ts => showingIntraday ? ts : new Date(ts*1000).toISOString().slice(0,10);
    const dedupe = (arr) => { const s=new Set(); return arr.filter(d=>d&&!s.has(d.time)&&s.add(d.time)); };

    // Show/hide series based on chartType
    area.applyOptions({ visible: chartType === "area" });
    candle.applyOptions({ visible: chartType === "candle" });

    // Area series
    const priceData = dedupe(sliceC.map((v,i)=>v!=null?{time:toDate(sliceTs[i]),value:v}:null).filter(Boolean));
    const pct = priceData.length>=2 ? priceData[priceData.length-1].value/priceData[0].value-1 : 0;
    const lc = pct>=0?"#10b981":"#f43f5e";
    area.applyOptions({lineColor:lc, topColor:lc+"20"});
    area.setData(priceData);

    // Candlestick series
    const candleData = dedupe(sliceC.map((c,i)=> c!=null ? {
      time:toDate(sliceTs[i]),
      open: sliceO[i]??c, high: sliceH[i]??c, low: sliceL[i]??c, close: c
    } : null).filter(Boolean));
    candle.setData(candleData);

    // MA-er deaktiveres på intraday/sub-interval (daglige MAs gir ikke mening på 15m/1h)
    const maData = (vals) => dedupe(vals.slice(-n).map((v,i)=>v!=null?{time:toDate(sliceTs[i]),value:v}:null).filter(Boolean));
    if (!showingIntraday && closes?.length) {
      const allEma9  = calcEMALine(closes,9);
      const allEma20 = calcEMALine(closes,20);
      const allMa50  = calcMALine(closes,50);
      const allMa200 = calcMALine(closes,200);
      ema9s.setData( showMA.ema9  ? maData(allEma9.slice(startIdx))  : []);
      ema20s.setData(showMA.ema20 ? maData(allEma20.slice(startIdx)) : []);
      ma50s.setData( showMA.ma50  ? maData(allMa50.slice(startIdx))  : []);
      ma200s.setData(showMA.ma200 ? maData(allMa200.slice(startIdx)) : []);
    } else {
      ema9s.setData([]); ema20s.setData([]); ma50s.setData([]); ma200s.setData([]);
    }

    // Volum histogram — RVOL-farget: lys grønn/rød ved høy RVOL
    const allRvol = calcRVOL(volumes||[], 20);
    const rvolSlice = allRvol.slice(-n);
    const volRaw = sliceC.map((c,i)=>{
      const up = i===0||c>=(sliceC[i-1]??c);
      const rv = rvolSlice[i];
      const highVol = rv !== null && rv >= 1.5;
      return { time:toDate(sliceTs[i]), value:sliceV[i]||0, color: up ? (highVol?"#10b981bb":"#10b98150") : (highVol?"#f43f5ebb":"#f43f5e50") };
    });
    const dedupedVol = dedupe(volRaw);
    const volMap = new Map();
    if (showMA.vol) dedupedVol.forEach(d => volMap.set(d.time, d.value));
    volDataRef.current = volMap;
    volS.setData(showMA.vol ? dedupedVol : []);

    // Build markers: trade entries/exits + institutional patterns
    const markers = [];
    const firstDate = sliceTs[0] ? toDate(sliceTs[0]) : "";

    // Trade markers
    trades.forEach(t => {
      if (firstDate && t.date >= firstDate) {
        markers.push({ time: t.date, position: "belowBar", color: "#10b981", shape: "arrowUp", text: `B ${t.reason ? t.reason.split(" ")[0] : ""}` });
      }
      if (t.exitDate && firstDate && t.exitDate >= firstDate) {
        markers.push({ time: t.exitDate, position: "aboveBar", color: "#f43f5e", shape: "arrowDown", text: "S" });
      }
    });

    // Institutional pattern markers
    const patternColors = { HIGH_RVOL_UP:"#f59e0b", HIGH_RVOL_DOWN:"#f43f5e", LIQUIDITY_SWEEP:"#c084fc", REJECTION_WICK:"#38bdf8", MA_BOUNCE_EMA20:"#34d399", MA_BOUNCE_MA50:"#10b981", ACCUMULATION:"#fb923c", HAMMER:"#10b981", SHOOTING_STAR:"#f43f5e", BULL_TRAP:"#f97316", BREAKOUT_20D:"#22d3ee", RSI_DIV_BULL:"#f472b6" };
    const patternShapes = { HIGH_RVOL_UP:"circle", HIGH_RVOL_DOWN:"circle", LIQUIDITY_SWEEP:"arrowUp", REJECTION_WICK:"arrowUp", MA_BOUNCE_EMA20:"circle", MA_BOUNCE_MA50:"square", ACCUMULATION:"square", HAMMER:"arrowUp", SHOOTING_STAR:"arrowDown", BULL_TRAP:"circle", BREAKOUT_20D:"arrowUp", RSI_DIV_BULL:"arrowUp" };
    const patternLabels = { HIGH_RVOL_UP:"RVOL↑", HIGH_RVOL_DOWN:"RVOL↓", LIQUIDITY_SWEEP:"🎯", REJECTION_WICK:"↩", MA_BOUNCE_EMA20:"E20", MA_BOUNCE_MA50:"M50", ACCUMULATION:"ACC", HAMMER:"🔨", SHOOTING_STAR:"⭐", BULL_TRAP:"TRAP", BREAKOUT_20D:"B20", RSI_DIV_BULL:"RSI↗" };
    const patternPosition = { HIGH_RVOL_UP:"belowBar", HIGH_RVOL_DOWN:"aboveBar", LIQUIDITY_SWEEP:"belowBar", REJECTION_WICK:"belowBar", MA_BOUNCE_EMA20:"belowBar", MA_BOUNCE_MA50:"belowBar", ACCUMULATION:"aboveBar", HAMMER:"belowBar", SHOOTING_STAR:"aboveBar", BULL_TRAP:"aboveBar", BREAKOUT_20D:"belowBar", RSI_DIV_BULL:"belowBar" };

    if (!isIntraDay && showPatterns && opens?.length && highs?.length && lows?.length) {
      const detected = detectInstitutionalPatterns(closes, opens, highs, lows, volumes||[], timestamps);
      patternsRef.current = detected;
      detected.forEach(p => {
        if (firstDate && p.date >= firstDate) {
          const seen = markers.find(m => m.time === p.date && m.shape === (patternShapes[p.type]||"circle") && m.color === (patternColors[p.type]||"#f59e0b"));
          if (!seen) markers.push({
            time: p.date,
            position: patternPosition[p.type] || "belowBar",
            color: patternColors[p.type] || "#f59e0b",
            shape: patternShapes[p.type] || "circle",
            text: patternLabels[p.type] || p.type,
          });
        }
      });
    }

    markers.sort((a,b)=>a.time.localeCompare(b.time));
    if (markersPluginRef.current) markersPluginRef.current.setMarkers(markers);
    if (candleMarkersPluginRef.current) candleMarkersPluginRef.current.setMarkers(markers);

    // Forankret VWAP: faste ankere (årets bunn, topp, siste kurshopp) + egen dato hvis valgt
    const { avwapS, vwapFixed } = chartRef.current;
    const toLine = arr => dedupe(arr.slice(startIdx).map((v, i) => v != null ? { time: toDate(sliceTs[i]), value: v } : null).filter(Boolean));
    const vwShow = !showingIntraday && showAVWAP && closes?.length;
    const fixed = vwShow ? anchoredVwaps({ closes, highs: highs || [], lows: lows || [], volumes: volumes || [], opens: opens || [], timestamps }) || [] : [];
    (vwapFixed || []).forEach(({ key, s }) => {
      const f = fixed.find(x => x.key === key);
      s.setData(f ? toLine(f.series) : []);
      s.applyOptions({ visible: !!f });
    });
    if (vwShow && avwapAnchorDate && avwapS) {
      const idx = timestamps.findIndex(ts => new Date(ts * 1000).toISOString().slice(0, 10) === avwapAnchorDate);
      if (idx >= 0) {
        avwapS.setData(toLine(calcAVWAP(closes, highs || [], lows || [], volumes || [], idx)));
        avwapS.applyOptions({ visible: true });
      }
    } else if (avwapS) {
      avwapS.setData([]);
      avwapS.applyOptions({ visible: false });
    }

    // Støtte/motstand-linjer
    priceLineRefs.current.forEach(({ series, pl }) => { try { series.removePriceLine(pl); } catch(_) {} });
    priceLineRefs.current = [];
    if (!isIntraDay && showSR && highs?.length && lows?.length) {
      // samme nivåsoner som panelet «Nivåer og risiko»: de tre nærmeste over og under kursen
      const lv = computeLevels({ closes, highs, lows, volumes: volumes || [] });
      const supports = lv ? lv.supports.slice(0, 3) : [], resistances = lv ? lv.resistances.slice(0, 3) : [];
      const drawLine = (price, color, title) => {
        [area, candle].forEach(series => {
          try {
            const pl = series.createPriceLine({ price, color, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title });
            priceLineRefs.current.push({ series, pl });
          } catch(_) {}
        });
      };
      supports.forEach(s => drawLine(s.p, "#10b98180", `S ${s.labels[0]}`));
      resistances.forEach(r => drawLine(r.p, "#f43f5e80", `R ${r.labels[0]}`));
    }

    chart.timeScale().fitContent();
  }, [closes, timestamps, volumes, opens, highs, lows, period, showMA, trades, chartType, showPatterns, showSR, showAVWAP, avwapAnchorDate, intraDayData, subInterval, subIntervalData]);

  // Volume Profile effect
  useEffect(() => {
    vpLineRefs.current.forEach(({ series, pl }) => { try { series.removePriceLine(pl); } catch(_) {} });
    vpLineRefs.current = [];
    if (!showVP || !chartRef.current || !closes?.length || !highs?.length || !lows?.length) {
      vpDataRef.current = null;
      setVpInfo(null);
      const canvas = vpCanvasRef.current;
      if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    const dp = PERIODS.find(p => p.label === period) || PERIODS[2];
    const n = period === "D" ? 0 : Math.min(dp.days, closes.length);
    // Profilen bygges av dagsstolper; under 20 dager blir den for grov til å si noe
    if (n < 20) { vpDataRef.current = null; setVpInfo({ tooShort: true }); drawVPFnRef.current(); return; }
    const vp = calcVolumeProfile(highs, lows, volumes || [], n, n < 60 ? 40 : 80);
    if (!vp) return;
    vpDataRef.current = vp;
    const px = closes.filter(Boolean).pop();
    setVpInfo({ poc: vp.poc, vah: vp.vah, val: vp.val, n, px });
    const { area, candle } = chartRef.current;
    const addLine = (price, color, title) => {
      [area, candle].forEach(s => {
        try {
          const pl = s.createPriceLine({ price, color, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: false, title: "" });
          vpLineRefs.current.push({ series: s, pl });
        } catch(_) {}
      });
    };
    addLine(vp.poc, "#fbbf2499", "POC");
    addLine(vp.vah, "#60a5fa60", "VAH");
    addLine(vp.val, "#60a5fa60", "VAL");
    drawVPFnRef.current();
  }, [showVP, closes, highs, lows, volumes, period]);

  // Prediction levels from options data — kun på D-periode
  useEffect(() => {
    predLineRefs.current.forEach(({ series, pl }) => { try { series.removePriceLine(pl); } catch(_) {} });
    predLineRefs.current = [];
    if (!chartRef.current || !prediction || period !== "D") {
      if (chartRef.current?.projS) { chartRef.current.projS.setData([]); chartRef.current.projS.applyOptions({ visible: false }); }
      return;
    }
    const { area, candle, projS } = chartRef.current;
    const { maxPain, volMaxPain, volumeMagnet, zeroGamma, expectedMoveUp, expectedMoveDown, predictedClose } = prediction;
    const addPL = (price, color, title) => {
      if (!price) return;
      [area, candle].forEach(series => {
        try {
          const pl = series.createPriceLine({ price, color, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title });
          predLineRefs.current.push({ series, pl });
        } catch(_) {}
      });
    };
    if (expectedMoveUp)   addPL(expectedMoveUp,   "#10b98155", "+1σ");
    if (expectedMoveDown) addPL(expectedMoveDown, "#f43f5e55", "-1σ");
    if (zeroGamma)        addPL(zeroGamma,        "#f97316cc", "γ=0");
    if (maxPain)          addPL(maxPain,           "#f59e0b88", "MP(OI)");
    if (volMaxPain)       addPL(volMaxPain,        "#f59e0bcc", "MP(vol)");
    if (volumeMagnet)     addPL(volumeMagnet,      "#38bdf8aa", "Vol Magnet");
    if (predictedClose)   addPL(predictedClose,   "#a78bfadd", "▶ Pred");

    // Intraday projection path when on D period
    if (projS && period === "D" && predictedClose) {
      const idc = intraDayData?.closes ?? [];
      const idt = intraDayData?.timestamps ?? [];
      const validPairs = idc.map((c, i) => [idt[i], c]).filter(([, c]) => c != null);
      if (validPairs.length >= 2) {
        const lastTs    = validPairs[validPairs.length - 1][0];
        const lastPrice = validPairs[validPairs.length - 1][1];
        // NYSE close ≈ 20:00 UTC (summer DST) or 21:00 UTC (winter)
        const closeHour = new Date().getTimezoneOffset() < -120 ? 20 : 20; // keep 20 as best guess
        const endDate = new Date(); endDate.setUTCHours(closeHour, 0, 0, 0);
        const endTs = endDate.getTime() / 1000;
        if (lastTs < endTs) {
          const projData = [];
          const step = 300;
          for (let ts = lastTs + step; ts < endTs; ts += step) {
            const frac = Math.pow((ts - lastTs) / (endTs - lastTs), 0.85);
            projData.push({ time: ts, value: lastPrice + (predictedClose - lastPrice) * frac });
          }
          projData.push({ time: endTs, value: predictedClose });
          try { projS.setData(projData); projS.applyOptions({ visible: true }); }
          catch(_) { projS.setData([]); projS.applyOptions({ visible: false }); }
        } else {
          projS.setData([]); projS.applyOptions({ visible: false });
        }
      } else {
        projS.setData([]); projS.applyOptions({ visible: false });
      }
    } else if (projS) {
      projS.setData([]); projS.applyOptions({ visible: false });
    }
  }, [prediction, period, intraDayData]);

  // Compute AVWAP info for info panel (same logic as chart effect, but derived in render)
  const avwapInfo = (() => {
    if (!showAVWAP || !closes?.length || period === "D") return null;
    const lines = [...(anchoredVwaps({ closes, highs: highs || [], lows: lows || [], volumes: volumes || [], opens: opens || [], timestamps }) || [])];
    const lastPrice = [...closes].reverse().find(v => v != null);
    if (avwapAnchorDate && lastPrice) {
      const idx = (timestamps || []).findIndex(ts => new Date(ts * 1000).toISOString().slice(0, 10) === avwapAnchorDate);
      const val = idx >= 0 ? [...calcAVWAP(closes, highs || [], lows || [], volumes || [], idx)].reverse().find(v => v != null) : null;
      if (val) lines.push({ key: "egen", date: avwapAnchorDate, label: "VWAP fra valgt dato", color: "#c084fc", meaning: "Snittprisen siden datoen du klikket.", value: val, pct: (lastPrice / val - 1) * 100 });
    }
    return lines.length ? { lines } : null;
  })();

  return (
    <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px 14px 8px",marginTop:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:6}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{fontSize:9,color:"#334155",letterSpacing:1}}>KURSUTVIKLING — DAGLIG DATA</div>
          <span style={{fontSize:9,color:"#475569",background:"#0a1628",border:"1px solid #1e293b",borderRadius:3,padding:"1px 6px"}}>1D</span>
        </div>
        <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
          {PERIODS.map(p=>(
            <button key={p.label} onClick={()=>{ setPeriod(p.label); setSubInterval(null); setSubIntervalData(null); }}
              style={{padding:isMobile?"2px 8px":"5px 13px",border:"1px solid",borderColor:period===p.label?"#3b82f6":"#1e293b",background:period===p.label?"#3b82f620":"transparent",color:period===p.label?"#3b82f6":"#475569",borderRadius:3,cursor:"pointer",fontSize:isMobile?9:12,fontFamily:"inherit"}}>
              {p.label}
            </button>
          ))}
          {(period!=="6M"&&period!=="1Å")&&(
            <>
              <div style={{width:1,height:12,background:"#1e293b",margin:"0 2px"}}/>
              {["D","1U","1M"].includes(period)&&(
                <button onClick={()=>setSubInterval(v=>v==="15m"?null:"15m")}
                  style={{padding:isMobile?"2px 7px":"4px 10px",border:`1px solid ${subInterval==="15m"?"#a78bfa80":"#1e293b"}`,background:subInterval==="15m"?"#a78bfa18":"transparent",color:subInterval==="15m"?"#a78bfa":"#475569",borderRadius:3,cursor:"pointer",fontSize:isMobile?9:11,fontFamily:"inherit"}}>
                  {subIntervalLoading&&subInterval==="15m"?"…":"15m"}
                </button>
              )}
              <button onClick={()=>setSubInterval(v=>v==="1h"?null:"1h")}
                style={{padding:isMobile?"2px 7px":"4px 10px",border:`1px solid ${subInterval==="1h"?"#a78bfa80":"#1e293b"}`,background:subInterval==="1h"?"#a78bfa18":"transparent",color:subInterval==="1h"?"#a78bfa":"#475569",borderRadius:3,cursor:"pointer",fontSize:isMobile?9:11,fontFamily:"inherit"}}>
                {subIntervalLoading&&subInterval==="1h"?"…":"1h"}
              </button>
            </>
          )}
          {(period==="6M"||period==="1Å")&&(
            <>
              <div style={{width:1,height:12,background:"#1e293b",margin:"0 2px"}}/>
              <button onClick={()=>setSubInterval(v=>v==="1h"?null:"1h")}
                style={{padding:isMobile?"2px 7px":"4px 10px",border:`1px solid ${subInterval==="1h"?"#a78bfa80":"#1e293b"}`,background:subInterval==="1h"?"#a78bfa18":"transparent",color:subInterval==="1h"?"#a78bfa":"#475569",borderRadius:3,cursor:"pointer",fontSize:isMobile?9:11,fontFamily:"inherit"}}>
                {subIntervalLoading&&subInterval==="1h"?"…":"1h"}
              </button>
            </>
          )}
          <div style={{width:1,height:12,background:"#1e293b",margin:"0 2px"}}/>
          <button onClick={()=>setChartType(t=>t==="area"?"candle":"area")}
            style={{padding:isMobile?"2px 7px":"4px 11px",border:`1px solid ${chartType==="candle"?"#f59e0b80":"#1e293b"}`,background:chartType==="candle"?"#f59e0b18":"transparent",color:chartType==="candle"?"#f59e0b":"#475569",borderRadius:3,cursor:"pointer",fontSize:isMobile?9:12,fontFamily:"inherit",fontWeight:chartType==="candle"?700:400}}>
            {chartType==="candle"?"Lys":"Område"}
          </button>
          <button onClick={()=>setShowPatterns(v=>!v)}
            style={{padding:isMobile?"2px 7px":"4px 11px",border:`1px solid ${showPatterns?"#c084fc80":"#1e293b"}`,background:showPatterns?"#c084fc18":"transparent",color:showPatterns?"#c084fc":"#475569",borderRadius:3,cursor:"pointer",fontSize:isMobile?9:12,fontFamily:"inherit",fontWeight:showPatterns?700:400}}>
            Mønstre
          </button>
          <button onClick={()=>setShowSR(v=>!v)}
            style={{padding:isMobile?"2px 7px":"4px 11px",border:`1px solid ${showSR?"#38bdf880":"#1e293b"}`,background:showSR?"#38bdf818":"transparent",color:showSR?"#38bdf8":"#475569",borderRadius:3,cursor:"pointer",fontSize:isMobile?9:12,fontFamily:"inherit",fontWeight:showSR?700:400}}>
            S/R
          </button>
          <button
            onClick={()=>{ if(showAVWAP){ setShowAVWAP(false); setAvwapAnchorDate(null); } else setShowAVWAP(true); }}
            title={showAVWAP ? (avwapAnchorDate ? `Egen VWAP fra ${avwapAnchorDate} i tillegg — klikk en annen dato for å flytte, klikk her for å slå av` : "VWAP fra årets bunn, topp og siste kurshopp — klikk en dato i grafen for å legge til din egen") : "Forankret VWAP (årets bunn, topp og siste kurshopp)"}
            style={{padding:isMobile?"2px 7px":"4px 11px",border:`1px solid ${showAVWAP?"#fb923c80":"#1e293b"}`,background:showAVWAP?"#fb923c18":"transparent",color:showAVWAP?"#fb923c":"#475569",borderRadius:3,cursor:"pointer",fontSize:isMobile?9:12,fontFamily:"inherit",fontWeight:showAVWAP?700:400}}>
            {showAVWAP && avwapAnchorDate ? `VWAP ${avwapAnchorDate.slice(5)}` : "VWAP"}
          </button>
          <button
            onClick={() => setShowVP(v => !v)}
            title="Volume Profile — viser volum per prisnivå (POC, VAH, VAL)"
            style={{padding:isMobile?"2px 7px":"4px 11px",border:`1px solid ${showVP?"#22d3ee80":"#1e293b"}`,background:showVP?"#22d3ee18":"transparent",color:showVP?"#22d3ee":"#475569",borderRadius:3,cursor:"pointer",fontSize:isMobile?9:12,fontFamily:"inherit",fontWeight:showVP?700:400}}>
            VP
          </button>
          <div style={{width:1,height:12,background:"#1e293b",margin:"0 2px"}}/>
          {MA_CFG.map(([k,lbl,c])=>(
            <button key={k} onClick={()=>setShowMA(p=>({...p,[k]:!p[k]}))}
              style={{padding:isMobile?"2px 7px":"4px 11px",border:`1px solid ${showMA[k]?c+"80":"#1e293b"}`,background:showMA[k]?c+"18":"transparent",color:showMA[k]?c:"#334155",borderRadius:3,cursor:"pointer",fontSize:isMobile?9:12,fontFamily:"inherit",fontWeight:showMA[k]?700:400}}>
              {lbl}
            </button>
          ))}
        </div>
      </div>
      <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
        <div style={{flex:1,minWidth:0,position:"relative"}}>
          <div ref={containerRef} style={{width:"100%",height:chartHeight}}/>
          <span ref={volTooltipRef} style={{position:"absolute",bottom:16,left:12,fontSize:11,color:"#94a3b8",background:"#060f1ccc",padding:"2px 7px",borderRadius:4,pointerEvents:"none",zIndex:2}}/>
          <canvas ref={vpCanvasRef} width={80} height={chartHeight} style={{position:"absolute",top:0,right:0,width:80,height:chartHeight,pointerEvents:"none",zIndex:1}}/>
          {!closes?.length && (
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#475569",fontSize:12}}>
              ⟳ Henter kursdata…
            </div>
          )}
          {period==="D" && intraDayLoading && (
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#475569",fontSize:12,background:"#060f1ccc"}}>
              ⟳ Henter daglig data…
            </div>
          )}
        </div>
        {!isMobile && (
          <div style={{width:210,flexShrink:0,background:"#0a1628",border:"1px solid #1e293b",borderRadius:6,padding:"12px 14px",display:"flex",flexDirection:"column",gap:10,alignSelf:"stretch"}}>
            <div style={{fontSize:10,color:"#334155",letterSpacing:1,fontWeight:700}}>RISIKOBEREGNER</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <label style={{fontSize:10,color:"#475569"}}>Konto ($)</label>
              <input value={riskAccount} onChange={e=>setRiskAccount(e.target.value)} style={{background:"#060f1c",border:"1px solid #1e293b",color:"#e2e8f0",borderRadius:4,padding:"5px 8px",fontSize:12,width:"100%",fontFamily:"inherit",boxSizing:"border-box"}}/>
              <label style={{fontSize:10,color:"#475569"}}>Risiko per trade (%)</label>
              <input value={riskPct} onChange={e=>setRiskPct(e.target.value)} style={{background:"#060f1c",border:"1px solid #1e293b",color:"#e2e8f0",borderRadius:4,padding:"5px 8px",fontSize:12,width:"100%",fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div style={{height:1,background:"#1e293b"}}/>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <label style={{fontSize:10,color:"#475569"}}>Entry</label>
              <input value={riskEntry} onChange={e=>setRiskEntry(e.target.value)} style={{background:"#060f1c",border:"1px solid #1e293b",color:"#e2e8f0",borderRadius:4,padding:"5px 8px",fontSize:12,width:"100%",fontFamily:"inherit",boxSizing:"border-box"}}/>
              <label style={{fontSize:10,color:"#f43f5e"}}>Stop Loss</label>
              <input value={riskStop} onChange={e=>setRiskStop(e.target.value)} placeholder="0.00" style={{background:"#060f1c",border:"1px solid #f43f5e40",color:"#f43f5e",borderRadius:4,padding:"5px 8px",fontSize:12,width:"100%",fontFamily:"inherit",boxSizing:"border-box"}}/>
              <label style={{fontSize:10,color:"#10b981"}}>Target (valgfri)</label>
              <input value={riskTarget} onChange={e=>setRiskTarget(e.target.value)} placeholder="0.00" style={{background:"#060f1c",border:"1px solid #10b98140",color:"#10b981",borderRadius:4,padding:"5px 8px",fontSize:12,width:"100%",fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div style={{height:1,background:"#1e293b"}}/>
            {riskCalc ? (
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                  <span style={{color:"#64748b"}}>Risikert</span>
                  <span style={{color:"#f43f5e",fontWeight:700}}>${riskCalc.dollarRisk.toLocaleString("nb-NO",{maximumFractionDigits:0})}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                  <span style={{color:"#64748b"}}>Aksjer</span>
                  <span style={{color:"#e2e8f0",fontWeight:700}}>{riskCalc.shares.toLocaleString("nb-NO")}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                  <span style={{color:"#64748b"}}>Posisjon</span>
                  <span style={{color:"#94a3b8"}}>${riskCalc.posSize >= 1000 ? (riskCalc.posSize/1000).toFixed(1)+"k" : riskCalc.posSize.toFixed(0)}</span>
                </div>
                {riskCalc.rr != null && (
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                    <span style={{color:"#64748b"}}>R:R</span>
                    <span style={{color: riskCalc.rr >= 2 ? "#10b981" : riskCalc.rr >= 1 ? "#f59e0b" : "#f43f5e",fontWeight:700}}>{riskCalc.rr.toFixed(1)}:1 {riskCalc.rr >= 2 ? "✓" : riskCalc.rr >= 1 ? "~" : "✗"}</span>
                  </div>
                )}
                <div style={{marginTop:2,padding:"6px 8px",background:"#060f1c",borderRadius:4,fontSize:10,color:"#475569",lineHeight:1.5}}>
                  ${riskCalc.riskPerShare.toFixed(2)} risiko/aksje
                </div>
              </div>
            ) : (
              <div style={{fontSize:11,color:"#334155",textAlign:"center",padding:"8px 0"}}>Fyll inn entry og stop</div>
            )}
          </div>
        )}
      </div>

      {/* AVWAP info panel */}
      {avwapInfo && (
        <div style={{marginTop:8,padding:"10px 14px",background:"#0a1628",border:"1px solid #1e293b",borderRadius:6}}>
          <div style={{fontSize:11,color:"#94a3b8",fontWeight:700,marginBottom:6}}>FORANKRET VWAP · snittprisen kjøperne har betalt siden en hendelse</div>
          {avwapInfo.lines.map(l => (
            <div key={l.key} style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"190px 80px 140px 1fr",gap:8,alignItems:"baseline",padding:"3px 0",borderBottom:"1px solid #060f1c"}}>
              <span style={{fontSize:11,fontWeight:700,color:l.color}}>{l.label} <span style={{color:"#475569",fontWeight:400}}>{l.date}</span></span>
              <span style={{fontSize:11,color:"#e2e8f0"}}>${l.value.toFixed(2)}</span>
              <span style={{fontSize:11,fontWeight:700,color:l.pct>=0?"#10b981":"#f43f5e"}}>kurs {l.pct>=0?"+":""}{l.pct.toFixed(1)}% {l.pct>=0?"over":"under"}</span>
              <span style={{fontSize:10,color:"#475569"}}>{l.meaning} {l.pct>=0?"De som har kjøpt siden da er i snitt i pluss.":"De som har kjøpt siden da er i snitt i minus."}</span>
            </div>
          ))}
          <div style={{fontSize:10,color:"#334155",marginTop:6,lineHeight:1.6}}>
            Testet (5 år): kurs <b style={{color:"#f43f5e"}}>under VWAP fra årets bunn</b> ga AI-aksjer 1,5 prosentpoeng svakere avkastning de neste 20 dagene. Over/under de andre linjene ga ingen målbar forskjell, så bruk dem som referanse.
            {" "}Klikk en dato i grafen for å legge til din egen VWAP.
          </div>
        </div>
      )}

      {/* Volume Profile info panel */}
      {showVP && vpInfo && (
        <div style={{marginTop:8,padding:"10px 14px",background:"#0a1628",border:"1px solid #22d3ee40",borderLeft:"3px solid #22d3ee",borderRadius:"0 6px 6px 0",display:"flex",flexWrap:"wrap",gap:16,alignItems:"center"}}>
          <div style={{fontSize:11,color:"#22d3ee",fontWeight:700}}>VP</div>
          {vpInfo.tooShort ? (
            <div style={{fontSize:11,color:"#64748b",flex:1}}>Volumprofilen trenger minst 1 måned med data. Velg 1M eller lengre.</div>
          ) : (<>
          <div style={{fontSize:11,color:"#64748b"}}>POC: <span style={{color:"#fbbf24",fontWeight:700}}>${vpInfo.poc.toFixed(2)}</span></div>
          <div style={{fontSize:11,color:"#64748b"}}>VAH: <span style={{color:"#60a5fa"}}>${vpInfo.vah.toFixed(2)}</span></div>
          <div style={{fontSize:11,color:"#64748b"}}>VAL: <span style={{color:"#60a5fa"}}>${vpInfo.val.toFixed(2)}</span></div>
          <div style={{fontSize:11,color:"#94a3b8"}}>Kurs {vpInfo.px > vpInfo.vah ? "over verdisonen" : vpInfo.px < vpInfo.val ? "under verdisonen" : "inne i verdisonen"}</div>
          <div style={{fontSize:11,color:"#475569",flex:1,minWidth:240}}>
            Siste {vpInfo.n} handelsdager. POC (gull) = prisen der mest volum er omsatt. VAH–VAL (blå) = sonen der 70 % av volumet ble handlet — der flest har kjøpt.
            Testet på 5 år: kursen snur ved POC ikke oftere enn ved tilfeldige priser (≈50 %). Bruk den til å se hvor mange sitter med gevinst eller tap, ikke som kjøps- eller salgssignal.
          </div>
          </>)}
        </div>
      )}

      {/* Prediction panel */}
      {prediction && (
        <div style={{marginTop:8,padding:"10px 14px",background:"#060f1c",border:"1px solid #a78bfa40",borderLeft:"3px solid #a78bfa",borderRadius:"0 6px 6px 0"}}>
          <div style={{display:"flex",flexWrap:"wrap",gap:12,alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:11,color:"#a78bfa",fontWeight:700}}>📡 KURSPREDIKSJON</div>
            {prediction.predictedClose && (
              <div style={{display:"flex",alignItems:"center",gap:5,background:"#a78bfa15",border:"1px solid #a78bfa40",borderRadius:5,padding:"3px 10px"}}>
                <span style={{fontSize:10,color:"#64748b"}}>Sluttkurs {prediction.targetDate ? new Date(prediction.targetDate + "T12:00:00").toLocaleDateString("nb-NO", { weekday: "short", day: "numeric", month: "short" }) : ""}:</span>
                <span style={{fontSize:13,fontWeight:700,color:"#a78bfa"}}>${prediction.predictedClose.toFixed(2)}</span>
                {prediction.currentPrice && (
                  <span style={{fontSize:10,fontWeight:700,color:prediction.predictedClose>prediction.currentPrice?"#10b981":"#f43f5e"}}>
                    {prediction.predictedClose>prediction.currentPrice?"▲":"▼"} {Math.abs((prediction.predictedClose-prediction.currentPrice)/prediction.currentPrice*100).toFixed(2)}%
                  </span>
                )}
              </div>
            )}
            {prediction.atmIV && <div style={{fontSize:10,color:"#64748b"}}>ATM IV: <span style={{color:"#94a3b8"}}>{(prediction.atmIV*100).toFixed(0)}%</span></div>}
            {prediction.dailyMove && <div style={{fontSize:10,color:"#64748b"}}>±1σ dag: <span style={{color:"#94a3b8"}}>${prediction.dailyMove.toFixed(2)}</span></div>}
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:8}}>
            {/* DTE badge */}
            {prediction.dte != null && (
              <div style={{fontSize:10,padding:"3px 8px",background: prediction.dte <= 2 ? "#f59e0b20" : prediction.dte <= 7 ? "#f59e0b10" : "#0a1628", border:`1px solid ${prediction.dte<=2?"#f59e0b60":"#1e293b"}`,borderRadius:4,display:"flex",gap:5,alignItems:"center"}}>
                <span style={{color:"#64748b"}}>DTE:</span>
                <span style={{color: prediction.dte<=2?"#f59e0b": prediction.dte<=7?"#94a3b8":"#475569", fontWeight:700}}>{prediction.dte}d</span>
                <span style={{color:"#334155"}}>→ Max Pain {prediction.mpWeight}% vekt</span>
              </div>
            )}
            {prediction.volMaxPain && (
              <div style={{fontSize:10,padding:"3px 8px",background:"#f59e0b15",border:"1px solid #f59e0b40",borderRadius:4}}>
                <span style={{color:"#64748b"}}>MP(vol+OI): </span><span style={{color:"#f59e0b",fontWeight:700}}>${prediction.volMaxPain}</span>
                <span style={{color:"#334155",marginLeft:4,fontSize:9}}>{Math.round(prediction.mpWeight*0.60)}%</span>
              </div>
            )}
            {prediction.maxPain && (
              <div style={{fontSize:10,padding:"3px 8px",background:"#f59e0b0a",border:"1px solid #f59e0b28",borderRadius:4}}>
                <span style={{color:"#64748b"}}>MP(OI): </span><span style={{color:"#f59e0b99",fontWeight:700}}>${prediction.maxPain}</span>
                <span style={{color:"#334155",marginLeft:4,fontSize:9}}>{Math.round(prediction.mpWeight*0.40)}%</span>
              </div>
            )}
            {prediction.zeroGamma && (
              <div style={{fontSize:10,padding:"3px 8px",background:"#f9731615",border:"1px solid #f9731640",borderRadius:4}}>
                <span style={{color:"#64748b"}}>Zero γ: </span><span style={{color:"#f97316",fontWeight:700}}>${prediction.zeroGamma}</span>
                <span style={{color:"#334155",marginLeft:4,fontSize:9}}>{Math.round((100-prediction.mpWeight)*0.70)}%</span>
              </div>
            )}
            {prediction.volumeMagnet && (
              <div style={{fontSize:10,padding:"3px 8px",background:"#38bdf815",border:"1px solid #38bdf840",borderRadius:4}}>
                <span style={{color:"#64748b"}}>Vol Magnet: </span><span style={{color:"#38bdf8",fontWeight:700}}>${prediction.volumeMagnet}</span>
                <span style={{color:"#334155",marginLeft:4,fontSize:9}}>{Math.round((100-prediction.mpWeight)*0.30)}%</span>
              </div>
            )}
          </div>
          <div style={{fontSize:10,color:"#334155",lineHeight:1.5}}>
            Max Pain og Vol Magnet gjelder nærmeste utløp. MP(vol+OI) = Max Pain justert for dagens handelsvolum. Zero γ = kursen der dealernes samlede gamma (alle utløp) skifter fortegn. Vol Magnet = striket med høyest volum i dag.
          </div>
          {predStats && (
            <div style={{marginTop:8,paddingTop:8,borderTop:"1px solid #1e293b",fontSize:10,color:"#64748b",lineHeight:1.7}}>
              <span style={{color:"#a78bfa",fontWeight:700}}>🎯 TREFFSIKKERHET </span>
              {predStats.all.n === 0
                ? <span>Logger prediksjoner ({predStats.logged} så langt). Resultater kommer når første måldato er passert.</span>
                : [["Alle aksjer", predStats.all], [ticker, predStats.ticker]].filter(([, s]) => s.n > 0).map(([lbl, s]) => (
                    <div key={lbl}>
                      {lbl} ({s.n} evaluert): snittavvik <b style={{color: s.err < s.naive ? "#10b981" : "#f43f5e"}}>{s.err.toFixed(2)}%</b> mot {s.naive.toFixed(2)}% for «uendret kurs»
                      {s.dirN > 0 && <> · riktig retning <b style={{color: s.dir > 0.5 ? "#10b981" : "#f43f5e"}}>{Math.round(s.dir * 100)}%</b> ({s.dirN})</>}
                    </div>
                  ))}
            </div>
          )}
        </div>
      )}

      {/* Pattern click info panel */}
      {selectedPattern && (
        <div style={{marginTop:8}}>
          {selectedPattern.map((pat, idx) => {
            const info = SIGNAL_INFO[pat.type];
            if (!info) return null;
            return (
              <div key={idx} style={{marginBottom:6,padding:"12px 14px",background:"#0a1628",border:`1px solid ${info.color}40`,borderLeft:`3px solid ${info.color}`,borderRadius:"0 6px 6px 0",display:"flex",gap:12,alignItems:"flex-start"}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                    <span style={{fontSize:13,fontWeight:700,color:info.color}}>{info.name}</span>
                    <span style={{fontSize:10,padding:"2px 7px",borderRadius:3,background:info.bias==="bullish"?"#10b98120":"#f43f5e20",color:info.bias==="bullish"?"#10b981":"#f43f5e",fontWeight:700}}>{info.bias==="bullish"?"BULLISH":"BEARISH"}</span>
                    <span style={{fontSize:10,color:"#334155"}}>{pat.date}</span>
                  </div>
                  <div style={{fontSize:12,color:"#94a3b8",marginBottom:5}}>{info.summary}</div>
                  <div style={{fontSize:11,color:"#64748b",lineHeight:1.6,marginBottom:6}}>{info.detail}</div>
                  <div style={{display:"flex",gap:16,fontSize:11}}>
                    <span style={{color:"#475569"}}>⏱ Tidshorisont: <b style={{color:"#94a3b8"}}>{info.timeframe}</b></span>
                  </div>
                  <div style={{marginTop:7,fontSize:11,color:info.bias==="bullish"?"#10b981":"#f43f5e",fontWeight:700}}>→ {info.action}</div>
                </div>
                {idx===0&&<button onClick={()=>setSelectedPattern(null)} style={{background:"none",border:"none",color:"#475569",cursor:"pointer",fontSize:18,lineHeight:1,padding:0,flexShrink:0}}>×</button>}
              </div>
            );
          })}
        </div>
      )}
      {showPatterns&&!selectedPattern&&(
        <div style={{marginTop:6,fontSize:10,color:"#1e3a5f",textAlign:"center"}}>Klikk på et mønster-symbol i grafen for forklaring</div>
      )}
    </div>
  );
}

function CompareTable({ tickerA, tickerB, allStockList }) {
  const iA = getIntel(tickerA), iB = getIntel(tickerB);
  const nameA = allStockList.find(s => s.ticker === tickerA)?.name || tickerA;
  const nameB = allStockList.find(s => s.ticker === tickerB)?.name || tickerB;
  const getCatColor = (t) => {
    const cat = Object.entries(DEFAULT_STOCKS).find(([, v]) => v.find(s => s.ticker === t))?.[0];
    return CAT_COLORS[cat] || "#94a3b8";
  };
  const rows = [
    { label: "Revenue Growth (YoY)", a: `${iA.revenueGrowth}%`, b: `${iB.revenueGrowth}%`, sA: scoreRevGrowth(iA.revenueGrowth), sB: scoreRevGrowth(iB.revenueGrowth) },
    { label: "Gross Margin", a: `${iA.grossMargin}%`, b: `${iB.grossMargin}%`, sA: scoreMargin(iA.grossMargin), sB: scoreMargin(iB.grossMargin) },
    { label: "Forward P/E", a: `${iA.forwardPE}x`, b: `${iB.forwardPE}x`, sA: scorePE(iA.forwardPE), sB: scorePE(iB.forwardPE) },
    { label: "PEG Ratio", a: iA.peg !== null ? `${iA.peg}x` : "N/A", b: iB.peg !== null ? `${iB.peg}x` : "N/A", sA: scorePEG(iA.peg), sB: scorePEG(iB.peg) },
    { label: "Earnings Beat Rate", a: `${iA.beatRate}%`, b: `${iB.beatRate}%`, sA: scoreBeat(iA.beatRate), sB: scoreBeat(iB.beatRate) },
    { label: "AI Eksponering", a: iA.aiExposure, b: iB.aiExposure, sA: { dot: iA.aiExposure === "Meget høy" ? "green" : iA.aiExposure === "Høy" ? "green" : "yellow", label: iA.aiExposure }, sB: { dot: iB.aiExposure === "Meget høy" ? "green" : iB.aiExposure === "Høy" ? "green" : "yellow", label: iB.aiExposure } },
    { label: "Earnings Surprise (snitt)", a: `+${iA.earningsSurprise}%`, b: `+${iB.earningsSurprise}%`, sA: { dot: iA.earningsSurprise >= 8 ? "green" : iA.earningsSurprise >= 4 ? "yellow" : "red", label: iA.earningsSurprise >= 8 ? "Sterk" : "OK" }, sB: { dot: iB.earningsSurprise >= 8 ? "green" : iB.earningsSurprise >= 4 ? "yellow" : "red", label: iB.earningsSurprise >= 8 ? "Sterk" : "OK" } },
    { label: "IV Percentile", a: `${iA.pf.ivPct}`, b: `${iB.pf.ivPct}`, sA: scoreIV(iA.pf.ivPct), sB: scoreIV(iB.pf.ivPct) },
    { label: "Call/Put Ratio", a: `${iA.pf.callPutRatio}x`, b: `${iB.pf.callPutRatio}x`, sA: scoreCPR(iA.pf.callPutRatio), sB: scoreCPR(iB.pf.callPutRatio) },
    { label: "Gamma Regime", a: iA.pf.gammaRegime, b: iB.pf.gammaRegime, sA: scoreGamma(iA.pf.gammaRegime), sB: scoreGamma(iB.pf.gammaRegime) },
    { label: "Expected Move", a: `±${iA.expectedMove}%`, b: `±${iB.expectedMove}%`, sA: { dot: iA.expectedMove <= 5 ? "green" : iA.expectedMove <= 9 ? "yellow" : "red", label: iA.expectedMove <= 5 ? "Rolig" : iA.expectedMove <= 9 ? "Moderat" : "Stor" }, sB: { dot: iB.expectedMove <= 5 ? "green" : iB.expectedMove <= 9 ? "yellow" : "red", label: iB.expectedMove <= 5 ? "Rolig" : iB.expectedMove <= 9 ? "Moderat" : "Stor" } },
    { label: "Pressure Score", a: `${calcPressure(iA)}/100`, b: `${calcPressure(iB)}/100`, sA: { dot: calcPressure(iA) >= 70 ? "red" : calcPressure(iA) >= 45 ? "yellow" : "green", label: calcPressure(iA) >= 70 ? "Høy" : calcPressure(iA) >= 45 ? "Moderat" : "Lav" }, sB: { dot: calcPressure(iB) >= 70 ? "red" : calcPressure(iB) >= 45 ? "yellow" : "green", label: calcPressure(iB) >= 70 ? "Høy" : calcPressure(iB) >= 45 ? "Moderat" : "Lav" } },
  ];
  return (
    <div style={{ background: "#060f1c", border: "1px solid #1e293b", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", background: "#0a1628", borderBottom: "1px solid #1e293b" }}>
        <div style={{ padding: "10px 14px", fontSize: 9, color: "#334155", letterSpacing: 1 }}>NØKKELTALL</div>
        <div style={{ padding: "10px 14px", fontSize: 12, fontWeight: 700, color: getCatColor(tickerA) }}>{tickerA} — {nameA}</div>
        <div style={{ padding: "10px 14px", fontSize: 12, fontWeight: 700, color: getCatColor(tickerB) }}>{tickerB} — {nameB}</div>
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderBottom: "1px solid #0d1e30" }}>
          <div style={{ padding: "9px 14px", fontSize: 10, color: "#475569" }}>{r.label}</div>
          <div style={{ padding: "9px 14px", display: "flex", alignItems: "center", gap: 7 }}>
            <Dot color={r.sA.dot} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#e2e8f0" }}>{r.a}</div>
              <div style={{ fontSize: 9, color: r.sA.dot === "green" ? "#10b981" : r.sA.dot === "yellow" ? "#f59e0b" : "#f43f5e" }}>{r.sA.label}</div>
            </div>
          </div>
          <div style={{ padding: "9px 14px", display: "flex", alignItems: "center", gap: 7 }}>
            <Dot color={r.sB.dot} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#e2e8f0" }}>{r.b}</div>
              <div style={{ fontSize: 9, color: r.sB.dot === "green" ? "#10b981" : r.sB.dot === "yellow" ? "#f59e0b" : "#f43f5e" }}>{r.sB.label}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function getThirdFriday(year, month) {
  let count = 0;
  for (let d = 1; d <= 31; d++) {
    const date = new Date(year, month, d);
    if (date.getMonth() !== month) break;
    if (date.getDay() === 5) { count++; if (count === 3) return date; }
  }
}

function buildCalendarEvents() {
  const now = new Date();
  const events = [];
  const D = s => new Date(s + "T12:00:00");
  // Offisielle datoer, sjekket 2. nov 2026 mot federalreserve.gov, bls.gov og bea.gov. Må fylles på når nye planer kommer.
  // FOMC: beslutningsdagen (andre møtedag), kl 14:00 ET.
  ["2026-01-28","2026-03-18","2026-04-29","2026-06-17","2026-07-29","2026-09-16","2026-10-28","2026-12-09",
   "2027-01-27","2027-03-17","2027-04-28","2027-06-09","2027-07-28","2027-09-15","2027-10-27","2027-12-08"]
    .forEach(s => events.push({ date: D(s), type: "FOMC", title: "Fed rentebeslutning", desc: "FOMC-møte — rentebeslutning kl 14:00 ET, pressekonferanse 14:30", color: "#ef4444", cat: "FOMC / Renter" }));
  ["2026-11-10","2026-12-10","2027-01-13","2027-02-11","2027-03-10","2027-04-13","2027-05-12","2027-06-10","2027-07-14","2027-08-11","2027-09-14","2027-10-14","2027-11-10","2027-12-10"]
    .forEach(s => events.push({ date: D(s), type: "CPI", title: "CPI Inflasjonsdata", desc: "Konsumprisindeks fra Bureau of Labor Statistics kl 08:30 ET", color: "#f97316", cat: "CPI / Inflasjon" }));
  ["2026-11-06","2026-12-04","2027-01-08","2027-02-05","2027-03-05","2027-04-02","2027-05-07","2027-06-04","2027-07-02","2027-08-06","2027-09-03","2027-10-08","2027-11-05","2027-12-03"]
    .forEach(s => events.push({ date: D(s), type: "NFP", title: "Non-Farm Payrolls", desc: "Arbeidsmarkedsrapporten fra BLS kl 08:30 ET", color: "#f97316", cat: "NFP" }));
  // PCE: BEA har bare publisert ut 2026. Etter det: siste fredag i måneden som anslag, merket «ca.».
  const pce = ["2026-11-25", "2026-12-23"];
  pce.forEach(s => events.push({ date: D(s), type: "PCE", title: "PCE Inflasjonsdata", desc: "Personal Consumption Expenditures – Feds foretrukne inflasjonsmål. Kl 08:30 ET", color: "#fb923c", cat: "CPI / Inflasjon" }));
  for (let m = 0; m < 12; m++) {
    const last = new Date(2027, m + 1, 0);
    while (last.getDay() !== 5) last.setDate(last.getDate() - 1);
    events.push({ date: last, type: "PCE", title: "PCE Inflasjonsdata (ca.)", desc: "Anslått dato — BEA har ikke publisert 2027-planen ennå. Kl 08:30 ET", color: "#fb923c", cat: "CPI / Inflasjon" });
  }
  // Opsjonsutløp: tredje fredag, eller torsdagen før når børsen er stengt (Juneteenth 2026, Langfredag)
  const closed = new Set(["2026-06-19", "2027-03-26", "2028-04-14"]);
  for (let m = 0; m < 24; m++) {
    const yr = now.getFullYear() + Math.floor((now.getMonth() - 2 + m) / 12);
    const mo = ((now.getMonth() - 2 + m) % 12 + 12) % 12;
    const d = getThirdFriday(yr, mo);
    if (!d) continue;
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (closed.has(iso)) d.setDate(d.getDate() - 1);
    events.push({ date: d, type: "OPEX", title: "Opsjonsutløp (OPEX)", desc: "Månedlig opsjonsutløp – økt volatilitet", color: "#ec4899", cat: "Opsjonsutløp" });
  }
  [
    { date: new Date(2026,2,16), title: "NVIDIA GTC 2026", desc: "GPU Technology Conference, San Jose" },
    { date: new Date(2026,10,30), title: "AWS re:Invent", desc: "30. nov–4. des, Las Vegas" },
    { date: new Date(2027,0,6), title: "CES 2027", desc: "6.–9. jan, Las Vegas" },
    { date: new Date(2027,2,15), title: "NVIDIA GTC 2027", desc: "15.–18. mars, San Jose — Jensen Huangs keynote flytter ofte NVDA og hele kjeden" },
  ].forEach(c => events.push({ ...c, type: "CONF", color: "#a78bfa", cat: "AI-konferanser" }));
  for (let m = 0; m < 12; m++) {
    events.push({ date: new Date(now.getFullYear(), now.getMonth() - 1 + m, 10), type: "TSMC", title: "TSMC månedssalg (ca.)", desc: "TSMC publiserer månedlig omsetning rundt den 10.", color: "#94a3b8", cat: "TSMC-salgstall" });
  }
  // Rapportdatoer kommer fra Nasdaq-kalenderen (liveEarningsEvents) — de faste datoene i STOCK_INTEL er utdaterte.
  return events.sort((a, b) => a.date - b.date);
}

const ALL_CAL_CATS = ["FOMC / Renter","CPI / Inflasjon","NFP","Opsjonsutløp","AI-konferanser","Produktlanseringer","TSMC-salgstall","Earnings"];
const CAL_COLORS = { "FOMC / Renter":"#ef4444","CPI / Inflasjon":"#f97316","NFP":"#f97316","Opsjonsutløp":"#ec4899","AI-konferanser":"#a78bfa","Produktlanseringer":"#3b82f6","TSMC-salgstall":"#94a3b8","Earnings":"#f59e0b" };

function Sparkline({ prices, color, w=80, h=28 }) {
  if (!prices || prices.length < 2) return <svg width={w} height={h} />;
  const min = Math.min(...prices), max = Math.max(...prices), range = max - min || 1;
  const pts = prices.map((p,i) => `${(i/(prices.length-1))*w},${h-((p-min)/range)*h}`).join(" ");
  return <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function pearson(x, y) {
  const n = Math.min(x.length, y.length);
  if (n < 5) return 0;
  let sx=0,sy=0,sxx=0,syy=0,sxy=0;
  for (let i=0;i<n;i++){sx+=x[i];sy+=y[i];sxx+=x[i]*x[i];syy+=y[i]*y[i];sxy+=x[i]*y[i];}
  const mx=sx/n,my=sy/n;
  const num=sxy/n-mx*my;
  const den=Math.sqrt(Math.max(0,sxx/n-mx*mx))*Math.sqrt(Math.max(0,syy/n-my*my));
  return den<1e-10?0:Math.max(-1,Math.min(1,num/den));
}

// ── MaxPainChart ──────────────────────────────────────────────────────────────
function MaxPainChart({ points, maxPainStrike, currentPrice, calls = [], puts = [] }) {
  const [tip, setTip] = useState(null); // { x, y, strike, cv, pv, coi, poi }

  if (!points?.length) return null;
  const maxPain = Math.max(...points.map(p => p.pain));
  const W = 960, H = 200, PAD = { l: 10, r: 10, t: 8, b: 28 };
  const chartW = W - PAD.l - PAD.r;
  const chartH = H - PAD.t - PAD.b;
  const n = points.length;
  const barW = Math.max(2, Math.floor(chartW / n) - 1);
  const step = Math.max(1, Math.ceil(n / 8));
  const xOf = i => PAD.l + i * (chartW / n) + barW / 2;
  const mpIdx = points.findIndex(p => p.strike === maxPainStrike);
  const cpIdx = currentPrice != null
    ? points.reduce((best, p, i) => Math.abs(p.strike - currentPrice) < Math.abs(points[best].strike - currentPrice) ? i : best, 0)
    : -1;

  // Build per-strike volume/OI map from calls + puts
  const volMap = {};
  calls.forEach(c => {
    if (!volMap[c.strike]) volMap[c.strike] = { cv: 0, pv: 0, coi: 0, poi: 0 };
    volMap[c.strike].cv  += c.volume       || 0;
    volMap[c.strike].coi += c.openInterest || 0;
  });
  puts.forEach(p => {
    if (!volMap[p.strike]) volMap[p.strike] = { cv: 0, pv: 0, coi: 0, poi: 0 };
    volMap[p.strike].pv  += p.volume       || 0;
    volMap[p.strike].poi += p.openInterest || 0;
  });
  const maxTotalVol = Math.max(1, ...Object.values(volMap).map(v => v.cv + v.pv));
  const VOL_MAX_H = chartH * 0.65; // volume bars up to 65% of chart height

  return (
    <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"10px 14px",overflowX:"auto",position:"relative"}}>
      <div style={{fontSize:10,color:"#64748b",marginBottom:6}}>
        <span style={{color:"#94a3b8"}}>Max Pain: </span>
        <span style={{color:"#f59e0b",fontWeight:700}}>${maxPainStrike}</span>
        {currentPrice != null && (
          <span style={{color:"#94a3b8"}}> · Kurs: <span style={{color:"#22c55e",fontWeight:700}}>${currentPrice.toFixed(2)}</span></span>
        )}
        <span style={{color:"#475569",marginLeft:12,fontSize:9}}>Blå = smertekurve · Grønn/rød overlay = dagens volum · Hover for detaljer</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{display:"block",width:"100%",height:"auto"}}
        onMouseLeave={() => setTip(null)}>
        {/* Pain bars (background shape) */}
        {points.map((p, i) => {
          const x = PAD.l + i * (chartW / n);
          const bh = maxPain > 0 ? (p.pain / maxPain) * chartH : 0;
          const isMP = p.strike === maxPainStrike;
          return (
            <rect key={i} x={x} y={PAD.t + chartH - bh} width={barW} height={bh}
              fill={isMP ? "#f59e0b" : "#1e3a5f"} />
          );
        })}

        {/* Volume bars (foreground overlay) — semi-transparent, in front of pain bars */}
        {points.map((p, i) => {
          const v = volMap[p.strike];
          if (!v) return null;
          const totalVol = v.cv + v.pv;
          if (totalVol === 0) return null;
          const volH = (totalVol / maxTotalVol) * VOL_MAX_H;
          const x = PAD.l + i * (chartW / n);
          const callRatio = v.coi > 0 ? v.cv / v.coi : 0;
          const putRatio  = v.poi > 0 ? v.pv / v.poi : 0;
          const maxRatio  = Math.max(callRatio, putRatio);
          const alpha = maxRatio > 1.0 ? "bb" : maxRatio > 0.3 ? "66" : "22";
          const color = v.cv >= v.pv ? `#22c55e${alpha}` : `#ef4444${alpha}`;

          return (
            <rect key={`vol-${i}`} x={x} y={PAD.t + chartH - volH} width={barW} height={volH}
              fill={color} rx={1}
              style={{cursor:"crosshair"}}
              onMouseEnter={e => setTip({ x: e.clientX, y: e.clientY, strike: p.strike, ...v,
                cr: callRatio, pr: putRatio })}
              onMouseMove={e => setTip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null)}
            />
          );
        })}

        {mpIdx >= 0 && (
          <line x1={xOf(mpIdx)} y1={PAD.t} x2={xOf(mpIdx)} y2={PAD.t + chartH}
            stroke="#f59e0b" strokeWidth={1} strokeDasharray="3,2" />
        )}
        {cpIdx >= 0 && (
          <line x1={xOf(cpIdx)} y1={PAD.t} x2={xOf(cpIdx)} y2={PAD.t + chartH}
            stroke="#22c55e" strokeWidth={1} strokeDasharray="3,2" />
        )}
        {points.map((p, i) => i % step === 0 && (
          <text key={i} x={xOf(i)} y={H - 6} fill="#475569" fontSize={8} textAnchor="middle">
            ${p.strike}
          </text>
        ))}
      </svg>

      {/* Hover tooltip */}
      {tip && (
        <div style={{position:"fixed",left:tip.x+12,top:tip.y-10,
          background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:6,
          padding:"8px 12px",fontSize:10,color:"#e2e8f0",pointerEvents:"none",zIndex:9999,
          boxShadow:"0 4px 16px #00000060",minWidth:160}}>
          <div style={{fontWeight:700,color:"#e2e8f0",marginBottom:4}}>Strike ${tip.strike}</div>
          <div style={{color:"#22c55e"}}>Call vol: {tip.cv.toLocaleString()} / OI {tip.coi.toLocaleString()}</div>
          <div style={{color:"#ef4444"}}>Put vol:  {tip.pv.toLocaleString()} / OI {tip.poi.toLocaleString()}</div>
          <div style={{color:"#f59e0b",marginTop:4,fontWeight:700}}>
            Vol/OI: {Math.max(tip.cr, tip.pr).toFixed(1)}×
            {Math.max(tip.cr, tip.pr) > 1.0 ? " 🚨" : Math.max(tip.cr, tip.pr) > 0.3 ? " ⚠️" : ""}
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:14,fontSize:9,color:"#475569",marginTop:4,flexWrap:"wrap"}}>
        <span><span style={{color:"#f59e0b"}}>▌</span> Max Pain</span>
        <span><span style={{color:"#22c55e"}}>▌</span> Kurs nå</span>
        <span><span style={{color:"#22c55e88"}}>▌</span> Call vol</span>
        <span><span style={{color:"#ef444488"}}>▌</span> Put vol</span>
        <span style={{color:"#334155"}}>Lysere = Vol/OI &gt; 0.3 · Sterk = Vol/OI &gt; 1.0</span>
      </div>
    </div>
  );
}

// ── GEXChart ───────────────────────────────────────────────────────────────────
function GEXChart({ gexData, instZone }) {
  const [tip, setTip] = useState(null);
  const [mode, setMode] = useState("oi"); // "oi" | "vol"

  const profile = mode === "vol" ? gexData?.vol : gexData?.oi;
  const { bars: allBars, maxAbs, netGex, majorPos, majorNeg, zeroGamma } = profile ?? {};
  const cp = gexData?.cp;

  if (!allBars?.length || !maxAbs || !cp) return null;

  // Vis kun strikes innenfor ±10% av nåværende kurs
  const bars = allBars.filter(b => Math.abs(b.strike - cp) / cp <= 0.10);

  const ROW_H = 15, LABEL_W = 54, BAR_AREA = 260, CENTER_X = LABEL_W + BAR_AREA, W = CENTER_X + BAR_AREA + 70;
  const H = bars.length * ROW_H + 50;
  const yOf = i => i * ROW_H + 36;

  const idxOf = strike => bars.reduce((b, _, i) => Math.abs(bars[i].strike - strike) < Math.abs(bars[b].strike - strike) ? i : b, 0);
  const cpIdx = idxOf(cp);
  const zeroIdx = zeroGamma != null ? idxOf(zeroGamma) : -1;
  const posWallIdx = majorPos ? idxOf(majorPos.strike) : -1;
  const negWallIdx = majorNeg ? idxOf(majorNeg.strike) : -1;

  const d25Y = instZone?.delta25 != null ? yOf(idxOf(instZone.delta25)) : null;
  const d50Y = instZone?.delta50 != null ? yOf(idxOf(instZone.delta50)) : null;

  const fmtGex = v => {
    const abs = Math.abs(v);
    if (abs >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${(v / 1e6).toFixed(0)}M`;
    return `${(v / 1e3).toFixed(0)}K`;
  };

  return (
    <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"10px 14px"}}>

      {/* Header row */}
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10,flexWrap:"wrap"}}>
        <span style={{color:"#94a3b8",fontWeight:700,fontSize:11}}>⚡ GEX-profil</span>
        {/* Mode toggle */}
        <div style={{display:"flex",gap:0,border:"1px solid #1e293b",borderRadius:4,overflow:"hidden",fontSize:9}}>
          {[["oi","OI (strukturelt)"],["vol","Volum (i dag)"]].map(([m,label]) => (
            <button key={m} onClick={() => setMode(m)}
              style={{padding:"3px 8px",background: mode===m ? "#1e3a5f" : "transparent",
                border:"none",color: mode===m ? "#93c5fd" : "#475569",cursor:"pointer",fontFamily:"inherit"}}>
              {label}
            </button>
          ))}
        </div>
        <span style={{fontSize:9,color:"#334155",marginLeft:"auto"}}>15 min forsinket · planlegging</span>
      </div>

      {/* Key levels summary */}
      <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
        {[
          { label:"Zero Gamma", val: zeroGamma != null ? `$${zeroGamma}` : "—", color:"#f97316",
            hint: cp > (zeroGamma ?? 0) ? "✅ Pris over → lav vol" : "⚠️ Pris under → høy vol" },
          { label:"Major Positiv", val: majorPos ? `$${majorPos.strike}` : "—", color:"#22c55e",
            hint:"Sterkeste MM-støtte / pin-nivå" },
          { label:"Major Negativ", val: majorNeg ? `$${majorNeg.strike}` : "—", color:"#ef4444",
            hint:"Sterkeste MM-salgspress-nivå" },
          { label:"Net GEX", val: netGex != null ? fmtGex(netGex) : "—",
            color: (netGex ?? 0) >= 0 ? "#22c55e" : "#ef4444",
            hint: (netGex ?? 0) >= 0 ? "Positiv → MMs demper volatilitet" : "Negativ → MMs forsterker bevegelser" },
        ].map(({ label, val, color, hint }) => (
          <div key={label} title={hint}
            style={{background:"#040d1a",border:"1px solid #1e293b",borderRadius:6,
              padding:"5px 10px",cursor:"default"}}>
            <div style={{fontSize:8,color:"#475569",marginBottom:1}}>{label}</div>
            <div style={{fontSize:13,fontWeight:800,color}}>{val}</div>
            <div style={{fontSize:8,color:"#334155",marginTop:1}}>{hint}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{overflowX:"auto"}}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{display:"block",width:"100%",maxWidth:W,height:"auto"}}
          onMouseLeave={() => setTip(null)}>

          {/* Inst zone band */}
          {d25Y != null && d50Y != null && (
            <rect x={0} y={Math.min(d25Y, d50Y) - ROW_H / 2} width={W}
              height={Math.abs(d25Y - d50Y) + ROW_H} fill="#c084fc0c" />
          )}

          {/* Center line */}
          <line x1={CENTER_X} y1={20} x2={CENTER_X} y2={H - 18} stroke="#1e293b" strokeWidth={1} />

          {/* Bars */}
          {bars.map((b, i) => {
            const y = yOf(i);
            const barW = Math.abs(b.gex) / maxAbs * BAR_AREA;
            const isPos = b.gex >= 0;
            const isMajPos = majorPos && b.strike === majorPos.strike;
            const isMajNeg = majorNeg && b.strike === majorNeg.strike;
            const inInst = instZone?.instStrikes?.has(b.strike);
            const baseColor = isPos ? "#22c55e" : "#ef4444";
            const alpha = (isMajPos || isMajNeg) ? "ee" : inInst ? "99" : "44";
            return (
              <g key={b.strike}
                onMouseEnter={e => setTip({ x: e.clientX, y: e.clientY, ...b })}
                onMouseMove={e => setTip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null)}
                style={{ cursor: "crosshair" }}>
                <rect x={isPos ? CENTER_X : CENTER_X - barW} y={y - ROW_H / 2 + 1}
                  width={barW} height={ROW_H - 2} fill={`${baseColor}${alpha}`} rx={1} />
                <text x={CENTER_X - 4} y={y + 4} textAnchor="end"
                  fill={(isMajPos || isMajNeg) ? "#e2e8f0" : "#475569"} fontSize={9}>
                  {b.strike}
                </text>
                {(isMajPos || isMajNeg) && (
                  <text x={isPos ? CENTER_X + barW + 4 : CENTER_X - barW - 4}
                    y={y + 4} textAnchor={isPos ? "start" : "end"}
                    fill={isMajPos ? "#22c55e" : "#ef4444"} fontSize={8}>
                    {isMajPos ? "▲ major+" : "▼ major−"}
                  </text>
                )}
              </g>
            );
          })}

          {/* Zero Gamma line */}
          {zeroIdx >= 0 && (
            <>
              <line x1={0} y1={yOf(zeroIdx)} x2={W} y2={yOf(zeroIdx)}
                stroke="#f97316" strokeWidth={1.5} strokeDasharray="5,3" />
              <text x={4} y={yOf(zeroIdx) - 3} fill="#f97316" fontSize={8} fontWeight="bold">ZERO γ</text>
            </>
          )}

          {/* Current price line */}
          {cpIdx >= 0 && (
            <>
              <line x1={0} y1={yOf(cpIdx)} x2={W} y2={yOf(cpIdx)}
                stroke="#22c55e" strokeWidth={1} strokeDasharray="4,3" />
              <text x={W - 4} y={yOf(cpIdx) - 3} textAnchor="end" fill="#22c55e" fontSize={8}>${cp.toFixed(0)}</text>
            </>
          )}

          {/* Major pos wall line */}
          {posWallIdx >= 0 && (
            <line x1={0} y1={yOf(posWallIdx)} x2={W} y2={yOf(posWallIdx)}
              stroke="#22c55e" strokeWidth={1} strokeDasharray="2,4" opacity={0.4} />
          )}

          {/* Major neg wall line */}
          {negWallIdx >= 0 && (
            <line x1={0} y1={yOf(negWallIdx)} x2={W} y2={yOf(negWallIdx)}
              stroke="#ef4444" strokeWidth={1} strokeDasharray="2,4" opacity={0.4} />
          )}

          {/* Inst zone labels */}
          {d25Y != null && (
            <text x={W - 4} y={d25Y - 3} textAnchor="end" fill="#c084fc" fontSize={8}>δ0.25 kjøper→mål</text>
          )}
          {d50Y != null && (
            <text x={W - 4} y={d50Y - 3} textAnchor="end" fill="#f43f5e80" fontSize={8}>δ0.50 selger→motstand</text>
          )}

          {/* Axis labels */}
          <text x={CENTER_X - BAR_AREA / 2} y={14} textAnchor="middle" fill="#ef444450" fontSize={8}>← PUT gamma</text>
          <text x={CENTER_X + BAR_AREA / 2} y={14} textAnchor="middle" fill="#22c55e50" fontSize={8}>CALL gamma →</text>
        </svg>
      </div>

      {/* Tooltip */}
      {tip && (
        <div style={{position:"fixed",left:tip.x+12,top:tip.y-10,
          background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:6,
          padding:"8px 12px",fontSize:10,color:"#e2e8f0",pointerEvents:"none",zIndex:9999,minWidth:160}}>
          <div style={{fontWeight:700,marginBottom:4}}>Strike ${tip.strike}</div>
          <div style={{color: tip.gex >= 0 ? "#22c55e" : "#ef4444",fontWeight:600}}>
            {tip.gex >= 0 ? "+" : ""}{fmtGex(tip.gex)} GEX
          </div>
          <div style={{color:"#64748b",fontSize:9,marginTop:3}}>
            {tip.gex >= 0
              ? "Call gamma → MM kjøper på dip (pin/magnet)"
              : "Put gamma → MM selger på fall (forsterker)"}
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:12,fontSize:9,color:"#475569",marginTop:6,flexWrap:"wrap"}}>
        <span><span style={{color:"#22c55e"}}>▌</span> Call gamma (pos GEX)</span>
        <span><span style={{color:"#ef4444"}}>▌</span> Put gamma (neg GEX)</span>
        <span><span style={{color:"#f97316"}}>╌</span> Zero Gamma (flipnivå)</span>
        <span><span style={{color:"#22c55e"}}>╌</span> Major+ / <span style={{color:"#ef4444"}}>╌</span> Major−</span>
        {instZone?.delta25 != null && <span><span style={{color:"#c084fc"}}>▌</span> Inst. sone (δ 0.25–0.50)</span>}
      </div>
    </div>
  );
}

// ── OptionsFlow ────────────────────────────────────────────────────────────────
function OptionsFlow({ initialTicker = "SPY", initialExp = null, onDeepDive = null }) {
  const [input,   setInput]   = useState(initialTicker);
  const [ticker,  setTicker]  = useState(initialTicker);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchErr,setFetchErr]= useState(null);
  const [selExp,  setSelExp]  = useState(null);
  const [uSort,   setUSort]   = useState({ col: "ratio", dir: "desc" });
  const [fetchedAt, setFetchedAt] = useState(null);

  const isProxy = window.location.hostname !== "localhost";

  function doFetch(t, exp) {
    setLoading(true);
    setFetchErr(null);
    const url = `/options?t=${encodeURIComponent(t)}${exp ? `&exp=${exp}` : ""}`;
    fetch(url)
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        setData(d);
        setFetchedAt(Date.now());
        if (!exp) setSelExp(d.expirations?.[0] ?? null);
      })
      .catch(e => setFetchErr(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    setInput(initialTicker); setTicker(initialTicker); setData(null);
    setSelExp(initialExp); setFetchedAt(null);
  }, [initialTicker, initialExp]);

  useEffect(() => {
    if (!isProxy) { setFetchErr("Kun tilgjengelig i produksjon (Cloudflare)"); return; }
    doFetch(ticker, null);
    const id = setInterval(() => doFetch(ticker, null), 15 * 60 * 1000);
    return () => clearInterval(id);
  }, [ticker]);

  // Derive calls/puts for the selected expiration — no re-fetch needed
  const activeCalls = useMemo(() => {
    if (!data) return [];
    const src = data.contracts ?? data.calls ?? [];
    if (!selExp) return (data.calls ?? []);
    return src.filter(o => o.expiration === selExp && o.type === "C").sort((a,b) => a.strike-b.strike);
  }, [data, selExp]);

  const activePuts = useMemo(() => {
    if (!data) return [];
    const src = data.contracts ?? data.puts ?? [];
    if (!selExp) return (data.puts ?? []);
    return src.filter(o => o.expiration === selExp && o.type === "P").sort((a,b) => a.strike-b.strike);
  }, [data, selExp]);

  // Max Pain
  const maxPainData = useMemo(() => {
    if (!activeCalls.length || !activePuts.length) return null;
    const cp = data?.currentPrice;
    const allStrikes = [...new Set([...activeCalls.map(c => c.strike), ...activePuts.map(p => p.strike)])].sort((a,b) => a-b);
    const range = cp ? allStrikes.filter(s => s >= cp * 0.75 && s <= cp * 1.25) : allStrikes;
    if (range.length === 0) return null;

    const points = range.map(S => {
      const cPain = activeCalls.reduce((s,c) => c.strike < S ? s + (S - c.strike) * (c.openInterest||0) : s, 0);
      const pPain = activePuts.reduce((s,p)  => p.strike > S ? s + (p.strike - S) * (p.openInterest||0) : s, 0);
      return { strike: S, pain: cPain + pPain };
    });

    const min = Math.min(...points.map(p => p.pain));
    const maxPainStrike = points.find(p => p.pain === min)?.strike ?? null;
    return { points, maxPainStrike };
  }, [activeCalls, activePuts, data]);

  // Unusual activity — scan alle utløp
  const unusual = useMemo(() => {
    const allContracts = data?.contracts ?? [];
    const cp = data?.currentPrice;
    if (!allContracts.length || !cp) return [];
    return allContracts
      .filter(c => (c.volume||0) >= 50 && (c.openInterest||0) > 0)
      .map(c => {
        const ratio = (c.volume||0) / Math.max(1, c.openInterest||1);
        const mid   = ((c.bid||0) + (c.ask||0)) / 2;
        const px    = mid > 0.01 ? mid : (c.last||0);
        let side = null;
        if (c.last != null && mid > 0.01)
          side = c.last < mid * 0.97 ? "BID" : c.last > mid * 1.03 ? "ASK" : "MID";
        const premium = Math.round((c.volume||0) * px * 100);
        const otmPct  = c.type === "C"
          ? (c.strike - cp) / cp * 100
          : (cp - c.strike) / cp * 100;
        const otmBonus  = otmPct > 5 ? 2.5 : otmPct > 2 ? 1.5 : 1;
        const askBonus  = side === "ASK" ? 1.5 : 1;
        const smartScore = premium * otmBonus * askBonus * Math.min(ratio, 10);
        return { ...c, ratio, side, premium, otmPct, smartScore };
      })
      .filter(c => c.ratio > 0.3)
      .sort((a,b) => b.ratio - a.ratio)
      .slice(0, 40);
  }, [data]);

  // Smart money: OTM-opsjoner med stort premiumbeløp — "noen vet noe"
  const smartMoney = useMemo(() => {
    if (!unusual.length) return [];
    return [...unusual]
      .filter(c => c.premium >= 100000 && c.otmPct > 1)
      .sort((a,b) => b.smartScore - a.smartScore)
      .slice(0, 6);
  }, [unusual]);

  // Put/Call ratio
  const pcRatio = useMemo(() => {
    const cv = activeCalls.reduce((s,c) => s + (c.volume||0), 0);
    const pv = activePuts.reduce((s,p)  => s + (p.volume||0), 0);
    return cv > 0 ? (pv / cv) : null;
  }, [activeCalls, activePuts]);

  // ATM IV — average IV of the call and put closest to current price
  const atmIV = useMemo(() => {
    if (!activeCalls.length || !activePuts.length || data?.currentPrice == null) return null;
    const cp = data.currentPrice;
    const nearCall = activeCalls.reduce((b,c) => Math.abs(c.strike-cp) < Math.abs(b.strike-cp) ? c : b, activeCalls[0]);
    const nearPut  = activePuts.reduce((b,p)  => Math.abs(p.strike-cp) < Math.abs(b.strike-cp) ? p : b, activePuts[0]);
    const ivs = [nearCall?.impliedVolatility, nearPut?.impliedVolatility].filter(v => v != null && v > 0);
    return ivs.length ? ivs.reduce((s,v) => s+v, 0) / ivs.length : null;
  }, [activeCalls, activePuts, data]);

  // GEX by strike — Volume GEX (today's flow) + OI GEX (structural), all expirations
  const gexData = useMemo(() => {
    if (!data?.currentPrice || !data?.contracts?.length) return null;
    const cp = data.currentPrice;
    const now = new Date();
    const oiMap = {}, volMap = {};

    data.contracts.forEach(o => {
      if (!o.expiration) return;
      const dL = Math.max(1, Math.round((new Date(o.expiration + "T12:00:00") - now) / (1000 * 60 * 60 * 24)));
      const T = dL / 365;
      const g = o.gamma != null ? Math.abs(o.gamma)
        : (o.impliedVolatility ? bsGamma(cp, o.strike, T, o.impliedVolatility) : 0);
      if (!g || !isFinite(g)) return;
      const sign = o.type === "C" ? 1 : -1;
      if ((o.openInterest || 0) > 0) {
        const gex = o.openInterest * g * cp * cp * 0.01;
        oiMap[o.strike] = (oiMap[o.strike] || 0) + sign * gex;
      }
      if ((o.volume || 0) > 0) {
        const gex = o.volume * g * cp * cp * 0.01;
        volMap[o.strike] = (volMap[o.strike] || 0) + sign * gex;
      }
    });

    function buildProfile(map) {
      const bars = Object.entries(map)
        .map(([k, v]) => ({ strike: +k, gex: v }))
        .filter(d => d.strike >= cp * 0.78 && d.strike <= cp * 1.25)
        .sort((a, b) => b.strike - a.strike);
      if (!bars.length) return null;
      const maxAbs = Math.max(...bars.map(d => Math.abs(d.gex)));
      const netGex = bars.reduce((s, b) => s + b.gex, 0);
      const majorPos = [...bars].filter(b => b.gex > 0).sort((a, b) => b.gex - a.gex)[0] ?? null;
      const majorNeg = [...bars].filter(b => b.gex < 0).sort((a, b) => a.gex - b.gex)[0] ?? null;
      // Zero gamma: cumulative from high to low, find sign change
      let cum = 0, zeroGamma = null;
      for (const b of bars) {
        const prev = cum;
        cum += b.gex;
        if (prev !== 0 && ((prev > 0 && cum <= 0) || (prev < 0 && cum >= 0))) {
          zeroGamma = b.strike;
          break;
        }
      }
      return { bars, maxAbs, netGex, majorPos, majorNeg, zeroGamma };
    }

    return { oi: buildProfile(oiMap), vol: buildProfile(volMap), cp };
  }, [data]);

  // Institutional zone — calls with delta 0.20–0.55 (the entry-to-exit zone institutions trade)
  const instZone = useMemo(() => {
    if (!activeCalls.length || !data?.currentPrice || !selExp) return null;
    const cp = data.currentPrice;
    const dL = Math.max(1, Math.round((new Date(selExp + "T12:00:00") - new Date()) / (1000 * 60 * 60 * 24)));
    const T = dL / 365;
    const withDelta = activeCalls.map(c => {
      const d = c.delta != null ? Math.abs(c.delta)
        : (c.impliedVolatility ? bsDelta(cp, c.strike, T, c.impliedVolatility, true) : null);
      return { ...c, calcDelta: d };
    }).filter(c => c.calcDelta != null && c.calcDelta > 0);

    // Find strikes closest to delta 0.25 (OTM — where inst buys) and 0.50 (ATM — where inst sells)
    const aboveATM = withDelta.filter(c => c.strike > cp);
    const atOrBelow = withDelta.filter(c => c.strike <= cp);
    const d25 = aboveATM.length
      ? aboveATM.reduce((b, c) => Math.abs(c.calcDelta - 0.25) < Math.abs(b.calcDelta - 0.25) ? c : b, aboveATM[0])
      : null;
    const d50 = withDelta.reduce((b, c) => Math.abs(c.calcDelta - 0.50) < Math.abs(b.calcDelta - 0.50) ? c : b, withDelta[0]);

    const inZone = withDelta.filter(c => c.calcDelta >= 0.20 && c.calcDelta <= 0.55);
    const instStrikes = new Set(inZone.map(c => c.strike));
    const topByVol = [...inZone].sort((a, b) => (b.volume || 0) - (a.volume || 0)).slice(0, 6);

    return {
      delta25: d25?.strike ?? null,
      delta50: d50?.strike ?? null,
      instStrikes,
      topByVol,
      d25Call: d25,
      d50Call: d50,
    };
  }, [activeCalls, data, selExp]);

  const fmtDate = d => {
    const [y,m,dd] = d.split("-");
    const months = ["jan","feb","mar","apr","mai","jun","jul","aug","sep","okt","nov","des"];
    return `${parseInt(dd)} ${months[parseInt(m)-1]} ${y}`;
  };

  return (
    <div style={{padding:"20px 28px",maxWidth:1300,margin:"0 auto"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <span style={{fontSize:15,fontWeight:700,color:"#e2e8f0"}}>🎯 Opsjons-analyse</span>
        <span style={{fontSize:10,color:"#475569"}}>CBOE · OI oppdateres etter børsslutt · Vol oppdateres ~15min</span>
        {fetchedAt && (
          <span style={{fontSize:10,color:"#334155"}}>
            {data?.cboeTimestamp
              ? <>Data fra CBOE: <span style={{color:"#22c55e"}}>{new Date(data.cboeTimestamp).toLocaleTimeString("nb-NO",{hour:"2-digit",minute:"2-digit",second:"2-digit"})}</span></>
              : <>Hentet: <span style={{color:"#64748b"}}>{new Date(fetchedAt).toLocaleTimeString("nb-NO",{hour:"2-digit",minute:"2-digit",second:"2-digit"})}</span> · CBOE oppgir ikke datatidspunkt</>
            }
          </span>
        )}
        {onDeepDive && ticker && (
          <button onClick={() => onDeepDive(ticker)}
            style={{padding:"4px 10px",fontSize:10,background:"#0a1f38",border:"1px solid #3b82f6",
              borderRadius:4,color:"#93c5fd",cursor:"pointer",fontFamily:"inherit"}}>
            🔬 Deep Dive {ticker} →
          </button>
        )}
        <div style={{display:"flex",gap:6,marginLeft:"auto"}}>
          <input
            value={input}
            onChange={e => setInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === "Enter" && setTicker(input)}
            style={{width:76,padding:"5px 8px",background:"#060f1c",border:"1px solid #1e293b",
              borderRadius:4,color:"#e2e8f0",fontFamily:"inherit",fontSize:12}}
            placeholder="TICKER"
          />
          <button onClick={() => { setSelExp(null); setTicker(input); }}
            style={{padding:"5px 10px",background:"#1e3a5f",border:"1px solid #3b82f6",
              borderRadius:4,color:"#93c5fd",fontSize:11,cursor:"pointer"}}>
            Søk
          </button>
        </div>
      </div>

      {loading && <div style={{color:"#64748b",fontSize:11,padding:12}}>Henter CBOE-data for {ticker}...</div>}
      {fetchErr && <div style={{color:"#f43f5e",fontSize:11,padding:12}}>Feil: {fetchErr}</div>}

      {data && !loading && (
        <>
          {/* Expiration tabs */}
          <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:14}}>
            {data.expirations?.map(exp => (
              <button key={exp} onClick={() => setSelExp(exp)}
                style={{
                  padding:"4px 9px",fontSize:10,borderRadius:4,cursor:"pointer",
                  background: exp === selExp ? "#1e3a5f" : "transparent",
                  border: `1px solid ${exp === selExp ? "#3b82f6" : "#1e293b"}`,
                  color:  exp === selExp ? "#93c5fd" : "#64748b",
                }}>
                {fmtDate(exp)}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
            {[
              ["Kurs nå", data.currentPrice != null ? `$${data.currentPrice.toFixed(2)}` : "–", "#22c55e"],
              ["Max Pain", maxPainData?.maxPainStrike != null ? `$${maxPainData.maxPainStrike}` : "–", "#f59e0b"],
              ["Put/Call ratio", pcRatio != null ? pcRatio.toFixed(2) : "–", pcRatio > 1 ? "#ef4444" : "#22c55e"],
              ["Utløp", selExp ? fmtDate(selExp) : "–", "#94a3b8"],
              ["Call vol", activeCalls.reduce((s,c)=>s+(c.volume||0),0).toLocaleString(), "#22c55e"],
              ["Put vol",  activePuts.reduce((s,p)=>s+(p.volume||0),0).toLocaleString(), "#ef4444"],
              ["ATM IV", atmIV != null ? `${(atmIV*100).toFixed(0)}%` : "–",
                atmIV == null ? "#64748b" : atmIV > 0.60 ? "#ef4444" : atmIV > 0.35 ? "#f59e0b" : atmIV > 0.20 ? "#94a3b8" : "#22c55e"],
            ].map(([label, val, color]) => (
              <div key={label} style={{background:"#060f1c",border:"1px solid #1e293b",
                borderRadius:6,padding:"6px 12px",minWidth:80}}>
                <div style={{fontSize:9,color:"#475569",marginBottom:2}}>{label}</div>
                <div style={{fontSize:13,fontWeight:700,color}}>{val}</div>
              </div>
            ))}
          </div>

          {/* Put/Call forklaring */}
          {pcRatio != null && (
            <div style={{fontSize:10,color:"#475569",marginBottom:12,padding:"6px 10px",
              background:"#060f1c",border:"1px solid #1e293b",borderRadius:6}}>
              {pcRatio > 1.2
                ? "🔴 P/C > 1.2 — uvanlig mange Puts. Markedet kjøper forsikring mot fall. Bearish sentiment."
                : pcRatio < 0.7
                ? "🟢 P/C < 0.7 — uvanlig mange Calls. Aggressivt bullish. Kan signalere topp (grådighet)."
                : "🟡 P/C 0.7–1.2 — normalt nivå. Ingen klar retning fra opsjonsmarkedet."}
            </div>
          )}

          {/* ATM IV tolkning */}
          {atmIV != null && (
            <div style={{fontSize:10,color:"#475569",marginBottom:12,padding:"6px 10px",
              background:"#060f1c",border:"1px solid #1e293b",borderRadius:6}}>
              {atmIV > 0.60
                ? `🔴 ATM IV ${(atmIV*100).toFixed(0)}% — svært høyt (earnings/event-nivå). Opsjoner er ekstremt dyre. IV Crush-risiko etter nyheten.`
                : atmIV > 0.35
                ? `🟠 ATM IV ${(atmIV*100).toFixed(0)}% — forhøyet. Markedet priser inn usikkerhet (FOMC, earnings e.l.). Kjøp opsjoner med varsomhet.`
                : atmIV > 0.20
                ? `🟡 ATM IV ${(atmIV*100).toFixed(0)}% — normalt nivå. Opsjoner er rimelig priset.`
                : `🟢 ATM IV ${(atmIV*100).toFixed(0)}% — lavt. Opsjoner er billige. Godt tidspunkt å kjøpe beskyttelse.`}
            </div>
          )}

          {/* Max Pain chart */}
          {maxPainData && (
            <div style={{marginBottom:14}}>
              <MaxPainChart
                points={maxPainData.points}
                maxPainStrike={maxPainData.maxPainStrike}
                currentPrice={data.currentPrice}
                calls={activeCalls}
                puts={activePuts}
              />
            </div>
          )}

          {/* GEX profile + Institutional zone */}
          {(gexData?.oi || gexData?.vol) && (
            <div style={{marginBottom:14}}>
              <GEXChart gexData={gexData} instZone={instZone} />
            </div>
          )}

          {/* Institutional zone panel */}
          {instZone && (instZone.delta25 || instZone.topByVol?.length > 0) && (
            <div style={{background:"#060f1c",border:"1px solid #2d1b4e",borderRadius:8,
              padding:"12px 16px",marginBottom:14}}>
              <div style={{fontSize:10,color:"#c084fc",fontWeight:600,marginBottom:10,letterSpacing:0.5}}>
                🏦 INSTITUSJONELL SONE (δ 0.25 – 0.50)
                <span style={{color:"#475569",fontWeight:400,marginLeft:8}}>
                  Kjøper OTM calls (δ≈0.25), selger ved ATM (δ≈0.50) — slik kapitaliserer de maksimalt på gammaen
                </span>
              </div>
              <div style={{display:"flex",gap:10,marginBottom:10,flexWrap:"wrap"}}>
                {instZone.delta25 != null && (
                  <div style={{background:"#1a0f2e",border:"1px solid #6d28d9",borderRadius:6,padding:"8px 14px"}}>
                    <div style={{fontSize:9,color:"#7c3aed",marginBottom:2}}>🎯 NESTE AKSJEMÅL / MOTSTAND</div>
                    <div style={{fontSize:16,fontWeight:800,color:"#c084fc"}}>${instZone.delta25.toFixed(0)}</div>
                    <div style={{fontSize:9,color:"#64748b",marginTop:2}}>δ≈0.25 — inst. kjøper calls her nå</div>
                  </div>
                )}
                {instZone.delta50 != null && (
                  <div style={{background:"#0a1628",border:"1px solid #3b1f6e",borderRadius:6,padding:"8px 14px"}}>
                    <div style={{fontSize:9,color:"#a78bfa",marginBottom:2}}>🔴 INST. EXIT / SALGSTRYKK</div>
                    <div style={{fontSize:16,fontWeight:800,color:"#c4b5fd"}}>${instZone.delta50.toFixed(0)}</div>
                    <div style={{fontSize:9,color:"#64748b",marginTop:2}}>δ≈0.50 ATM — inst. selger calls her → motstand</div>
                  </div>
                )}
                {(gexData?.oi?.zeroGamma ?? gexData?.vol?.zeroGamma) != null && (
                  <div style={{background:"#1a0f00",border:"1px solid #f97316",borderRadius:6,padding:"8px 14px"}}>
                    <div style={{fontSize:9,color:"#f97316",marginBottom:2}}>⚡ ZERO GAMMA</div>
                    <div style={{fontSize:16,fontWeight:800,color:"#fb923c"}}>
                      ${(gexData?.oi?.zeroGamma ?? gexData?.vol?.zeroGamma).toFixed(0)}
                    </div>
                    <div style={{fontSize:9,color:"#64748b",marginTop:2}}>
                      {data.currentPrice > (gexData?.oi?.zeroGamma ?? 0) ? "✅ Pris over → lav vol-regime" : "⚠️ Pris under → høy vol-regime"}
                    </div>
                  </div>
                )}
              </div>
              {instZone.topByVol.length > 0 && (
                <>
                  <div style={{fontSize:9,color:"#64748b",marginBottom:6}}>
                    Topp calls i sonen (δ 0.20–0.55) · sortert etter volum — disse er aktive institusjonelle posisjoner:
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                      <thead>
                        <tr style={{color:"#475569",borderBottom:"1px solid #1e293b"}}>
                          {["Strike","Delta","Vol","OI","IV","Bid/Ask","Tolkning"].map(h => (
                            <th key={h} style={{textAlign:"left",padding:"3px 8px",fontWeight:500,fontSize:9}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {instZone.topByVol.map((c, i) => {
                          const vol = c.volume || 0;
                          const oi = c.openInterest || 0;
                          const ratio = oi > 0 ? vol / oi : 0;
                          const isNew = ratio > 0.8;
                          const isTarget = instZone.delta25 != null && Math.abs(c.strike - instZone.delta25) < 2;
                          return (
                            <tr key={i} style={{borderBottom:"1px solid #0d1f35",
                              background: isTarget ? "#1a0f2e40" : "transparent"}}>
                              <td style={{padding:"4px 8px",color:"#e2e8f0",fontWeight:700}}>
                                ${c.strike}
                                {isTarget && <span style={{color:"#c084fc",marginLeft:4,fontSize:8}}>🎯target</span>}
                              </td>
                              <td style={{padding:"4px 8px",color:"#a78bfa"}}>{c.calcDelta != null ? c.calcDelta.toFixed(2) : "—"}</td>
                              <td style={{padding:"4px 8px",color:"#22c55e"}}>{vol.toLocaleString()}</td>
                              <td style={{padding:"4px 8px",color:"#94a3b8"}}>{oi.toLocaleString()}</td>
                              <td style={{padding:"4px 8px",color:"#f59e0b"}}>{c.impliedVolatility ? `${(c.impliedVolatility*100).toFixed(0)}%` : "—"}</td>
                              <td style={{padding:"4px 8px",color:"#64748b",fontSize:10}}>
                                {c.bid != null && c.ask != null ? `$${c.bid.toFixed(2)}/$${c.ask.toFixed(2)}` : "—"}
                              </td>
                              <td style={{padding:"4px 8px",fontSize:9,color: isNew ? "#fbbf24" : "#475569"}}>
                                {isNew ? "🔥 Ny stor posisjon" : ratio > 0.3 ? "Aktiv" : "Lav aktivitet"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <div style={{marginTop:10,padding:"8px 10px",background:"#040d1a",borderRadius:6,
                border:"1px solid #1e293b",fontSize:9,color:"#64748b",lineHeight:1.6}}>
                <b style={{color:"#94a3b8"}}>Slik bruker du dette:</b>{" "}
                Institusjonene <b style={{color:"#22c55e"}}>kjøper</b> calls ved δ≈0.25 (OTM, billig leverage) — det er
                {" "}<b style={{color:"#c084fc"}}>${instZone.delta25?.toFixed(0) ?? "?"}</b> nå.
                Når aksjen stiger dit, blir disse callene ATM (δ≈0.50) og institusjonene <b style={{color:"#f43f5e"}}>selger</b> dem.
                Market makers re-hedger ved å selge aksjer → salgstrykk og motstand ved{" "}
                <b style={{color:"#c4b5fd"}}>${instZone.delta50?.toFixed(0) ?? "?"}</b>.
                Bruk δ0.25-striken som <b style={{color:"#fbbf24"}}>aksjemål / exit-nivå</b> — ikke støtte.
              </div>
            </div>
          )}

          {/* Smart Money Alerts */}
          {smartMoney.length > 0 && (
            <div style={{marginBottom:16,background:"#0a0f1a",border:"1px solid #f59e0b40",borderRadius:8,padding:"10px 14px"}}>
              <div style={{fontSize:11,color:"#f59e0b",fontWeight:700,marginBottom:8,letterSpacing:0.5}}>
                🔥 SMART MONEY ALERTS — store OTM-bets på tvers av alle utløp
                <span style={{fontSize:9,color:"#78350f",marginLeft:8,fontWeight:400}}>Premium &gt; $100k · OTM &gt; 1% · skalert etter størrelse + avstand</span>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {smartMoney.map((c, i) => {
                  const isCall = c.type === "C";
                  const fmtPrem = c.premium >= 1e6 ? `$${(c.premium/1e6).toFixed(1)}M` : `$${(c.premium/1000).toFixed(0)}K`;
                  const dir = isCall
                    ? (c.side === "ASK" ? "🟢 Aggressiv CALL-kjøp" : "🟡 CALL-aktivitet")
                    : (c.side === "ASK" ? "🔴 Aggressiv PUT-kjøp" : "🟡 PUT-aktivitet");
                  return (
                    <div key={i} style={{display:"flex",alignItems:"center",gap:10,background:"#060f1c",borderRadius:6,padding:"7px 10px",borderLeft:`3px solid ${isCall?"#22c55e":"#ef4444"}`}}>
                      <span style={{color:isCall?"#22c55e":"#ef4444",fontWeight:700,fontSize:11,minWidth:36}}>{c.type}</span>
                      <span style={{color:"#e2e8f0",fontSize:12,fontWeight:700,minWidth:54}}>${c.strike}</span>
                      <span style={{color:"#94a3b8",fontSize:10,minWidth:60}}>{c.expiration}</span>
                      <span style={{color:"#f59e0b",fontSize:11,fontWeight:700,minWidth:44}}>{c.otmPct > 0 ? `+${c.otmPct.toFixed(1)}%` : `${c.otmPct.toFixed(1)}%`} OTM</span>
                      <span style={{color:"#22d3ee",fontSize:11,fontWeight:700,minWidth:56}}>{fmtPrem}</span>
                      <span style={{color:"#64748b",fontSize:10,minWidth:44}}>{c.ratio.toFixed(1)}× V/OI</span>
                      <span style={{fontSize:10}}>{dir}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Unusual activity table */}
          <div>
            <div style={{fontSize:11,color:"#64748b",marginBottom:8,letterSpacing:0.5}}>
              🚨 UVANLIG AKTIVITET — alle utløp
              <span style={{color:"#334155",marginLeft:8,fontSize:9}}>Vol/OI &gt; 0.3 · min 50 kontrakter · sortert etter aktivitetsratio</span>
            </div>

            {unusual.length === 0
              ? <div style={{fontSize:10,color:"#334155",padding:8}}>Ingen uvanlig aktivitet funnet.</div>
              : (
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{color:"#475569",borderBottom:"1px solid #1e293b"}}>
                      {[
                        {h:"Type",      k:"type"},
                        {h:"Strike",    k:"strike"},
                        {h:"Utløp",     k:"expiration"},
                        {h:"OTM%",      k:"otmPct"},
                        {h:"Premium $", k:"premium"},
                        {h:"Vol",       k:"volume"},
                        {h:"OI",        k:"openInterest"},
                        {h:"Vol/OI",    k:"ratio"},
                        {h:"IV",        k:"impliedVolatility"},
                        {h:"Side",      k:"side"},
                        {h:"Signal",    k:null},
                      ].map(({h, k}) => (
                        <th key={h} onClick={k ? () => setUSort(s => ({
                          col: k, dir: s.col === k && s.dir === "desc" ? "asc" : "desc"
                        })) : undefined}
                          style={{padding:"6px 10px",textAlign:"left",fontWeight:400,
                            whiteSpace:"nowrap",cursor:k?"pointer":"default",
                            color: uSort.col === k ? "#93c5fd" : "#475569",
                            userSelect:"none"}}>
                          {h}{k && uSort.col === k ? (uSort.dir === "desc" ? " ↓" : " ↑") : ""}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...unusual].sort((a, b) => {
                      const v = r => r[uSort.col] ?? (uSort.col === "impliedVolatility" ? -1 : 0);
                      const d = uSort.dir === "desc" ? -1 : 1;
                      return typeof v(a) === "string"
                        ? d * v(a).localeCompare(v(b))
                        : d * (v(a) - v(b));
                    }).map((c, i) => {
                      const isCall = c.type === "C";
                      const sideColor = c.side === "ASK" ? "#22c55e" : c.side === "BID" ? "#ef4444" : "#64748b";
                      const signal = c.side === "ASK"
                        ? (isCall ? "🟢 Aggressiv kjøp (bull)" : "🔴 Kjøper forsikring (bear)")
                        : c.side === "BID"
                        ? (isCall ? "🔴 Inst. selger premium (bear)" : "🟢 Selger forsikring (bull)")
                        : "–";
                      const fmtPrem = c.premium >= 1e6 ? `$${(c.premium/1e6).toFixed(1)}M` : c.premium >= 1000 ? `$${(c.premium/1000).toFixed(0)}K` : `$${c.premium}`;
                      const otmColor = c.otmPct > 5 ? "#f59e0b" : c.otmPct > 2 ? "#94a3b8" : "#475569";
                      return (
                        <tr key={i} style={{borderBottom:"1px solid #0a1628",color:"#94a3b8"}}>
                          <td style={{padding:"6px 10px",color:isCall?"#22c55e":"#ef4444",fontWeight:700}}>
                            {isCall ? "CALL" : "PUT"}
                          </td>
                          <td style={{padding:"6px 10px",color:"#e2e8f0"}}>${c.strike}</td>
                          <td style={{padding:"6px 10px",fontSize:10}}>{c.expiration}</td>
                          <td style={{padding:"6px 10px",color:otmColor,fontWeight: c.otmPct > 3 ? 700 : 400}}>
                            {c.otmPct > 0 ? `+${c.otmPct.toFixed(1)}%` : `${c.otmPct.toFixed(1)}%`}
                          </td>
                          <td style={{padding:"6px 10px",color: c.premium >= 500000 ? "#f59e0b" : c.premium >= 100000 ? "#94a3b8" : "#475569", fontWeight: c.premium >= 100000 ? 700 : 400}}>
                            {fmtPrem}
                          </td>
                          <td style={{padding:"6px 10px",color:"#e2e8f0"}}>{(c.volume||0).toLocaleString()}</td>
                          <td style={{padding:"6px 10px"}}>{(c.openInterest||0).toLocaleString()}</td>
                          <td style={{padding:"6px 10px",color:"#f59e0b",fontWeight:700}}>{c.ratio.toFixed(1)}×</td>
                          <td style={{padding:"6px 10px"}}>
                            {c.impliedVolatility != null ? `${(c.impliedVolatility*100).toFixed(0)}%` : "–"}
                          </td>
                          <td style={{padding:"6px 10px",color:sideColor,fontWeight:700}}>{c.side ?? "–"}</td>
                          <td style={{padding:"6px 10px",fontSize:11}}>{signal}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{marginTop:12,padding:"10px 12px",background:"#060f1c",border:"1px solid #1e293b",borderRadius:6}}>
              <div style={{fontSize:9,color:"#475569",fontWeight:700,marginBottom:6,letterSpacing:0.5}}>TOLKNING AV SIGNALER</div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:9}}>
                <thead>
                  <tr style={{color:"#334155",borderBottom:"1px solid #0a1628"}}>
                    {["Type","Side","Signal","Hva det betyr"].map(h=>(
                      <th key={h} style={{padding:"3px 8px",textAlign:"left",fontWeight:400}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["CALL","ASK","🟢 Kjøp aggressivt","Noen kjøpte calls raskt — tror på oppgang. MM må kjøpe aksjer for å hedgе."],
                    ["CALL","BID","🔴 Inst. selger premium","Selger calls = vedder mot oppgang, eller lukker long-posisjon."],
                    ["PUT","ASK","🔴 Kjøper forsikring","Kjøper puts = betaler for beskyttelse mot fall. Bearish sentiment."],
                    ["PUT","BID","🟢 Selger forsikring","Selger puts = lover å kjøpe aksjen om den faller til strike. Bullish — tror kursen holder."],
                  ].map(([type,side,signal,forklaring])=>(
                    <tr key={type+side} style={{borderBottom:"1px solid #0a1628",verticalAlign:"top"}}>
                      <td style={{padding:"4px 8px",color:type==="CALL"?"#22c55e":"#ef4444",fontWeight:700}}>{type}</td>
                      <td style={{padding:"4px 8px",color:"#64748b"}}>{side}</td>
                      <td style={{padding:"4px 8px",whiteSpace:"nowrap"}}>{signal}</td>
                      <td style={{padding:"4px 8px",color:"#475569",lineHeight:1.4}}>{forklaring}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{fontSize:9,color:"#334155",marginTop:8,lineHeight:1.5}}>
                Vol/OI &gt; 1× = mer handel i dag enn totale åpne kontrakter → nye posisjoner (Scenario 1: Krutttønne).
                Vol/OI 0.3–1× = høy aktivitet på eksisterende posisjoner → kan være lukking (Scenario 2).
                Sjekk OI neste morgen: økte OI = varig posisjon; falt OI = daytrading/lukket (Scenario 3).
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── OpexGrid ──────────────────────────────────────────────────────────────────
function OpexGrid({ onSelect }) {
  const DEFAULT = ["SPX","XSP","SPY","QQQ","AAPL","MSFT","NVDA","AMZN","META"];
  const [tickers,  setTickers]  = useState(DEFAULT);
  const [addInput, setAddInput] = useState("");
  const [results,  setResults]  = useState({});
  const isProxy = window.location.hostname !== "localhost";

  function fetchOne(t) {
    setResults(prev => ({ ...prev, [t]: { loading: true } }));
    fetch(`/options?t=${encodeURIComponent(t)}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        setResults(prev => ({ ...prev, [t]: {
          loading: false,
          cp: d.currentPrice,
          expirations: d.expirations || [],
          byExp: d.byExpiration || {},
        }}));
      })
      .catch(e => setResults(prev => ({ ...prev, [t]: { loading: false, error: e.message } })));
  }

  function fetchAll() { tickers.forEach(fetchOne); }
  useEffect(() => { if (isProxy) fetchAll(); }, []);

  function addTicker() {
    const t = addInput.trim().toUpperCase().replace(/[^A-Z0-9.]/g, "");
    if (!t || tickers.includes(t)) return;
    setTickers(p => [...p, t]); setAddInput(""); fetchOne(t);
  }
  function removeTicker(t) {
    setTickers(p => p.filter(x => x !== t));
    setResults(p => { const n = { ...p }; delete n[t]; return n; });
  }

  // Consensus: count tickers where nearest-exp Max Pain < current price
  const consensus = useMemo(() => {
    const loaded = tickers.filter(t => {
      const r = results[t];
      if (!r?.cp || !r.expirations?.length) return false;
      return r.byExp[r.expirations[0]]?.maxPain != null;
    });
    if (!loaded.length) return null;
    const bear = loaded.filter(t => {
      const r = results[t];
      return r.byExp[r.expirations[0]].maxPain < r.cp;
    }).length;
    return { bear, bull: loaded.length - bear, total: loaded.length };
  }, [tickers, results]);

  const fmtExp = d => {
    if (!d) return "–";
    const [,m,dd] = d.split("-");
    const mo = ["jan","feb","mar","apr","mai","jun","jul","aug","sep","okt","nov","des"];
    return `${parseInt(dd)} ${mo[parseInt(m)-1]}`;
  };

  return (
    <div style={{padding:"20px 28px",maxWidth:1100,margin:"0 auto"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14,flexWrap:"wrap"}}>
        <span style={{fontSize:14,fontWeight:700,color:"#94a3b8",letterSpacing:0.5}}>📊 OPEX OVERSIKT — ALLE UTLØP</span>
        <span style={{fontSize:10,color:"#334155"}}>Klikk badge → se detaljer</span>
        <button onClick={fetchAll}
          style={{padding:"4px 10px",fontSize:11,background:"transparent",border:"1px solid #1e293b",
            borderRadius:4,color:"#64748b",cursor:"pointer"}}>↻ Oppdater</button>
        <div style={{display:"flex",gap:4,marginLeft:"auto"}}>
          <input value={addInput} onChange={e => setAddInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key==="Enter" && addTicker()}
            placeholder="+ Ticker"
            style={{width:70,padding:"3px 6px",background:"#060f1c",border:"1px solid #1e293b",
              borderRadius:4,color:"#e2e8f0",fontFamily:"inherit",fontSize:11}} />
          <button onClick={addTicker}
            style={{padding:"3px 8px",background:"#1e3a5f",border:"1px solid #3b82f6",
              borderRadius:4,color:"#93c5fd",fontSize:11,cursor:"pointer"}}>+</button>
        </div>
      </div>

      {!isProxy && <div style={{fontSize:10,color:"#475569",padding:8}}>Kun tilgjengelig i produksjon.</div>}

      {/* Consensus */}
      {consensus && (
        <div style={{marginBottom:14,padding:"8px 14px",background:"#060f1c",border:"1px solid #1e293b",
          borderRadius:6,fontSize:12,color:"#64748b"}}>
          {consensus.bear > consensus.bull
            ? `🔴 ${consensus.bear}/${consensus.total} har nærmeste Max Pain under kurs — nedadgående press`
            : consensus.bull > consensus.bear
            ? `🟢 ${consensus.bull}/${consensus.total} har nærmeste Max Pain over kurs — oppadsrettet press`
            : `🟡 Delt signal — ${consensus.bear} ned / ${consensus.bull} opp`}
        </div>
      )}

      {/* Ticker rows */}
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {tickers.map(t => {
          const r = results[t];
          if (!r || r.loading) return (
            <div key={t} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",
              background:"#060f1c",border:"1px solid #1e293b",borderRadius:8}}>
              <span style={{color:"#94a3b8",fontWeight:700,fontSize:13,minWidth:54}}>{t}</span>
              <span style={{color:"#334155",fontSize:10}}>henter...</span>
            </div>
          );
          if (r.error) return (
            <div key={t} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",
              background:"#060f1c",border:"1px solid #1e293b",borderRadius:8}}>
              <span style={{color:"#94a3b8",fontWeight:700,fontSize:13,minWidth:54}}>{t}</span>
              <span style={{color:"#ef444490",fontSize:10}}>{r.error}</span>
              <button onClick={() => removeTicker(t)}
                style={{marginLeft:"auto",background:"transparent",border:"none",color:"#334155",cursor:"pointer",fontSize:15}}>×</button>
            </div>
          );

          return (
            <div key={t} style={{padding:"12px 14px",background:"#060f1c",
              border:"1px solid #1e293b",borderRadius:8}}>
              {/* Header row: ticker + price + remove */}
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <span style={{color:"#e2e8f0",fontWeight:700,fontSize:14,minWidth:54}}>{t}</span>
                <span style={{color:"#64748b",fontSize:12}}>
                  ${r.cp?.toFixed(2) ?? "–"}
                </span>
                <button onClick={() => removeTicker(t)}
                  style={{background:"transparent",border:"none",color:"#1e293b",cursor:"pointer",
                    fontSize:16,padding:"0 4px",marginLeft:"auto"}}>×</button>
              </div>
              {/* Badge grid: full card width → auto-fills 3-4 per row on mobile */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(78px,1fr))",gap:6}}>
                {r.expirations.map(exp => {
                  const s = r.byExp[exp];
                  if (!s?.maxPain) return null;
                  const dist = ((s.maxPain - r.cp) / r.cp * 100);
                  const down = dist < 0;
                  const strong = Math.abs(dist) > 3;
                  const bg   = down ? (strong ? "#3b0a0a" : "#1a0a0a") : (strong ? "#0a2a14" : "#0a1a0e");
                  const clr  = down ? (strong ? "#ef4444" : "#f87171") : (strong ? "#22c55e" : "#4ade80");
                  const border = down ? (strong ? "#ef444460" : "#ef444430") : (strong ? "#22c55e60" : "#22c55e30");
                  return (
                    <button key={exp}
                      onClick={() => onSelect && onSelect(t, exp)}
                      title={`Max Pain $${s.maxPain} · IV ${s.atmIV!=null?(s.atmIV*100).toFixed(0)+"%" : "–"} · P/C ${s.callVol>0?(s.putVol/s.callVol).toFixed(2):"–"}`}
                      style={{padding:"5px 10px",background:bg,border:`1px solid ${border}`,
                        borderRadius:5,cursor:"pointer",fontFamily:"inherit",
                        display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                      <span style={{fontSize:10,color:"#475569",lineHeight:1}}>{fmtExp(exp)}</span>
                      <span style={{fontSize:13,color:clr,fontWeight:700,lineHeight:1.2}}>
                        {down?"↓":"↑"} {Math.abs(dist).toFixed(1)}%
                      </span>
                      <span style={{fontSize:10,color:"#475569",lineHeight:1}}>${s.maxPain}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{fontSize:10,color:"#475569",marginTop:12,lineHeight:1.6}}>
        Hver badge = ett utløp · Pil + % = avstand fra kurs til Max Pain · Mørk rød = sterkt nedadgående press (&gt;3%) · Hover for IV og P/C-ratio · Data: CBOE delayed
      </div>
    </div>
  );
}

// ── OpexView ──────────────────────────────────────────────────────────────────
// ── MacroCorrelationChart ─────────────────────────────────────────────────────
function MacroCorrelationChart({ marketData, spyTechData }) {
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const seriesRef    = useRef({});
  const legendRef    = useRef(null);

  const [active,   setActive]   = useState({ vix:true, spread:false, curve:false, jpy:false, dxy:false });
  const [win,      setWin]      = useState("3M");   // "3M" | "6M" | "1Y"
  const [extData,  setExtData]  = useState({});     // fetched data for 6M / 1Y
  const [extLoad,  setExtLoad]  = useState(false);
  const [lagKey,   setLagKey]   = useState(null);   // series to shift
  const [lagDays,  setLagDays]  = useState(0);      // -15 … +15

  const CLR = { spy:"#22c55e", vix:"#ef4444", spread:"#f59e0b", curve:"#a78bfa", jpy:"#38bdf8", dxy:"#fb923c" };
  const SERIES_CFG = [
    { key:"vix",    label:"VIX",        hint:"Fryktindeks — stiger når markedet stresser" },
    { key:"spread", label:"HYG/LQD",    hint:"Kredittforhold — fall = kredittstress" },
    { key:"curve",  label:"Rentekurve", hint:"10Y−3M spread — invertering varsler resesjon" },
    { key:"jpy",    label:"USD/JPY",    hint:"Yen carry — fall betyr yen styrkes, press på US aksjer" },
    { key:"dxy",    label:"DXY",        hint:"Dollar-indeks (57% EUR) — styrke presser S&P ned" },
  ];

  // Fetch longer-range data when window changes
  useEffect(() => {
    if (win === "3M") { setExtData({}); return; }
    setExtLoad(true);
    const range   = win === "6M" ? "6mo" : "1y";
    const tickers = ["SPY","^VIX","HYG","LQD","^TNX","^IRX","JPY=X","DX-Y.NYB"];
    const isProxy = window.location.hostname !== "localhost";
    const fetchOne = async (t) => {
      const url = `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(t)}?range=${range}&interval=1d`;
      const proxies = isProxy ? [`/yahoo?url=${encodeURIComponent(url)}`]
        : [`https://corsproxy.io/?url=${encodeURIComponent(url)}`];
      for (const p of proxies) {
        try {
          const res = await fetch(p, { signal: AbortSignal.timeout(10000) });
          if (!res.ok) continue;
          const j = await res.json();
          const r = j?.chart?.result?.[0]; if (!r) continue;
          const closes = r.indicators?.quote?.[0]?.close?.filter(Boolean) || [];
          return { history: closes, timestamps: r.timestamp || [] };
        } catch {}
      }
      return null;
    };
    Promise.all(tickers.map(t => fetchOne(t).then(d => [t,d]))).then(res => {
      const md = {};
      res.forEach(([t,d]) => { if (d) md[t] = d; });
      setExtData(md);
      setExtLoad(false);
    });
  }, [win]);

  // Pick data source
  const src = (t) => (win !== "3M" && extData[t]) ? extData[t] : marketData[t];

  function buildSeries() {
    const spy = src("SPY");
    if (!spy?.timestamps?.length || !spy.history.length) return null;
    const n      = Math.min(spy.timestamps.length, spy.history.length);
    const baseTs = spy.timestamps.slice(-n);

    const pctNorm = (arr) => {
      if (!arr || arr.length < 2) return [];
      const len = Math.min(arr.length, n);
      const sl  = arr.slice(-len);
      const b   = sl[0]; if (!b) return [];
      return sl.map((v,i) => ({ time: baseTs[n-len+i], value: (v/b-1)*100 }));
    };

    const hyg = src("HYG")?.history, lqd = src("LQD")?.history;
    let spreadData = [];
    if (hyg && lqd) {
      const len = Math.min(hyg.length, lqd.length, n);
      const rat = Array.from({length:len}, (_,i) => hyg[hyg.length-len+i]/lqd[lqd.length-len+i]);
      const b   = rat[0];
      if (b) spreadData = rat.map((v,i) => ({ time: baseTs[n-len+i], value: (v/b-1)*100 }));
    }

    const tnx = src("^TNX")?.history, irx = src("^IRX")?.history;
    let curveData = [];
    if (tnx && irx) {
      const len = Math.min(tnx.length, irx.length, n);
      const sp  = Array.from({length:len}, (_,i) => tnx[tnx.length-len+i]-irx[irx.length-len+i]);
      const b   = sp[0];
      if (b && Math.abs(b) >= 0.05)
        curveData = sp.map((v,i) => ({ time: baseTs[n-len+i], value: (v/b-1)*100 }));
      else
        curveData = sp.map((v,i) => ({ time: baseTs[n-len+i], value: (v-(b||0))*100 }));
    }

    const raw = {
      spy:    pctNorm(spy.history.slice(-n)),
      vix:    pctNorm(src("^VIX")?.history),
      spread: spreadData,
      curve:  curveData,
      jpy:    pctNorm(src("JPY=X")?.history),
      dxy:    pctNorm(src("DX-Y.NYB")?.history),
    };

    // Apply lag: shift selected series timestamps by lagDays * 86400 s
    if (lagKey && lagDays !== 0 && raw[lagKey]?.length) {
      const shift = lagDays * 86400;
      raw[lagKey] = raw[lagKey].map(p => ({ ...p, time: p.time + shift }));
    }
    return raw;
  }

  // Build RVOL histogram data — trimmed to startTime so it matches the visible window
  function buildVolumeData(startTime = 0) {
    const vols = spyTechData?.volumes, ts = spyTechData?.timestamps, cls = spyTechData?.closes;
    if (!vols?.length || !ts?.length || !cls?.length) return [];
    const n = Math.min(vols.length, ts.length, cls.length);
    const result = [];
    for (let i = 1; i < n; i++) {
      if (ts[i] < startTime) continue;
      const start = Math.max(0, i - 20);
      let sum = 0, cnt = 0;
      for (let j = start; j < i; j++) { if (vols[j]) { sum += vols[j]; cnt++; } }
      const avg = cnt > 0 ? sum / cnt : 0;
      const rvol = avg > 0 ? (vols[i] || 0) / avg : 1;
      const isUp = cls[i] >= cls[i-1];
      const opacity = Math.min(1, Math.max(0.25, rvol / 2));
      const hex = Math.round(opacity * 255).toString(16).padStart(2,"0");
      result.push({ time: ts[i], value: rvol, color: isUp ? `#22c55e${hex}` : `#ef4444${hex}` });
    }
    return result;
  }

  // Create chart (once on mount)
  useEffect(() => {
    if (!containerRef.current) return;
    // samme høyderegel ved oppstart og ved endring av vindusstørrelse
    const heightFor = w => (w < 600 ? Math.max(200, Math.round(w * 0.55)) : Math.min(380, Math.max(260, Math.round(w * 0.38))));
    const h = heightFor(containerRef.current.clientWidth);
    const chart = createChart(containerRef.current, {
      layout: { background:{color:"#040d1a"}, textColor:"#94a3b8", fontSize:11 },
      grid:   { vertLines:{color:"#0d1f35"}, horzLines:{color:"#0d1f35"} },
      crosshair: { mode:1 },
      localization: { timeFormatter: ts => typeof ts === "object" && ts !== null
        ? new Date(ts.year, ts.month-1, ts.day).toLocaleDateString("nb-NO", {day:"numeric",month:"short",year:"numeric"})
        : new Date(ts * 1000).toLocaleDateString("nb-NO", {day:"numeric",month:"short",year:"numeric"}) },
      rightPriceScale: { borderColor:"#1e293b", scaleMargins:{top:0.12,bottom:0.12} },
      timeScale: { borderColor:"#1e293b", timeVisible:true, secondsVisible:false },
      width: containerRef.current.clientWidth, height: h,
    });

    const addL = (key, color, title, visible) => {
      const s = chart.addSeries(LineSeries, { color, lineWidth:2, title, priceLineVisible:false, lastValueVisible:true, visible });
      seriesRef.current[key] = s;
    };
    addL("spy",    CLR.spy,    "SPY",        true);
    addL("vix",    CLR.vix,    "VIX",        true);
    addL("spread", CLR.spread, "HYG/LQD",    false);
    addL("curve",  CLR.curve,  "Rentekurve", false);
    addL("jpy",    CLR.jpy,    "USD/JPY",    false);
    addL("dxy",    CLR.dxy,    "DXY",        false);

    // Volume histogram (RVOL) — sub-pane at bottom
    const volSeries = chart.addSeries(HistogramSeries, {
      priceScaleId: "vol",
      lastValueVisible: false,
      priceLineVisible: false,
    });
    chart.priceScale("vol").applyOptions({
      scaleMargins: { top: 0.85, bottom: 0 },
      visible: false,
    });
    seriesRef.current.vol = volSeries;

    // Hover legend
    const leg = document.createElement("div");
    leg.style.cssText = "position:absolute;top:10px;left:12px;font-size:10.5px;line-height:1.55;pointer-events:none;z-index:2;background:#040d1acc;padding:5px 8px;border-radius:5px;border:1px solid #1e293b;min-width:130px;";
    containerRef.current.style.position = "relative";
    containerRef.current.appendChild(leg);
    legendRef.current = leg;

    chart.subscribeCrosshairMove(param => {
      if (!param.time || !param.seriesData?.size) { leg.innerHTML = ""; return; }
      let html = `<div style="color:#64748b;font-size:9px;margin-bottom:3px">${new Date(param.time*1000).toLocaleDateString("nb-NO",{day:"2-digit",month:"short",year:"numeric"})}</div>`;
      Object.entries(seriesRef.current).forEach(([k, s]) => {
        if (!s || k === "vol") return;
        const opts = s.options(); if (!opts.visible) return;
        const v = param.seriesData.get(s);
        if (v === undefined) return;
        const val = typeof v === "object" ? v.value : v;
        html += `<div style="color:${opts.color};display:flex;justify-content:space-between;gap:12px"><span>${opts.title}</span><span style="font-weight:700">${val>=0?"+":""}${val.toFixed(2)}%</span></div>`;
      });
      // Show RVOL separately at bottom of tooltip
      const volS = seriesRef.current.vol;
      if (volS) {
        const vv = param.seriesData.get(volS);
        if (vv !== undefined) {
          const rv = typeof vv === "object" ? vv.value : vv;
          html += `<div style="color:#94a3b8;margin-top:3px;border-top:1px solid #1e293b;padding-top:3px">Volum: ${rv.toFixed(2)}× snitt</div>`;
        }
      }
      leg.innerHTML = html;
    });

    const ro = new ResizeObserver(() => {
      if (!containerRef.current) return;
      chart.resize(containerRef.current.clientWidth, heightFor(containerRef.current.clientWidth));
    });
    ro.observe(containerRef.current);
    chartRef.current = chart;
    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; seriesRef.current = {}; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Find pivot S/R levels from a raw price array, returned as {type,absPrice,pct} relative to startPrice
  function calcSR(prices, startPrice, win = 8, maxLevels = 3) {
    if (!prices || prices.length < win * 2 + 1 || !startPrice) return [];
    const pivots = [];
    for (let i = win; i < prices.length - win; i++) {
      const slice = prices.slice(i - win, i + win + 1);
      const v = prices[i];
      if (v === Math.max(...slice)) pivots.push({ type:"R", absPrice: v, idx: i });
      else if (v === Math.min(...slice)) pivots.push({ type:"S", absPrice: v, idx: i });
    }
    const result = [];
    for (const p of pivots.slice().reverse()) {
      const pct = (p.absPrice / startPrice - 1) * 100;
      if (!result.some(r => Math.abs(r.pct - pct) < 1.0)) {
        result.push({ ...p, pct });
        if (result.length >= maxLevels) break;
      }
    }
    return result;
  }

  // Update series data whenever source data or lag changes
  useEffect(() => {
    if (!seriesRef.current.spy) return;
    const data = buildSeries();
    if (!data) return;
    Object.entries(data).forEach(([k, d]) => {
      if (k !== "vol") seriesRef.current[k]?.setData(d);
    });
    // Volume histogram — trim to same start as SPY price data
    const startTime = data.spy?.[0]?.time ?? 0;
    const volData = buildVolumeData(startTime);
    if (volData.length) seriesRef.current.vol?.setData(volData);
    chartRef.current?.timeScale().fitContent();

    // S/R lines for DXY and USD/JPY — remove old, add new
    ["dxy","jpy"].forEach(k => {
      const yahooKey = k === "dxy" ? "DX-Y.NYB" : "JPY=X";
      const label    = k === "dxy" ? "DXY" : "JPY";
      const series   = seriesRef.current[k];
      if (!series) return;
      // Remove old price lines
      (seriesRef.current[`${k}_sr`] || []).forEach(pl => { try { series.removePriceLine(pl); } catch {} });
      seriesRef.current[`${k}_sr`] = [];
      const seriesData = data[k];
      if (!seriesData?.length) return;
      const rawHistory = src(yahooKey)?.history;
      if (!rawHistory?.length) return;
      const slice = rawHistory.slice(-seriesData.length);
      const startPrice = slice[0];
      if (!startPrice) return;
      const levels = calcSR(slice, startPrice);
      const newLines = levels.map(l => series.createPriceLine({
        price:            l.pct,
        color:            l.type === "R" ? "#ef444870" : "#22c55e70",
        lineWidth:        1,
        lineStyle:        2, // dashed
        axisLabelVisible: false,
        title:            `${label} ${l.type} ${l.absPrice.toFixed(1)}`,
      }));
      seriesRef.current[`${k}_sr`] = newLines;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketData, extData, lagKey, lagDays, spyTechData, win]);

  // Update visibility
  useEffect(() => {
    const s = seriesRef.current;
    if (!s.vix) return;
    ["vix","spread","curve","jpy","dxy"].forEach(k => s[k]?.applyOptions({ visible: active[k] }));
  }, [active]);

  // Raw value arrays (no lag) for Pearson computation
  function getRawArrays() {
    const spy = src("SPY");
    if (!spy?.history?.length || !spy?.timestamps?.length) return null;
    const n = Math.min(spy.timestamps.length, spy.history.length);
    const pctArr = (arr) => {
      if (!arr || arr.length < 2) return null;
      const len = Math.min(arr.length, n);
      const sl = arr.slice(-len);
      const b = sl[0]; if (!b) return null;
      return sl.map(v => (v/b-1)*100);
    };
    const hyg=src("HYG")?.history, lqd=src("LQD")?.history;
    let spreadArr=null;
    if(hyg&&lqd){const len=Math.min(hyg.length,lqd.length,n);const rat=Array.from({length:len},(_,i)=>hyg[hyg.length-len+i]/lqd[lqd.length-len+i]);const b=rat[0];if(b)spreadArr=rat.map(v=>(v/b-1)*100);}
    const tnx=src("^TNX")?.history, irx=src("^IRX")?.history;
    let curveArr=null;
    if(tnx&&irx){const len=Math.min(tnx.length,irx.length,n);const sp=Array.from({length:len},(_,i)=>tnx[tnx.length-len+i]-irx[irx.length-len+i]);const b=sp[0];if(b&&Math.abs(b)>=0.05)curveArr=sp.map(v=>(v/b-1)*100);else curveArr=sp.map(v=>(v-(b||0))*100);}
    return { spy:pctArr(spy.history.slice(-n)), vix:pctArr(src("^VIX")?.history), spread:spreadArr, curve:curveArr, jpy:pctArr(src("JPY=X")?.history) };
  }

  // Pearson profile: r at each lag from -15 to +15
  const lagCorrs = (() => {
    if (!lagKey) return null;
    const raw = getRawArrays();
    if (!raw) return null;
    const spy = raw.spy, ind = raw[lagKey];
    if (!spy?.length || !ind?.length) return null;
    return Array.from({length:31},(_,i)=>{
      const L = i-15;
      let x, y;
      if(L===0){ x=ind; y=spy; }
      else if(L>0){ x=ind.slice(0,ind.length-L); y=spy.slice(L,L+x.length); }
      else{ const a=-L; x=ind.slice(a); y=spy.slice(0,x.length); }
      return { lag:L, r:pearson(x,y) };
    });
  })();
  const bestLag = lagCorrs?.reduce((b,c)=>Math.abs(c.r)>Math.abs(b.r)?c:b, lagCorrs[0]) ?? null;

  const lagCfg = SERIES_CFG.find(s => s.key === lagKey);
  const lagLabel = lagDays === 0 ? "Ingen lag"
    : lagDays > 0 ? `${lagCfg?.label} skiftet +${lagDays}d fremover → viser om den leder SPY`
    : `${lagCfg?.label} skiftet ${lagDays}d bakover → viser om SPY leder den`;

  return (
    <div style={{marginTop:16,background:"#040d1a",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden"}}>

      {/* ── Header row ── */}
      <div style={{padding:"10px 14px 8px",borderBottom:"1px solid #1e293b",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:"#f8fafc",letterSpacing:1}}>📈 MAKROKORRELASJONSKART</div>
          <div style={{fontSize:9,color:"#475569",marginTop:2}}>Normalisert % endring fra start av vinduet · SPY alltid synlig · Hover for detaljer</div>
        </div>
        {/* Window selector */}
        <div style={{display:"flex",gap:4}}>
          {["3M","6M","1Y"].map(w => (
            <button key={w} onClick={()=>setWin(w)}
              style={{padding:"3px 10px",border:"1px solid",borderColor:win===w?"#f59e0b":"#1e293b",
                background:win===w?"#f59e0b18":"transparent",color:win===w?"#f59e0b":"#475569",
                borderRadius:4,cursor:"pointer",fontFamily:"inherit",fontSize:10,fontWeight:win===w?700:400}}>
              {w}{extLoad&&win===w?" …":""}
            </button>
          ))}
        </div>
      </div>

      {/* ── Toggle row ── */}
      <div style={{padding:"8px 14px",borderBottom:"1px solid #0d1f35",display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
        <div style={{display:"flex",alignItems:"center",gap:4,padding:"3px 8px",border:`1px solid ${CLR.spy}`,borderRadius:4,background:`${CLR.spy}15`}}>
          <div style={{width:10,height:2,background:CLR.spy,borderRadius:1}}/>
          <span style={{fontSize:10,color:CLR.spy,fontWeight:700}}>SPY</span>
        </div>
        {SERIES_CFG.map(s => (
          <button key={s.key} title={s.hint}
            onClick={() => setActive(p => ({...p,[s.key]:!p[s.key]}))}
            style={{display:"flex",alignItems:"center",gap:4,padding:"3px 8px",
              border:`1px solid ${active[s.key]?CLR[s.key]:"#1e293b"}`,
              borderRadius:4,background:active[s.key]?`${CLR[s.key]}15`:"transparent",
              cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}>
            <div style={{width:10,height:2,background:active[s.key]?CLR[s.key]:"#475569",borderRadius:1}}/>
            <span style={{fontSize:10,color:active[s.key]?CLR[s.key]:"#475569",fontWeight:600}}>{s.label}</span>
          </button>
        ))}
      </div>

      {/* ── Chart ── */}
      <div ref={containerRef} style={{width:"100%"}}/>

      {/* ── Lag-analyse row ── */}
      <div style={{padding:"10px 14px 12px",borderTop:"1px solid #0d1f35",background:"#040d1a"}}>
        <div style={{fontSize:9,color:"#64748b",letterSpacing:1,marginBottom:7,fontWeight:700}}>LAG-ANALYSE</div>

        {/* Series selector */}
        <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8}}>
          {SERIES_CFG.map(s => (
            <button key={s.key}
              onClick={() => { setLagKey(s.key); if(lagKey!==s.key) setLagDays(0); }}
              style={{padding:"3px 9px",border:"1px solid",
                borderColor:lagKey===s.key?CLR[s.key]:"#1e293b",
                background:lagKey===s.key?`${CLR[s.key]}18`:"transparent",
                color:lagKey===s.key?CLR[s.key]:"#475569",
                borderRadius:4,cursor:"pointer",fontFamily:"inherit",fontSize:10}}>
              {s.label}
            </button>
          ))}
          {lagKey && (
            <button onClick={()=>{setLagKey(null);setLagDays(0);}}
              style={{padding:"3px 8px",border:"1px solid #1e293b",borderRadius:4,background:"transparent",color:"#475569",cursor:"pointer",fontFamily:"inherit",fontSize:10}}>
              ✕
            </button>
          )}
        </div>

        {lagKey && (<>
          {/* Slider */}
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
            <span style={{fontSize:9,color:"#475569",whiteSpace:"nowrap"}}>−15d</span>
            <input type="range" min={-15} max={15} value={lagDays}
              onChange={e => setLagDays(Number(e.target.value))}
              style={{flex:1,accentColor:CLR[lagKey],cursor:"pointer"}}/>
            <span style={{fontSize:9,color:"#475569",whiteSpace:"nowrap"}}>+15d</span>
            <span style={{fontSize:11,fontWeight:700,color:lagDays===0?"#475569":CLR[lagKey],minWidth:32,textAlign:"right"}}>
              {lagDays===0?"0d":(lagDays>0?`+${lagDays}d`:`${lagDays}d`)}
            </span>
          </div>
          <div style={{marginBottom:10,fontSize:9,color:lagDays===0?"#475569":CLR[lagKey]}}>{lagLabel}</div>

          {/* Pearson correlation profile bar chart */}
          {lagCorrs && (()=>{
            const W=310, H=80, mid=36, barW=8, gap=2;
            const clr = CLR[lagKey];
            return (
              <div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:8.5,color:"#475569"}}>Pearson-korrelasjon med SPY ved hvert lag (klikk for å hoppe dit)</span>
                  {bestLag && (
                    <button onClick={()=>setLagDays(bestLag.lag)}
                      style={{padding:"2px 7px",fontSize:8.5,border:`1px solid ${clr}`,borderRadius:3,
                        background:"transparent",color:clr,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                      Beste: {bestLag.lag>0?"+":""}{bestLag.lag}d (r={bestLag.r.toFixed(2)})
                    </button>
                  )}
                </div>
                <svg viewBox={`0 0 ${W} ${H+16}`} style={{width:"100%",maxWidth:520,display:"block",overflow:"visible"}}>
                  {/* r=+1 / 0 / -1 guide lines */}
                  <line x1={0} y1={mid-36} x2={W} y2={mid-36} stroke="#0d1f35" strokeWidth="0.5" strokeDasharray="3,3"/>
                  <line x1={0} y1={mid}    x2={W} y2={mid}    stroke="#1e293b" strokeWidth="1"/>
                  <line x1={0} y1={mid+36} x2={W} y2={mid+36} stroke="#0d1f35" strokeWidth="0.5" strokeDasharray="3,3"/>
                  <text x="2" y={mid-30} fontSize="6" fill="#334155">r=+1</text>
                  <text x="2" y={mid+4}  fontSize="6" fill="#334155">r=0</text>
                  <text x="2" y={mid+40} fontSize="6" fill="#334155">r=−1</text>
                  {/* Bars */}
                  {lagCorrs.map((c,i)=>{
                    const x = 22 + i*(barW+gap);
                    const barH = Math.abs(c.r)*36;
                    const y   = c.r>=0 ? mid-barH : mid;
                    const isSel  = c.lag===lagDays;
                    const isBest = c.lag===bestLag?.lag;
                    const fill = isBest ? clr : c.r>=0 ? "#22c55e40" : "#ef444440";
                    return (
                      <g key={c.lag} style={{cursor:"pointer"}} onClick={()=>setLagDays(c.lag)}>
                        <rect x={x} y={y} width={barW} height={Math.max(barH,1.5)}
                          fill={fill} stroke={isSel?"#ffffff":isBest?clr:"none"} strokeWidth={isSel?1:0.5} rx="1"/>
                        {isSel && <line x1={x+barW/2} y1={0} x2={x+barW/2} y2={H} stroke="#ffffff30" strokeWidth="0.5"/>}
                      </g>
                    );
                  })}
                  {/* X-axis labels */}
                  {[-15,-10,-5,0,5,10,15].map(L=>(
                    <text key={L} x={22+(L+15)*(barW+gap)+barW/2} y={H+13} textAnchor="middle" fontSize="6.5" fill="#475569">
                      {L>0?`+${L}`:L}
                    </text>
                  ))}
                </svg>
                <div style={{fontSize:8,color:"#334155",marginTop:2}}>
                  Negativ r = invers korrelasjon (VIX går opp når SPY går ned = r≈−0.8) · Positiv r = samme retning · Klikk på søyle for å sette lag
                </div>
              </div>
            );
          })()}
        </>)}

        {!lagKey && (
          <div style={{fontSize:9,color:"#334155"}}>
            Velg en indikator over → se Pearson-korrelasjonsgrafen for alle lag (−15 til +15 dager) → finn optimalt lag automatisk.
          </div>
        )}
      </div>

      {/* ── Footer hints ── */}
      <div style={{padding:"8px 14px 10px",borderTop:"1px solid #0d1f35",display:"flex",gap:14,flexWrap:"wrap"}}>
        {SERIES_CFG.map(s => (
          <div key={s.key} style={{fontSize:10,color:"#64748b"}}>
            <span style={{color:CLR[s.key]}}>●</span> <b style={{color:active[s.key]?CLR[s.key]:"#94a3b8"}}>{s.label}</b>: {s.hint}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Passord-gate ──────────────────────────────────────────────────────────────
const APP_PASSWORD = "aksjer2026"; // <-- endre passord her

function PasswordGate({ onAuth }) {
  const [input, setInput] = useState("");
  const [err,   setErr]   = useState(false);

  function attempt() {
    if (input === APP_PASSWORD) { onAuth(); }
    else { setErr(true); setInput(""); setTimeout(() => setErr(false), 1200); }
  }

  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",
      background:"#020810",fontFamily:"'SF Mono','Fira Code',monospace"}}>
      <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:12,padding:"40px 48px",
        maxWidth:340,width:"100%",textAlign:"center"}}>
        <div style={{fontSize:28,marginBottom:8}}>⚡</div>
        <div style={{fontSize:16,fontWeight:700,color:"#e2e8f0",marginBottom:4}}>AI Aksjer</div>
        <div style={{fontSize:11,color:"#334155",marginBottom:28,letterSpacing:1}}>PRIVAT DASHBOARD</div>
        <input
          type="password"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && attempt()}
          placeholder="Passord"
          autoFocus
          style={{
            width:"100%",padding:"10px 14px",background:"#0a1628",
            border:`1px solid ${err ? "#ef4444" : "#1e293b"}`,borderRadius:6,
            color:"#e2e8f0",fontFamily:"inherit",fontSize:13,
            boxSizing:"border-box",outline:"none",
            transition:"border-color 0.2s",
          }}
        />
        {err && <div style={{color:"#ef4444",fontSize:11,marginTop:8}}>Feil passord</div>}
        <button onClick={attempt}
          style={{marginTop:14,width:"100%",padding:"10px",background:"#1e3a5f",
            border:"1px solid #3b82f6",borderRadius:6,color:"#93c5fd",
            fontFamily:"inherit",fontSize:13,cursor:"pointer",fontWeight:600}}>
          Logg inn
        </button>
      </div>
    </div>
  );
}

// Dato og time i New York (markedet stenger 16:00 ET)
const nyDay = (d = new Date()) => d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const nyHour = (d = new Date()) => +d.toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", hourCycle: "h23" });

function AppContent() {
  const [stocks, setStocks] = useState(() => {
    try {
      const saved = localStorage.getItem("ai-aksjer-stocks");
      if (!saved) return DEFAULT_STOCKS;
      const parsed = JSON.parse(saved);
      const merged = { ...parsed };
      Object.entries(DEFAULT_STOCKS).forEach(([cat, list]) => {
        if (!merged[cat]) merged[cat] = list;
      });
      return merged;
    } catch { return DEFAULT_STOCKS; }
  });
  const [liveData, setLiveData] = useState({});
  const [selected, setSelected] = useState(["NVDA","AMD","MSFT","PLTR"]);
  const [activeCategory, setActiveCategory] = useState("Alle");
  const [period, setPeriod] = useState("1M");
  const [view, setView] = useState("idag");
  const [chainNode, setChainNode] = useState(null);
  const [chainPerf, setChainPerf] = useState(null);   // { TICKER: { price, d5, m1, m3 } }
  const [chainPer, setChainPer] = useState("m1");
  const [showOldSetups, setShowOldSetups] = useState(false);
  const [ivHist, setIvHist] = useState({});           // { TICKER: [{ date, iv, price }] }
  const [optionsData, setOptionsData] = useState({});
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsRetry, setOptionsRetry] = useState(0);
  const [shortData, setShortData] = useState({});
  const [search, setSearch] = useState("");
  const [calFilters, setCalFilters] = useState(Object.fromEntries(ALL_CAL_CATS.map(c=>[c,true])));
  const [calEvents] = useState(buildCalendarEvents);
  const [calMonth, setCalMonth] = useState(new Date());
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [addTicker, setAddTicker] = useState("");
  const [addCat, setAddCat] = useState("Egendefinert");
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState("");
  const [deepTicker, setDeepTicker] = useState("NVDA");

  const [deepTab, setDeepTab] = useState("intel");
  const [opxMode, setOpxMode] = useState("kjede");      // Aksje › Opsjoner: "kjede" for valgt aksje, "utlop" for oversikt
  const [opxNav, setOpxNav] = useState(null);           // { ticker, exp } valgt fra utløpsoversikten
  const [introPhase, setIntroPhase] = useState(0);
  const introFiredRef = useRef(false);
  const [cotData, setCotData] = useState(null);
  const [cotLoading, setCotLoading] = useState(false);
  const [cotError, setCotError] = useState(null);
  const cotTriedRef = useRef(false);
  const [techData, setTechData] = useState({});
  const [techLoading, setTechLoading] = useState(false);
  const [deepPeriod, setDeepPeriod] = useState("3M");
  const [chartHover, setChartHover] = useState(null);
  const [showMA, setShowMA] = useState({ ema20: true, ma50: true, ma200: false });
  const [spyHover, setSpyHover] = useState(null);
  const [spyChartPeriod, setSpyChartPeriod] = useState("6M");

  const fetchTechData = useCallback(async (ticker) => {
    if (techFetchingRef.current.has(ticker)) return;
    techFetchingRef.current.add(ticker);
    setTechLoading(true);
    const isNetlify = window.location.hostname !== "localhost";
    const proxify = url => isNetlify
      ? [`/yahoo?url=${encodeURIComponent(url)}`]
      : [`https://corsproxy.io/?url=${encodeURIComponent(url)}`,`https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`];
    const url = `https://query1.finance.yahoo.com/v8/finance/chart/${toYahooTicker(ticker)}?range=1y&interval=1d`;
    for (const proxy of proxify(url)) {
      try {
        const res = await fetch(proxy, { signal: AbortSignal.timeout(8000) });
        if (!res.ok) continue;
        const json = await res.json();
        const r = json?.chart?.result?.[0];
        if (!r) continue;
        const q = r.indicators?.quote?.[0] || {};
        const closes = q.close?.map(v => v ?? null) || [];
        const opens  = q.open?.map(v => v ?? null) || [];
        const highs  = q.high?.map(v => v ?? null) || [];
        const lows   = q.low?.map(v => v ?? null) || [];
        const volumes = q.volume || [];
        const timestamps = r.timestamp || [];
        setTechData(prev => ({ ...prev, [ticker]: { closes, opens, highs, lows, volumes, timestamps } }));
        techFetchingRef.current.delete(ticker);
        setTechLoading(false);
        return;
      } catch {}
    }
    techFetchingRef.current.delete(ticker);
    setTechLoading(false);
  }, []);

  const [compareA, setCompareA] = useState("NVDA");
  const [compareB, setCompareB] = useState("AMD");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const [portfolio, setPortfolio] = useState(() => {
    try { const s = localStorage.getItem("ai-aksjer-portfolio"); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [pfTicker, setPfTicker] = useState("");
  const [pfShares, setPfShares] = useState("");
  const [pfAvg, setPfAvg] = useState("");
  const [pfTab, setPfTab] = useState("aksjer");
  const [options, setOptions] = useState(() => {
    try { const s = localStorage.getItem("ai-aksjer-options"); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [optTicker, setOptTicker] = useState("");
  const [optType, setOptType] = useState("CALL");
  const [optStrike, setOptStrike] = useState("");
  const [optExpiry, setOptExpiry] = useState("");
  const [optContracts, setOptContracts] = useState("");
  const [optPremium, setOptPremium] = useState("");
  useEffect(() => { localStorage.setItem("ai-aksjer-portfolio", JSON.stringify(portfolio)); }, [portfolio]);
  useEffect(() => { localStorage.setItem("ai-aksjer-options", JSON.stringify(options)); }, [options]);
  const [favorites, setFavorites] = useState(() => {
    try { const s = localStorage.getItem("ai-aksjer-favorites"); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  useEffect(() => { localStorage.setItem("ai-aksjer-favorites", JSON.stringify(favorites)); }, [favorites]);

  // ── JOURNAL ──
  const [journal, setJournal] = useState(() => {
    try { const s = localStorage.getItem("ai-aksjer-journal"); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  useEffect(() => { localStorage.setItem("ai-aksjer-journal", JSON.stringify(journal)); }, [journal]);
  const [journalForm, setJournalForm] = useState(null);
  const [closeForm, setCloseForm] = useState(null);
  const [plans, setPlans] = useState(() => { try { const s = localStorage.getItem("ai-aksjer-plans"); return s ? JSON.parse(s) : []; } catch { return []; } });
  useEffect(() => { localStorage.setItem("ai-aksjer-plans", JSON.stringify(plans)); }, [plans]);
  const [planForm, setPlanForm] = useState(null);
  const [journalTab, setJournalTab] = useState("journal"); // "journal" | "planer" | "statistikk" | "backtest"
  const [idagMode, setIdagMode] = useState("oversikt");     // I dag: "oversikt" | "makro" | "kalender" | "morgen"
  const [wlMode, setWlMode] = useState("tabell");           // Watchlist: "tabell" | "signaler"
  // Logg over kursprediksjoner: { id, ticker, made, target, cp, pred } – evalueres mot faktisk sluttkurs
  const [predLog, setPredLog] = useState(() => { try { const s = localStorage.getItem("ai-aksjer-predlog"); return s ? JSON.parse(s) : []; } catch { return []; } });
  useEffect(() => { localStorage.setItem("ai-aksjer-predlog", JSON.stringify(predLog)); }, [predLog]);

  // ── CLOUD SYNC ──
  const [syncPin, setSyncPin] = useState(() => localStorage.getItem("ai-aksjer-pin") || "");
  const [syncStatus, setSyncStatus] = useState("idle"); // idle | syncing | synced | error
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [infoSearch, setInfoSearch] = useState("");
  const [infoCat, setInfoCat] = useState("alle");
  const syncTimeoutRef = useRef(null);

  const callSync = useCallback(async (pin, method, body) => {
    const res = await fetch("/api/sync", {
      method,
      headers: { "Content-Type": "application/json", "X-Pin": pin },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (res.status === 401) throw new Error("pin");
    if (!res.ok) throw new Error("server");
    return method === "GET" ? res.json() : null;
  }, []);

  const syncFromCloud = useCallback(async (pin) => {
    setSyncStatus("syncing");
    try {
      const data = await callSync(pin, "GET");
      if (data.journal)   { setJournal(data.journal);   localStorage.setItem("ai-aksjer-journal", JSON.stringify(data.journal)); }
      if (data.favorites) { setFavorites(data.favorites); localStorage.setItem("ai-aksjer-favorites", JSON.stringify(data.favorites)); }
      if (Array.isArray(data.predlog)) setPredLog(prev => {   // slå sammen, så logger fra flere enheter ikke overskriver hverandre
        const m = new Map([...data.predlog, ...prev].map(p => [p.id, p]));
        return [...m.values()].sort((a, b) => a.made.localeCompare(b.made));
      });
      setSyncStatus("synced");
    } catch(e) {
      setSyncStatus(e.message === "pin" ? "badpin" : "error");
    }
  }, [callSync]);

  const pushToCloud = useCallback((pin, journalData, favData, predData, stocksData) => {
    if (!pin) return;
    clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(async () => {
      setSyncStatus("syncing");
      try {
        await callSync(pin, "POST", { journal: journalData, favorites: favData, predlog: predData, stocks: stocksData });   // stocks: watchlisten, brukt av den daglige skanneren
        setSyncStatus("synced");
      } catch { setSyncStatus("error"); }
    }, 1500);
  }, [callSync]);

  // Auto-push when journal or favorites change
  useEffect(() => {
    if (syncPin) pushToCloud(syncPin, journal, favorites, predLog, stocks);
  }, [journal, favorites, predLog, stocks]); // eslint-disable-line

  // Auto-sync from cloud on load if PIN is saved
  useEffect(() => {
    if (syncPin) syncFromCloud(syncPin);
  }, []); // eslint-disable-line

  const handlePinSubmit = async () => {
    setPinError("");
    try {
      await syncFromCloud(pinInput);
      localStorage.setItem("ai-aksjer-pin", pinInput);
      setSyncPin(pinInput);
      setShowPinModal(false);
      setPinInput("");
    } catch(e) {
      setPinError(e.message === "pin" ? "Feil PIN — prøv igjen" : "Tilkoblingsfeil, prøv igjen");
    }
  };
  const loadingRef = useRef({});
  // Rapportkalender fra Nasdaq (/api/earnings). Yahoo gir ikke lenger rapportdatoer i kursdataene.
  const earnCalRef = useRef(null);
  const nextFromCal = useCallback(t => {
    const today = nyDay();
    return (earnCalRef.current?.[t] || []).find(e => e.d >= today) || null;
  }, []);
  useEffect(() => {
    if (window.location.hostname === "localhost") return;
    fetch("/api/earnings").then(r => r.json()).then(d => {
      if (!d?.map) return;
      earnCalRef.current = d.map;
      // oppdater aksjer som allerede er lastet
      setLiveData(prev => Object.fromEntries(Object.entries(prev).map(([t, v]) => {
        const ne = nextFromCal(t);
        return [t, ne ? { ...v, nextEarnings: ne.d, nextEarningsTime: ne.t } : v];
      })));
    }).catch(() => {});
  }, [nextFromCal]);
  const techFetchingRef = useRef(new Set());
  const optionsFetchingRef = useRef(new Set());
  const [marketData, setMarketData] = useState({});
  const [marketHistory, setMarketHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ai-aksjer-market-history") || "[]"); } catch { return []; }
  });
  const [marketLoading, setMarketLoading] = useState(false);

  const fetchMarketData = useCallback(async () => {
    setMarketLoading(true);
    const tickers = ["^VIX","^VVIX","^GSPC","^TNX","HYG","LQD","SPY","QQQ","ES=F","NQ=F","^DJI","^VIX3M","^SKEW","^IRX","JPY=X","DX-Y.NYB","005930.KS","000660.KS"];
    const isNetlify = window.location.hostname !== "localhost";
    const proxify = url => isNetlify
      ? [`/yahoo?url=${encodeURIComponent(url)}`]
      : [`https://corsproxy.io/?url=${encodeURIComponent(url)}`,`https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`];

    const fetchOne = async (ticker) => {
      const url = `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(ticker)}?range=3mo&interval=1d`;
      for (const proxy of proxify(url)) {
        try {
          const res = await fetch(proxy, { signal: AbortSignal.timeout(8000) });
          if (!res.ok) continue;
          const json = await res.json();
          const r = json?.chart?.result?.[0];
          if (!r) continue;
          const closes = r.indicators?.quote?.[0]?.close?.filter(Boolean) || [];
          const meta = r.meta;
          const livePrice = meta.regularMarketPrice || closes[closes.length-1];
          const prevClose = meta.chartPreviousClose || closes[closes.length-2];
          return {
            price: livePrice,
            prev: prevClose,
            change: meta.regularMarketChangePercent != null
              ? meta.regularMarketChangePercent
              : prevClose ? ((livePrice - prevClose) / prevClose) * 100 : 0,
            history: closes,
            timestamps: r.timestamp || [],
          };
        } catch {}
      }
      return null;
    };

    const results = await Promise.all(tickers.map(fetchOne));
    const md = {};
    tickers.forEach((t, i) => { if (results[i]) md[t] = results[i]; });
    setMarketData(md);

    // Beregn og lagre score til historikk
    const vix = md["^VIX"]?.price, sp = md["^GSPC"], tny = md["^TNX"]?.price, hyg = md["HYG"], vvix = md["^VVIX"]?.price;
    const spMom = sp?.history?.length > 20 ? ((sp.history[sp.history.length-1]/sp.history[Math.max(0,sp.history.length-21)]-1)*100) : null;
    const hygMom = hyg?.history?.length > 20 ? ((hyg.history[hyg.history.length-1]/hyg.history[Math.max(0,hyg.history.length-21)]-1)*100) : null;
    let s = 0, mx = 0;
    if (vix != null)    { s += vix<12?20:vix<16?17:vix<20?13:vix<25?8:vix<30?4:vix<40?1:0; mx+=20; }
    if (vvix != null)   { s += vvix<80?15:vvix<95?10:vvix<110?5:vvix<130?2:0; mx+=15; }
    if (spMom != null)  { s += spMom>5?20:spMom>2?16:spMom>0.5?11:spMom>0?7:spMom>-5?3:spMom>-10?1:0; mx+=20; }
    if (tny != null)    { s += tny<3?20:tny<3.5?16:tny<4?12:tny<4.5?8:tny<5?4:tny<5.5?1:0; mx+=20; }
    if (hygMom != null) { s += hygMom>2?15:hygMom>0.5?11:hygMom>0?7:hygMom>-2?3:hygMom>-5?1:0; mx+=15; }
    const score = mx > 0 ? Math.round((s/mx)*100) : null;
    if (score !== null) {
      const today = new Date().toISOString().split("T")[0];
      setMarketHistory(prev => {
        const updated = [...prev.filter(h => h.date !== today), { date: today, score }].slice(-60);
        localStorage.setItem("ai-aksjer-market-history", JSON.stringify(updated));
        return updated;
      });
    }
    setMarketLoading(false);
  }, []);

  const fetchCOT = useCallback(async () => {
    if (cotLoading) return;
    cotTriedRef.current = true;
    setCotLoading(true);
    setCotError(null);
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 15000);
      const res = await fetch("/cot", { signal: ctrl.signal });
      clearTimeout(timer);
      if (!res.ok) throw new Error(`CFTC svarte med ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      if (!Array.isArray(data) || data.length === 0) throw new Error("Ingen S&P 500-data i CFTC-svaret");
      const nets = data.map(d => ({
        net: (parseInt(d.lev_money_positions_long_all) || 0) - (parseInt(d.lev_money_positions_short_all) || 0),
        date: (d.report_date_as_yyyy_mm_dd || "").split("T")[0],
      }));
      const netVals = nets.map(n => n.net);
      // nyeste først fra CFTC; historikken snus så minigrafen går fra eldst til nyest
      setCotData({ net: nets[0].net, date: nets[0].date, max: Math.max(...netVals), min: Math.min(...netVals),
        weeks: nets.length, history: [...netVals].reverse() });
    } catch(e) {
      setCotError(e.message || "Ukjent feil");
      setCotData(null);
    }
    setCotLoading(false);
  }, [cotLoading]);

  const allStockList = Object.entries(stocks).flatMap(([cat,list]) => list.map(s => ({...s,cat})));

  const fetchQuote = useCallback(async (ticker) => {
    if (loadingRef.current[ticker]) return;
    loadingRef.current[ticker] = true;
    const p = PERIODS.find(x=>x.label===period)||PERIODS[2];

    // Always fetch today's price separately so dayChange is always correct
    const yTicker = toYahooTicker(ticker);
    const dayUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${yTicker}?range=1d&interval=5m&includePrePost=true`;
    const histUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${yTicker}?range=${p.range}&interval=${p.interval}&events=div,splits`;

    const isNetlify = window.location.hostname !== "localhost";
    const proxify = url => isNetlify
      ? [`/yahoo?url=${encodeURIComponent(url)}`]
      : [
          `https://corsproxy.io/?url=${encodeURIComponent(url)}`,
          `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
        ];

    const fetchWithFallback = async (url) => {
      for (const proxy of proxify(url)) {
        try {
          const res = await fetch(proxy, { signal: AbortSignal.timeout(8000) });
          if (!res.ok) continue;
          const json = await res.json();
          if (json?.chart?.result?.[0]) return json.chart.result[0];
        } catch {}
      }
      return null;
    };

    const [dayResult, histResult] = await Promise.all([
      fetchWithFallback(dayUrl),
      fetchWithFallback(histUrl),
    ]);

    if (dayResult) {
      const meta = dayResult.meta;
      const currentPrice = meta.regularMarketPrice || meta.chartPreviousClose;
      const prevClose = meta.chartPreviousClose || meta.previousClose;
      const dayChange = prevClose ? ((currentPrice - prevClose) / prevClose) * 100 : 0;
      const rawCloses = histResult?.indicators?.quote?.[0]?.close || [];
      const rawTimestamps = histResult?.timestamp || [];
      const validPairs = rawCloses.map((c, i) => [c, rawTimestamps[i]]).filter(([c]) => c != null);
      const histCloses = validPairs.map(([c]) => c);
      const histTimestamps = validPairs.map(([, t]) => t);
      // Pre/post market: extract from chart data points using trading period windows
      const dayCloses = dayResult.indicators?.quote?.[0]?.close || [];
      const dayTs = dayResult.timestamp || [];
      const preWin = meta.currentTradingPeriod?.pre;
      const postWin = meta.currentTradingPeriod?.post;
      const lastInWindow = (win) => {
        if (!win) return null;
        const pts = dayTs.map((t, i) => ({ t, c: dayCloses[i] }))
          .filter(p => p.t >= win.start && p.t < win.end && p.c != null);
        return pts.length > 0 ? pts[pts.length - 1].c : null;
      };
      const preMarketPrice = lastInWindow(preWin);
      const postMarketPrice = lastInWindow(postWin);
      // Pre/post market % vs most recent regular session close (currentPrice), not vs day-before-yesterday (prevClose)
      const preMarketPct = preMarketPrice != null && currentPrice ? ((preMarketPrice - currentPrice) / currentPrice) * 100 : null;
      const postMarketPct = postMarketPrice != null && currentPrice ? ((postMarketPrice - currentPrice) / currentPrice) * 100 : null;
      // Rapportdato fra Nasdaq-kalenderen; Yahoo-feltet brukes bare hvis det mot formodning finnes og ligger fram i tid
      const nowSec = Math.floor(Date.now() / 1000);
      const earningsTs = meta.earningsTimestampStart || meta.earningsTimestamp;
      const cal = nextFromCal(ticker);
      const nextEarnings = cal?.d ?? ((earningsTs && earningsTs > nowSec) ? new Date(earningsTs * 1000).toISOString().slice(0, 10) : null);
      const nextEarningsTime = cal?.t ?? "";
      setLiveData(prev => ({
        ...prev,
        [ticker]: { price: currentPrice, change: dayChange, history: histCloses, timestamps: histTimestamps, weekHigh52: meta.fiftyTwoWeekHigh || null, volume: meta.regularMarketVolume || null, avgVolume: meta.averageDailyVolume3Month || meta.averageDailyVolume10Day || null, preMarket: preMarketPct, postMarket: postMarketPct, preMarketPrice, postMarketPrice, nextEarnings, nextEarningsTime },
      }));
    }

    loadingRef.current[ticker] = false;
  }, [period]);

  useEffect(() => { localStorage.setItem("ai-aksjer-stocks", JSON.stringify(stocks)); }, [stocks]);
  useEffect(() => {
    loadingRef.current = {};
    allStockList.forEach((s, i) => {
      setTimeout(() => fetchQuote(s.ticker), i * 150);
    });
  }, [period, stocks]);

  useEffect(() => {
    if (!techData[deepTicker] && !techLoading) fetchTechData(deepTicker);
  }, [deepTicker]);

  useEffect(() => {
    const od = optionsData[deepTicker];
    const stale = !od?.fetchedAt || (Date.now() - od.fetchedAt > 10 * 60 * 1000);
    if (od?.ok === true && !stale) return;
    setOptionsLoading(true);
    const ctrl = new AbortController();
    const giveUp = setTimeout(() => {
      ctrl.abort();
      setOptionsData(prev => ({ ...prev, [deepTicker]: { ok: false } }));
      setOptionsLoading(false);
    }, 12000);
    fetch(`/options?t=${encodeURIComponent(deepTicker)}`, { signal: ctrl.signal })
      .then(async r => {
        const d = await r.json();
        if (!r.ok || d?.error) { setOptionsData(prev => ({ ...prev, [deepTicker]: { ok: false, errMsg: d?.error || r.status } })); return; }
        const calls = d.calls || [];
        const puts  = d.puts  || [];
        const currentPrice   = d.currentPrice   || null;
        const expirationDate = d.expiration     || null;
        if (!calls.length && !puts.length) { setOptionsData(prev => ({ ...prev, [deepTicker]: { ok: false, errMsg: "no chain" } })); return; }
        const contracts = d.contracts || [];
        setOptionsData(prev => ({ ...prev, [deepTicker]: { calls, puts, contracts, currentPrice, expirationDate, expirations: d.expirations || [], iv30: d.iv30 ?? null, ok: true, fetchedAt: Date.now() } }));
      })
      .catch(() => { if (!ctrl.signal.aborted) setOptionsData(prev => ({ ...prev, [deepTicker]: { ok: false } })); })
      .finally(() => { clearTimeout(giveUp); setOptionsLoading(false); });
  }, [deepTicker, optionsRetry]);

  useEffect(() => {
    if (deepTab !== "intel") return;
    if (shortData[deepTicker]) return;
    const ctrl = new AbortController();
    const giveUp = setTimeout(() => {
      ctrl.abort();
      setShortData(prev => ({ ...prev, [deepTicker]: { ok: false } }));
    }, 8000);
    fetch(`/shorts?t=${encodeURIComponent(deepTicker)}`, { signal: ctrl.signal })
      .then(async r => {
        const d = await r.json();
        if (!r.ok || !d?.ok) { setShortData(prev => ({ ...prev, [deepTicker]: { ok: false } })); return; }
        setShortData(prev => ({ ...prev, [deepTicker]: { ok: true, shortFloat: d.shortFloat, shortRatio: d.shortRatio, shortDate: d.shortDate } }));
      })
      .catch(() => { if (!ctrl.signal.aborted) setShortData(prev => ({ ...prev, [deepTicker]: { ok: false } })); })
      .finally(() => clearTimeout(giveUp));
  }, [deepTicker, deepTab]);

  useEffect(() => {
    if (view === "idag" && (idagMode === "makro" || idagMode === "morgen") && !techData["SPY"] && !techLoading) fetchTechData("SPY");
    if (view === "idag" && idagMode === "morgen") {
      favorites.forEach((ticker, i) => {
        if (!techData[ticker]) setTimeout(() => fetchTechData(ticker), 500 + i * 400);
      });
      // Fetch options for all favorites (staggered to avoid CBOE rate limits)
      favorites.forEach((ticker, i) => {
        if (optionsData[ticker]?.ok || optionsFetchingRef.current.has(ticker)) return;
        setTimeout(async () => {
          optionsFetchingRef.current.add(ticker);
          const ctrl = new AbortController();
          const giveUp = setTimeout(() => {
            ctrl.abort();
            setOptionsData(prev => ({ ...prev, [ticker]: prev[ticker] || { ok: false } }));
            optionsFetchingRef.current.delete(ticker);
          }, 12000);
          try {
            const r = await fetch(`/options?t=${encodeURIComponent(ticker)}`, { signal: ctrl.signal });
            const d = await r.json();
            if (!r.ok || d?.error || !(d.calls?.length || d.puts?.length)) {
              setOptionsData(prev => ({ ...prev, [ticker]: { ok: false } }));
            } else {
              setOptionsData(prev => ({ ...prev, [ticker]: {
                calls: d.calls || [], puts: d.puts || [], contracts: d.contracts || [],
                currentPrice: d.currentPrice || null, expirationDate: d.expiration || null,
                expirations: d.expirations || [], ok: true, fetchedAt: Date.now(),
              }}));
            }
          } catch { if (!ctrl.signal.aborted) setOptionsData(prev => ({ ...prev, [ticker]: { ok: false } })); }
          finally { clearTimeout(giveUp); optionsFetchingRef.current.delete(ticker); }
        }, 2000 + i * 2500);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, idagMode]);
  useEffect(() => {
    if (view === "watchlist") {
      allStockList.forEach((s, i) => {
        if (!techData[s.ticker]) setTimeout(() => fetchTechData(s.ticker), i * 400);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);
  useEffect(() => {
    if (view === "watchlist" && favorites.length > 0) {
      // Refresh all favorites every time the tab is opened
      loadingRef.current = { ...loadingRef.current };
      favorites.forEach((t, i) => setTimeout(() => fetchQuote(t), i * 150));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);


  const addStock = async () => {
    if (!addTicker.trim()) return;
    const ticker = addTicker.trim().toUpperCase();
    setAddLoading(true); setAddError("");
    const yahoUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${ticker}?range=1d&interval=1d`;
    const proxies = [
      `/yahoo?url=${encodeURIComponent(yahoUrl)}`,
      `https://corsproxy.io/?url=${encodeURIComponent(yahoUrl)}`,
      `https://api.allorigins.win/raw?url=${encodeURIComponent(yahoUrl)}`,
    ];
    let found = false;
    for (const url of proxies) {
      try {
        const res = await fetch(url);
        if (!res.ok) continue;
        const json = await res.json();
        const result = json?.chart?.result?.[0];
        if (!result) continue;
        const name = result.meta.longName||result.meta.shortName||ticker;
        const cat = addCat||"Egendefinert";
        setStocks(prev => ({...prev,[cat]:[...(prev[cat]||[]),{ticker,name:name.split(" ").slice(0,2).join(" ")}]}));
        setSelected(prev => [...prev,ticker]);
        setAddModal(false); setAddTicker("");
        found = true;
        break;
      } catch {}
    }
    if (!found) setAddError(`Fant ikke "${ticker}". Sjekk at ticker-symbolet er riktig (f.eks. TDY, MSFT, NVDA).`);
    setAddLoading(false);
  };

  const toggleSelected = ticker => setSelected(prev => prev.includes(ticker)?prev.filter(t=>t!==ticker):[...prev,ticker]);
  const removeStock = ticker => {
    setStocks(prev => {
      const updated = {};
      for (const [cat, list] of Object.entries(prev)) {
        const filtered = list.filter(s => s.ticker !== ticker);
        if (filtered.length > 0) updated[cat] = filtered;
      }
      return updated;
    });
    setSelected(prev => prev.filter(t => t !== ticker));
  };
  const filteredStocks = allStockList.filter(s => {
    const inCat = activeCategory==="Alle"||s.cat===activeCategory;
    const q = search.toLowerCase();
    return inCat&&(!q||s.ticker.toLowerCase().includes(q)||s.name.toLowerCase().includes(q));
  });

  const calStart=new Date(calMonth.getFullYear(),calMonth.getMonth(),1);
  const calEnd=new Date(calMonth.getFullYear(),calMonth.getMonth()+1,0);
  const todayD=new Date();todayD.setHours(0,0,0,0);
  // Live earnings from Yahoo quotes — override stale hardcoded dates
  const liveEarningsEvents=allStockList.flatMap(s=>{
    const dateStr=liveData[s.ticker]?.nextEarnings;
    if(!dateStr)return[];
    const d=new Date(dateStr+"T12:00:00");
    if(isNaN(d.getTime())||d<todayD)return[];
    const intel=getIntel(s.ticker);
    const isFav=favorites.includes(s.ticker);
    const when=liveData[s.ticker]?.nextEarningsTime;
    return[{date:d,type:"EARN",title:`${s.ticker} Earnings${when?` (${when==="før"?"før åpning":"etter stengning"})`:""}`,desc:`Kvartalsrapport (Nasdaq-kalender). Expected move: ±${intel.expectedMove}% | Beat rate: ${intel.beatRate}%`,color:CAL_COLORS["Earnings"],cat:"Earnings",ticker:s.ticker,live:true,expectedMove:intel.expectedMove,isFav,name:s.name}];
  });
  const liveEarningsTickers=new Set(liveEarningsEvents.map(e=>e.ticker));
  const allCalEvents=[
    ...calEvents.filter(e=>e.type!=="EARN"||!liveEarningsTickers.has(e.ticker)),
    ...liveEarningsEvents,
  ].sort((a,b)=>a.date-b.date);
  const visibleEvents=allCalEvents.filter(e=>e.date>=calStart&&e.date<=calEnd&&calFilters[e.cat]);
  const eventsByDay={};
  visibleEvents.forEach(e=>{const k=e.date.getDate();if(!eventsByDay[k])eventsByDay[k]=[];eventsByDay[k].push(e);});
  const firstDow=(calStart.getDay()+6)%7;
  const daysInMonth=calEnd.getDate();
  const in14=new Date(todayD);in14.setDate(todayD.getDate()+14);
  const upcoming=allCalEvents.filter(e=>e.date>=todayD&&e.date<=in14&&calFilters[e.cat]).slice(0,8);

  const NAV=[["idag","☀️","I dag"],["watchlist","📋","Watchlist"],["deepdive","🔬","Aksje"],["journal","📓","Journal"],["kjeden","🔗","AI-Kjeden"],["info","ℹ️","Info"]];
  const deepLive = liveData[deepTicker];

  const deepPrediction = useMemo(() => {
    const od = optionsData[deepTicker];
    const cp = od?.currentPrice ?? deepLive?.price;
    if (!cp) return null;

    const allC = od?.contracts ?? [];
    // Max pain og volum gjelder ett utløp: det nærmeste, som er det som kan "pinne" kursen
    const nearestExpiry = od?.expirations?.[0] ?? od?.expirationDate ?? null;
    const nearC = nearestExpiry ? allC.filter(c => c.expiration === nearestExpiry) : allC;
    const calls = nearC.filter(c => c.type === "C");
    const puts  = nearC.filter(c => c.type === "P");
    if (!calls.length || !puts.length) return null;

    const now = new Date();

    // ATM IV
    let atmIV = null;
    const atmC = calls.filter(c => c.impliedVolatility > 0).sort((a, b) => Math.abs(a.strike - cp) - Math.abs(b.strike - cp));
    const atmP = puts.filter(p => p.impliedVolatility > 0).sort((a, b) => Math.abs(a.strike - cp) - Math.abs(b.strike - cp));
    const ivs = [atmC[0]?.impliedVolatility, atmP[0]?.impliedVolatility].filter(Boolean);
    if (ivs.length) atmIV = ivs.reduce((s, v) => s + v, 0) / ivs.length;
    const dailyMove = atmIV ? (atmIV / Math.sqrt(252)) * cp : null;

    // --- 1. OI-basert Max Pain (tradisjonell) ---
    const strikeSet = [...new Set([...calls.map(c => c.strike), ...puts.map(p => p.strike)])]
      .sort((a, b) => a - b).filter(s => s >= cp * 0.80 && s <= cp * 1.20);

    let maxPain = null;
    {
      let minLoss = Infinity;
      strikeSet.forEach(S => {
        const loss = calls.reduce((s, c) => c.strike < S ? s + (S - c.strike) * (c.openInterest || 0) : s, 0)
                   + puts.reduce((s, p)  => p.strike > S ? s + (p.strike - S) * (p.openInterest || 0) : s, 0);
        if (loss < minLoss) { minLoss = loss; maxPain = S; }
      });
    }

    // --- 2. Volume-justert Max Pain (OI + dagens volum vektet) ---
    // Skaler volum opp til OI-nivå: hvis snitt-ratio er 5%, gang med 20 for lik vekt
    const avgOI  = (nearC.reduce((s, c) => s + (c.openInterest || 0), 0) / Math.max(1, nearC.length)) || 1;
    const avgVol = (nearC.reduce((s, c) => s + (c.volume || 0), 0)       / Math.max(1, nearC.length)) || 1;
    const volScale = avgVol > 0 ? Math.min(10, avgOI / avgVol) : 3; // max 10× for å unngå ekstreme tall
    let volMaxPain = null;
    {
      let minLoss = Infinity;
      strikeSet.forEach(S => {
        const effOI = c => (c.openInterest || 0) + (c.volume || 0) * volScale;
        const loss = calls.reduce((s, c) => c.strike < S ? s + (S - c.strike) * effOI(c) : s, 0)
                   + puts.reduce((s, p)  => p.strike > S ? s + (p.strike - S) * effOI(p) : s, 0);
        if (loss < minLoss) { minLoss = loss; volMaxPain = S; }
      });
    }

    // --- 3. Volum-magnet: striket med høyest kombinert call+put volum i dag ---
    const volByStrike = {};
    nearC.forEach(c => {
      volByStrike[c.strike] = (volByStrike[c.strike] || 0) + (c.volume || 0);
    });
    const [volumeMagnetStrike] = Object.entries(volByStrike)
      .filter(([s]) => +s >= cp * 0.85 && +s <= cp * 1.15)
      .sort((a, b) => b[1] - a[1])[0] ?? [];
    const volumeMagnet = volumeMagnetStrike ? +volumeMagnetStrike : null;

    // --- 4. Zero gamma: kursen der dealernes samlede gamma (alle utløp, OI) skifter fortegn ---
    // Gamma regnes på nytt for hver tenkt kurs S, så nivået er en kurs og ikke bare et strike.
    const gexC = allC
      .filter(o => o.expiration && (o.openInterest || 0) > 0 && o.strike > cp * 0.7 && o.strike < cp * 1.3)
      .map(o => ({
        K: o.strike, oi: o.openInterest, sign: o.type === "C" ? 1 : -1,
        T: Math.max(1, Math.round((new Date(o.expiration + "T16:00:00") - now) / 86400000)) / 365,
        iv: o.impliedVolatility > 0 ? o.impliedVolatility : atmIV,
      }))
      .filter(o => o.iv > 0);
    let zeroGamma = null;
    if (gexC.length) {
      const gexAt = S => gexC.reduce((s, o) => s + o.sign * o.oi * bsGamma(S, o.K, o.T, o.iv) * S * S * 0.01, 0);
      const grid = Array.from({ length: 61 }, (_, k) => cp * (1 + (k - 30) * 0.005));
      const vals = grid.map(gexAt);
      for (let i = 1; i < grid.length; i++) {
        if ((vals[i - 1] <= 0) === (vals[i] <= 0)) continue;
        const S = grid[i - 1] + (grid[i] - grid[i - 1]) * (-vals[i - 1]) / (vals[i] - vals[i - 1]);
        if (zeroGamma == null || Math.abs(S - cp) < Math.abs(zeroGamma - cp)) zeroGamma = +S.toFixed(2);
      }
    }

    // --- 5. DTE-basert vekting ---
    // Nærmeste utløp bestemmer hvor mye Max Pain pinning-effekten dominerer
    const dte = nearestExpiry
      ? Math.max(0, Math.round((new Date(nearestExpiry + "T12:00:00") - now) / (1000 * 60 * 60 * 24)))
      : 21;

    // mpTotalWeight: 70% ved DTE=0, 25% ved DTE≥20 (lineært)
    const mpTotalWeight = Math.max(0.25, Math.min(0.70, 0.70 - (dte / 20) * 0.45));
    const rest = 1 - mpTotalWeight;

    // Max Pain-andelen deles: volMaxPain 60%, maxPain 40%
    const w_volMaxPain   = mpTotalWeight * 0.60;
    const w_maxPain      = mpTotalWeight * 0.40;
    // Resten: zeroGamma 70%, volumeMagnet 30%
    const w_zeroGamma    = rest * 0.70;
    const w_volumeMagnet = rest * 0.30;

    const sources = [
      [volMaxPain,   w_volMaxPain],
      [maxPain,      w_maxPain],
      [zeroGamma,    w_zeroGamma],
      [volumeMagnet, w_volumeMagnet],
    ].filter(([v]) => v != null);

    let predictedClose = null;
    if (sources.length) {
      const totalW = sources.reduce((s, [, w]) => s + w, 0);
      predictedClose = sources.reduce((s, [v, w]) => s + v * (w / totalW), 0);
    }
    if (predictedClose && dailyMove) {
      predictedClose = Math.max(cp - dailyMove * 2.5, Math.min(cp + dailyMove * 2.5, predictedClose));
    }

    return {
      maxPain,
      volMaxPain,
      volumeMagnet,
      zeroGamma,
      atmIV,
      dte,
      mpWeight: Math.round(mpTotalWeight * 100),
      currentPrice: cp,
      dailyMove,
      expectedMoveUp:   dailyMove ? +(cp + dailyMove).toFixed(2) : null,
      expectedMoveDown: dailyMove ? +(cp - dailyMove).toFixed(2) : null,
      predictedClose: predictedClose ? +predictedClose.toFixed(2) : null,
      targetDate: nearestExpiry ?? null,
    };
  }, [optionsData, deepTicker, deepLive]);

  // Logg én prediksjon per aksje per dag – bare før markedet stenger på måldagen, så loggen ikke kan "se fasiten"
  useEffect(() => {
    const p = deepPrediction;
    if (!p?.predictedClose || !p.targetDate || !p.currentPrice) return;
    const today = nyDay();
    if (p.targetDate < today || (p.targetDate === today && nyHour() >= 16)) return;
    const id = `${deepTicker}|${today}|${p.targetDate}`;
    setPredLog(prev => prev.some(e => e.id === id) ? prev
      : [...prev, { id, ticker: deepTicker, made: today, target: p.targetDate, cp: +p.currentPrice.toFixed(2), pred: p.predictedClose }]);
  }, [deepPrediction, deepTicker]);

  // AI-Kjeden: kursutvikling for alle segmentenes aksjer + SPY som referanse
  useEffect(() => {
    if (view !== "kjeden" || chainPerf) return;
    const t = [...new Set(["SPY", ...AI_CHAIN.flatMap(n => n.tickers)])].join(",");
    fetch(`/perf?t=${encodeURIComponent(t)}`).then(r => r.json()).then(d => setChainPerf(d?.error ? {} : d)).catch(() => setChainPerf({}));
  }, [view, chainPerf]);

  // IV-historikk for aksjen i Deep Dive (logges daglig på serveren)
  useEffect(() => {
    if (view !== "deepdive" || ivHist[deepTicker]) return;
    fetch(`/api/iv?t=${encodeURIComponent(deepTicker)}`).then(r => r.json())
      .then(d => setIvHist(prev => ({ ...prev, [deepTicker]: Array.isArray(d?.rows) ? d.rows : [] })))
      .catch(() => setIvHist(prev => ({ ...prev, [deepTicker]: [] })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, deepTicker]);
  const ivInfo = useMemo(() => {
    const rows = ivHist[deepTicker];
    if (!rows?.length) return null;
    const cur = rows[rows.length - 1].iv, ivs = rows.map(r => r.iv);
    const lo = Math.min(...ivs), hi = Math.max(...ivs);
    return {
      cur, n: rows.length,
      rank: rows.length >= 20 && hi > lo ? Math.round((cur - lo) / (hi - lo) * 100) : null,
      pctl: rows.length >= 20 ? Math.round(ivs.filter(v => v < cur).length / ivs.length * 100) : null,
    };
  }, [ivHist, deepTicker]);

  // Aksje-siden: faste tall fra STOCK_INTEL, med live-verdier oppå der vi har dem
  // (IV-rang fra loggingen, gamma og call/put fra opsjonskjeden, rapportdato fra Yahoo).
  const deepIntel = useMemo(() => {
    const base = getIntel(deepTicker), pf = { ...base.pf };
    let ivLive = false, gammaLive = false;
    if (ivInfo?.rank != null) { pf.ivPct = ivInfo.rank; ivLive = true; }
    const od = optionsData[deepTicker];
    if (od?.ok && od.contracts?.length && od.currentPrice) {
      const g = gammaProfile(od.contracts, od.currentPrice, od.iv30 ?? null);
      if (g) {
        pf.gammaRegime = g.positive ? "POSITIVE" : "NEGATIVE";
        if (g.zeroGamma) pf.gammaFlip = +g.zeroGamma.toFixed(2);
        gammaLive = true;
      }
      const f = flowSummary(od.contracts, od.currentPrice);
      if (f.pcVol) pf.callPutRatio = +(1 / f.pcVol).toFixed(2);
    }
    const earn = liveData[deepTicker]?.nextEarnings;
    const when = liveData[deepTicker]?.nextEarningsTime;
    const nextEarnings = earn ? `${earn}${when ? ` (${when === "før" ? "før åpning" : "etter stengning"})` : ""}` : (base.nextEarnings >= nyDay() ? base.nextEarnings : "Ikke kjent");
    return { ...base, pf, nextEarnings, ivLive, gammaLive };
  }, [deepTicker, ivInfo, optionsData, liveData]);

  // Hent kurshistorikk for aksjer i loggen der måldatoen er passert
  useEffect(() => {
    if (view !== "deepdive" && !(view === "journal" && journalTab === "statistikk")) return;
    const today = nyDay();
    const need = [...new Set(predLog.filter(e => e.target <= today).map(e => e.ticker))].filter(t => !techData[t]);
    need.forEach((t, i) => setTimeout(() => fetchTechData(t), i * 300));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, journalTab, predLog.length]);

  // Treffsikkerhet: snittavvik mot faktisk sluttkurs, sammenlignet med "kursen står stille"
  const predStats = useMemo(() => {
    const today = nyDay(), h = nyHour();
    const closeOn = (ticker, day) => {
      const td = techData[ticker];
      if (!td?.timestamps) return null;
      const i = td.timestamps.findIndex(ts => nyDay(new Date(ts * 1000)) === day);
      return i >= 0 ? td.closes[i] : null;
    };
    const acc = () => ({ n: 0, err: 0, naive: 0, dirN: 0, dirOk: 0 });
    const all = acc(), tk = acc();
    for (const e of predLog) {
      if (!(e.target < today || (e.target === today && h >= 17))) continue;
      const act = closeOn(e.ticker, e.target);
      if (!act) continue;
      for (const s of e.ticker === deepTicker ? [all, tk] : [all]) {
        s.n++;
        s.err += Math.abs(e.pred - act) / act * 100;
        s.naive += Math.abs(e.cp - act) / act * 100;
        if (e.pred !== e.cp && act !== e.cp) { s.dirN++; if ((e.pred > e.cp) === (act > e.cp)) s.dirOk++; }
      }
    }
    const fin = s => ({ n: s.n, err: s.n ? s.err / s.n : 0, naive: s.n ? s.naive / s.n : 0, dirN: s.dirN, dir: s.dirN ? s.dirOk / s.dirN : 0 });
    return { logged: predLog.length, all: fin(all), ticker: fin(tk) };
  }, [predLog, techData, deepTicker]);


  return (
    <div style={{background:"#030b14",minHeight:"100vh",color:"#e2e8f0",fontFamily:"'IBM Plex Mono',monospace",display:"flex",flexDirection:"column"}}>
      {/* HEADER */}
      <div style={{borderBottom:"1px solid #1e293b",padding:"13px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",background:"#020810",position:"sticky",top:0,zIndex:50}}>
        <div style={{display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
          {isMobile&&<button onClick={()=>setSidebarOpen(o=>!o)}
            style={{padding:"5px 8px",background:sidebarOpen?"#0d1f35":"transparent",border:"1px solid #1e293b",borderRadius:4,color:"#64748b",cursor:"pointer",fontSize:14,lineHeight:1}}>☰</button>}
          <div style={{width:32,height:32,background:"linear-gradient(135deg,#f59e0b,#8b5cf6)",borderRadius:7,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>⚡</div>
          <div>
            <div style={{fontSize:isMobile?12:15,fontWeight:700,letterSpacing:2,color:"#f8fafc"}}>AI AKSJER</div>
            {!isMobile&&<div style={{fontSize:9,color:"#334155",letterSpacing:1}}>LIVE MARKEDSOVERSIKT</div>}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6,flex:"1 1 0",minWidth:0}}>
          <div className="nav-scroll" style={{display:"flex",gap:4,flex:"1 1 0",minWidth:0}}>
            {NAV.map(([v,icon,label])=>(
              <button key={v} onClick={()=>{setView(v);setSidebarOpen(false);}}
                style={{padding:"6px 12px",border:"1px solid",borderColor:view===v?"#f59e0b":"#1e293b",background:view===v?"#f59e0b15":"transparent",color:view===v?"#f59e0b":"#64748b",borderRadius:5,cursor:"pointer",fontSize:isMobile?14:12,fontFamily:"inherit",letterSpacing:0.3,whiteSpace:"nowrap",transition:"all 0.15s"}}>
                {isMobile?icon:`${icon} ${label}`}
              </button>
            ))}
            <button onClick={()=>setShowPinModal(true)} title={syncPin?"Sky-synkronisering aktiv":"Koble til sky"}
              style={{padding:"5px 8px",background:"transparent",border:"1px solid",borderColor:syncPin?"#10b98140":"#1e293b",borderRadius:5,cursor:"pointer",fontSize:14,lineHeight:1,flexShrink:0,transition:"all 0.2s"}}>
              {syncStatus==="syncing"?"⟳":syncStatus==="synced"?"☁️":syncStatus==="error"?"⚠️":syncPin?"☁️":"🔒"}
            </button>
          </div>
        </div>
      </div>

      <div style={{display:"flex",flex:1,overflow:"hidden",flexDirection:isMobile?"column":"row"}}>
        {/* SIDEBAR */}
        <div style={{width:isMobile?"100%":298,borderRight:isMobile?"none":"1px solid #1e293b",borderBottom:isMobile?"1px solid #1e293b":"none",display:isMobile&&!sidebarOpen?"none":"flex",flexDirection:"column",background:"#040d18",flexShrink:0,maxHeight:isMobile?"60vw":"none",overflowY:isMobile?"auto":"visible"}}>
          <div style={{padding:"14px 14px 6px"}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Søk ticker / navn..."
              style={{width:"100%",padding:"10px 12px",background:"#0a1628",border:"1px solid #1e293b",borderRadius:5,color:"#e2e8f0",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
          </div>
          <div style={{padding:"6px 14px 6px"}}>
            {["Alle",...Object.keys(stocks)].map(cat=>(
              <button key={cat} onClick={()=>setActiveCategory(cat)}
                style={{display:"block",width:"100%",textAlign:"left",padding:"7px 10px",marginBottom:3,background:activeCategory===cat?"#0d1f35":"transparent",border:"1px solid",borderColor:activeCategory===cat?(CAT_COLORS[cat]||"#f59e0b")+"50":"transparent",borderRadius:4,cursor:"pointer",fontSize:12,color:activeCategory===cat?(CAT_COLORS[cat]||"#f59e0b"):"#475569",fontFamily:"inherit",letterSpacing:0.5}}>
                {cat==="Alle"?"▦ ALLE":cat.toUpperCase()}
              </button>
            ))}
          </div>
          <div style={{margin:"6px 14px 8px",display:"flex",gap:4}}>
            <button onClick={()=>setAddModal(true)}
              style={{flex:1,padding:"9px",background:"#0d1f35",border:"1px dashed #1e4080",borderRadius:4,color:"#3b82f6",cursor:"pointer",fontSize:13,fontFamily:"inherit",letterSpacing:1}}>
              + LEGG TIL AKSJE
            </button>
            <button onClick={()=>{if(window.confirm("Tilbakestill til standard aksjelist?"))setStocks(DEFAULT_STOCKS);}}
              style={{padding:"6px 8px",background:"transparent",border:"1px solid #3f1a1a",borderRadius:4,color:"#f43f5e",cursor:"pointer",fontSize:10,fontFamily:"inherit"}} title="Tilbakestill">
              ↺
            </button>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"0 14px 14px"}}>
            {filteredStocks.map(s=>{
              const d=liveData[s.ticker];
              const isSel=selected.includes(s.ticker);
              const color=CAT_COLORS[s.cat]||"#94a3b8";
              const dotColor=d?(d.change>=0?"#10b981":"#f43f5e"):"#334155";
              return (
                <div key={s.ticker} style={{padding:"9px 10px",marginBottom:4,borderRadius:5,background:isSel?"#0d1f35":"transparent",border:"1px solid",borderColor:isSel?color+"40":"transparent",display:"flex",alignItems:"center",gap:7}}>
                  <div onClick={()=>toggleSelected(s.ticker)} style={{display:"flex",alignItems:"center",gap:7,flex:1,cursor:"pointer",minWidth:0}}>
                    <div style={{width:3,height:22,borderRadius:2,background:isSel?color:"#1e293b",flexShrink:0}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:700,color:isSel?"#f8fafc":"#94a3b8"}}>{s.ticker}</div>
                      <div style={{fontSize:11,color:"#334155",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:dotColor,display:"inline-block"}}/>
                    <span style={{fontSize:11,color:dotColor}}>{d?`${Math.abs(d.change).toFixed(1)}%`:"…"}</span>
                    <button onClick={()=>{setDeepTicker(s.ticker);setView("deepdive");}} title="Deep Dive"
                      style={{padding:"3px 6px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:3,color:"#3b82f6",cursor:"pointer",fontSize:10,fontFamily:"inherit"}}>🔬</button>
                    <button onClick={()=>removeStock(s.ticker)} title="Fjern aksje"
                      style={{padding:"3px 6px",background:"transparent",border:"1px solid #3f1a1a",borderRadius:3,color:"#f43f5e",cursor:"pointer",fontSize:10,fontFamily:"inherit"}}>×</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div style={{flex:1,overflowY:"auto",overflowX:"hidden",padding:isMobile?10:16}}>

          {/* ── HJEM ── */}
          {view==="idag"&&(
            <div style={{display:"flex",gap:4,marginBottom:16,flexWrap:"wrap",borderBottom:"1px solid #1e293b",paddingBottom:10}}>
              {[["oversikt","☀️ Oversikt"],["makro","🌡️ Makro og krasj-radar"],["kalender","🗓 Kalender"],["morgen","🌅 Futures og neste dag"]].map(([k,l])=>(
                <button key={k} onClick={()=>setIdagMode(k)}
                  style={{padding:"6px 14px",fontSize:12,fontFamily:"inherit",cursor:"pointer",borderRadius:4,border:"1px solid",
                    borderColor:idagMode===k?"#3b82f6":"#1e293b",background:idagMode===k?"#3b82f620":"transparent",color:idagMode===k?"#93c5fd":"#64748b",fontWeight:idagMode===k?700:400}}>{l}</button>
              ))}
            </div>
          )}
          {view==="watchlist"&&(
            <div style={{display:"flex",gap:4,marginBottom:16,flexWrap:"wrap",borderBottom:"1px solid #1e293b",paddingBottom:10}}>
              {[["tabell","📋 Oversikt"],["signaler","📡 Signaler siste 5 dager"]].map(([k,l])=>(
                <button key={k} onClick={()=>setWlMode(k)}
                  style={{padding:"6px 14px",fontSize:12,fontFamily:"inherit",cursor:"pointer",borderRadius:4,border:"1px solid",
                    borderColor:wlMode===k?"#3b82f6":"#1e293b",background:wlMode===k?"#3b82f620":"transparent",color:wlMode===k?"#93c5fd":"#64748b",fontWeight:wlMode===k?700:400}}>{l}</button>
              ))}
            </div>
          )}
          {view==="idag"&&idagMode==="morgen"&&(()=>{
            const ORB_CX=410,ORB_CY=272,RADIUS=222,CW=142,CH=70;
            const sc=isMobile?(window.innerWidth-20)/586:Math.min(1.45,(window.innerWidth-32)/820);
            const allM=Object.entries(liveData)
              .filter(([,d])=>d?.change!=null&&d?.price!=null)
              .map(([ticker,d])=>({ticker,price:d.price,change:d.change,name:allStockList.find(s=>s.ticker===ticker)?.name||ticker}));
            const gainers=allM.slice().sort((a,b)=>b.change-a.change).slice(0,3);
            const losers=allM.slice().sort((a,b)=>a.change-b.change).slice(0,3);
            const slots=[gainers[0],gainers[1],gainers[2],losers[0],losers[1],losers[2]].filter(Boolean);
            const angles=[-90,-28,34,90,152,-152];
            const allCh=Object.values(liveData).filter(d=>d?.change!=null).map(d=>d.change);
            const avgCh=allCh.length?allCh.reduce((a,b)=>a+b,0)/allCh.length:0;
            const moodScore=Math.max(0,Math.min(100,50+avgCh*9));
            const mc=moodScore>62?"#10b981":moodScore>42?"#f59e0b":"#f43f5e";
            const ml=moodScore>68?"BULLISH":moodScore>58?"POSITIV":moodScore>42?"NØYTRAL":moodScore>32?"FORSIKTIG":"BEARISH";
            const up=allCh.filter(c=>c>0).length, down=allCh.filter(c=>c<0).length;
            if(Object.keys(marketData).length===0&&!marketLoading)fetchMarketData();
            if(!cotData&&!cotLoading&&!cotTriedRef.current)fetchCOT();
            if(!introFiredRef.current){introFiredRef.current=true;setIntroPhase(1);setTimeout(()=>setIntroPhase(2),1300);setTimeout(()=>setIntroPhase(0),2700);}
            return(
              <div>
                <style>{`
                  @keyframes orb-ring{0%{transform:scale(1);opacity:.55}100%{transform:scale(3.4);opacity:0}}
                  @keyframes dot-fl{0%,100%{opacity:var(--op);transform:translateY(0) scale(1)}50%{opacity:calc(var(--op)*2);transform:translateY(-6px) scale(1.2)}}
                  @keyframes shoot{0%{stroke-dashoffset:80;opacity:0}8%{opacity:0.85}80%{opacity:0.5}100%{stroke-dashoffset:-600;opacity:0}}
                  @keyframes aurora{0%,100%{opacity:0.06}50%{opacity:0.16}}
                  @keyframes fut-pulse{0%,100%{opacity:1}50%{opacity:0.6}}
                  @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
                  @keyframes intro-ring-dot{0%{opacity:0;transform:scale(0)}40%{opacity:0.9;transform:scale(1)}80%{opacity:0.8;transform:scale(1)}100%{opacity:0;transform:scale(0.2)}}
                  @keyframes intro-wave{0%{transform:scale(1);opacity:0.9}100%{transform:scale(9);opacity:0}}
                  @keyframes dot-scatter{0%{transform:translate(var(--sx),var(--sy));opacity:0.85}100%{transform:translate(0,0);opacity:var(--op)}}
                  .hjem-card:hover{transform:scale(1.05)!important}
                `}</style>

                {/* Futures strip */}
                {(()=>{
                  const futs=[
                    {t:"ES=F",label:"S&P 500 Fut"},
                    {t:"NQ=F",label:"NASDAQ Fut"},
                    {t:"^VIX",label:"VIX"},
                    {t:"^TNX",label:"10Y Yield"},
                    {t:"^DJI",label:"DOW Fut"},
                  ];
                  const hasAny=futs.some(f=>marketData[f.t]);
                  if(!hasAny)return null;
                  return(
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14,padding:"10px 14px",background:"#040d18",border:"1px solid #1e293b",borderRadius:8,alignItems:"center"}}>
                      <div style={{fontSize:9,color:"#334155",letterSpacing:2,marginRight:4}}>FUTURES</div>
                      {futs.map(({t,label})=>{
                        const d=marketData[t];
                        if(!d)return null;
                        const isVix=t==="^VIX";
                        const isTnx=t==="^TNX";
                        const c=isVix?(d.change>0?"#f43f5e":"#10b981"):(d.change>=0?"#10b981":"#f43f5e");
                        const arrow=isVix?(d.change>0?"▲":"▼"):(d.change>=0?"▲":"▼");
                        return(
                          <div key={t} style={{display:"flex",alignItems:"center",gap:6,background:"#060f1c",border:`1px solid ${c}25`,borderRadius:6,padding:"6px 11px",flex:"0 0 auto"}}>
                            <div>
                              <div style={{fontSize:11,color:"#475569",letterSpacing:1}}>{label}</div>
                              <div style={{fontSize:14,fontWeight:700,color:"#e2e8f0"}}>{isTnx?`${d.price.toFixed(2)}%`:d.price<10?d.price.toFixed(3):d.price.toFixed(0)}</div>
                            </div>
                            <div style={{fontSize:11,fontWeight:700,color:c,minWidth:44,textAlign:"right"}}>
                              <span style={{animation:"fut-pulse 2.5s ease-in-out infinite"}}>{arrow}</span> {Math.abs(d.change).toFixed(2)}%
                            </div>
                          </div>
                        );
                      })}
                      <div style={{width:1,height:28,background:"#1e293b",margin:"0 4px",flexShrink:0}}/>
                      {(()=>{
                        const spyCloses = techData["SPY"]?.closes;
                        if (!spyCloses || spyCloses.length < 20) return null;
                        const rsi = calcRSI(spyCloses);
                        const bb = calcBollinger(spyCloses);
                        const ma20 = calcMA(spyCloses, 20);
                        const ma50 = calcMA(spyCloses, 50);
                        let label, icon, color;
                        if (bb?.squeeze && rsi !== null && rsi >= 38 && rsi <= 63) {
                          label = "SPY SQUEEZE"; icon = "⚡"; color = "#a78bfa";
                        } else if (rsi !== null && rsi < 35) {
                          label = "SPY OVERSOLD"; icon = "🎯"; color = "#06b6d4";
                        } else if (rsi !== null && rsi > 72) {
                          label = "SPY OVERBOUGHT"; icon = "⚠️"; color = "#f43f5e";
                        } else if (ma20 && ma50 && ma20 > ma50 && rsi !== null && rsi >= 52 && rsi <= 70) {
                          label = "SPY MOMENTUM"; icon = "🚀"; color = "#10b981";
                        } else if (rsi !== null) {
                          label = `SPY RSI ${rsi}`; icon = "📊"; color = "#475569";
                        } else return null;
                        return (
                          <div onClick={()=>setIdagMode("makro")} title="Gå til Marked for full SPY-analyse"
                            style={{display:"flex",alignItems:"center",gap:6,background:color+"15",border:`1px solid ${color}40`,borderRadius:6,padding:"6px 11px",cursor:"pointer",flexShrink:0,transition:"background 0.15s"}}
                            onMouseEnter={e=>e.currentTarget.style.background=color+"28"}
                            onMouseLeave={e=>e.currentTarget.style.background=color+"15"}>
                            <span style={{fontSize:11}}>{icon}</span>
                            <div>
                              <div style={{fontSize:8,color:color,letterSpacing:1,fontWeight:700}}>{label}</div>
                              {rsi!==null&&<div style={{fontSize:9,color:"#475569"}}>RSI {rsi}{bb?.squeeze?" · Squeeze":""}</div>}
                            </div>
                          </div>
                        );
                      })()}
                      <button onClick={()=>fetchMarketData()} disabled={marketLoading}
                        style={{marginLeft:"auto",padding:"6px 12px",background:"transparent",border:"1px solid #1e293b",borderRadius:6,color:marketLoading?"#334155":"#475569",cursor:marketLoading?"default":"pointer",fontSize:13,fontFamily:"inherit",transition:"color 0.15s",flexShrink:0}}
                        title="Oppdater futures">
                        {marketLoading?"⟳":"↻"}
                      </button>
                    </div>
                  );
                })()}

                {/* Favoritt-varsler */}
                {(()=>{
                  const favTickers = favorites.filter(t => techData[t]?.closes?.length);
                  if (!favTickers.length) return null;
                  const cutoff = new Date(Date.now() - 5*24*3600*1000).toISOString().slice(0,10);
                  const alerts = favTickers.flatMap(ticker => {
                    const td = techData[ticker];
                    const patterns = detectInstitutionalPatterns(td.closes, td.opens||[], td.highs||[], td.lows||[], td.volumes||[], td.timestamps||[]);
                    return patterns.filter(p => p.date >= cutoff).map(p => ({ ticker, ...p }));
                  }).sort((a,b) => b.date.localeCompare(a.date));
                  if (!alerts.length) return null;
                  const patColors = { HIGH_RVOL_UP:"#f59e0b", HIGH_RVOL_DOWN:"#f43f5e", LIQUIDITY_SWEEP:"#c084fc", REJECTION_WICK:"#38bdf8", MA_BOUNCE_EMA20:"#34d399", MA_BOUNCE_MA50:"#10b981", ACCUMULATION:"#fb923c", HAMMER:"#10b981", SHOOTING_STAR:"#f43f5e", BULL_TRAP:"#f97316", BREAKOUT_20D:"#22d3ee", RSI_DIV_BULL:"#f472b6" };
                  const patEmoji = { HIGH_RVOL_UP:"📈", HIGH_RVOL_DOWN:"📉", LIQUIDITY_SWEEP:"🎯", REJECTION_WICK:"↩", MA_BOUNCE_EMA20:"📗", MA_BOUNCE_MA50:"📗", ACCUMULATION:"📦", HAMMER:"🔨", SHOOTING_STAR:"⭐", BULL_TRAP:"⚠️", BREAKOUT_20D:"🚀", RSI_DIV_BULL:"↗" };
                  return (
                    <div style={{marginBottom:16,padding:"12px 14px",background:"#060f1c",border:"1px solid #1e293b",borderRadius:8}}>
                      <div style={{marginBottom:10}}>
                        <div style={{fontSize:11,color:"#334155",letterSpacing:1}}>🔔 TEKNISKE SIGNALER — FAVORITTER</div>
                        <div style={{fontSize:10,color:"#1e3a5f",marginTop:3}}>Basert på kurshistorikk · siste 5 dager · retning bekreftes av markedet</div>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:5}}>
                        {alerts.slice(0,8).map((a,i) => {
                          const info = SIGNAL_INFO[a.type];
                          const color = patColors[a.type] || "#94a3b8";
                          const emoji = patEmoji[a.type] || "•";
                          return (
                            <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px",background:"#0a1628",borderRadius:5,borderLeft:`2px solid ${color}`,cursor:"pointer"}}
                              onClick={()=>{ setDeepTicker(a.ticker); setView("deepdive"); setDeepTab("teknisk"); }}>
                              <span style={{fontSize:13}}>{emoji}</span>
                              <span style={{fontSize:12,fontWeight:700,color:"#e2e8f0",minWidth:44}}>{a.ticker}</span>
                              <span style={{fontSize:11,color:color,flex:1}}>{info?.name || a.type}</span>
                              <span style={{fontSize:10,padding:"2px 7px",borderRadius:3,background:info?.bias==="bullish"?"#10b98118":"#f43f5e18",color:info?.bias==="bullish"?"#10b981":"#f43f5e"}}>{info?.bias==="bullish"?"↑ bullish":"↓ bearish"}</span>
                              <span style={{fontSize:10,color:"#64748b"}}>{a.date}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Favoritt-opsjons-varsler */}
                {(()=>{
                  const favWithOpts = favorites.filter(t => optionsData[t]?.ok === true);
                  const favFailed  = favorites.filter(t => optionsData[t]?.ok === false);
                  const favPending = favorites.filter(t => !optionsData[t]);
                  const optAlerts = favWithOpts.flatMap(ticker => {
                    const od = optionsData[ticker];
                    const calls = od.calls || [], puts = od.puts || [];
                    const gs = detectGammaSqueeze(od);
                    // Gamma squeeze absorbs individual call/put rows for this ticker
                    if (gs) return [{ ticker, type:"GAMMA_SQUEEZE", strength:gs.strength, topCall:gs.topCall, bias:"bullish", volume: gs.topCall?.volume||0 }];
                    const uCalls = calls.filter(c=>(c.volume||0)>0&&(c.openInterest||0)>0&&c.volume>c.openInterest*1.5).sort((a,b)=>b.volume-a.volume).slice(0,1);
                    const uPuts  = puts.filter(p=>(p.volume||0)>0&&(p.openInterest||0)>0&&p.volume>p.openInterest*1.5).sort((a,b)=>b.volume-a.volume).slice(0,1);
                    return [
                      ...uCalls.map(c=>({ ticker, type:"CALL", strike:c.strike, volume:c.volume, oi:c.openInterest, bias:"bullish" })),
                      ...uPuts.map(p=>({ ticker, type:"PUT",  strike:p.strike, volume:p.volume, oi:p.openInterest, bias:"bearish" })),
                    ];
                  }).sort((a,b)=>b.volume-a.volume);

                  if (!favorites.length) return null;

                  return (
                    <div style={{marginBottom:16,padding:"12px 14px",background:"#060f1c",border:"1px solid #1e293b",borderRadius:8}}>
                      <div style={{marginBottom:10}}>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{fontSize:11,color:"#334155",letterSpacing:1}}>📊 OPSJONSFLOW — FAVORITTER</div>
                          {favPending.length > 0 && <div style={{fontSize:10,color:"#334155"}}>Henter {favPending.length} til…</div>}
                        </div>
                        <div style={{fontSize:10,color:"#1e3a5f",marginTop:3}}>Basert på CBOE live opsjonsdata · til neste forfall · mekanisk kjøpspress</div>
                      </div>
                      {optAlerts.length === 0 && favWithOpts.length > 0 && (
                        <div style={{fontSize:11,color:"#334155"}}>Ingen uvanlig opsjonsaktivitet funnet i dag for dine favoritter.</div>
                      )}
                      {favPending.length > 0 && favWithOpts.length === 0 && (
                        <div style={{fontSize:11,color:"#334155"}}>Henter opsjonsdata…</div>
                      )}
                      {favFailed.length > 0 && favPending.length === 0 && favWithOpts.length === 0 && (
                        <div style={{fontSize:11,color:"#475569"}}>CBOE data ikke tilgjengelig akkurat nå for: {favFailed.join(", ")}. Prøv å oppdatere siden om et par minutter.</div>
                      )}
                      <div style={{display:"flex",flexDirection:"column",gap:5}}>
                        {optAlerts.map((a,i)=>{
                          if (a.type === "GAMMA_SQUEEZE") return (
                            <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px",background:"#f59e0b08",borderRadius:5,borderLeft:`2px solid ${a.strength==="STERK"?"#f59e0b":"#a78bfa"}`,cursor:"pointer"}}
                              onClick={()=>{ setDeepTicker(a.ticker); setView("deepdive"); setDeepTab("teknisk"); }}>
                              <span style={{fontSize:12}}>⚡</span>
                              <span style={{fontSize:12,fontWeight:700,color:"#e2e8f0",minWidth:44}}>{a.ticker}</span>
                              <span style={{fontSize:11,color:a.strength==="STERK"?"#f59e0b":"#a78bfa",fontWeight:700,flex:1}}>GAMMA SQUEEZE {a.strength}</span>
                              {a.topCall&&<span style={{fontSize:10,color:"#94a3b8"}}>CALL ${a.topCall.strike} — {(a.topCall.volume||0).toLocaleString()} vol</span>}
                            </div>
                          );
                          return (
                            <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px",background:"#0a1628",borderRadius:5,borderLeft:`2px solid ${a.bias==="bullish"?"#10b981":"#f43f5e"}`,cursor:"pointer"}}
                              onClick={()=>{ setDeepTicker(a.ticker); setView("deepdive"); setDeepTab("teknisk"); }}>
                              <span style={{fontSize:12}}>{a.bias==="bullish"?"📈":"📉"}</span>
                              <span style={{fontSize:12,fontWeight:700,color:"#e2e8f0",minWidth:44}}>{a.ticker}</span>
                              <span style={{fontSize:11,color:a.bias==="bullish"?"#10b981":"#f43f5e",fontWeight:700}}>{a.type} ${a.strike}</span>
                              <span style={{fontSize:10,color:"#94a3b8",flex:1}}>Vol {a.volume.toLocaleString()} vs {a.oi} OI</span>
                              <span style={{fontSize:10,padding:"2px 7px",borderRadius:3,background:a.bias==="bullish"?"#10b98118":"#f43f5e18",color:a.bias==="bullish"?"#10b981":"#f43f5e",flexShrink:0}}>{a.bias==="bullish"?"↑ bullish":"↓ bearish"}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Orb section */}
                <div style={{width:"100%",margin:"0 auto 20px",position:"relative",height:544*sc,overflow:"visible"}}>
                <div style={{position:"absolute",width:820,height:544,left:"50%",marginLeft:-410,transformOrigin:"top center",transform:`scale(${sc})`}}>

                  {/* SVG: dots + lines */}
                  <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",overflow:"visible"}} viewBox="0 0 820 544" preserveAspectRatio="xMidYMid meet">
                    {/* Shooting stars */}
                    {[[90,60,3.8,0],[580,140,4.5,1.9],[200,420,3.2,3.5],[680,320,5.1,6.2]].map(([x,y,dur,delay],i)=>(
                      <line key={`s${i}`} x1={x} y1={y} x2={x+70} y2={y-12}
                        stroke={mc} strokeWidth="1.5" strokeLinecap="round"
                        strokeDasharray="70 600"
                        style={{animation:`shoot ${dur}s linear ${delay}s infinite`}}/>
                    ))}
                    {/* Intro ring dots + wave (phase 1 & 2) */}
                    {introPhase>0&&Array.from({length:40},(_,i)=>{
                      const ang=i/40*Math.PI*2;
                      const rx=ORB_CX+Math.cos(ang)*38, ry=ORB_CY+Math.sin(ang)*38;
                      return<circle key={`ir${i}`} cx={rx} cy={ry} r={1.8} fill={mc}
                        style={{animation:`intro-ring-dot ${introPhase===1?"1.3s":"0.5s"} ease-in-out ${i*0.025}s ${introPhase===1?"infinite":"forwards"}`}}/>;
                    })}
                    {introPhase===2&&<circle cx={ORB_CX} cy={ORB_CY} r={38} fill="none" stroke={mc} strokeWidth="1.5"
                      style={{transformOrigin:`${ORB_CX}px ${ORB_CY}px`,animation:"intro-wave 0.9s ease-out forwards"}}/>}
                    {/* Dots (scatter in phase 2, normal otherwise) */}
                    {HOME_DOTS.map((d,i)=>{
                      const x=ORB_CX+Math.cos(d.angle)*d.radius;
                      const y=ORB_CY+Math.sin(d.angle)*d.radius;
                      if(introPhase===1){
                        const sx=ORB_CX+Math.cos(i/32*Math.PI*2)*38-x;
                        const sy=ORB_CY+Math.sin(i/32*Math.PI*2)*38-y;
                        return<circle key={i} cx={x} cy={y} r={d.size} fill={mc}
                          style={{transform:`translate(${sx}px,${sy}px)`,opacity:0}}/>;
                      }
                      if(introPhase===2){
                        const sx=ORB_CX+Math.cos(i/32*Math.PI*2)*38-x;
                        const sy=ORB_CY+Math.sin(i/32*Math.PI*2)*38-y;
                        return<circle key={i} cx={x} cy={y} r={d.size} fill={mc}
                          style={{"--op":d.op,"--sx":`${sx}px`,"--sy":`${sy}px`,
                            animation:`dot-scatter 1.1s cubic-bezier(0.2,0.8,0.3,1) ${0.05+i*0.03}s forwards`}}/>;
                      }
                      return<circle key={i} cx={x} cy={y} r={d.size} fill={mc}
                        style={{"--op":d.op,opacity:d.op,animation:`dot-fl ${d.dur}s ease-in-out ${d.delay}s infinite`}}/>;
                    })}
                    {/* Connection lines */}
                    {slots.map((card,i)=>{
                      const ang=(angles[i]*Math.PI)/180;
                      const cx2=ORB_CX+Math.cos(ang)*RADIUS;
                      const cy2=ORB_CY+Math.sin(ang)*RADIUS;
                      const x1=ORB_CX+Math.cos(ang)*72;
                      const y1=ORB_CY+Math.sin(ang)*72;
                      const x2=cx2-Math.cos(ang)*(CW/2+4);
                      const y2=cy2-Math.sin(ang)*(CH/2+4);
                      const cc=card.change>=0?"#10b981":"#f43f5e";
                      return<line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={cc} strokeWidth="1" strokeDasharray="5 4" opacity="0.25"/>;
                    })}
                  </svg>

                  {/* Orb */}
                  <div style={{position:"absolute",left:ORB_CX-65,top:ORB_CY-65,width:130,height:130,pointerEvents:"none"}}>
                    {[0,1,2].map(i=>(
                      <div key={i} style={{position:"absolute",inset:0,borderRadius:"50%",
                        border:`1px solid ${mc}50`,
                        animation:`orb-ring 3.2s ease-out ${i*1.07}s infinite`}}/>
                    ))}
                    <div style={{position:"absolute",inset:0,borderRadius:"50%",
                      background:`radial-gradient(circle at 38% 32%, ${mc}50, ${mc}12 55%, transparent 75%)`,
                      border:`1.5px solid ${mc}70`,
                      boxShadow:`0 0 50px ${mc}25,0 0 100px ${mc}10`,
                      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:1}}>
                      <div style={{fontSize:8,color:mc+"99",letterSpacing:2.5,marginBottom:1}}>AI-AKSJER</div>
                      <div style={{fontSize:17,fontWeight:800,color:mc,letterSpacing:1}}>{ml}</div>
                      <div style={{fontSize:10,color:mc+"99",fontWeight:600,marginTop:1}}>
                        {avgCh>=0?"+":""}{avgCh.toFixed(2)}%
                      </div>
                    </div>
                  </div>

                  {/* Satellite cards */}
                  {slots.map((card,i)=>{
                    const ang=(angles[i]*Math.PI)/180;
                    const cx2=ORB_CX+Math.cos(ang)*RADIUS;
                    const cy2=ORB_CY+Math.sin(ang)*RADIUS;
                    const isPos=card.change>=0;
                    const cc=isPos?"#10b981":"#f43f5e";
                    return(
                      <div key={i} className="hjem-card"
                        onClick={()=>{setDeepTicker(card.ticker);setView("deepdive");}}
                        style={{position:"absolute",left:cx2-CW/2,top:cy2-CH/2,width:CW,height:CH,
                          background:`rgba(6,15,28,0.52)`,backdropFilter:"blur(10px)",border:`1px solid ${cc}35`,borderRadius:10,
                          padding:"8px 12px",cursor:"pointer",transition:"transform 0.18s, border-color 0.18s",
                          boxShadow:`0 4px 20px ${cc}10`}}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=cc}
                        onMouseLeave={e=>e.currentTarget.style.borderColor=cc+"35"}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                          <div>
                            <div style={{fontSize:12,fontWeight:800,color:"#e2e8f0",letterSpacing:0.5}}>{card.ticker}</div>
                            <div style={{fontSize:8,color:"#334155",marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:80}}>{card.name}</div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:15,fontWeight:800,color:cc}}>{isPos?"+":""}{card.change.toFixed(2)}%</div>
                            <div style={{fontSize:9,color:"#475569"}}>${card.price.toFixed(2)}</div>
                          </div>
                        </div>
                        {/* Mini bar */}
                        <div style={{marginTop:6,height:3,background:"#0d1e30",borderRadius:2,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${Math.min(100,Math.abs(card.change)*12)}%`,background:cc,borderRadius:2}}/>
                        </div>
                      </div>
                    );
                  })}
                </div></div>

                {/* Stats bar */}
                <div style={{maxWidth:820,margin:"0 auto",display:"flex",gap:10,flexWrap:"wrap"}}>
                  {[
                    {label:"AKSJER OPP",val:`${up}`,color:"#10b981"},
                    {label:"AKSJER NED",val:`${down}`,color:"#f43f5e"},
                    {label:"FØLGER",val:`${allCh.length}`,color:"#3b82f6"},
                    {label:"SNITT ENDRING",val:`${avgCh>=0?"+":""}${avgCh.toFixed(2)}%`,color:mc},
                    {label:"STEMNING",val:ml,color:mc},
                  ].map(s=>(
                    <div key={s.label} style={{flex:1,minWidth:120,background:"#060f1c",border:`1px solid ${s.color}20`,borderTop:`2px solid ${s.color}`,borderRadius:"0 0 8px 8px",padding:"10px 14px"}}>
                      <div style={{fontSize:8,color:"#334155",letterSpacing:1,marginBottom:4}}>{s.label}</div>
                      <div style={{fontSize:16,fontWeight:800,color:s.color}}>{s.val}</div>
                    </div>
                  ))}
                  <div style={{flex:1,minWidth:120,background:"#060f1c",border:"1px solid #3b82f620",borderTop:"2px solid #3b82f6",borderRadius:"0 0 8px 8px",padding:"10px 14px",cursor:"pointer"}}
                    onClick={()=>{setView("watchlist");setWlMode("signaler");}}>
                    <div style={{fontSize:8,color:"#334155",letterSpacing:1,marginBottom:4}}>SIGNALER</div>
                    <div style={{fontSize:13,fontWeight:700,color:"#3b82f6"}}>→ Se kjøpssignaler</div>
                  </div>
                </div>

                {/* ── NESTE DAG SIGNAL PANEL ── */}
                {(()=>{
                  const esF=marketData["ES=F"],nqF=marketData["NQ=F"];
                  const vixD=marketData["^VIX"],tnxD=marketData["^TNX"];
                  const samsD=marketData["005930.KS"],hynxD=marketData["000660.KS"];
                  const tsmD=liveData["TSM"];

                  // Futures score (0-25)
                  const esChg=esF?.change??null,nqChg=nqF?.change??null;
                  const futAvg=esChg!==null&&nqChg!==null?(esChg+nqChg)/2:(esChg??nqChg);
                  const futScore=futAvg===null?null:futAvg>1?25:futAvg>0.3?20:futAvg>0?14:futAvg>-0.3?8:futAvg>-1?3:0;

                  // VIX score (0-20)
                  let vixScore=null;
                  if(vixD){
                    const h=vixD.history||[];
                    const tr=h.length>=5?h[h.length-1]-h[h.length-6]:null;
                    const lvPts=vixD.price<14?10:vixD.price<18?8:vixD.price<22?5:vixD.price<28?2:0;
                    const trPts=tr===null?4:tr<-2?10:tr<0?7:tr<2?2:0;
                    vixScore=Math.min(20,lvPts+trPts);
                  }

                  // Breadth score (0-20)
                  const breadthPct=allCh.length?(up/allCh.length*100):50;
                  const breadthScore=breadthPct>70?20:breadthPct>60?15:breadthPct>50?10:breadthPct>40?5:0;

                  // Asia score (0-20): Samsung + Hynix + TSMC
                  const asiaItems=[samsD,hynxD,tsmD].filter(Boolean);
                  const asiaAvg=asiaItems.length?asiaItems.reduce((a,d)=>a+d.change,0)/asiaItems.length:null;
                  const asiaScore=asiaAvg===null?null:asiaAvg>2?20:asiaAvg>0.5?14:asiaAvg>-0.5?9:asiaAvg>-2?3:0;

                  // Yield score (0-15)
                  let yieldScore=null;
                  if(tnxD){
                    const h=tnxD.history||[];
                    const tr=h.length>=5?h[h.length-1]-h[h.length-6]:null;
                    const lvPts=tnxD.price<3.5?10:tnxD.price<4?8:tnxD.price<4.5?5:tnxD.price<5?2:0;
                    const trPts=tr===null?2:tr<-0.2?5:tr<0?3:tr<0.2?1:0;
                    yieldScore=Math.min(15,lvPts+trPts);
                  }

                  const known=[futScore,vixScore,breadthScore,asiaScore,yieldScore].filter(v=>v!==null);
                  if(known.length<2)return(
                    <div style={{maxWidth:820,margin:"16px auto 0",padding:"14px 18px",background:"#060f1c",border:"1px solid #1e293b",borderRadius:10,color:"#334155",fontSize:11}}>
                      📡 NESTE DAG SIGNAL — laster markedsdata…
                    </div>
                  );

                  const total=known.reduce((a,b)=>a+b,0);
                  const label=total>=72?"BULLISH":total>=58?"POSITIV":total>=40?"NØYTRAL":total>=25?"FORSIKTIG":"BEARISH";
                  const sc=total>=72?"#10b981":total>=58?"#34d399":total>=40?"#f59e0b":total>=25?"#f97316":"#f43f5e";

                  const sigs=[
                    {label:"S&P / NQ Futures",
                     val:futAvg!==null?`${futAvg>=0?"▲":"▼"} ${Math.abs(futAvg).toFixed(2)}%`:"…",
                     sub:nqChg!==null?`ES: ${esChg>=0?"+":""}${esChg?.toFixed(2)}%  ·  NQ: ${nqChg>=0?"+":""}${nqChg.toFixed(2)}%`:"",
                     score:futScore??0,max:25,col:futAvg===null?"#475569":futAvg>=0?"#10b981":"#f43f5e"},
                    {label:"VIX Fryktindeks",
                     val:vixD?`${vixD.price.toFixed(1)}`:"…",
                     sub:vixD&&vixD.history.length>=5?`5-dagers trend: ${(vixD.history[vixD.history.length-1]-vixD.history[vixD.history.length-6]).toFixed(2)}  ·  Dag: ${vixD.change>=0?"+":""}${vixD.change.toFixed(2)}%`:"",
                     score:vixScore??0,max:20,col:vixD?(vixD.price<18?"#10b981":vixD.price<25?"#f59e0b":"#f43f5e"):"#475569"},
                    {label:"Bredde – AI-aksjer",
                     val:`${Math.round(breadthPct)}% opp`,
                     sub:`${up} av ${allCh.length} aksjer stiger i dag`,
                     score:breadthScore,max:20,col:breadthPct>55?"#10b981":breadthPct>40?"#f59e0b":"#f43f5e"},
                    {label:"Asia (Samsung / Hynix / TSMC)",
                     val:asiaAvg!==null?`${asiaAvg>=0?"▲":"▼"} ${Math.abs(asiaAvg).toFixed(2)}%`:"…",
                     sub:[samsD&&`Samsung ${samsD.change>=0?"+":""}${samsD.change.toFixed(1)}%`,hynxD&&`SK Hynix ${hynxD.change>=0?"+":""}${hynxD.change.toFixed(1)}%`,tsmD&&`TSMC ${tsmD.change>=0?"+":""}${tsmD.change.toFixed(1)}%`].filter(Boolean).join("  ·  ")||"Henter asiatiske markeder…",
                     score:asiaScore??0,max:20,col:asiaAvg===null?"#475569":asiaAvg>=0?"#10b981":"#f43f5e"},
                    {label:"10Y Rente (TNX)",
                     val:tnxD?`${tnxD.price.toFixed(2)}%`:"…",
                     sub:tnxD?`Dag: ${tnxD.change>=0?"+":""}${tnxD.change.toFixed(2)}%  ·  Lavere rente = positivt for tech`:"",
                     score:yieldScore??0,max:15,col:tnxD?(tnxD.price<4?"#10b981":tnxD.price<4.5?"#f59e0b":"#f43f5e"):"#475569"},
                  ];

                  return(
                    <div style={{maxWidth:820,margin:"16px auto 0",background:"#060f1c",border:`1px solid ${sc}30`,borderRadius:10,overflow:"hidden"}}>
                      {/* Header */}
                      <div style={{padding:"14px 18px",borderBottom:"1px solid #0d1e30",display:"flex",alignItems:"center",justifyContent:"space-between",background:"#040d18"}}>
                        <div>
                          <div style={{fontSize:10,color:"#334155",letterSpacing:2,marginBottom:3}}>📡 NESTE DAG SIGNAL</div>
                          <div style={{fontSize:11,color:"#475569"}}>Aggregert fra futures, VIX-trend, bredde og Asia-markeder</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:32,fontWeight:800,color:sc,lineHeight:1}}>{total}</div>
                          <div style={{fontSize:9,color:"#334155",letterSpacing:1}}>AV 100</div>
                        </div>
                      </div>
                      {/* Score bar */}
                      <div style={{padding:"12px 18px 10px",borderBottom:"1px solid #0a1628"}}>
                        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:7}}>
                          <div style={{flex:1,height:10,background:"#0a1628",borderRadius:5,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${total}%`,background:`linear-gradient(90deg,${sc}70,${sc})`,borderRadius:5}}/>
                          </div>
                          <div style={{fontSize:14,fontWeight:800,color:sc,minWidth:90,textAlign:"right",letterSpacing:1}}>{label}</div>
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between"}}>
                          {[["0","BEARISH"],["25","FORSIKTIG"],["50","NØYTRAL"],["75","POSITIV"],["100","BULLISH"]].map(([x,l])=>(
                            <div key={l} style={{fontSize:8,color:"#1e3a5f",letterSpacing:0.5}}>{l}</div>
                          ))}
                        </div>
                      </div>
                      {/* Signals */}
                      <div style={{padding:"6px 18px 10px"}}>
                        {sigs.map((sig,i)=>(
                          <div key={i} style={{display:"flex",alignItems:"center",gap:isMobile?6:12,padding:"8px 0",borderBottom:i<sigs.length-1?"1px solid #080f1c":"none",flexWrap:isMobile?"wrap":"nowrap"}}>
                            <div style={{flex:"0 0 auto",width:isMobile?"100%":210}}>
                              <div style={{fontSize:11,color:"#94a3b8",fontWeight:600}}>{sig.label}</div>
                              {sig.sub&&<div style={{fontSize:9,color:"#334155",marginTop:1}}>{sig.sub}</div>}
                            </div>
                            <div style={{fontSize:13,fontWeight:700,color:sig.col,minWidth:70,flexShrink:0}}>{sig.val}</div>
                            {!isMobile&&<div style={{flex:1,height:5,background:"#0a1628",borderRadius:3,overflow:"hidden"}}>
                              <div style={{height:"100%",width:`${(sig.score/sig.max)*100}%`,background:sig.col,borderRadius:3}}/>
                            </div>}
                            <div style={{fontSize:11,color:"#334155",flexShrink:0,minWidth:36,textAlign:"right"}}>{sig.score}/{sig.max}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{padding:"5px 18px 9px",fontSize:9,color:"#1e3a5f",borderTop:"1px solid #080f1c"}}>
                        Signaler, ikke spådom — ingen modell treffer konsekvent over ~60% på daglig retning. Bruk som ett av flere verktøy.
                      </div>
                    </div>
                  );
                })()}

                {/* ── KRASJ-RADAR BADGE (full panel under I dag › Makro) ── */}
                {(()=>{
                  const hygD=marketData["HYG"],lqdD=marketData["LQD"];
                  const vixD=marketData["^VIX"],vix3mD=marketData["^VIX3M"];
                  const tnxD=marketData["^TNX"],irxD=marketData["^IRX"];
                  const jpyD=marketData["JPY=X"];
                  if(!hygD&&!vixD) return null;
                  let reds=0,ambers=0;
                  if(vixD&&vix3mD&&vixD.price/vix3mD.price>1.0) reds++;
                  else if(vixD&&vix3mD&&vixD.price/vix3mD.price>0.97) ambers++;
                  if(hygD&&lqdD){const s=(lqdD.change||0)-(hygD.change||0);if(s>2)reds++;else if(s>0.5)ambers++;}
                  if(tnxD&&irxD){const sp=tnxD.price-irxD.price;if(sp<-0.5)reds++;else if(sp<0.5)ambers++;}
                  if(jpyD?.history?.length>=11){const r=(jpyD.history[jpyD.history.length-1]/jpyD.history[jpyD.history.length-11]-1)*100;if(r<-3)reds++;else if(r<-1.5)ambers++;}
                  const label=reds>=3?"KRITISK":reds>=2?"HØY":reds>=1||ambers>=3?"MODERAT":"LAV";
                  const color=reds>=3?"#f43f5e":reds>=2?"#f97316":reds>=1||ambers>=3?"#f59e0b":"#10b981";
                  return(
                    <div onClick={()=>setIdagMode("makro")} style={{cursor:"pointer",marginTop:12,padding:"10px 14px",background:"#060f1c",border:`1px solid ${color}40`,borderRadius:8,display:"flex",alignItems:"center",gap:10,maxWidth:820,margin:"12px auto 0"}}>
                      <div style={{width:9,height:9,borderRadius:"50%",background:color,boxShadow:`0 0 7px ${color}`,flexShrink:0}}/>
                      <div>
                        <div style={{fontSize:9,color:"#334155",letterSpacing:2}}>⚠ KRASJ-RADAR</div>
                        <div style={{fontSize:11,color:"#475569"}}>Systemiske risikosignaler</div>
                      </div>
                      <div style={{marginLeft:"auto",textAlign:"right"}}>
                        <div style={{fontSize:16,fontWeight:800,color,letterSpacing:1}}>{label} RISIKO</div>
                        <div style={{fontSize:9,color:"#334155"}}>{reds} rød · {ambers} gul — åpne Marked for detaljer →</div>
                      </div>
                    </div>
                  );
                })()}

              </div>
            );
          })()}

          {/* ── MARKED ── */}
          {view==="idag"&&idagMode==="makro"&&(()=>{
            if (Object.keys(marketData).length === 0 && !marketLoading) fetchMarketData();

            const vix = marketData["^VIX"];
            const vvix = marketData["^VVIX"];
            const sp = marketData["^GSPC"];
            const tny = marketData["^TNX"];
            const hyg = marketData["HYG"];

            const spRSI = sp ? calcRSI(sp.history) : null;
            const spMom = sp?.history?.length > 20 ? ((sp.history[sp.history.length-1]/sp.history[Math.max(0,sp.history.length-21)]-1)*100) : null;
            const hygMom = hyg?.history?.length > 20 ? ((hyg.history[hyg.history.length-1]/hyg.history[Math.max(0,hyg.history.length-21)]-1)*100) : null;
            const breadthPct = allStockList.length > 0 ? (allStockList.filter(s => liveData[s.ticker]?.change > 0).length / allStockList.length) * 100 : null;

            // Score 0-100
            let score = 0, maxScore = 0;
            const components = [];

            if (vix?.price != null) {
              const v = vix.price;
              const pts = v < 12 ? 20 : v < 16 ? 17 : v < 20 ? 13 : v < 25 ? 8 : v < 30 ? 4 : v < 40 ? 1 : 0;
              score += pts; maxScore += 20;
              components.push({ label:"VIX", value:`${v.toFixed(1)}`, pts, max:20,
                status: v<16?"Rolig":v<20?"Normalt":v<25?"Forhøyet":v<30?"Stress":v<40?"Frykt":"Ekstrem frykt",
                color: v<16?"#10b981":v<20?"#10b981":v<25?"#f59e0b":v<30?"#f59e0b":"#f43f5e",
                desc: v<16?"Lav frykt, markedet er selvsikkert":v<20?"Normal volatilitet":v<25?"Noe usikkerhet i markedet":v<30?"Tydelig frykt — vær forsiktig":"Ekstremt stressede markeder",
                history: vix.history });
            }
            if (vvix?.price != null) {
              const v = vvix.price;
              const pts = v < 80 ? 15 : v < 95 ? 10 : v < 110 ? 5 : v < 130 ? 2 : 0;
              score += pts; maxScore += 15;
              components.push({ label:"VVIX", value:`${v.toFixed(0)}`, pts, max:15,
                status: v<80?"Stabilt":v<95?"Normalt":v<110?"Urolig":v<130?"Turbulent":"Ekstrem",
                color: v<80?"#10b981":v<95?"#f59e0b":v<110?"#f59e0b":"#f43f5e",
                desc: "Volatilitet-på-volatilitet — hvor mye VIX selv beveger seg. Over 100 = ustabilt regime.",
                history: vvix.history });
            }
            if (spMom != null) {
              const pts = spMom > 5 ? 20 : spMom > 2 ? 16 : spMom > 0.5 ? 11 : spMom > 0 ? 7 : spMom > -5 ? 3 : spMom > -10 ? 1 : 0;
              score += pts; maxScore += 20;
              components.push({ label:"S&P 500 (20d)", value:`${spMom>=0?"+":""}${spMom.toFixed(1)}%`, pts, max:20,
                status: spMom>5?"Sterk bullish":spMom>2?"Bullish":spMom>0?"Svakt positiv":spMom>-5?"Svakt negativ":spMom>-10?"Bearish":"Korreksjon",
                color: spMom>2?"#10b981":spMom>0?"#f59e0b":spMom>-5?"#f59e0b":"#f43f5e",
                desc:`S&P 500 20-dagers momentum. RSI: ${spRSI ?? "—"}`,
                history: sp.history });
            }
            if (tny?.price != null) {
              const v = tny.price;
              const pts = v < 3 ? 20 : v < 3.5 ? 16 : v < 4 ? 12 : v < 4.5 ? 8 : v < 5 ? 4 : v < 5.5 ? 1 : 0;
              score += pts; maxScore += 20;
              components.push({ label:"10Y Rente", value:`${v.toFixed(2)}%`, pts, max:20,
                status: v<3?"Svært lav":v<4?"Sunn":v<4.5?"Moderat":v<5?"Høy":"Svært høy",
                color: v<4?"#10b981":v<4.5?"#10b981":v<5?"#f59e0b":"#f43f5e",
                desc: v>5?"Høye renter legger press på aksjeverdier og øker lånekostnader":v<3?"Ekstremt lave renter kan indikere resesjonsbekymring":"10-års statsobligasjon. 3.5-4.5% er sunt for aksjemarkedet.",
                history: tny.history });
            }
            if (hygMom != null) {
              const pts = hygMom > 2 ? 15 : hygMom > 0.5 ? 11 : hygMom > 0 ? 7 : hygMom > -2 ? 3 : hygMom > -5 ? 1 : 0;
              score += pts; maxScore += 15;
              components.push({ label:"HYG Kreditt (20d)", value:`${hygMom>=0?"+":""}${hygMom.toFixed(1)}%`, pts, max:15,
                status: hygMom>1?"Sunn":hygMom>0?"OK":hygMom>-2?"Svakt":"Stress",
                color: hygMom>0?"#10b981":hygMom>-2?"#f59e0b":"#f43f5e",
                desc:"High yield obligasjoner. Når HYG faller = kredittstress = aksjer følger ofte etter.",
                history: hyg.history });
            }
            const jpy = marketData["JPY=X"];
            if (jpy?.history?.length >= 11) {
              const jpyChg = (jpy.history[jpy.history.length-1]/jpy.history[jpy.history.length-11]-1)*100;
              const pts = jpyChg > 0 ? 10 : jpyChg > -1.5 ? 8 : jpyChg > -3 ? 4 : jpyChg > -5 ? 1 : 0;
              score += pts; maxScore += 10;
              components.push({ label:"Yen Carry (10d)", value:`¥${jpy.price?.toFixed(1)} (${jpyChg>=0?"+":""}${jpyChg.toFixed(1)}%)`, pts, max:10,
                status: jpyChg>0?"Stabil":jpyChg>-1.5?"OK":jpyChg>-3?"Press":"Avvikling",
                color: jpyChg>0?"#10b981":jpyChg>-1.5?"#10b981":jpyChg>-3?"#f59e0b":"#f43f5e",
                desc: jpyChg<-3?"⚠ Yen styrkes raskt — carry trade avvikling kan presse US tech ned":jpyChg<0?"Yen noe sterkere — carry trade under lett press":"USD/JPY stabil — Yen Carry Trade intakt.",
                history: jpy.history });
            }
            if (breadthPct != null) {
              const pts = breadthPct > 65 ? 10 : breadthPct > 50 ? 8 : breadthPct > 35 ? 5 : breadthPct > 20 ? 2 : 0;
              score += pts; maxScore += 10;
              components.push({ label:"Market Breadth", value:`${breadthPct.toFixed(0)}%`, pts, max:10,
                status: breadthPct>65?"Bred oppgang":breadthPct>50?"Flertall stiger":breadthPct>35?"Blandet":"Bred nedgang",
                color: breadthPct>50?"#10b981":breadthPct>35?"#f59e0b":"#f43f5e",
                desc:`${breadthPct.toFixed(0)}% av AI-aksjene er positive i dag. Bred oppgang = sterkere signal.`,
                history: [] });
            }

            const finalScore = maxScore > 0 ? Math.round((score/maxScore)*100) : 50;
            const gaugeColor = finalScore >= 65 ? "#10b981" : finalScore >= 40 ? "#f59e0b" : "#f43f5e";
            const gaugeLabel = finalScore >= 65 ? "BULLISH" : finalScore >= 50 ? "NØYTRAL" : finalScore >= 35 ? "FORSIKTIG" : "BEARISH";
            const gaugeDesc = finalScore >= 65 ? "Markedet er i god helse. Trend og indikatorer peker oppover."
              : finalScore >= 50 ? "Blandede signaler. Vær selektiv og følg med på endringer."
              : finalScore >= 35 ? "Svakhetstegn i markedet. Reduser risiko og vær defensiv."
              : "Høy korreksjon-risiko. Flere indikatorer er røde. Vær svært forsiktig.";

            // Gauge SVG
            const toRad = deg => (deg * Math.PI) / 180;
            const cx=150, cy=115, r=90;
            const gaugeAngle = (finalScore/100) * 180;
            const needleLen = r * 0.78;
            const needleX = cx + needleLen * Math.cos(toRad(180 - gaugeAngle));
            const needleY = cy - needleLen * Math.sin(toRad(gaugeAngle));

            const arcPath = (startDeg, endDeg, color) => {
              const s = toRad(startDeg), e = toRad(endDeg);
              const x1=cx+r*Math.cos(Math.PI-s), y1=cy-r*Math.sin(s);
              const x2=cx+r*Math.cos(Math.PI-e), y2=cy-r*Math.sin(e);
              return <path d={`M${x1},${y1} A${r},${r} 0 0,1 ${x2},${y2}`} fill="none" stroke={color} strokeWidth="16" strokeLinecap="round"/>;
            };

            return (
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:"#f8fafc",letterSpacing:1}}>🌡️ MARKEDSBAROMETER</div>
                    <div style={{fontSize:9,color:"#475569",letterSpacing:1,marginTop:2}}>KOMBINERT MARKEDSHELSE — LIVE DATA</div>
                  </div>
                  <button onClick={fetchMarketData} style={{padding:"5px 12px",border:"1px solid #1e293b",background:"#0d1f35",color:"#f59e0b",borderRadius:4,cursor:"pointer",fontSize:10,fontFamily:"inherit"}}>
                    {marketLoading?"⟳ Henter...":"↻ Oppdater"}
                  </button>
                </div>

                {marketLoading ? (
                  <div style={{textAlign:"center",color:"#334155",padding:"60px 0",fontSize:13}}>⟳ Henter markedsdata...</div>
                ) : (
                  <div>
                    {/* SPX/SPY COMMAND CENTER */}
                    {(()=>{
                      const spy = marketData["SPY"];
                      const qqq = marketData["QQQ"];
                      const vixPrice = marketData["^VIX"]?.price;
                      if (!spy && !qqq) return null;

                      const dailyMove = vixPrice ? (vixPrice / 16).toFixed(2) : null;
                      const weeklyMove = vixPrice ? (vixPrice / Math.sqrt(52)).toFixed(2) : null;

                      const getNext0DTE = () => {
                        const dates = [];
                        const d = new Date();
                        while (dates.length < 4) {
                          d.setDate(d.getDate() + 1);
                          const day = d.getDay();
                          if (day === 1 || day === 3 || day === 5)
                            dates.push({ date: new Date(d), label: ["","Man","","Ons","","Fre",""][day] });
                        }
                        return dates;
                      };
                      const next0DTE = getNext0DTE();
                      const spyRSI = spy ? calcRSI(spy.history) : null;
                      const spyBB = spy ? calcBollinger(spy.history) : null;
                      const spyMA50 = spy ? calcMA(spy.history, 50) : null;

                      return (
                        <div style={{background:"linear-gradient(135deg,#07111f,#0a1628)",border:"1px solid #1e3a5f",borderRadius:10,padding:"18px 20px",marginBottom:16}}>
                          <div style={{fontSize:10,fontWeight:700,color:"#3b82f6",letterSpacing:2,marginBottom:14}}>📈 SPX / SPY COMMAND CENTER</div>
                          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:10,marginBottom:14}}>
                            {spy&&<div style={{background:"#060f1c",borderRadius:8,padding:"12px 14px",borderTop:"2px solid #3b82f6"}}>
                              <div style={{fontSize:11,color:"#475569",letterSpacing:1,marginBottom:4}}>SPY</div>
                              <div style={{fontSize:20,fontWeight:700,color:"#e2e8f0"}}>${spy.price?.toFixed(2)}</div>
                              <div style={{fontSize:12,color:spy.change>=0?"#10b981":"#f43f5e",fontWeight:600}}>{spy.change>=0?"+":""}{spy.change?.toFixed(2)}% i dag</div>
                              {spyMA50&&<div style={{fontSize:11,color:"#475569",marginTop:4}}>MA50: ${spyMA50.toFixed(1)} {spy.price>spyMA50?"↑ over":"↓ under"}</div>}
                            </div>}
                            {qqq&&<div style={{background:"#060f1c",borderRadius:8,padding:"12px 14px",borderTop:"2px solid #a78bfa"}}>
                              <div style={{fontSize:11,color:"#475569",letterSpacing:1,marginBottom:4}}>QQQ</div>
                              <div style={{fontSize:20,fontWeight:700,color:"#e2e8f0"}}>${qqq.price?.toFixed(2)}</div>
                              <div style={{fontSize:12,color:qqq.change>=0?"#10b981":"#f43f5e",fontWeight:600}}>{qqq.change>=0?"+":""}{qqq.change?.toFixed(2)}% i dag</div>
                            </div>}
                            {vixPrice&&dailyMove&&<div style={{background:"#060f1c",borderRadius:8,padding:"12px 14px",borderTop:"2px solid #f43f5e"}}>
                              <div style={{fontSize:11,color:"#475569",letterSpacing:1,marginBottom:4}}>IMPLISERT BEVEGELSE</div>
                              <div style={{fontSize:16,fontWeight:700,color:"#f43f5e"}}>±{dailyMove}% / dag</div>
                              <div style={{fontSize:12,color:"#f59e0b"}}>±{weeklyMove}% / uke</div>
                              <div style={{fontSize:11,color:"#475569",marginTop:4}}>VIX {vixPrice?.toFixed(1)} ÷ 16</div>
                            </div>}
                            <div style={{background:"#060f1c",borderRadius:8,padding:"12px 14px",borderTop:"2px solid #10b981"}}>
                              <div style={{fontSize:11,color:"#475569",letterSpacing:1,marginBottom:6}}>SPX 0DTE UTLØP</div>
                              {next0DTE.map((d,i)=>{
                                const isToday = d.date.toDateString()===new Date().toDateString();
                                return <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11,color:isToday?"#f59e0b":"#64748b",marginBottom:3}}>
                                  <span>{d.label}</span>
                                  <span>{d.date.toLocaleDateString("nb-NO",{day:"numeric",month:"short"})}</span>
                                </div>;
                              })}
                            </div>
                          </div>
                          {spy&&<div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                            {spyRSI!==null&&<span style={{fontSize:11,padding:"4px 10px",borderRadius:4,background:spyRSI<35?"#06b6d420":spyRSI>70?"#f43f5e20":"#1e293b",color:spyRSI<35?"#06b6d4":spyRSI>70?"#f43f5e":"#64748b"}}>RSI {spyRSI} — {spyRSI<35?"Oversold":spyRSI>70?"Overbought":"Nøytral"}</span>}
                            {spyBB?.squeeze&&<span style={{fontSize:11,padding:"4px 10px",borderRadius:4,background:"#a78bfa20",color:"#a78bfa"}}>⚡ SPY Bollinger Squeeze</span>}
                            {spyMA50&&spy.price&&<span style={{fontSize:11,padding:"4px 10px",borderRadius:4,background:spy.price>spyMA50?"#10b98120":"#f43f5e20",color:spy.price>spyMA50?"#10b981":"#f43f5e"}}>{spy.price>spyMA50?"↑ SPY over MA50 — bullish":"↓ SPY under MA50 — bearish"}</span>}
                          </div>}
                        </div>
                      );
                    })()}
                    {/* Gauge + score */}
                    <div style={{display:"flex",gap:16,marginBottom:16,flexWrap:"wrap",alignItems:"flex-start"}}>
                      <div style={{background:"#060f1c",border:`1px solid ${gaugeColor}30`,borderRadius:10,padding:"16px 20px",display:"flex",flexDirection:"column",alignItems:"center",minWidth:300}}>
                        <svg width="300" height="185" viewBox="0 0 300 185">
                          {/* Background arc (grey) */}
                          <path d={`M${cx-r},${cy} A${r},${r} 0 0,1 ${cx+r},${cy}`} fill="none" stroke="#1e293b" strokeWidth="16" strokeLinecap="round"/>
                          {/* Coloured arc segments */}
                          {arcPath(0,60,"#f43f5e")}
                          {arcPath(60,120,"#f59e0b")}
                          {arcPath(120,180,"#10b981")}
                          {/* Zone labels — below arc endpoints */}
                          <text x={cx-r-2} y={cy+18} textAnchor="middle" fill="#f43f5e" fontSize="9" opacity="0.7">BEARISH</text>
                          <text x={cx+r+2} y={cy+18} textAnchor="middle" fill="#10b981" fontSize="9" opacity="0.7">BULLISH</text>
                          {/* Needle */}
                          <path d={`M${cx},${cy} L${needleX},${needleY}`} stroke={gaugeColor} strokeWidth="3" strokeLinecap="round"/>
                          <circle cx={cx} cy={cy} r="7" fill={gaugeColor} stroke="#030b14" strokeWidth="2"/>
                          {/* Score inside the arc opening */}
                          <text x={cx} y={cy+38} textAnchor="middle" fill={gaugeColor} fontSize="38" fontWeight="700">{finalScore}</text>
                          <text x={cx} y={cy+58} textAnchor="middle" fill={gaugeColor} fontSize="12" fontWeight="700" letterSpacing="2">{gaugeLabel}</text>
                        </svg>
                        <div style={{fontSize:10,color:"#64748b",textAlign:"center",lineHeight:1.6,maxWidth:260,marginTop:-8}}>{gaugeDesc}</div>
                      </div>

                      {/* Signal sammendrag */}
                      <div style={{flex:1,display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:8,minWidth:280}}>
                        {components.map((c,i)=>{
                          const barPct = c.max > 0 ? (c.pts/c.max)*100 : 0;
                          return (
                            <div key={i} style={{background:"#060f1c",border:`1px solid ${c.color}25`,borderLeft:`3px solid ${c.color}`,borderRadius:"0 6px 6px 0",padding:"10px 12px"}}>
                              <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                                <div style={{fontSize:11,color:"#475569",letterSpacing:1}}>{c.label}</div>
                                <div style={{fontSize:13,fontWeight:700,color:c.color}}>{c.value}</div>
                              </div>
                              <div style={{height:4,background:"#0d1e30",borderRadius:2,marginBottom:4}}>
                                <div style={{height:"100%",width:`${barPct}%`,background:c.color,borderRadius:2,transition:"width 0.5s"}}/>
                              </div>
                              <div style={{fontSize:11,color:c.color,fontWeight:600,marginBottom:2}}>{c.status}</div>
                              <div style={{fontSize:11,color:"#475569",lineHeight:1.4}}>{c.desc}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Historikk-graf */}
                    {marketHistory.length > 0 && (()=>{
                      const W=660, H=90, padL=36, padB=20, padR=8, padT=8;
                      const cW=W-padL-padR, cH=H-padT-padB;
                      const scores = marketHistory.map(h=>h.score);
                      const minS=Math.min(...scores,0), maxS=Math.max(...scores,100), rS=maxS-minS||1;
                      const toX = i => padL + (i/(Math.max(marketHistory.length-1,1)))*cW;
                      const toY = s => padT + cH - ((s-minS)/rS)*cH;
                      const pts = marketHistory.map((h,i)=>`${toX(i)},${toY(h.score)}`).join(" ");
                      const labelEvery = Math.ceil(marketHistory.length/5);
                      return (
                        <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"12px 14px",marginBottom:12}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                        <div style={{fontSize:9,color:"#334155",letterSpacing:1}}>MARKEDSHELSE HISTORIKK — SISTE {marketHistory.length} DAGER</div>
                        <button onClick={()=>{setMarketHistory([]);localStorage.removeItem("ai-aksjer-market-history");}} style={{fontSize:8,color:"#334155",background:"transparent",border:"none",cursor:"pointer",padding:"0 2px",fontFamily:"inherit"}}>nullstill</button>
                      </div>
                          <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{overflow:"visible"}}>
                            {[35,50,65].map(v=>(
                              <g key={v}>
                                <line x1={padL} x2={W-padR} y1={toY(v)} y2={toY(v)} stroke="#1e293b" strokeWidth="0.5" strokeDasharray={v===50?"4,3":"2,4"}/>
                                <text x={padL-4} y={toY(v)} fill="#334155" fontSize="8" textAnchor="end" dominantBaseline="middle">{v}</text>
                              </g>
                            ))}
                            <rect x={padL} y={toY(65)} width={cW} height={toY(100)-toY(65)} fill="#10b98108"/>
                            <rect x={padL} y={toY(35)} width={cW} height={toY(65)-toY(35)} fill="#f59e0b08"/>
                            <rect x={padL} y={padT} width={cW} height={toY(35)-padT} fill="#f43f5e08"/>
                            <polyline points={pts} fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            {marketHistory.map((h,i)=>{
                              const c = h.score>=65?"#10b981":h.score>=35?"#f59e0b":"#f43f5e";
                              return <circle key={i} cx={toX(i)} cy={toY(h.score)} r="3" fill={c}/>;
                            })}
                            {marketHistory.filter((_,i)=>i%labelEvery===0||i===marketHistory.length-1).map((h,_,arr)=>{
                              const i = marketHistory.indexOf(h);
                              return <text key={i} x={toX(i)} y={H-4} fill="#475569" fontSize="8" textAnchor="middle">{h.date.slice(5)}</text>;
                            })}
                          </svg>
                          <div style={{display:"flex",gap:12,marginTop:4,fontSize:8}}>
                            {[{c:"#10b981",l:"Bullish (65+)"},{c:"#f59e0b",l:"Nøytral (35-65)"},{c:"#f43f5e",l:"Bearish (<35)"}].map(({c,l})=>(
                              <span key={l} style={{display:"flex",alignItems:"center",gap:4,color:"#475569"}}>
                                <span style={{width:8,height:8,borderRadius:"50%",background:c,display:"inline-block"}}/>
                                {l}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Mini sparklines for market data */}
                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(5,1fr)",gap:8,marginBottom:10}}>
                      {[
                        {label:"VIX",d:vix,color:"#f43f5e",invert:true},
                        {label:"VVIX",d:vvix,color:"#f59e0b",invert:true},
                        {label:"S&P 500",d:sp,color:"#10b981",invert:false},
                        {label:"10Y Rente",d:tny,color:"#3b82f6",invert:false},
                        {label:"HYG",d:hyg,color:"#a78bfa",invert:false},
                      ].map(({label,d,color,invert})=>{
                        if (!d?.history?.length) return <div key={label} style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:6,padding:"8px 10px",display:"flex",alignItems:"center",justifyContent:"center",color:"#334155",fontSize:9}}>Henter {label}…</div>;
                        const chg = d.change;
                        const dotC = chg >= 0 ? "#10b981" : "#f43f5e";
                        return (
                          <div key={label} style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:6,padding:"8px 10px"}}>
                            <div style={{fontSize:8,color:"#334155",letterSpacing:1,marginBottom:3}}>{label}</div>
                            <div style={{fontSize:13,fontWeight:700,color:"#e2e8f0",marginBottom:2}}>{d.price?.toFixed(label==="10Y Rente"?2:1)}{label==="10Y Rente"?"%":""}</div>
                            <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:4}}>
                              <span style={{width:5,height:5,borderRadius:"50%",background:dotC,display:"inline-block"}}/>
                              <span style={{fontSize:9,color:dotC}}>{chg>=0?"+":""}{chg.toFixed(2)}% i dag</span>
                            </div>
                            <Sparkline prices={d.history.slice(-30)} color={color} w={100} h={24}/>
                          </div>
                        );
                      })}
                    </div>

                    {/* SPY Technical Chart med signaler */}
                    {(()=>{
                      const spyTech = techData["SPY"];
                      if (!spyTech) return (
                        <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px",marginBottom:12,textAlign:"center",fontSize:10,color:"#334155"}}>
                          ⟳ Henter SPY daglig data for teknisk analyse…
                        </div>
                      );
                      const allCloses = spyTech.closes || [];
                      const allTimestamps = spyTech.timestamps || [];
                      if (allCloses.length < 55) return null;
                      const periodDays = spyChartPeriod==="1U"?5:spyChartPeriod==="1M"?21:spyChartPeriod==="3M"?63:spyChartPeriod==="1Å"?252:126;
                      const prices = allCloses.slice(-periodDays);
                      const tss = allTimestamps.slice(-periodDays);
                      const n = prices.length;
                      // Rolling MA
                      const rollMA = (arr,k) => arr.map((_,i)=>i>=k-1?arr.slice(i-k+1,i+1).reduce((a,b)=>a+b,0)/k:null);
                      const ma20 = rollMA(prices,20);
                      const ma50 = rollMA(prices,50);
                      // Signals
                      const signals=[];
                      let lastBuy=-8, lastSell=-8;
                      for(let i=50;i<n;i++){
                        const rsi = calcRSI(prices.slice(Math.max(0,i-28),i+1));
                        const maBelow = ma50[i]&&prices[i]<ma50[i];
                        const maAbove = ma50[i]&&prices[i]>ma50[i];
                        const prevMaBelow = ma50[i-1]&&prices[i-1]<ma50[i-1];
                        const prevMaAbove = ma50[i-1]&&prices[i-1]>ma50[i-1];
                        if((rsi!==null&&rsi<33&&i-lastBuy>5)||(maAbove&&prevMaBelow)){
                          signals.push({i,type:"buy",rsi});lastBuy=i;
                        } else if((rsi!==null&&rsi>68&&i-lastSell>5)||(maBelow&&prevMaAbove)){
                          signals.push({i,type:"caution",rsi});lastSell=i;
                        }
                      }
                      const W=680,H=200,pad={t:12,r:16,b:32,l:62};
                      const cW=W-pad.l-pad.r,cH=H-pad.t-pad.b;
                      const minP=Math.min(...prices.filter(Boolean)),maxP=Math.max(...prices),rP=maxP-minP||1;
                      const toX=i=>(i/Math.max(n-1,1))*cW;
                      const toY=p=>cH-((p-minP)/rP)*cH;
                      const pts=prices.map((p,i)=>`${toX(i)},${toY(p)}`).join(" ");
                      const ma20pts=ma20.map((v,i)=>v?`${toX(i)},${toY(v)}`:null);
                      const ma50pts=ma50.map((v,i)=>v?`${toX(i)},${toY(v)}`:null);
                      const ma20path=ma20pts.reduce((acc,pt,i)=>pt?(acc?(acc+" L"+pt):("M"+pt)):acc,"");
                      const ma50path=ma50pts.reduce((acc,pt,i)=>pt?(acc?(acc+" L"+pt):("M"+pt)):acc,"");
                      const lineColor=prices[n-1]>prices[0]?"#10b981":"#f43f5e";
                      const labelIdxs=Array.from({length:5},(_,i)=>Math.round(i*(n-1)/4));
                      const getDate=i=>{
                        const ts=tss[i];
                        if(ts){return new Date(ts*1000).toLocaleDateString("nb-NO",{day:"numeric",month:"short"});}
                        const d=new Date();d.setDate(d.getDate()-(n-1-i));return d.toLocaleDateString("nb-NO",{day:"numeric",month:"short"});
                      };
                      const yLabels=[minP,minP+(maxP-minP)/2,maxP];
                      const hv=spyHover;
                      const hvTooltipX=hv?(hv.x+pad.l+72>W?hv.x-70:hv.x+6):0;
                      const handleMM=e=>{
                        const rect=e.currentTarget.getBoundingClientRect();
                        const sx=((e.clientX-rect.left)/rect.width)*W-pad.l;
                        if(sx<0||sx>cW){setSpyHover(null);return;}
                        const ci=Math.max(0,Math.min(n-1,Math.round((sx/cW)*(n-1))));
                        setSpyHover({x:toX(ci),y:toY(prices[ci]),price:prices[ci],ma20:ma20[ci],ma50:ma50[ci],label:getDate(ci)});
                      };
                      return (
                        <div style={{background:"#060f1c",border:"1px solid #1e3a5f",borderRadius:8,padding:"14px 14px 6px",marginBottom:12}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:6}}>
                            <div>
                              <div style={{fontSize:9,color:"#3b82f6",letterSpacing:1,fontWeight:700}}>📉 SPY TEKNISK ANALYSE — DAGLIGE STENGER</div>
                              <div style={{display:"flex",gap:12,marginTop:4,fontSize:8,color:"#475569"}}>
                                <span style={{display:"flex",alignItems:"center",gap:3}}><span style={{width:16,height:2,background:"#f59e0b",display:"inline-block",borderRadius:1}}/> MA20</span>
                                <span style={{display:"flex",alignItems:"center",gap:3}}><span style={{width:16,height:2,background:"#3b82f6",display:"inline-block",borderRadius:1}}/> MA50</span>
                                <span style={{color:"#10b981"}}>▲ Kjøpssignal</span>
                                <span style={{color:"#f43f5e"}}>▼ Forsiktig</span>
                              </div>
                            </div>
                            <div style={{display:"flex",gap:4,alignItems:"center"}}>
                              {["1U","1M","3M","6M","1Å"].map(p=>(
                                <button key={p} onClick={()=>setSpyChartPeriod(p)}
                                  style={{padding:"2px 8px",border:"1px solid",borderColor:spyChartPeriod===p?"#3b82f6":"#1e293b",background:spyChartPeriod===p?"#3b82f620":"transparent",color:spyChartPeriod===p?"#3b82f6":"#475569",borderRadius:3,cursor:"pointer",fontSize:9,fontFamily:"inherit"}}>
                                  {p}
                                </button>
                              ))}
                              <button onClick={()=>{setTechData(prev=>{const n={...prev};delete n["SPY"];return n;});setSpyHover(null);}}
                                style={{padding:"2px 8px",border:"1px solid #1e3a5f",background:"transparent",color:"#3b82f6",borderRadius:3,cursor:"pointer",fontSize:9,fontFamily:"inherit",marginLeft:4}}
                                title="Hent ny SPY-data">
                                ↻
                              </button>
                            </div>
                          </div>
                          <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{overflow:"visible",cursor:"crosshair"}}
                            onMouseMove={handleMM} onMouseLeave={()=>setSpyHover(null)}>
                            <defs>
                              <linearGradient id="spyFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={lineColor} stopOpacity="0.1"/>
                                <stop offset="100%" stopColor={lineColor} stopOpacity="0"/>
                              </linearGradient>
                            </defs>
                            <g transform={`translate(${pad.l},${pad.t})`}>
                              {yLabels.map((v,i)=>(
                                <g key={i}>
                                  <line x1={0} x2={cW} y1={toY(v)} y2={toY(v)} stroke="#1e293b" strokeWidth="0.5"/>
                                  <text x={-6} y={toY(v)} fill="#475569" fontSize="10" textAnchor="end" dominantBaseline="middle">${v.toFixed(0)}</text>
                                </g>
                              ))}
                              <polygon points={`${pts} ${toX(n-1)},${cH} 0,${cH}`} fill="url(#spyFill)"/>
                              <polyline points={pts} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d={ma20path} fill="none" stroke="#f59e0b" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.8"/>
                              <path d={ma50path} fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9"/>
                              {/* Signals */}
                              {signals.map((s,i)=>{
                                const x=toX(s.i),yBase=cH+2;
                                const isBuy=s.type==="buy";
                                return (
                                  <g key={i}>
                                    <line x1={x} x2={x} y1={0} y2={cH} stroke={isBuy?"#10b981":"#f43f5e"} strokeWidth="0.5" strokeDasharray="2,3" opacity="0.4"/>
                                    <polygon points={isBuy?`${x},${toY(prices[s.i])-10} ${x-5},${toY(prices[s.i])-2} ${x+5},${toY(prices[s.i])-2}`:`${x},${toY(prices[s.i])+10} ${x-5},${toY(prices[s.i])+2} ${x+5},${toY(prices[s.i])+2}`}
                                      fill={isBuy?"#10b981":"#f43f5e"} opacity="0.9"/>
                                    <text x={x} y={isBuy?toY(prices[s.i])-14:toY(prices[s.i])+20} textAnchor="middle" fill={isBuy?"#10b981":"#f43f5e"} fontSize="7" fontWeight="700">{isBuy?"B":"S"}</text>
                                  </g>
                                );
                              })}
                              {/* MA50 area highlight */}
                              {!hv&&<circle cx={toX(n-1)} cy={toY(prices[n-1])} r="3" fill={lineColor}/>}
                              {labelIdxs.map((idx,i)=>(
                                <g key={i}>
                                  <line x1={toX(idx)} x2={toX(idx)} y1={cH} y2={cH+4} stroke="#334155" strokeWidth="0.5"/>
                                  <text x={toX(idx)} y={cH+14} fill="#475569" fontSize="9" textAnchor="middle">{getDate(idx)}</text>
                                </g>
                              ))}
                              {/* Hover */}
                              {hv&&(
                                <g>
                                  <line x1={hv.x} x2={hv.x} y1={0} y2={cH} stroke="#475569" strokeWidth="1" strokeDasharray="3,3"/>
                                  <circle cx={hv.x} cy={hv.y} r="4" fill={lineColor} stroke="#060f1c" strokeWidth="2"/>
                                  <rect x={hvTooltipX} y={hv.y-46} width={68} height={58} rx={4} fill="#0a1628" stroke="#1e3a5f" strokeWidth="1"/>
                                  <text x={hvTooltipX+34} y={hv.y-33} fill="#e2e8f0" fontSize="12" fontWeight="700" textAnchor="middle">${hv.price?.toFixed(1)}</text>
                                  {hv.ma20&&<text x={hvTooltipX+34} y={hv.y-19} fill="#f59e0b" fontSize="9" textAnchor="middle">MA20 ${hv.ma20.toFixed(1)}</text>}
                                  {hv.ma50&&<text x={hvTooltipX+34} y={hv.y-7} fill="#3b82f6" fontSize="9" textAnchor="middle">MA50 ${hv.ma50.toFixed(1)}</text>}
                                  <text x={hvTooltipX+34} y={hv.y+6} fill="#475569" fontSize="8" textAnchor="middle">{hv.label}</text>
                                </g>
                              )}
                            </g>
                          </svg>
                          <div style={{fontSize:8,color:"#334155",marginTop:4,lineHeight:1.5}}>
                            Signaler: ▲B = RSI&lt;33 oversold eller pris krysser over MA50 · ▼S = RSI&gt;68 overbought eller pris bryter under MA50 · {signals.length} signal{signals.length!==1?"er":""} i valgt periode · 15-20 min forsinkelse
                          </div>
                        </div>
                      );
                    })()}

                    <div style={{padding:"9px 13px",background:"#060f1c",border:"1px solid #1e293b",borderRadius:6}}>
                      <div style={{fontSize:9,color:"#334155",lineHeight:1.5}}>⚠️ Markedsbarometeret er et hjelpeverktøy basert på historiske korrelasjoner. Det kan ikke forutsi fremtidige bevegelser. Bruk alltid egen vurdering og risikostyring. Market Breadth baseres på AI-aksjer i denne appen, ikke hele S&P 500.</div>
                    </div>
                  </div>
                )}

                {/* ── KRASJ-RADAR ── */}
                {(()=>{
                  const hygD=marketData["HYG"],lqdD=marketData["LQD"];
                  const vixD=marketData["^VIX"],vix3mD=marketData["^VIX3M"];
                  const skewD=marketData["^SKEW"],tnxD=marketData["^TNX"],irxD=marketData["^IRX"];
                  const vvixD=marketData["^VVIX"];

                  // Sparkline SVG helper
                  const Spark=({data,color,w=80,h=24})=>{
                    if(!data||data.length<2)return<span style={{color:"#1e293b",fontSize:9}}>—</span>;
                    const mn=Math.min(...data),mx=Math.max(...data);
                    const rng=mx-mn||1;
                    const pts=data.map((v,i)=>`${(i/(data.length-1))*w},${h-((v-mn)/rng)*h}`).join(" ");
                    return<svg width={w} height={h} style={{display:"block"}}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
                  };

                  // 1. Credit spread
                  let crSt=null,crVal="…",crSub="",crHistory=[];
                  if(hygD&&lqdD){
                    const hh=hygD.history,lh=lqdD.history;
                    const hR=hh.length>=6?(hh[hh.length-1]/hh[hh.length-6]-1)*100:hygD.change;
                    const lR=lh.length>=6?(lh[lh.length-1]/lh[lh.length-6]-1)*100:lqdD.change;
                    const s=lR-hR;
                    crSt=s>2?"red":s>0.5?"amber":"green";
                    crVal=`${s>=0?"+":""}${s.toFixed(2)}% stress (5d)`;
                    crSub=`HYG ${hR>=0?"+":""}${hR.toFixed(2)}%  ·  LQD ${lR>=0?"+":""}${lR.toFixed(2)}%  ·  Positiv = HYG underpresterer`;
                    // Rolling spread history
                    const len=Math.min(hh.length,lh.length);
                    crHistory=Array.from({length:len},(_,i)=>i).slice(5).map(i=>{
                      const hr2=(hh[i]/hh[i-5]-1)*100,lr2=(lh[i]/lh[i-5]-1)*100;return lr2-hr2;});
                  }

                  // 2. VIX term structure
                  let vtSt=null,vtVal="…",vtSub="";
                  if(vixD&&vix3mD){
                    const r=vixD.price/vix3mD.price;
                    vtSt=r>1.1?"red":r>0.97?"amber":"green";
                    vtVal=`${r.toFixed(3)} (VIX ${vixD.price.toFixed(1)} / VIX3M ${vix3mD.price.toFixed(1)})`;
                    vtSub=r>1?"⚠ Invertert terminstruktur — akutt frykt dominerer":"Normal contango — ingen umiddelbar panikk";
                  }

                  // 3. SKEW
                  let skSt=null,skVal="…",skSub="";
                  if(skewD){
                    skSt=skewD.price>145?"red":skewD.price>130?"amber":"green";
                    skVal=`${skewD.price.toFixed(0)} pt  (dag: ${skewD.change>=0?"+":""}${skewD.change.toFixed(1)}%)`;
                    skSub=skewD.price>145?"⚠ Institusjonelle kjøper massivt med put-beskyttelse":skewD.price>130?"Forhøyet — noe tail-risk prising":"Normalt nivå — ingen uvanlig sikringsaktivitet";
                  }

                  // 4. Yield curve
                  let ycSt=null,ycVal="…",ycSub="",ycHistory=[];
                  if(irxD&&tnxD){
                    const sp=tnxD.price-irxD.price;
                    ycSt=sp<-0.5?"red":sp<0.5?"amber":"green";
                    ycVal=`${sp>=0?"+":""}${sp.toFixed(2)}%  (3mnd ${irxD.price.toFixed(2)}% · 10y ${tnxD.price.toFixed(2)}%)`;
                    ycSub=sp<0?"⚠ Invertert — varslet alle resesjoner siden 1970":sp<0.5?"Flat kurve — moderat bekymring":"Normal stigende kurve";
                    const len=Math.min((tnxD.history||[]).length,(irxD.history||[]).length);
                    if(len>1) ycHistory=Array.from({length:len},(_,i)=>tnxD.history[i]-irxD.history[i]);
                  }

                  // 5. VVIX
                  let vvSt=null,vvVal="…",vvSub="";
                  if(vvixD){
                    vvSt=vvixD.price>120?"red":vvixD.price>95?"amber":"green";
                    vvVal=`${vvixD.price.toFixed(1)}  (${vvixD.change>=0?"+":""}${vvixD.change.toFixed(2)}% dag)`;
                    vvSub=vvixD.price>120?"⚠ Ekstremt — markedet priser inn sjokk":vvixD.price>95?"Forhøyet — ustabilitet i VIX selv":"Rolig — ingen tegn på sjokk";
                  }

                  // 6. COT
                  let cotSt=null,cotValStr=cotLoading?"Henter CFTC-data…":cotError?`Feil: ${cotError}`:"Ikke lastet — trykk 'Hent COT'";
                  let cotSubStr=cotError?"CFTC API kan være nede. Prøv igjen om litt.":"Ukentlig CFTC Commitment of Traders rapport";
                  if(cotData){
                    const range=cotData.max-cotData.min;
                    const pct=range>0?(cotData.net-cotData.min)/range:0.5;
                    cotSt=pct>0.9?"amber":pct<0.1?"amber":"green";
                    const extreme=pct>0.9?"⚠ Ekstremt strukket long — kontrær advarsel":pct<0.1?"⚠ Ekstremt strukket short — mulig short squeeze":`Innenfor normalområdet (${Math.round(pct*100)} % av årets spenn)`;
                    cotValStr=`${cotData.net>0?"+":""}${cotData.net.toLocaleString("nb-NO")} netto kontrakter`;
                    cotSubStr=`${extreme}  ·  Per ${cotData.date}  ·  ${cotData.weeks}-ukers range: ${cotData.min.toLocaleString("nb-NO")} → ${cotData.max.toLocaleString("nb-NO")}  ·  Hedgefond er nesten alltid netto short her (sikring), så det er endringen som teller, ikke fortegnet.`;
                  }

                  const sigs=[
                    {icon:"💳",label:"Kredittspread (HYG vs LQD)",val:crVal,sub:crSub||"Henter…",st:crSt,history:crHistory,histColor:"#f59e0b",
                     warn:"Vider seg FØR aksjer faller — tydelig i 2008 og COVID-19-krasjet. > 0.5%: stress  ·  > 2%: krise"},
                    {icon:"📉",label:"VIX terminstruktur (VIX/VIX3M)",val:vtVal,sub:vtSub||"Henter…",st:vtSt,history:vixD?.history||[],histColor:"#f43f5e",
                     warn:"Invertert kurve = front-month frykt dominerer. Signalerte 2020-krasjet uker i forveien"},
                    {icon:"⚙",label:"CBOE Options Skew",val:skVal,sub:skSub||"Henter…",st:skSt,history:skewD?.history||[],histColor:"#a78bfa",
                     warn:"Mål på institusjonell sikring. > 130: forhøyet  ·  > 145: ekstrem tail-risk prising"},
                    {icon:"📈",label:"Rentekurven (3mnd vs 10år)",val:ycVal,sub:ycSub||"Henter…",st:ycSt,history:ycHistory,histColor:"#38bdf8",
                     warn:"Invertert kurve varslet alle resesjoner siden 1970. Var negativ 18 mnd FØR 2008-krasjet"},
                    {icon:"🌪",label:"Volatilitet av VIX (VVIX)",val:vvVal,sub:vvSub||"Henter…",st:vvSt,history:vvixD?.history||[],histColor:"#fb923c",
                     warn:"Volatilitet av fryktindeksen. > 95: forhøyet  ·  > 120: ekstremt, sjokk prises inn"},
                    {icon:"📊",label:"CFTC Hedge fund posisjon (ES=F)",val:cotValStr,sub:cotSubStr,st:cotSt,history:cotData?.history||[],histColor:"#10b981",
                     warn:"Grafen viser netto posisjon siste år: nedover = mer short. Ekstremt strukket long/short (ytterste 10 % av årets spenn) = kontrært signal. Data er 3 dager forsinket, publiseres fredager"},
                  ];

                  const reds=sigs.filter(s=>s.st==="red").length;
                  const ambers=sigs.filter(s=>s.st==="amber").length;
                  const riskLabel=reds>=4?"KRITISK":reds>=2?"HØY":reds>=1||ambers>=3?"MODERAT":"LAV";
                  const riskColor=reds>=4?"#f43f5e":reds>=2?"#f97316":reds>=1||ambers>=3?"#f59e0b":"#10b981";
                  const stColor={green:"#10b981",amber:"#f59e0b",red:"#f43f5e"};

                  return(
                    <div style={{marginTop:20,background:"#060f1c",border:`1px solid ${riskColor}30`,borderRadius:10,overflow:"hidden"}}>
                      <div style={{padding:"13px 18px",borderBottom:"1px solid #0d1e30",display:"flex",alignItems:"center",justifyContent:"space-between",background:`linear-gradient(90deg,#040d18,${riskColor}0a)`}}>
                        <div>
                          <div style={{fontSize:10,color:"#334155",letterSpacing:2,marginBottom:3}}>⚠ KRASJ-RADAR</div>
                          <div style={{fontSize:11,color:"#475569"}}>Systemiske risikosignaler — aktive lenge FØR markedet faller</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:2}}>SYSTEMRISIKO</div>
                          <div style={{fontSize:18,fontWeight:800,color:riskColor,letterSpacing:2}}>{riskLabel}</div>
                          <div style={{fontSize:9,color:"#334155"}}>{reds} rød · {ambers} gul av {sigs.length}</div>
                        </div>
                      </div>
                      <div style={{padding:"4px 0"}}>
                        {sigs.map((sig,i)=>{
                          const dot=stColor[sig.st]||"#334155";
                          return(
                            <div key={i} style={{padding:"11px 18px",borderBottom:i<sigs.length-1?"1px solid #06111e":"none",
                              background:sig.st==="red"?"rgba(244,63,94,0.04)":sig.st==="amber"?"rgba(245,158,11,0.04)":"transparent"}}>
                              <div style={{display:"flex",alignItems:"flex-start",gap:10,flexWrap:isMobile?"wrap":"nowrap"}}>
                                <div style={{display:"flex",alignItems:"center",gap:8,flex:isMobile?"0 0 100%":"0 0 220px",marginBottom:isMobile?4:0}}>
                                  <div style={{width:9,height:9,borderRadius:"50%",background:dot,flexShrink:0,boxShadow:sig.st&&sig.st!=="green"?`0 0 7px ${dot}90`:"none"}}/>
                                  <div style={{fontSize:11,fontWeight:700,color:"#94a3b8"}}>{sig.icon} {sig.label}</div>
                                </div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:2}}>
                                    <div style={{fontSize:12,fontWeight:700,color:dot}}>{sig.val}</div>
                                    {sig.history.length>4&&<Spark data={sig.history} color={sig.histColor}/>}
                                  </div>
                                  <div style={{fontSize:10,color:"#475569",marginBottom:3}}>{sig.sub}</div>
                                  <div style={{fontSize:9,color:"#1e3a5f",borderLeft:`2px solid ${dot}40`,paddingLeft:6}}>{sig.warn}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{padding:"8px 18px 10px",fontSize:9,color:"#1e3a5f",borderTop:"1px solid #080f1c",display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                        <span>I 2008 var kredittspreader, rentekurve og VIX-terminstruktur alle røde 6–12 måneder FØR kollapsen. I 2020 kom det brattere og raskere.</span>
                        {!cotData&&<button onClick={()=>fetchCOT()} disabled={cotLoading} style={{padding:"4px 10px",background:"transparent",border:"1px solid #1e3a5f",borderRadius:4,color:cotLoading?"#334155":"#3b82f6",cursor:cotLoading?"default":"pointer",fontSize:9,fontFamily:"inherit",flexShrink:0}}>{cotLoading?"Henter…":"Hent COT-data"}</button>}
                        {cotData&&<button onClick={()=>fetchCOT()} disabled={cotLoading} style={{padding:"4px 10px",background:"transparent",border:"1px solid #1e293b",borderRadius:4,color:"#334155",cursor:"pointer",fontSize:9,fontFamily:"inherit",flexShrink:0}}>↻ Oppdater COT</button>}
                      </div>
                    </div>
                  );
                })()}

                {/* ── YEN CARRY TRADE / SYSTEMSTRESS ── */}
                {(()=>{
                  const jpyD=marketData["JPY=X"];
                  const tnxD=marketData["^TNX"],irxD=marketData["^IRX"];
                  const hygD=marketData["HYG"],lqdD=marketData["LQD"];
                  const vixD=marketData["^VIX"];

                  // USD/JPY: 10-day change
                  let jpySt=null,jpyVal="…",jpySub="",jpyChg=null;
                  if(jpyD){
                    const h=jpyD.history||[];
                    jpyChg=h.length>=11?(h[h.length-1]/h[h.length-11]-1)*100:null;
                    jpySt=jpyChg!=null?(jpyChg<-4?"red":jpyChg<-2?"amber":"green"):null;
                    jpyVal=`¥${jpyD.price?.toFixed(2)} / $1${jpyChg!=null?`  (${jpyChg>=0?"+":""}${jpyChg.toFixed(1)}% 10d)`:""}`;
                    jpySub=jpyChg!=null?(jpyChg<-2?"⚠ Yen styrkes raskt — carry trade avvikling mulig":jpyChg<0?"Lett yen-styrking — overvåk":"Stabil / yen svekkes — carry trade intakt"):"Henter…";
                  }

                  // Fed-BoJ spread (US 10Y - proxy for JP rate ~0.75)
                  const jpRate=0.75;
                  let fbjSt=null,fbjVal="…",fbjSub="";
                  if(tnxD){
                    const diff=tnxD.price-jpRate;
                    fbjSt=diff<2?"red":diff<3?"amber":"green";
                    fbjVal=`${diff.toFixed(2)}% spread  (US 10Y ${tnxD.price.toFixed(2)}% · BoJ ~${jpRate}%)`;
                    fbjSub=diff<2?"⚠ Spreaden er lav — carry trade gevinst krymper":diff<3?"Spreaden smalner — carry trade under press":"Stor spread — carry trade er fortsatt lønnsomt";
                  }

                  // Spark helper
                  const Spark=({data,color,w=80,h=24})=>{
                    if(!data||data.length<2)return null;
                    const mn=Math.min(...data),mx=Math.max(...data),rng=mx-mn||1;
                    const pts=data.map((v,i)=>`${(i/(data.length-1))*w},${h-((v-mn)/rng)*h}`).join(" ");
                    return<svg width={w} height={h} style={{display:"block"}}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
                  };

                  const sigs2=[
                    {icon:"¥",label:"USD/JPY kurs (Yen styrke)",val:jpyVal,sub:jpySub,st:jpySt,history:jpyD?.history||[],histColor:"#e2e8f0",
                     warn:"Fall fra 160→140 tvinger carry-investorer til panikksalg av US tech og bonds. Viktigste enkeltindikator."},
                    {icon:"📐",label:"Fed vs BoJ rentespread",val:fbjVal,sub:fbjSub,st:fbjSt,history:(tnxD?.history||[]).map(v=>v-jpRate),histColor:"#38bdf8",
                     warn:"Carry trade lever av stor rentedifferanse. Stenger Fed-kutt + BoJ-heving gapet → tvunget avvikling."},
                  ];

                  const cr2=sigs2.filter(s=>s.st==="red").length;
                  const am2=sigs2.filter(s=>s.st==="amber").length;
                  const risk2=cr2>=2?"KRITISK":cr2>=1?"HØY":am2>=1?"MODERAT":"LAV";
                  const col2=cr2>=2?"#f43f5e":cr2>=1?"#f97316":am2>=1?"#f59e0b":"#10b981";
                  const stColor2={green:"#10b981",amber:"#f59e0b",red:"#f43f5e"};

                  return(
                    <div style={{marginTop:16,background:"#060f1c",border:`1px solid ${col2}30`,borderRadius:10,overflow:"hidden"}}>
                      <div style={{padding:"13px 18px",borderBottom:"1px solid #0d1e30",display:"flex",alignItems:"center",justifyContent:"space-between",background:`linear-gradient(90deg,#040d18,${col2}0a)`}}>
                        <div>
                          <div style={{fontSize:10,color:"#334155",letterSpacing:2,marginBottom:3}}>🇯🇵 YEN CARRY TRADE — SYSTEMSTRESS</div>
                          <div style={{fontSize:11,color:"#475569"}}>Yen-lån i dollar — avvikling varsler globalt aksjefall</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:2}}>CARRY-RISIKO</div>
                          <div style={{fontSize:18,fontWeight:800,color:col2,letterSpacing:2}}>{risk2}</div>
                          <div style={{fontSize:9,color:"#334155"}}>{cr2} rød · {am2} gul av {sigs2.length}</div>
                        </div>
                      </div>
                      <div style={{padding:"4px 0"}}>
                        {sigs2.map((sig,i)=>{
                          const dot=stColor2[sig.st]||"#334155";
                          return(
                            <div key={i} style={{padding:"11px 18px",borderBottom:i<sigs2.length-1?"1px solid #06111e":"none",
                              background:sig.st==="red"?"rgba(244,63,94,0.04)":sig.st==="amber"?"rgba(245,158,11,0.04)":"transparent"}}>
                              <div style={{display:"flex",alignItems:"flex-start",gap:10,flexWrap:isMobile?"wrap":"nowrap"}}>
                                <div style={{display:"flex",alignItems:"center",gap:8,flex:isMobile?"0 0 100%":"0 0 220px",marginBottom:isMobile?4:0}}>
                                  <div style={{width:9,height:9,borderRadius:"50%",background:dot,flexShrink:0,boxShadow:sig.st&&sig.st!=="green"?`0 0 7px ${dot}90`:"none"}}/>
                                  <div style={{fontSize:11,fontWeight:700,color:"#94a3b8"}}>{sig.icon} {sig.label}</div>
                                </div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:2}}>
                                    <div style={{fontSize:12,fontWeight:700,color:dot}}>{sig.val}</div>
                                    {sig.history.length>4&&<Spark data={sig.history} color={sig.histColor}/>}
                                  </div>
                                  <div style={{fontSize:10,color:"#475569",marginBottom:3}}>{sig.sub}</div>
                                  <div style={{fontSize:9,color:"#1e3a5f",borderLeft:`2px solid ${dot}40`,paddingLeft:6}}>{sig.warn}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{padding:"8px 18px 10px",fontSize:9,color:"#1e3a5f",borderTop:"1px solid #080f1c"}}>
                        USD/JPY fra 160→140 på kort tid = Yen Carry Trade avvikling. Investorer selger US tech/bonds for å kjøpe tilbake yen og nedbetale lån. Følg USD/JPY + Fed-uttalelser + BoJ-møter.
                      </div>
                    </div>
                  );
                })()}

                {/* ── SPY TEKNISK ANALYSE ── */}
                {(()=>{
                  const spyT = techData["SPY"];
                  const closes = spyT?.closes?.filter(Boolean) || [];
                  if (closes.length < 30) return null;
                  const last = closes[closes.length-1];

                  // MAs
                  const ma20  = calcMA(closes, 20);
                  const ma50  = calcMA(closes, Math.min(50, closes.length));
                  const ma200 = closes.length >= 200 ? calcMA(closes, 200) : null;
                  const ma50p = closes.length >= 51  ? calcMA(closes.slice(0,-1), 50) : null;
                  const ma200p= closes.length >= 201 ? calcMA(closes.slice(0,-1), 200) : null;

                  // RSI 14
                  const rsi = calcRSI(closes, 14);

                  // MACD via EMA lines
                  const ema12line = calcEMALine(closes, 12);
                  const ema26line = calcEMALine(closes, 26);
                  const macdLine  = ema12line.map((v,i)=> v!=null&&ema26line[i]!=null ? v-ema26line[i] : null);
                  const macdFiltered = macdLine.filter(v=>v!=null);
                  const signalLine = calcEMALine(macdFiltered, 9);
                  const macdVal  = macdFiltered[macdFiltered.length-1] ?? null;
                  const sigVal   = signalLine[signalLine.length-1] ?? null;
                  const macdHist = macdVal!=null&&sigVal!=null ? macdVal-sigVal : null;
                  const macdHistPrev = macdFiltered.length>2&&signalLine.length>2
                    ? macdFiltered[macdFiltered.length-2]-(signalLine[signalLine.length-2]??0) : null;

                  // Signals
                  const regime = ma200 ? (last>ma200?"BULLISH":"BEARISH") : null;
                  const regimePct = ma200 ? ((last/ma200-1)*100) : null;

                  let crossSig = null;
                  if (ma50&&ma200&&ma50p&&ma200p) {
                    if (ma50>ma200&&ma50p<=ma200p) crossSig="golden_fresh";
                    else if (ma50<ma200&&ma50p>=ma200p) crossSig="death_fresh";
                    else if (ma50>ma200) crossSig="above";
                    else crossSig="below";
                  }

                  const rsiLabel = rsi==null?"—":rsi>70?"OVERKJØPT":rsi<30?"OVERSOLGT":rsi>60?"Høy":"Nøytral";
                  const rsiDot   = rsi==null?"gray":rsi>70?"red":rsi<30?"green":rsi>60?"orange":"yellow";

                  const macdDot  = macdHist==null?"gray":macdHist>0&&(macdHistPrev==null||macdHist>macdHistPrev)?"green":macdHist>0?"yellow":macdHist<0&&(macdHistPrev==null||macdHist<macdHistPrev)?"red":"orange";
                  const macdLbl  = macdHist==null?"—":macdHist>0?"Positiv momentum":"Negativt momentum";

                  const shortTrend = ma20 ? (last>ma20?"OVER 20 MA":"UNDER 20 MA") : null;
                  const shortDot   = ma20 ? (last>ma20?"green":"red") : "gray";

                  // RVOL — relative volume vs 20-day average
                  const vols    = spyT?.volumes?.filter(Boolean) || [];
                  const todayVol = vols[vols.length-1] || 0;
                  const avgVol20 = vols.length >= 21 ? vols.slice(-21,-1).reduce((a,b)=>a+b,0)/20 : null;
                  const rvol     = avgVol20 ? todayVol/avgVol20 : null;
                  const isUpDay  = closes.length>=2 && closes[closes.length-1]>closes[closes.length-2];
                  const rvolDot  = !rvol?"gray":rvol>2&&!isUpDay?"red":rvol>2&&isUpDay?"green":rvol>1.5?"orange":rvol<0.7?"gray":"yellow";
                  const rvolLbl  = !rvol?"—":`${rvol.toFixed(2)}× snitt`;
                  const rvolTxt  = !rvol?"Ingen volum-data":
                    rvol>2&&!isUpDay?`${rvol.toFixed(1)}× snitt på NED-dag — ekstremt institusjonelt salgspress, bekrefter bearish signaler`:
                    rvol>2&&isUpDay?`${rvol.toFixed(1)}× snitt på OPP-dag — sterk institusjonell kjøpsoverbevisning, bekrefter bullish signaler`:
                    rvol>1.5?`${rvol.toFixed(1)}× snitt — forhøyet aktivitet, sjekk om det sammenfaller med VIX-bevegelse`:
                    rvol<0.7?`${rvol.toFixed(1)}× snitt — lavt volum, dagens bevegelse er lite overbevisende (mulig støy)`:
                    `${rvol.toFixed(1)}× snitt — normalt volum, ingen volumadvarsel`;

                  const DOT = { green:"#22c55e", yellow:"#eab308", orange:"#f59e0b", red:"#ef4444", gray:"#475569" };

                  const rows = [
                    { label:"Regime (200 MA)", dot:regime==="BULLISH"?"green":regime==="BEARISH"?"red":"gray",
                      val: regime ? `${regime} (${regimePct>=0?"+":""}${regimePct?.toFixed(1)}%)` : "—",
                      txt: ma200 ? `Pris ${regime==="BULLISH"?"over":"under"} 200 MA ($${ma200.toFixed(1)}) — ${regime==="BULLISH"?"bullish regime, bias long":"bearish regime, bias short eller cash"}` : "Trenger mer data" },
                    { label:"MA Kryss (50/200)", dot:crossSig==="golden_fresh"||crossSig==="above"?"green":crossSig==="death_fresh"||crossSig==="below"?"red":"gray",
                      val: crossSig==="golden_fresh"?"Golden Cross!":crossSig==="death_fresh"?"Death Cross!":crossSig==="above"?"50 MA > 200 MA":crossSig==="below"?"50 MA < 200 MA":"—",
                      txt: crossSig==="golden_fresh"?"Nytt Golden Cross — sterk langsiktig kjøpssignal":crossSig==="death_fresh"?"Nytt Death Cross — sterk langsiktig salgssignal":crossSig==="above"?"50 MA over 200 MA — langsiktig bullish trend intakt":"50 MA under 200 MA — langsiktig bearish trend" },
                    { label:"RSI 14", dot:rsiDot, val:rsi!=null?`${rsi} — ${rsiLabel}`:"—",
                      txt: rsi>70?"Overkjøpt — se etter bearish divergens eller reversal-stearinlys":rsi<30?"Oversolgt — potensielt kjøpssignal, bekreft med volum":rsi>60?"RSI i høy sone — momentum godt men watch for divergens":"RSI nøytral — ingen klar retning fra momentum alene" },
                    { label:"MACD Histogram", dot:macdDot, val:macdHist!=null?`${macdHist>=0?"+":""}${macdHist.toFixed(3)} (${macdLbl})`:"—",
                      txt: macdHist>0&&macdHistPrev!=null&&macdHist>macdHistPrev?"Histogram vokser — stigende momentum, bullish signal":macdHist>0?"Histogram positiv men krymper — momentum avtar, pass på":macdHist<0&&macdHistPrev!=null&&macdHist<macdHistPrev?"Histogram synker dypere — fallende momentum, bearish":"Histogram negativt men bedres — mulig reversal i gang" },
                    { label:"Kortsiktig (20 MA)", dot:shortDot, val:shortTrend||"—",
                      txt: last&&ma20 ? `Pris $${last.toFixed(1)} vs 20 MA $${ma20.toFixed(1)} — ${last>ma20?"over kortsiktig snitt, short-term bullish":"under kortsiktig snitt, short-term svakhet"}` : "" },
                    { label:"Volum (RVOL)", dot:rvolDot, val:rvolLbl, txt:rvolTxt },
                  ];

                  return (
                    <div style={{marginTop:16,background:"#040d1a",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden"}}>
                      <div style={{padding:"10px 14px 8px",borderBottom:"1px solid #1e293b",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                        <div>
                          <div style={{fontSize:11,fontWeight:700,color:"#f8fafc",letterSpacing:1}}>📊 SPY — TEKNISK ANALYSE</div>
                          <div style={{fontSize:9,color:"#475569",marginTop:2}}>Daglige lukninger · {closes.length} handelsdager · Buy/Sell signaler</div>
                        </div>
                        <div style={{fontSize:9,color:"#475569"}}>
                          Siste: <span style={{color:"#f8fafc",fontWeight:700}}>${last?.toFixed(2)}</span>
                        </div>
                      </div>
                      {rows.map((r,i)=>(
                        <div key={i} style={{display:"grid",gridTemplateColumns:"130px 180px 1fr",alignItems:"center",padding:"8px 14px",borderBottom:"1px solid #0a1628",gap:8}}>
                          <div style={{fontSize:10,color:"#94a3b8"}}>{r.label}</div>
                          <div style={{display:"flex",alignItems:"center",gap:5}}>
                            <div style={{width:7,height:7,borderRadius:"50%",background:DOT[r.dot],flexShrink:0}}/>
                            <span style={{fontSize:10,fontWeight:700,color:DOT[r.dot]}}>{r.val}</span>
                          </div>
                          <div style={{fontSize:10,color:"#94a3b8"}}>{r.txt}</div>
                        </div>
                      ))}
                      <div style={{padding:"7px 14px 8px",fontSize:9.5,color:"#64748b"}}>
                        ⚠ Teknisk analyse er bakoverblikkende og garanterer ikke fremtidig kurs. Kombiner alltid med makro og fundamentalt.
                      </div>
                    </div>
                  );
                })()}

                {/* ── MAKROKORRELASJONSKART ── */}
                <MacroCorrelationChart marketData={marketData} spyTechData={techData["SPY"]} />

              </div>
            );
          })()}

          {/* ── SIGNALER ── */}
          {view==="watchlist"&&wlMode==="signaler"&&(()=>{
            const setupOrder = ["EARNINGS PLAY","SQUEEZE PLAY","VOLUME BREAKOUT","OVERSOLD BOUNCE","EARNINGS SETUP","MOMENTUM","DEEP VALUE","REVERSAL WATCH"];
            const allSetups = allStockList.map(s => {
              const intel = getIntel(s.ticker);
              const liveD = liveData[s.ticker];
              const td = techData[s.ticker];
              const dailyCloses = td?.closes;
              const liveForSetup = dailyCloses ? { ...liveD, history: dailyCloses } : liveD;
              const setup = classifySetup(intel, liveForSetup);
              if (!setup) return null;
              const patterns = td?.closes?.length
                ? detectInstitutionalPatterns(td.closes, td.opens||[], td.highs||[], td.lows||[], td.volumes||[], td.timestamps||[])
                : [];
              const signalScore = calcSignalScore(patterns);
              const goldenSetups = detectGoldenSetups(patterns, td?.closes||[]);
              return { ...s, intel, liveD: liveForSetup, setup, patterns, signalScore, goldenSetups };
            }).filter(Boolean).sort((a,b) => b.setup.score - a.setup.score);

            const allGoldenSetups = allSetups.flatMap(s => s.goldenSetups.map(g => ({ ...g, ticker: s.ticker, name_s: s.name, liveD: s.liveD, cat: s.cat })));
            const uniqueGolden = allGoldenSetups.reduce((acc, g) => {
              const key = `${g.ticker}-${g.name}`;
              if (!acc.find(x => `${x.ticker}-${x.name}` === key)) acc.push(g);
              return acc;
            }, []);

            const grouped = {};
            setupOrder.forEach(t => { grouped[t] = allSetups.filter(s => s.setup.type === t); });
            const activeGroups = setupOrder.filter(t => grouped[t].length > 0);

            // Ferske mønstre (siste 5 handelsdager) for alle aksjer, med backtest-dom
            const loaded = allStockList.filter(s => techData[s.ticker]?.closes?.length).length;
            const fresh = allStockList.map(s => {
              const td = techData[s.ticker];
              if (!td?.closes?.length || !td.timestamps?.length) return null;
              const days = new Set(td.timestamps.slice(-5).map(ts => new Date(ts * 1000).toISOString().slice(0, 10)));
              const byType = new Map();
              detectInstitutionalPatterns(td.closes, td.opens||[], td.highs||[], td.lows||[], td.volumes||[], td.timestamps)
                .filter(p => days.has(p.date)).forEach(p => byType.set(p.type, p));
              const pats = [...byType.values()].map(p => ({ ...p, vd: verdictOf(p.type) }))
                .sort((a, b) => VERDICT_STYLE[b.vd.v].weight - VERDICT_STYLE[a.vd.v].weight);
              if (!pats.length) return null;
              const c = td.closes.filter(v => v != null), last = c[c.length - 1];
              const ma = n => c.length >= n ? c.slice(-n).reduce((x, y) => x + y, 0) / n : null;
              const m50 = ma(50), m200 = ma(200);
              const trend = m50 && m200 ? (last > m50 && m50 > m200 ? "opp" : last < m50 && m50 < m200 ? "ned" : "blandet") : null;
              return { ...s, pats, trend, best: VERDICT_STYLE[pats[0].vd.v].weight };
            }).filter(Boolean).sort((a, b) => b.best - a.best);
            const warnings = fresh.flatMap(f => f.pats.filter(p => p.vd.bias === "bearish" && (p.vd.v === "pålitelig" || p.vd.v === "lovende")).map(p => ({ ...f, p })))
              .sort((a, b) => VERDICT_STYLE[b.p.vd.v].weight - VERDICT_STYLE[a.p.vd.v].weight);
            const TREND = { opp: ["↗ opptrend", "#10b981"], ned: ["↘ nedtrend", "#f43f5e"], blandet: ["→ blandet", "#f59e0b"] };
            return (
              <div>
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:16,fontWeight:700,color:"#f8fafc",letterSpacing:1}}>📡 SIGNALER</div>
                  <div style={{fontSize:11,color:"#475569",marginTop:3,lineHeight:1.6}}>
                    Kurs- og volummønstre de siste 5 handelsdagene, merket med hva 5 års backtest sier om dem.
                    Backtesten fant at de fleste kjøpssignalene er støy. Det som virker er advarselen <b style={{color:"#10b981"}}>Bull Trap</b>.
                    For å velge aksjer: bruk <span style={{color:"#93c5fd",cursor:"pointer"}} onClick={()=>setView("idag")}>«Følg bølgen» i I dag</span>.
                  </div>
                  {loaded < allStockList.length && <div style={{fontSize:10,color:"#475569",marginTop:6}}>Henter kursdata … {loaded}/{allStockList.length}</div>}
                </div>

                {/* Advarsler */}
                <div style={{background:"#060f1c",border:`1px solid ${warnings.length?"#f43f5e50":"#1e293b"}`,borderRadius:10,padding:"14px 16px",marginBottom:16}}>
                  <div style={{fontSize:10,color:"#f43f5e",letterSpacing:1,fontWeight:700,marginBottom:8}}>⚠️ ADVARSLER · IKKE KJØP / VURDER Å SIKRE GEVINST</div>
                  {warnings.length ? warnings.map(w => (
                    <div key={w.ticker + w.p.type} style={{display:"flex",gap:10,alignItems:"baseline",padding:"5px 0",borderBottom:"1px solid #0a1628",flexWrap:"wrap"}}>
                      <span onClick={()=>{setDeepTicker(w.ticker);setView("deepdive");}} style={{fontSize:13,fontWeight:700,color:"#e2e8f0",cursor:"pointer",minWidth:56}}>{w.ticker}</span>
                      <span style={{fontSize:11,fontWeight:700,color:VERDICT_STYLE[w.p.vd.v].color}}>{w.p.vd.label} · {VERDICT_STYLE[w.p.vd.v].short}</span>
                      <span style={{fontSize:10,color:"#475569"}}>{new Date(w.p.date + "T12:00:00").toLocaleDateString("nb-NO",{weekday:"short",day:"numeric",month:"short"})}</span>
                      <span style={{fontSize:11,color:"#64748b",flex:"1 1 260px"}}>{w.p.vd.note}</span>
                    </div>
                  )) : <div style={{fontSize:12,color:"#475569"}}>Ingen testede advarsler de siste 5 handelsdagene.</div>}
                </div>

                {/* Alle ferske mønstre */}
                <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:10,padding:"14px 16px",marginBottom:16}}>
                  <div style={{fontSize:10,color:"#475569",letterSpacing:1,fontWeight:700,marginBottom:4}}>MØNSTRE SISTE 5 HANDELSDAGER</div>
                  <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:10}}>
                    {Object.entries(VERDICT_STYLE).map(([k, s]) => <span key={k} style={{fontSize:10,color:s.color}}>● {k}</span>)}
                  </div>
                  {fresh.length ? fresh.map(f => (
                    <div key={f.ticker} style={{display:"grid",gridTemplateColumns:isMobile?"60px 1fr":"64px 92px 1fr 34px",gap:8,alignItems:"center",padding:"5px 0",borderBottom:"1px solid #0a1628"}}>
                      <span onClick={()=>{setDeepTicker(f.ticker);setView("deepdive");}} style={{fontSize:13,fontWeight:700,color:CAT_COLORS[f.cat]||"#e2e8f0",cursor:"pointer"}}>{f.ticker}</span>
                      {!isMobile && <span style={{fontSize:10,color:f.trend?TREND[f.trend][1]:"#475569"}}>{f.trend?TREND[f.trend][0]:"–"}</span>}
                      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                        {f.pats.map(p => {
                          const st = VERDICT_STYLE[p.vd.v], dim = p.vd.v === "støy" || p.vd.v === "ikke testet";
                          return (
                            <span key={p.type} title={`${p.desc}\n\nBacktest: ${p.vd.note}`}
                              style={{fontSize:10,padding:"2px 7px",borderRadius:3,border:`1px solid ${st.color}50`,color:st.color,opacity:dim?0.6:1,
                                fontWeight:st.weight>=2?700:400,textDecoration:p.vd.v==="virker motsatt"?"line-through":"none"}}>
                              {p.vd.bias==="bearish"?"▼":"▲"} {p.vd.label}
                            </span>
                          );
                        })}
                      </div>
                      {!isMobile && <button title="Logg i journalen" onClick={()=>{
                          setJournalForm({ticker:f.ticker,date:new Date().toISOString().slice(0,10),price:liveData[f.ticker]?.price?liveData[f.ticker].price.toFixed(2):"",qty:"",reason:`📡 ${f.pats[0].vd.label}`,notes:f.pats.map(p=>`${p.vd.label} (${p.vd.v})`).join(", "),tradeType:"aksje",optionType:"call",optionStrike:"",optionExpiry:""});
                          setView("journal");
                        }}
                        style={{padding:"3px 6px",background:"transparent",border:"1px solid #10b98140",borderRadius:4,color:"#10b981",cursor:"pointer",fontSize:11}}>📓</button>}
                    </div>
                  )) : <div style={{fontSize:12,color:"#475569"}}>{loaded ? "Ingen mønstre de siste 5 handelsdagene." : "Henter kursdata …"}</div>}
                  <div style={{fontSize:10,color:"#334155",marginTop:8,lineHeight:1.6}}>Hold musepekeren over et mønster for detaljer og backtest-resultat. Gjennomstreket = historisk dårligere enn tilfeldig kjøp.</div>
                </div>

                {/* Eldre oppsett, ikke testet */}
                <button onClick={()=>setShowOldSetups(v=>!v)}
                  style={{width:"100%",padding:"9px 14px",background:"transparent",border:"1px dashed #1e293b",borderRadius:8,color:"#64748b",cursor:"pointer",fontSize:11,fontFamily:"inherit",textAlign:"left",marginBottom:showOldSetups?16:0}}>
                  {showOldSetups?"▾":"▸"} Eldre oppsett ({allSetups.length}) — ikke backtestet, og bygger delvis på faste tall for IV og gamma som ikke oppdateres
                </button>

                {showOldSetups&&(<>
                <div style={{display:"flex",gap:3,marginBottom:14}}>
                  {PERIODS.map(p=>(
                    <button key={p.label} onClick={()=>setPeriod(p.label)}
                      style={{padding:"4px 9px",border:"1px solid",borderColor:period===p.label?"#f59e0b":"#1e293b",background:period===p.label?"#f59e0b15":"transparent",color:period===p.label?"#f59e0b":"#64748b",borderRadius:4,cursor:"pointer",fontSize:10,fontFamily:"inherit"}}>
                      {p.label}
                    </button>
                  ))}
                </div>

                {uniqueGolden.length > 0 && (
                  <div style={{marginBottom:28}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,paddingBottom:8,borderBottom:"1px solid #f59e0b30"}}>
                      <span style={{fontSize:18}}>⭐</span>
                      <div>
                        <span style={{fontSize:12,fontWeight:700,color:"#f59e0b",letterSpacing:1}}>GOLDEN SETUPS</span>
                        <span style={{fontSize:11,color:"#475569",marginLeft:10}}>Multi-signal kombinasjoner — høyeste treffsikkerhet</span>
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(300px,1fr))",gap:10}}>
                      {uniqueGolden.map((g,i) => (
                        <div key={i} style={{background:"#07111f",border:`1px solid ${g.color}40`,borderTop:`3px solid ${g.color}`,borderRadius:"0 0 8px 8px",padding:"14px 16px"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                            <div>
                              <div style={{fontSize:13,fontWeight:700,color:g.color,marginBottom:3}}>{g.emoji} {g.name}</div>
                              <div style={{display:"flex",alignItems:"center",gap:6}}>
                                <span style={{fontSize:15,fontWeight:700,color:CAT_COLORS[g.cat]||"#94a3b8",cursor:"pointer"}}
                                  onClick={()=>{setDeepTicker(g.ticker);setView("deepdive");}}>{g.ticker}</span>
                                <span style={{fontSize:10,color:"#475569"}}>{g.name_s}</span>
                              </div>
                            </div>
                            <div style={{textAlign:"right"}}>
                              {g.liveD?.price && <div style={{fontSize:15,fontWeight:700,color:"#e2e8f0"}}>${g.liveD.price.toFixed(1)}</div>}
                              <span style={{fontSize:9,color:g.confidence==="high"?"#10b981":"#f59e0b",background:g.confidence==="high"?"#10b98118":"#f59e0b18",padding:"1px 6px",borderRadius:2,fontWeight:700}}>{g.confidence==="high"?"HØY SIKKERHET":"MODERAT"}</span>
                            </div>
                          </div>
                          <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:8}}>
                            {g.signals.map((sig,j) => (
                              <span key={j} style={{fontSize:10,padding:"2px 7px",borderRadius:3,background:g.color+"18",color:g.color,fontWeight:600}}>{sig}</span>
                            ))}
                          </div>
                          <div style={{fontSize:11,color:"#64748b",lineHeight:1.5,marginBottom:10}}>{g.desc}</div>
                          <button onClick={()=>{setDeepTicker(g.ticker);setView("deepdive");}}
                            style={{width:"100%",padding:"6px 0",background:`${g.color}15`,border:`1px solid ${g.color}40`,borderRadius:4,color:g.color,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
                            🔬 Åpne i Deep Dive
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {allSetups.length === 0 ? (
                  <div style={{textAlign:"center",color:"#334155",padding:"60px 0",fontSize:13}}>Ingen klare setups akkurat nå — prøv en annen periode</div>
                ) : (
                  <div>
                    {activeGroups.map(type => {
                      const group = grouped[type];
                      const setup = group[0].setup;
                      return (
                        <div key={type} style={{marginBottom:24}}>
                          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,paddingBottom:8,borderBottom:`1px solid ${setup.color}30`}}>
                            <span style={{fontSize:18}}>{setup.icon}</span>
                            <div>
                              <span style={{fontSize:12,fontWeight:700,color:setup.color,letterSpacing:1}}>{type}</span>
                              <span style={{fontSize:11,color:"#475569",marginLeft:10}}>{group.length} aksje{group.length>1?"r":""}</span>
                            </div>
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(280px,1fr))",gap:10}}>
                            {group.map(s => {
                              const color = CAT_COLORS[s.cat]||"#94a3b8";
                              const pct = s.liveD?.history?.length>1 ? (s.liveD.history[s.liveD.history.length-1]/s.liveD.history[0]-1)*100 : null;
                              const rsi = calcRSI(s.liveD?.history||[]);
                              const bb = calcBollinger(s.liveD?.history||[]);
                              const fromHigh = s.liveD?.weekHigh52 && s.liveD?.price ? ((s.liveD.price - s.liveD.weekHigh52)/s.liveD.weekHigh52*100) : null;
                              return (
                                <div key={s.ticker} style={{background:"#07111f",border:`1px solid ${setup.color}20`,borderTop:`2px solid ${setup.color}`,borderRadius:"0 0 8px 8px",padding:"14px 16px",position:"relative"}}>
                                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                                    <div>
                                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                                        <span style={{fontSize:16,fontWeight:700,color}}>{s.ticker}</span>
                                        <span style={{fontSize:11,color:"#475569"}}>{s.name}</span>
                                        {s.signalScore>0&&<span style={{fontSize:10,color:"#f59e0b",background:"#f59e0b18",padding:"1px 6px",borderRadius:3,fontWeight:700}} title={`${s.signalScore}/5 unike mønstre siste uke`}>★ {s.signalScore}/5</span>}
                                      </div>
                                      <span style={{fontSize:10,color:"#475569",background:"#1e293b",padding:"1px 6px",borderRadius:2}}>{s.cat}</span>
                                    </div>
                                    <div style={{textAlign:"right"}}>
                                      <div style={{fontSize:16,fontWeight:700,color:s.liveD?.price?"#e2e8f0":"#334155"}}>{s.liveD?.price?`$${s.liveD.price<10?s.liveD.price.toFixed(2):s.liveD.price.toFixed(1)}`:"—"}</div>
                                      {pct!=null&&<div style={{fontSize:10,color:pct>=0?"#10b981":"#f43f5e",fontWeight:600}}>{pct>=0?"▲":"▼"}{Math.abs(pct).toFixed(1)}%</div>}
                                    </div>
                                  </div>

                                  <div style={{fontSize:12,color:"#94a3b8",lineHeight:1.5,marginBottom:10,minHeight:32}}>{s.setup.desc}</div>

                                  <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:10}}>
                                    {rsi!==null&&<span style={{fontSize:10,padding:"2px 7px",borderRadius:3,background:"#1e293b",color:rsi<35?"#06b6d4":rsi>70?"#f43f5e":"#94a3b8"}}>RSI {rsi}</span>}
                                    {bb?.squeeze&&<span style={{fontSize:10,padding:"2px 7px",borderRadius:3,background:"#a78bfa20",color:"#a78bfa"}}>⚡ Squeeze</span>}
                                    {fromHigh!==null&&fromHigh<-15&&<span style={{fontSize:10,padding:"2px 7px",borderRadius:3,background:"#1e293b",color:"#06b6d4"}}>{fromHigh.toFixed(0)}% fra topp</span>}
                                    <span style={{fontSize:10,padding:"2px 7px",borderRadius:3,background:"#1e293b",color:s.intel.pf.gammaRegime==="POSITIVE"?"#10b981":"#f43f5e"}}>
                                      {s.intel.pf.gammaRegime==="POSITIVE"?"↑ Pos gamma":"↓ Neg gamma"}
                                    </span>
                                    <span style={{fontSize:10,padding:"2px 7px",borderRadius:3,background:"#1e293b",color:s.intel.pf.ivPct<50?"#10b981":s.intel.pf.ivPct<70?"#f59e0b":"#f43f5e"}}>IV {s.intel.pf.ivPct}</span>
                                  </div>

                                  <div style={{display:"flex",gap:6}}>
                                    <button onClick={()=>{setDeepTicker(s.ticker);setView("deepdive");}}
                                      style={{flex:1,padding:"6px 0",background:"#0d1f35",border:"1px solid #1e3a5f",borderRadius:4,color:"#3b82f6",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
                                      🔬 Deep Dive
                                    </button>
                                    <button onClick={()=>{setDeepTicker(s.ticker);setDeepTab("teknisk");setView("deepdive");}}
                                      style={{flex:1,padding:"6px 0",background:`${setup.color}15`,border:`1px solid ${setup.color}40`,borderRadius:4,color:setup.color,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
                                      📡 Signal
                                    </button>
                                    <button onClick={()=>{
                                        setJournalForm({ticker:s.ticker,date:new Date().toISOString().slice(0,10),price:s.liveD?.price?s.liveD.price.toFixed(2):"",qty:"",reason:`📡 ${s.setup.type}`,notes:s.setup.desc,tradeType:"aksje",optionType:"call",optionStrike:"",optionExpiry:""});
                                        setView("journal");
                                      }}
                                      style={{padding:"6px 10px",background:"#10b98115",border:"1px solid #10b98140",borderRadius:4,color:"#10b981",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
                                      📓
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                </>)}
                <div style={{padding:"9px 13px",background:"#060f1c",border:"1px solid #1e293b",borderRadius:6,marginTop:8}}>
                  <div style={{fontSize:11,color:"#334155",lineHeight:1.5}}>⚠️ Backtest-dommene er et øyeblikksbilde (okt 2026) og ingen garanti. Kjør Journal › Backtest på nytt for oppdaterte tall. Ikke finansiell rådgivning.</div>
                </div>
              </div>
            );
          })()}

          {/* ── STRATEGI ── */}
          {view==="idag"&&idagMode==="oversikt"&&<IDag tickers={allStockList.map(s=>s.ticker)} favorites={favorites} calEvents={calEvents} liveData={liveData} isMobile={isMobile} pin={syncPin}
            onOpen={t=>{setDeepTicker(t);setView("deepdive");}}/>}

          {view==="watchlist"&&wlMode==="tabell"&&<Watchlist stocks={stocks} favorites={favorites} setFavorites={setFavorites} liveData={liveData} techData={techData}
            fetchQuote={fetchQuote} catColors={CAT_COLORS} isMobile={isMobile} onOpen={t=>{setDeepTicker(t);setView("deepdive");}}/>}

          {/* ── AI-KJEDEN ── */}
          {view==="kjeden"&&(()=>{
            const WB=148,HB=68,hw=WB/2,hh=HB/2;
            const nodeMap=Object.fromEntries(AI_CHAIN.map(n=>[n.id,n]));
            const edgePt=(from,to)=>{
              const dx=to.cx-from.cx,dy=to.cy-from.cy;
              if(!dx&&!dy)return{x:from.cx,y:from.cy};
              if(Math.abs(dy/(dx||0.001))>hh/hw)
                return{x:from.cx+dx*hh/Math.abs(dy),y:from.cy+Math.sign(dy)*hh};
              return{x:from.cx+Math.sign(dx)*hw,y:from.cy+dy*hw/Math.abs(dx)};
            };
            // live utvikling: snitt av segmentets aksjer, og relativt til SPY
            const per=chainPer, spyCh=chainPerf?.SPY?.[per];
            const segPerf=Object.fromEntries(AI_CHAIN.map(n=>{
              const v=n.tickers.map(t=>chainPerf?.[t]?.[per]).filter(x=>x!=null);
              const avg=v.length?v.reduce((a,b)=>a+b,0)/v.length:null;
              return[n.id,{avg,rel:avg!=null&&spyCh!=null?avg-spyCh:null}];
            }));
            const relCol=r=>r==null?"#334155":r>=0?"#10b981":"#f43f5e";
            const fmtP=v=>v==null?"–":`${v>=0?"+":""}${v.toFixed(1)}%`;
            const PER_LBL={d5:"5 dager",m1:"1 måned",m3:"3 måneder"};
            const ranked=AI_CHAIN.filter(n=>segPerf[n.id].rel!=null).sort((a,b)=>segPerf[b.id].rel-segPerf[a.id].rel);
            return(
              <div>
                <div style={{fontSize:14,fontWeight:700,color:"#f8fafc",marginBottom:6}}>🔗 AI-Forsyningskjeden</div>
                <div style={{fontSize:10,color:"#475569",marginBottom:12}}>Hele verdikjeden bak AI — fra råvarer til applikasjoner. Tallet på hver boks er segmentets utvikling minus SPY: grønt = sterkere enn markedet. Klikk en boks for aksjer.</div>

                {/* Periode + rotasjon */}
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:10,color:"#475569",marginRight:4}}>Periode:</span>
                  {[["d5","5D"],["m1","1M"],["m3","3M"]].map(([k,l])=>(
                    <button key={k} onClick={()=>setChainPer(k)}
                      style={{padding:"3px 10px",fontSize:10,fontFamily:"inherit",cursor:"pointer",borderRadius:4,border:"1px solid",
                        borderColor:per===k?"#3b82f6":"#1e293b",background:per===k?"#3b82f620":"transparent",color:per===k?"#93c5fd":"#64748b"}}>{l}</button>
                  ))}
                  <span style={{fontSize:10,color:"#475569",marginLeft:8}}>SPY {fmtP(spyCh)}</span>
                  {!chainPerf&&<span style={{fontSize:10,color:"#475569"}}>· henter kurser…</span>}
                </div>
                {ranked.length>0&&(
                  <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:10,padding:"10px 14px",marginBottom:14}}>
                    <div style={{fontSize:10,color:"#475569",letterSpacing:1,marginBottom:6}}>ROTASJON · HVOR PENGENE GÅR ({PER_LBL[per]}, relativt til SPY)</div>
                    {ranked.map(n=>{
                      const r=segPerf[n.id].rel, w=Math.min(50,Math.abs(r)*2.5);
                      return(
                        <div key={n.id} onClick={()=>setChainNode(n)}
                          style={{display:"grid",gridTemplateColumns:isMobile?"120px 1fr 52px":"170px 1fr 60px",alignItems:"center",gap:8,cursor:"pointer",padding:"2px 0"}}>
                          <span style={{fontSize:11,color:chainNode?.id===n.id?"#e2e8f0":"#94a3b8",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{n.icon} {n.label}</span>
                          <div style={{position:"relative",height:8,background:"#0a1628",borderRadius:4}}>
                            <div style={{position:"absolute",top:0,bottom:0,left:r>=0?"50%":`${50-w}%`,width:`${w}%`,background:relCol(r),borderRadius:4}}/>
                            <div style={{position:"absolute",top:-2,bottom:-2,left:"50%",width:1,background:"#334155"}}/>
                          </div>
                          <span style={{fontSize:11,fontWeight:700,color:relCol(r),textAlign:"right"}}>{fmtP(r)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Legend */}
                <div style={{display:"flex",gap:12,marginBottom:14,flexWrap:"wrap"}}>
                  {[["#f59e0b","FLASKEHALS / MULIGHET"],["#f97316","ETTERSPURT"],["#a78bfa","UNDERVURDERT"],["#10b981","VOKSER"],["#3b82f6","DOMINERER"],["#475569","PRISET INN"],["#64748b","STABIL"]].map(([c,l])=>(
                    <div key={l} style={{display:"flex",alignItems:"center",gap:5}}>
                      <div style={{width:9,height:9,borderRadius:2,background:c}}/>
                      <span style={{fontSize:9,color:"#475569"}}>{l}</span>
                    </div>
                  ))}
                </div>

                {/* Grid: SVG left, detail right */}
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 360px",gap:16,alignItems:"start"}}>

                  {/* SVG with 3D tilt */}
                  <div style={isMobile?{overflowX:"auto",WebkitOverflowScrolling:"touch",borderRadius:12,border:"1px solid #1e293b"}:{}}>
                    <svg viewBox="0 0 800 725" onClick={e=>e.target===e.currentTarget&&setChainNode(null)}
                      style={{width:isMobile?"680px":"100%",minWidth:isMobile?"680px":"auto",display:"block",background:"#030b14",borderRadius:12,border:isMobile?"none":"1px solid #1e293b"}}>
                      <defs>
                        <marker id="ckarr" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
                          <polygon points="0 0,7 2.5,0 5" fill="#1e3a5f"/>
                        </marker>
                      </defs>

                      {CHAIN_EDGES.map(([fId,tId],i)=>{
                        const f=nodeMap[fId],t=nodeMap[tId];
                        if(!f||!t)return null;
                        const p1=edgePt(f,t),p2=edgePt(t,f);
                        return<line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#1e3a5f" strokeWidth="1.5" strokeDasharray="5 3" markerEnd="url(#ckarr)" opacity="0.8"/>;
                      })}

                      {AI_CHAIN.map(node=>{
                        const x=node.cx-hw,y=node.cy-hh;
                        const sel=chainNode?.id===node.id;
                        const sc=node.sc;
                        return(
                          <g key={node.id} onClick={()=>setChainNode(sel?null:node)} style={{cursor:"pointer"}}>
                            {sel&&<rect x={x-6} y={y-6} width={WB+12} height={HB+12} rx={13} fill={sc} opacity={0.15}/>}
                            <rect x={x} y={y} width={WB} height={HB} rx={8}
                              fill={sel?sc+"22":"#060f1c"} stroke={sc} strokeWidth={sel?2:1} opacity={sel?1:0.92}/>
                            <rect x={x} y={y} width={WB} height={5} rx={3} fill={sc}/>
                            {sel&&<rect x={x} y={y+HB-3} width={WB} height={3} rx={2} fill={sc} opacity={0.5}/>}
                            {segPerf[node.id]?.rel!=null&&<text x={x+7} y={y+16} fontSize="8.5" fontWeight="bold" fill={relCol(segPerf[node.id].rel)} style={{fontFamily:"inherit"}}>{fmtP(segPerf[node.id].rel)}</text>}
                            {node.opp>=4&&<circle cx={x+WB-9} cy={y+10} r={5} fill="#f59e0b" opacity={0.95}/>}
                            {node.opp>=4&&<text x={x+WB-9} y={y+14} textAnchor="middle" fontSize="7" fill="#030b14" fontWeight="bold">!</text>}
                            <text x={node.cx} y={node.cy-13} textAnchor="middle" fontSize="16" dominantBaseline="middle">{node.icon}</text>
                            <text x={node.cx} y={node.cy+3} textAnchor="middle" fontSize="8.5" fill="#e2e8f0" fontWeight="bold" dominantBaseline="middle" style={{fontFamily:"inherit"}}>{node.label}</text>
                            <text x={node.cx} y={node.cy+16} textAnchor="middle" fontSize="6.5" fill={sc} dominantBaseline="middle" style={{fontFamily:"inherit",letterSpacing:"0.5px"}}>{node.status}</text>
                            {[0,1,2,3,4].map(i=><circle key={i} cx={node.cx-14+i*7} cy={node.cy+28} r={2.5} fill={i<node.demand?sc:"#0d1e30"}/>)}
                          </g>
                        );
                      })}

                      <text x={14} y={16} fontSize="8" fill="#1e293b" style={{fontFamily:"inherit"}}>▲ SLUTTBRUKER</text>
                      <text x={14} y={714} fontSize="8" fill="#1e293b" style={{fontFamily:"inherit"}}>▼ RÅVARE</text>
                      <circle cx={698} cy={11} r={5} fill="#f59e0b" opacity={0.9}/>
                      <text x={707} y={15} fontSize="8" fill="#f59e0b" style={{fontFamily:"inherit"}}>= Mulighet</text>
                    </svg>
                  </div>

                  {/* Detail panel — always visible on desktop */}
                  <div style={{position:isMobile?"static":"sticky",top:80}}>
                    {chainNode?(()=>{
                      const n=chainNode;
                      return(
                        <div style={{background:"#060f1c",border:`1px solid ${n.sc}35`,borderRadius:12,overflow:"hidden",
                          boxShadow:`0 8px 32px ${n.sc}15`}}>
                          <div style={{background:`linear-gradient(135deg,${n.sc}20,transparent)`,padding:"16px 18px",borderBottom:`1px solid ${n.sc}20`}}>
                            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                              <span style={{fontSize:26}}>{n.icon}</span>
                              <div>
                                <div style={{fontSize:14,fontWeight:700,color:"#f8fafc"}}>{n.label}</div>
                                <span style={{fontSize:11,color:n.sc,background:n.sc+"25",padding:"2px 8px",borderRadius:3,fontWeight:700,letterSpacing:0.5}}>{n.status}</span>
                              </div>
                            </div>
                            <div style={{display:"flex",gap:16}}>
                              {[["ETTERSPØRSEL",n.demand,n.sc],["MULIGHET",n.opp,"#f59e0b"]].map(([lbl,val,c])=>(
                                <div key={lbl}>
                                  <div style={{fontSize:10,color:"#475569",letterSpacing:1,marginBottom:4}}>{lbl}</div>
                                  <div style={{display:"flex",gap:3}}>
                                    {[0,1,2,3,4].map(i=><div key={i} style={{width:11,height:11,borderRadius:2,background:i<val?c:"#0d1e30"}}/>)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{padding:"14px 18px"}}>
                              {segPerf[n.id]?.avg!=null&&(
                                <div style={{fontSize:12,color:"#94a3b8",marginBottom:12}}>
                                  Siste {PER_LBL[per]}: segmentet <b style={{color:relCol(segPerf[n.id].avg)}}>{fmtP(segPerf[n.id].avg)}</b>
                                  {" · "}<b style={{color:relCol(segPerf[n.id].rel)}}>{fmtP(segPerf[n.id].rel)}</b> mot SPY
                                </div>
                              )}
                              <>
                                <div style={{fontSize:10,color:"#334155",letterSpacing:1,marginBottom:4}}>VURDERING (SKREVET MANUELT)</div>
                                <div style={{fontSize:12,color:"#64748b",lineHeight:1.8,marginBottom:12}}>{n.desc}</div>
                                <div style={{background:"#0a1628",borderRadius:6,padding:"9px 12px",marginBottom:14,fontSize:12,color:"#94a3b8",borderLeft:`2px solid ${n.sc}50`,lineHeight:1.7}}>
                                  💡 {n.next}
                                </div>
                              </>
                            <div style={{fontSize:11,color:"#475569",letterSpacing:1,marginBottom:8}}>AKSJER I SEGMENTET</div>
                            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                              {n.tickers.map(t=>{
                                const pp=chainPerf?.[t], live=pp?{price:pp.price,change:pp[per]}:liveData[t];
                                return(
                                  <div key={t} onClick={()=>{setDeepTicker(t);setView("deepdive");}}
                                    style={{background:"#0a1628",border:`1px solid ${n.sc}30`,borderRadius:7,padding:"8px 11px",cursor:"pointer",transition:"border-color 0.15s,background 0.15s"}}
                                    onMouseEnter={e=>{e.currentTarget.style.borderColor=n.sc;e.currentTarget.style.background=n.sc+"12";}}
                                    onMouseLeave={e=>{e.currentTarget.style.borderColor=n.sc+"30";e.currentTarget.style.background="#0a1628";}}>
                                    <div style={{fontSize:12,fontWeight:700,color:n.sc}}>{t}</div>
                                    {live
                                      ?<div style={{fontSize:10,color:live.change>=0?"#10b981":"#f43f5e",fontWeight:600,marginTop:2}}>
                                        ${live.price?.toFixed(2)} <span style={{fontSize:9}}>{live.change>=0?"+":""}{live.change?.toFixed(1)}%</span>
                                      </div>
                                      :<div style={{fontSize:9,color:"#334155",marginTop:2}}>→ Deep Dive</div>}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })():(
                      <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:12,padding:"32px 20px",textAlign:"center"}}>
                        <div style={{fontSize:32,marginBottom:12}}>🔗</div>
                        <div style={{fontSize:15,fontWeight:700,color:"#e2e8f0",marginBottom:8}}>Klikk en sektor</div>
                        <div style={{fontSize:12,color:"#475569",lineHeight:1.7}}>Velg en node i kartet for å se analyse, strategi og aksjer i det segmentet.</div>
                        <div style={{marginTop:20,display:"flex",flexDirection:"column",gap:6}}>
                          {AI_CHAIN.filter(n=>n.opp>=4).map(n=>(
                            <button key={n.id} onClick={()=>setChainNode(n)}
                              style={{padding:"8px 12px",background:"#0a1628",border:`1px solid ${n.sc}40`,borderRadius:6,color:n.sc,cursor:"pointer",fontSize:12,fontFamily:"inherit",textAlign:"left",display:"flex",alignItems:"center",gap:7}}>
                              <span>{n.icon}</span><span>{n.label}</span>
                              <span style={{marginLeft:"auto",fontSize:11,color:"#f59e0b"}}>★ HOT</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── INFO ── */}
          {view==="info"&&(()=>{
            const INFO_CATS=[
              {id:"alle",label:"Alle"},
              {id:"grafen",label:"📊 Grafen"},
              {id:"marked",label:"🌡️ Markedet"},
              {id:"opsjoner",label:"🎯 Opsjoner"},
              {id:"fundamental",label:"💹 Fundamental"},
            ];
            const DOT_COLORS={"green":"#10b981","yellow":"#f59e0b","red":"#f43f5e","purple":"#c084fc","blue":"#38bdf8","orange":"#fb923c","gray":"#64748b"};
            const sections=[
                {cat:"marked",
                  icon:"🏠", title:"Hjemsiden — Oversikt over følgte aksjer",
                  body:"Hjemsiden viser en visuell oversikt over aksjene du følger i Oversikt-fanen. Orben i midten representerer stemningen i dine AI-aksjer — ikke det totale aksjemarkedet. Satellittkortene rundt orben viser de mest bevegede aksjene akkurat nå.",
                  items:[
                    {dot:"green",label:"3 grønne kort (høyre side)",txt:"De 3 aksjene i Oversikt med størst prosentvis oppgang i dag. Klikk på kortet for å gå direkte til Deep Dive for den aksjen."},
                    {dot:"red",label:"3 røde kort (venstre side)",txt:"De 3 aksjene i Oversikt med størst prosentvis nedgang i dag. Alltid 3 grønne og 3 røde — uavhengig av om markedet er opp eller ned totalt."},
                    {dot:"yellow",label:"Orb-stemning (BULLISH / NØYTRAL / BEARISH)",txt:"Beregnes fra gjennomsnittlig prosentendring på tvers av ALLE aksjene du følger. Ikke et mål på S&P 500 eller det generelle markedet — kun dine AI-aksjer."},
                    {dot:"yellow",label:"Statslinjen nederst",txt:"Viser antall aksjer opp/ned, antall fulgte aksjer, snittendring og stemningsetiketten. Alt basert på din personlige portefølje av AI-aksjer."},
                  ]
                },
                {cat:"marked",
                  icon:"📡", title:"Futures & Premarket — Hva ser tradere på?",
                  body:"Futures handles 23 timer i døgnet og gir deg en pekepinn på hvor markedet åpner FØR børsen åpner kl 09:30 ET. Stripen øverst på hjemssiden viser de viktigste indikatorene.",
                  items:[
                    {dot:"green",label:"ES=F — S&P 500 Futures",txt:"Prisen på en kontrakt for levering av S&P 500 indeksen. Stiger ES=F om natten → markedet åpner sannsynligvis opp. Faller det → rød åpning. Den viktigste enkeltindikatoren for neste dags retning."},
                    {dot:"green",label:"NQ=F — NASDAQ 100 Futures",txt:"Som ES=F men for tech-tunge NASDAQ. Viktigere for AI-aksjer enn S&P 500-futuresen siden NVDA, MSFT og AAPL veier tungt her."},
                    {dot:"yellow",label:"VIX — Fryktindeksen",txt:"Implisert 30-dagers volatilitet på S&P 500. Over 20 = markedet er nervøst. Over 30 = frykt. Over 40 = panikk. Merk: VIX beveger seg MOTSATT av markedet — rød pil i VIX er egentlig positivt."},
                    {dot:"yellow",label:"10Y Yield (TNX)",txt:"Renten på 10-årige amerikanske statsobligasjoner. Stiger renten raskt → dyrere lån → vekstaksjer og tech presses ned. Faller renten → positivt for AI/tech. Under 4% er generelt bra for vekstaksjer."},
                    {dot:"green",label:"Premarket-data i Oversikt",txt:"Oversikt-kortene viser 'PRE +/-X%' hvis Yahoo Finance har premarket-data tilgjengelig. Vises kun utenfor børsens åpningstid (09:30–16:00 ET)."},
                  ]
                },
                {cat:"marked",
                  icon:"📈", title:"Neste dag signal — Sannsynlighetsindikator",
                  body:"Panelet på hjemssiden aggregerer fem signaler til en samlet score 0–100. Det er ikke spådom — det er sannsynlighets-skewing. En score på 25 betyr ikke at markedet vil falle, men at odds-ene er mot deg.",
                  items:[
                    {dot:"green",label:"S&P / NQ Futures (0–25 poeng)",txt:"Gjennomsnittlig retning på ES=F og NQ=F. Begge opp > 1%: 25p. Begge i minus: 0–8p. Den viktigste enkeltfaktoren."},
                    {dot:"yellow",label:"VIX Fryktindeks (0–20 poeng)",txt:"Kombinerer VIX-nivå og 5-dagers trend. Lav VIX som faller gir 20 poeng. Høy VIX som stiger gir 0 poeng."},
                    {dot:"yellow",label:"Bredde — AI-aksjer (0–20 poeng)",txt:"Prosentandelen av dine fulgte aksjer som stiger i dag. Over 70% opp: 20p. Under 40% opp: 0p. Bred deltakelse er sunnere enn at bare 2–3 store aksjer trekker opp."},
                    {dot:"yellow",label:"Asia: Samsung / Hynix / TSMC (0–20 poeng)",txt:"Disse tre er upstream-leverandører i AI-verdikjeden. Faller Samsung og SK Hynix kraftig → dårlig nytt for NVDA og hele halvledersektoren neste dag. Tidlig varslingssystem siden de handler mens USA sover."},
                    {dot:"yellow",label:"10Y Rente (0–15 poeng)",txt:"Nivå (under 4% er bra) og 5-dagers trend (fallende rente = positivt for tech). Stigende rente er den største motvinden for AI-aksjer."},
                    {dot:"green",label:"Score-skala",txt:"0–25: BEARISH  ·  25–40: FORSIKTIG  ·  40–58: NØYTRAL  ·  58–72: POSITIV  ·  72–100: BULLISH"},
                  ]
                },
                {cat:"marked",
                  icon:"⚠️", title:"Krasj-Radar — Systemiske risikoadvarsler",
                  body:"Krasj-Radar overvåker signaler som historisk har varslet markedskrakk og resesjoner — ofte 3–12 måneder i forveien. I 2008 var alle disse røde lenge FØR Lehman Brothers kollapset. I 2020 kom signalene brattere. Dette er ikke dagsindikatorer — de er systemiske tidligadvarsler.",
                  items:[
                    {dot:"red",label:"💳 Kredittspread (HYG vs LQD)",txt:"Sammenligner 5-dagers avkastning for høyrente-obligasjoner (HYG) mot investment grade (LQD). Når risikofylte selskaper må betale mye mer enn trygge selskaper for å låne penger, er det et signal om at noe er galt i kredittmarkedet — og dette skjer ALLTID før aksjer faller. > 0.5%: stress. > 2%: krise."},
                    {dot:"red",label:"📉 VIX Terminstruktur (VIX/VIX3M-ratio)",txt:"Normalt er VIX3M (3-måneders volatilitet) høyere enn VIX (30-dagers) — markedet priser inn mer usikkerhet på sikt. Når dette inverteres (VIX > VIX3M, ratio > 1.0) betyr det at akutt kortsiktig frykt dominerer. Dette skjedde uker FØR COVID-krasjet i 2020. Ratio > 1.0: advarsel. > 1.1: alvorlig."},
                    {dot:"yellow",label:"⚙️ CBOE Options Skew",txt:"Skew-indeksen måler prisen på langt-OTM put-opsjoner vs call-opsjoner. Høy skew = institusjonelle investorer og hedgefond kjøper massiv forsikring mot store fall. De vet noe — eller de er veldig nervøse. Vanligvis 110–130. Over 130: forhøyet. Over 145: ekstrem tail-risk prising."},
                    {dot:"red",label:"📈 Rentekurven (3mnd vs 10år)",txt:"Normalt gir langsiktige obligasjoner (10 år) høyere rente enn kortsiktige (3 måneder). Når 3-månedersrenten er HØYERE enn 10-årsrenten (invertert kurve), er det et av de mest pålitelige resesjonssignalene som finnes — varslet alle resesjoner i USA siden 1970. Invertert i 2006–2007 (18 måneder FØR 2008-krasjet) og i 2019 (FØR COVID)."},
                    {dot:"yellow",label:"🌪️ VVIX — Volatilitet av VIX",txt:"Mens VIX måler forventet volatilitet i aksjer, måler VVIX volatiliteten i VIX selv. Ekstremt høy VVIX betyr at markedet ikke bare er nervøst — det vet ikke engang hvor nervøst det skal være. Signal på at store sjokk prises inn. Under 95: rolig. 95–120: forhøyet. Over 120: ekstremt."},
                    {dot:"yellow",label:"📊 CFTC Hedge fund posisjonering (COT)",txt:"CFTC (Commodity Futures Trading Commission) publiserer ukentlig data på spekulativ posisjonering i futures-markedet. 'Leveraged money' (hedgefond) sine netto long/short-posisjoner i S&P 500 futures (ES=F). Ekstremt strukket long = markedet er crowded og sårbart for en korreksjon. Ekstremt strukket short = potensial for et bearish short squeeze. Data er 3 dager forsinket og oppdateres fredager."},
                  ]
                },
                {cat:"marked",
                  icon:"🌡️", title:"SPX/SPY Command Center",
                  body:"Øverst på Marked-siden finner du et overblikk over S&P 500-markedet spesifikt tilpasset opsjonshandlere:",
                  items:[
                    {dot:"yellow",label:"Implisert daglig bevegelse",txt:"VIX ÷ 16 = forventet daglig bevegelse i SPY (±%). VIX på 20 betyr at markedet priser inn ±1.25% per dag."},
                    {dot:"yellow",label:"Implisert ukentlig bevegelse",txt:"VIX ÷ √52 = forventet ukentlig bevegelse. Nyttig for å velge strikes på ukentlige SPX-opsjoner."},
                    {dot:"green",label:"0DTE Kalender",txt:"SPX utløper mandag, onsdag og fredag. 0DTE (zero days to expiration) opsjoner er svært populære — høy tidsverdiforfald men ekstrem gamma-risiko."},
                    {dot:"green",label:"SPY Tekniske indikatorer",txt:"Rask oversikt over SPY-trenden: RSI-sone, Bollinger squeeze og om SPY handler over/under MA50."},
                  ]
                },
                {cat:"opsjoner",
                  icon:"🎯", title:"Gamma Regime",
                  body:"Market makers (store banker og meglerhus) må kontinuerlig sikre seg mot opsjoner de har solgt. Gamma beskriver hvor mye de må justere sine posisjoner når kursen beveger seg.",
                  items:[
                    {dot:"green",label:"POSITIV GAMMA",txt:"Market makers kjøper når kursen faller og selger når den stiger → de demper volatilitet. Markedet er mer stabilt og tenderer til å mean-reverte."},
                    {dot:"red",label:"NEGATIV GAMMA",txt:"Market makers gjør det motsatte → de forsterker bevegelser. Kursen kan gå langt og fort i en retning. Farlig miljø for svake posisjoner."},
                  ]
                },
                {cat:"opsjoner",
                  icon:"📍", title:"Gamma Flip",
                  body:"Prisnivået der markedet skifter mellom positiv og negativ gamma. Handler aksjen OVER flipnivået → stabilt. Faller den UNDER → volatilitet kan eksplodere. Et av de viktigste nivåene å overvåke.",
                  items:[]
                },
                {cat:"opsjoner",
                  icon:"🌡️", title:"IV Percentile (Implied Volatility)",
                  body:"Viser hvor høy den implisitte volatiliteten er i dag sammenlignet med de siste 52 ukene. IV er markedets forventning om fremtidig kursbevegelse, og prises inn i opsjoner.",
                  items:[
                    {dot:"green",label:"0–40",txt:"Lav IV — opsjoner er billige. Godt tidspunkt å kjøpe opsjoner hvis du forventer en stor bevegelse."},
                    {dot:"yellow",label:"40–70",txt:"Normal IV — rundt historisk snitt. Ikke spesielt billig eller dyrt."},
                    {dot:"red",label:"70–100",txt:"Høy IV — opsjoner er dyre. Markedet er nervøst og priser inn stor usikkerhet. Selling strategies kan lønne seg."},
                  ]
                },
                {cat:"opsjoner",
                  icon:"📞", title:"Call/Put Ratio",
                  body:"Forholdet mellom antall kjøpte calls (rett til å kjøpe) og puts (rett til å selge). Viser hva det smarte pengene satser på.",
                  items:[
                    {dot:"green",label:"Over 2.0x",txt:"Svært bullish — langt flere calls enn puts. Institusjonelle investorer posisjonerer seg for oppgang."},
                    {dot:"yellow",label:"1.0–2.0x",txt:"Nøytralt til bullish — balansert marked med lett overvekt av optimisme."},
                    {dot:"red",label:"Under 1.0x",txt:"Bearish — flest puts. Markedet sikrer seg mot nedgang eller satser aktivt på fall."},
                  ]
                },
                {cat:"opsjoner",
                  icon:"🔥", title:"Pressure Score",
                  body:"Vår sammensatte score (0–100) som måler hvor 'spent' markedet er rundt en aksje. Høy score betyr ikke nødvendigvis at aksjen faller — men at noe er i gjære. Beregnes fra 5 faktorer:",
                  items:[
                    {dot:"yellow",label:"Earnings Risiko",txt:"Hvor stor forventet kursreaksjon på neste kvartalsrapport (25 poeng maks)"},
                    {dot:"yellow",label:"Gamma Regime",txt:"Negativ gamma forsterker bevegelser (20 poeng)"},
                    {dot:"yellow",label:"IV Percentile",txt:"Høy IV = markedet priser inn stor usikkerhet (20 poeng)"},
                    {dot:"yellow",label:"Opsjonsaktivitet",txt:"Uvanlig høy aktivitet signalerer at noen vet noe (20 poeng)"},
                    {dot:"yellow",label:"Call/Put Ratio",txt:"Svært bearish ratio gir høy score (20 poeng)"},
                  ]
                },
                {cat:"opsjoner",
                  icon:"📐", title:"Expected Move",
                  body:"Hva opsjonsmarkedet priser inn som forventet kursbevegelse (±) rundt neste earnings. Beregnes fra ATM straddle-prisen: pris på call + pris på put ved dagens kurs. Representerer ±1 standardavvik (~68% sannsynlighet kursen lander innenfor).",
                  items:[
                    {dot:"green",label:"Under ±5%",txt:"Rolig aksje — markedet forventer liten reaksjon på earnings"},
                    {dot:"yellow",label:"±5–9%",txt:"Moderat bevegelse forventet — vanlig for de fleste tech-aksjer"},
                    {dot:"red",label:"Over ±9%",txt:"Stor bevegelse forventet — høy risiko, men også høy potensiell gevinst"},
                  ]
                },
                {cat:"fundamental",
                  icon:"📅", title:"Earnings Beat Rate",
                  body:"Prosentandelen av kvartalene der selskapet slo analytikernes estimater de siste 2–3 år. Et selskap som konsekvent slår estimater er forutsigbart og tryggere å holde rundt rapporteringsdatoer.",
                  items:[
                    {dot:"green",label:"Over 85%",txt:"Svært pålitelig — slår estimater nesten alltid"},
                    {dot:"yellow",label:"65–85%",txt:"Variabelt — sjekk de siste kvartalene for trend"},
                    {dot:"red",label:"Under 65%",txt:"Upålitelig — høy risiko rundt earnings"},
                  ]
                },
                {cat:"fundamental",
                  icon:"💹", title:"Revenue Growth / Gross Margin / Forward P/E / PEG",
                  body:"Klassiske fundamentale nøkkeltall for å vurdere om selskapet er sunt og riktig priset.",
                  items:[
                    {dot:"green",label:"Revenue Growth",txt:"Omsetningsvekst år over år. Over 25% er eksepsjonelt i dagens marked."},
                    {dot:"green",label:"Gross Margin",txt:"Bruttomargin — viser prismakt. Over 70% indikerer et svært lønnsomt produkt."},
                    {dot:"yellow",label:"Forward P/E",txt:"Pris/inntjening basert på neste års estimater. Høy P/E = høye vekstforventninger priset inn — liten feilmargin."},
                    {dot:"green",label:"PEG Ratio (Price/Earnings to Growth)",txt:"PEG = Forward P/E ÷ forventet EPS-vekst. Oppfunnet av Peter Lynch som et bedre mål enn P/E alene — fordi det justerer for veksten du faktisk får. Et selskap med P/E 35 og 35% vekst (PEG 1.0) er billigere enn et selskap med P/E 20 og 5% vekst (PEG 4.0). Tommelregel: PEG under 1 = undervurdert, 1–1.5 = rettferdig priset, over 2.5 = dyrt. For ikke-lønnsomme selskaper (PLTR, IONQ) er PEG meningsløs — se heller på pris/salg og fremtidig lønnsomhetsbane."},
                  ]
                },
                {cat:"grafen",
                  icon:"📊", title:"RSI — Relative Strength Index",
                  body:"RSI måler styrken i en kursbevegelse på en skala 0–100. Beregnes fra de siste 14 dagene og viser om en aksje har steget eller falt for raskt. Brukes i Opsjons Signal-fanen i Deep Dive.",
                  items:[
                    {dot:"green",label:"Under 30 — Oversold",txt:"Aksjen har falt for raskt og kan bounce. Potensielt kjøpssignal, spesielt kombinert med positiv gamma."},
                    {dot:"yellow",label:"30–70 — Nøytral",txt:"Normal sone. Ingen sterk retningsindikasjon — se på andre faktorer."},
                    {dot:"red",label:"Over 70 — Overbought",txt:"Aksjen har steget for raskt og kan korrigere. Potensielt salgs- eller put-signal."},
                  ]
                },
                {cat:"grafen",
                  icon:"📉", title:"Bollinger Bands & Squeeze",
                  body:"Bollinger Bands er tre linjer rundt kursen: midtlinje (MA20) og øvre/nedre bånd ±2 standardavvik. Når båndene snevres inn kalles det en Squeeze — kursen har beveget seg lite over tid, og en stor bevegelse er ofte nær forestående. Retningen vet vi ikke, men volatilitet kommer.",
                  items:[
                    {dot:"green",label:"BB Squeeze",txt:"Båndbredde under 8% — komprimert energi. Kombiner med RSI og gamma for å avgjøre retning. Straddle kan lønne seg."},
                    {dot:"yellow",label:"Normal båndbredde",txt:"Markedet beveger seg normalt. Ingen squeeze-signal akkurat nå."},
                    {dot:"red",label:"Veldig vide bånd",txt:"Høy volatilitet er allerede priset inn — opsjoner er dyre. Vurder å selge premium i stedet."},
                  ]
                },
                {cat:"grafen",
                  icon:"📈", title:"Moving Averages — trendkryss og momentum",
                  body:"Glidende gjennomsnitt jevner ut kursen over tid. Appen bruker tre MA-er med ulike formål: EMA20 (kortsiktig momentum), MA50 (mellomlang trend) og MA200 (langsiktig trend).",
                  items:[
                    {dot:"green",label:"MA20 > MA50 — Momentum-kryss",txt:"Brukes i de eldre oppsettene for å identifisere momentum-setups. MA20 over MA50 viser at kortsiktig fart er sterkere enn mellomlang trend — typisk sett tidlig i en oppgang."},
                    {dot:"green",label:"Golden Cross (MA50 > MA200)",txt:"Vises i Deep Dive teknisk panel. MA50 krysser over MA200 = langsiktig bullish trendskifte. Kraftig signal som institusjonelle investorer følger nøye."},
                    {dot:"red",label:"Death Cross (MA50 < MA200)",txt:"MA50 under MA200 = langsiktig bearish signal. Kan gi vedvarende nedgang over måneder."},
                    {dot:"yellow",label:"Pris vs MA",txt:"Handel over MA50 = kortsiktig styrke. Under MA50 = svakhet. Under MA200 = langsiktig bjørnemarked for aksjen."},
                  ]
                },
                {cat:"grafen",
                  icon:"🔥", title:"Volum Spike",
                  body:"Sammenligner dagens handelsvolum mot 20-dagers gjennomsnittsvolum. Uvanlig høyt volum er et av de sterkeste signalene i teknisk analyse — det betyr at institusjoner, hedgefond eller innsidere er aktive. Vises i Opsjons Signal-fanen.",
                  items:[
                    {dot:"red",label:"3x+ snitt — Ekstrem spike",txt:"Noen vet noe, eller en stor aktør tar posisjon. Svært sjeldent og svært betydningsfullt."},
                    {dot:"yellow",label:"2–3x snitt — Uvanlig høyt",txt:"Klar avvik fra normalen. Kombiner med prisretning — opp + høyt volum = sterkt kjøpssignal."},
                    {dot:"yellow",label:"1.5–2x snitt — Forhøyet",txt:"Over gjennomsnittet men ikke ekstremt. Hold øye med om det fortsetter."},
                  ]
                },
                {cat:"opsjoner",
                  icon:"🎯", title:"Opsjons-strategier — hva betyr de?",
                  body:"Opsjons Signal i Deep Dive anbefaler en av disse strategiene basert på RSI, IV, Bollinger og gamma kombinert:",
                  items:[
                    {dot:"green",label:"BUY CALL",txt:"RSI oversold + lav IV + positiv gamma. Kjøp en call-opsjon 5–10% OTM med 30–60 dager til utløp (DTE)."},
                    {dot:"red",label:"BUY PUT",txt:"RSI overbought + lav IV. Kjøp en put-opsjon for å tjene på nedgang."},
                    {dot:"green",label:"BUY STRADDLE",txt:"Bollinger squeeze + earnings nær. Kjøp både call og put ved samme strike — du tjener på stor bevegelse uansett retning."},
                    {dot:"yellow",label:"SELL PREMIUM",txt:"IV er svært høy uten nær earnings. Selg opsjoner (iron condor, cash-secured put) og la tidsverdien jobbe for deg."},
                    {dot:"green",label:"BULL CALL SPREAD",txt:"Golden cross + moderat IV. Kjøp ATM call og selg OTM call — reduserer kostnad, begrenset oppsidepotensial."},
                    {dot:"yellow",label:"BUY OPTIONS",txt:"Bollinger squeeze + lav IV. Stor bevegelse på vei men ukjent retning — straddle eller retningsbestemt avhengig av andre signaler."},
                  ]
                },
                {cat:"opsjoner",
                  icon:"⚡", title:"High Conviction Setups — Signaler-siden",
                  body:"Signaler-siden grupperer aksjer etter konkrete opsjons-setups. Hver type har spesifikke betingelser som må være oppfylt. Jo høyere score, jo sterkere er setuppet:",
                  items:[
                    {dot:"yellow",label:"📅 EARNINGS PLAY (95p)",txt:"Bollinger squeeze + earnings 5–21 dager unna + lav IV. Kjøp opsjoner nå mens IV ennå er lav — IV stiger inn mot rapporten."},
                    {dot:"yellow",label:"⚡ SQUEEZE PLAY (90p)",txt:"Bollinger squeeze + nøytral RSI + lav IV. Stor bevegelse er nær — retning ukjent, vurder straddle."},
                    {dot:"yellow",label:"📊 VOLUME BREAKOUT (88p)",txt:"Volum 2.5x+ snitt + pris opp > 1.5% + RSI 45–70. Institusjoner er inne — momentum-play."},
                    {dot:"green",label:"🎯 OVERSOLD BOUNCE (85p)",txt:"RSI under 33 + positiv gamma. Klassisk reversal-setup — kjøp calls når markedet gir opp."},
                    {dot:"green",label:"🚀 MOMENTUM (75p)",txt:"MA20 > MA50 + RSI 52–68 + pris over MA50 + positiv gamma. Kortisiktig momentum sterkere enn mellomlang trend — trendcontinuation."},
                    {dot:"green",label:"💎 DEEP VALUE (65p)",txt:"Over 25% under 52-ukerstopp + RSI under 45. Potensiell gjeninnhenting for tålmodige investorer."},
                    {dot:"red",label:"⚠️ REVERSAL WATCH (60p)",txt:"RSI over 72 + negativ gamma. Advarselsignal — vurder å ta gevinst eller kjøpe puts."},
                  ]
                },
                {cat:"grafen",
                  icon:"🏦", title:"Candlestick-mønstre — signaler i grafen på Aksje-siden",
                  body:"grafen på Aksje-siden oppdager 9 mønstre automatisk. Trykk 'Lys' for stearinlysgraf, 'Mønstre' for markeringer, 'S/R' for støtte/motstand-linjer. NB: ingen signal virker alene — kombiner alltid 2–3 for bekreftelse.",
                  items:[
                    {dot:"yellow",label:"RVOL↑ (amber sirkel, nedre kant) — Høy RVOL grønn dag",txt:"RVOL ≥ 1.5× på en grønn dag. OBS: dette betyr stor institusjonell aktivitet — men IKKE nødvendigvis kjøp. Det kan like gjerne være distribusjon (institusjonene selger inn i styrken). Kontekst avgjør: kommer signalet etter en lang nedgang nær støtte → sannsynlig akkumulering. Kommer det etter en lang oppgang nær motstand → pas på, kan være distribusjon. Aldri handle RVOL-signalet alene — kombiner med LIKVIDITETSJAKT eller MA-BOUNCE for bekreftelse."},
                    {dot:"red",label:"RVOL↓ (rød sirkel, øvre kant) — Høy RVOL rød dag",txt:"RVOL ≥ 1.5× på en rød dag. Stort salgspress — kan være panikkutsalg fra retailinvestorer (kjøpsmulighet hvis nær støtte) eller aktiv distribusjon fra institusjoner (advarsel). Ser du RVOL↓ etter en lang oppgang: institusjonene selger antakelig. Ser du RVOL↓ etter et krasj med lang rød hale og rask reversering: kan være ett-dags capitulation — entry-mulighet."},
                    {dot:"purple",label:"🎯 LIKVIDITETSJAKT (lilla pil opp, nedre kant)",txt:"Stearinlyset dukker under EMA20 eller MA50 og stenger OVER igjen — med lang nedre hale. Algoritmer trigget stop-losses under støtten og hentet billige aksjer. Svært presist reversal-signal. Best for: 30–40 DTE calls med stop rett under wickens lavpunkt. Høyest treffsikkerhet av alle signalene."},
                    {dot:"blue",label:"↩ REJECTION WICK (blå pil opp, nedre kant)",txt:"Lang nedre hale (wick) uten spesifikk MA-berøring. Stearinlysets kropp (åpning→lukking) er liten, men halen er minst 2.5× kroppens lengde — prisen ble dyttet langt ned i løpet av dagen, men kjøperne tok over og presset den tilbake opp. Selgerne ble absorberte og avvist. Forskjell fra LIKVIDITETSJAKT: rejection wick skjer hvor som helst, likviditetsjakt skjer spesifikt under EMA20/MA50 for å tappe stop-losses. Best for: kortsiktig 30–40 DTE call. Stop under wickens lavpunkt — brytes den, er signalet ugyldig."},
                    {dot:"green",label:"E20 (lysegrønn sirkel, nedre kant) — EMA20-bounce",txt:"Prisen berørte EMA20 presist og stengte over. EMA20 er den raskeste glidende snittet og følges av algoritmer og day-tradere. Bounce her er hyppig men kortvarig — typisk 3–10 dagers bevegelse. Best for: 30–45 DTE call nært pengene. Stop: stenger under EMA20 i to påfølgende dager."},
                    {dot:"green",label:"M50 (mørkegrønn firkant, nedre kant) — MA50-bounce",txt:"Prisen berørte MA50 presist og stengte over. MA50 er det institusjonelle snittet — pensjonsfond, hedgefond og ETF-rebalanseringer bruker dette nivået aktivt. Bounce her er sjeldnere men kraftigere og holder lenger. Best for: aksje/LEAPS eller 45–60 DTE call. Stop: stenger under MA50. Merk: firkant (ikke sirkel) for å skille fra EMA20."},
                    {dot:"orange",label:"ACC AKKUMULERING (oransje firkant, øvre kant)",txt:"Aksjen har beveget seg sidelengs i under 6% range de siste 15 dagene med synkende volum i andre halvdel. Dette er Wyckoff-akkumulering — institusjonene samler stille mens retailinvestorer kjedner seg og selger. Best for: langsiktig hold eller LEAPS (6–12 mnd). Breakout ut av rangen er entry-signalet."},
                    {dot:"green",label:"🔨 HAMMER (grønn pil opp, nedre kant)",txt:"Stearinlys med liten kropp øverst og lang nedre veike (≥2× kroppen), nesten ingen øvre veike — og det dukker opp etter minimum 2% nedgang de siste 5 dagene. Selgerne presset prisen hardt ned, men kjøperne stormet inn og hentet den tilbake til toppen av dagen. En grønn hammer er sterkere enn en rød. Best for: 30–45 DTE call. Stop: under hammeres lavpunkt."},
                    {dot:"red",label:"⭐ SHOOTING STAR (rød pil ned, øvre kant)",txt:"Speilbilde av Hammeren — liten kropp nederst, lang øvre veike (≥2× kroppen), nesten ingen nedre veike — etter minimum 2% oppgang siste 5 dager. Kjøperne presset prisen høyt opp, men selgerne tok over og banket den ned igjen til bunnen av dagen. Bearish advarsel: vurder å ta gevinst eller kjøpe puts. Stop for shorts: over Shooting Star-ens høyeste punkt."},
                    {dot:"orange",label:"TRAP (oransje sirkel, øvre kant) — Bull Trap / Falsk rally",txt:"Prisen stiger ≥1% på lav RVOL (under 0.7×) inn i en kjent MA-motstand — ingen stor aktør kjøper. Oppgangen skjer i tomrom fordi selgerne er borte én dag, ikke fordi kjøperne er tilbake. Signal er skjerpet til å kun vises nær MA-nivåer, som reduserer falske alarmer. Vent på en grønn dag med RVOL↑ som bryter over MA for ekte bekreftelse."},
                    {dot:"blue",label:"B20 (cyan pil opp, nedre kant) — 20-dagers breakout",txt:"Aksjen stenger på ny 20-dagers høy med RVOL ≥ 1.5×. Det er det klassiske momentum-signalet brukt av CAN SLIM og IBD: et prisbreakout bekreftet av institusjonsvolum. Skiller seg fra en vanlig oppdag ved at prisen faktisk setter ny high — selgere som har sittet og ventet fra tidligere topper er nå villige til å la aksjen gå høyere. Best for: 30–45 DTE call, stop under breakout-dagen. RVOL ≥ 2.0 = sterkt signal."},
                    {dot:"purple",label:"RSI↗ (lilla pil opp, nedre kant) — RSI-divergens (bullish)",txt:"Prisen setter en lavere bunn enn forrige bunn, men RSI setter en høyere bunn. Det betyr at selv om prisen faller videre, mister selgerne kraft — momentum snur før prisen. Et av de mest pålitelige reversal-signalene på kort sikt (1–3 uker). Kun vist når begge bunnene er i RSI under 50 (faktisk oversold) og det nyeste bunnen er innenfor siste 10 handelsdager. Best for: 30–45 DTE call mot neste motstandsnivå. Stop: under den nye bunnen."},
                  ]
                },
                {cat:"grafen",
                  icon:"📊", title:"RVOL — Relativ Volum",
                  body:"RVOL (Relative Volume) er daglig volum delt på gjennomsnittlig volum de siste 20 handelsdager. Det forteller deg OM noe viktig skjer, uten at du trenger å kjenne det absolutte volumet.",
                  items:[
                    {dot:"green",label:"RVOL ≥ 2.0× — Ekstraordinær aktivitet",txt:"To ganger normalt volum eller mer. Institusjonelle ordrer, nyheter, eller en stor aktør som bygger/avvikler posisjon. Volumbars lyser opp i grafen (høy opacity). Ta signalet på alvor."},
                    {dot:"yellow",label:"RVOL 1.5–2.0× — Forhøyet interesse",txt:"Merk i grafen med lysere farger. Ikke nok alene, men i kombinasjon med et prisbreakout eller mønster er det bekreftelse nok til å handle."},
                    {dot:"red",label:"RVOL under 0.5× — Lav overbevisning",txt:"Halvparten av normalt volum. Bevegelser på lav-RVOL-dager er upålitelige — de kan reversere fort. Unngå å chasse breakouts på veldig lav RVOL."},
                    {dot:"yellow",label:"RVOL↑ sirkel men volumbar er RØD — hvordan?",txt:"Disse to sammenligner forskjellige ting. Volumbar = lukking vs GÅRSDAGENS lukking (rød = lavere enn i går). RVOL↑ = lukking vs DAGENS åpning (grønt stearinlys = stengte over åpningen). Eksempel: i går $100, i dag åpnet $90 (gap ned), stengte $93. Volumbar er RØD ($93 < $100) men RVOL↑ vises fordi $93 > $90 (grønt stearinlys, høy aktivitet). Betyr: panikken drev volumet opp, men kjøperne absorberte fallet i løpet av dagen. Ganske lik en Hammer — verdt å følge med neste dag."},
                  ]
                },
                {cat:"grafen",
                  icon:"📐", title:"Støtte og Motstand (S/R-linjer)",
                  body:"Trykk 'S/R'-knappen i grafen på Aksje-siden for å tegne automatiske støtte- og motstandsnivåer. Grønne stiplede linjer = støtte (gulv), røde stiplede linjer = motstand (tak). Appen finner pivot-punkter — steder der prisen har snudd minst én gang — og grupperer nivåer som er nære hverandre.",
                  items:[
                    {dot:"green",label:"Støtte (S) — det usynlige gulvet",txt:"Et prisnivå der aksjen har stoppet å falle og snudd oppover flere ganger. Kjøperne ser på dette nivået som 'billig nok' og strømmer inn. Jo flere ganger prisen har snudd på samme nivå, jo sterkere er støtten (S2, S3 i grafen = testet 2–3 ganger)."},
                    {dot:"red",label:"Motstand (R) — det usynlige taket",txt:"Et prisnivå der aksjen har stoppet å stige og snudd nedover. Selgerne mener prisen er 'dyr nok' her. Motstand som brytes med høy RVOL er et sterkt breakout-signal — det gamle taket blir ofte det nye gulvet."},
                    {dot:"yellow",label:"Rollebytte — gammelt gulv blir tak",txt:"Når støtte brytes (prisen faller gjennom gulvet), snur psykologien. De som kjøpte på støttenivået sitter nå med tap og venter på å 'gå i null' når prisen stiger tilbake. Dette salgspresset gjør at det gamle gulvet nå fungerer som tak. Se etter Shooting Star eller Doji når prisen tester et gammelt støttenivå nedenfra."},
                    {dot:"green",label:"Den gylne kombinasjonen",txt:"Hammer eller Doji + RVOL↑ + Støttelinje på samme dag = svært sterkt kjøpssignal. Alle tre bekrefter det samme: prisen har truffet et kjent gulv, kjøperne er tilbake, og volumet bekrefter at det ikke er tilfeldig. Dette er setuppet du venter på."},
                  ]
                },
                {cat:"grafen",
                  icon:"⭐", title:"Golden Setups — navngitte multi-signal kombinasjoner",
                  body:"Når flere mønstre treffer på samme aksje innenfor en uke kombinerer appen dem til navngitte Golden Setups. Disse ligger under «Eldre oppsett» i Watchlist › Signaler og har historisk høyere treffsikkerhet enn enkeltmønstre alene — nettopp fordi flere uavhengige signaler peker i samme retning. Signal Quality Score (★ X/5) viser antall unike mønstertyper siste 7 dager.",
                  items:[
                    {dot:"purple",label:"🎯 Institutional Sweep — LIKVIDITETSJAKT + RVOL↑ + Nær MA50",txt:"Det kraftigste kjøpssignalet i appen. Algoritmer dyttet prisen under MA50 for å jakte stop-losses, institusjonene absorberte alt salget, og prisen snappet tilbake over MA50 på høyt RVOL. Du vet at en stor kjøper nettopp bygget posisjon — og de vil forsvare den. Best for: 30–45 DTE call, stop under sweepets lavpunkt."},
                    {dot:"red",label:"⭐ Distribution Reversal — SHOOTING STAR + RVOL + Nær MA200/Motstand",txt:"Selgerne vant kampen ved et kjent tak. Shooting Star viser at kjøperne prøvde å presse opp men ble fullstendig knust, og høy RVOL bekrefter at det var institusjonelle selgere — ikke panikk. Motsatt av Institutional Sweep. Best for: ta gevinst, kjøp puts, eller selg covered calls."},
                    {dot:"yellow",label:"🚀 Wyckoff Launchpad — AKKUMULERING + RVOL↑ ≥2.0×",txt:"Wyckoff-metoden beskriver en syklus der institusjonene stille samler aksjer i en konsolidering (lav volatilitet, synkende volum) før de driver prisen opp. Appen detekterer 15+ dager med under 6% prisrange etterfulgt av et breakout på dobbelt eller mer enn normalt volum. Optimalt R/R fordi stop er tett (under rangen) og target er udefinert oppside."},
                    {dot:"orange",label:"⚠️ Fakeout Warning — BULL TRAP + Nær MA-linje",txt:"Aksjen stiger svakt på lav RVOL inn i en kjent MA-motstand. Ingen institusjonell kjøper — bare selgere som er borte en dag. Møtet med MA-linja kan trigge en reversal ned. Unngå FOMO-kjøp her. Nyttig for å selge dekket call eller ta gevinst på eksisterende long-posisjon."},
                    {dot:"yellow",label:"★ Signal Quality Score — stjerner per aksje",txt:"Vises som '★ 3/5' på hvert eldre setup-kort i Watchlist › Signaler. Teller antall unike mønstertyper (fra mønsterdeteksjonen i grafen på Aksje-siden) som har truffet på aksjen de siste 7 dagene. 1 mønster = 2/5, 2 mønstre = 3/5, 3 mønstre = 4/5, 4+ mønstre = 5/5. Høy score betyr at markedet sender mange uavhengige signaler om det samme — økt sannsynlighet for at trenden holder."},
                  ]
                },
                {cat:"marked",
                  icon:"🇯🇵", title:"Yen Carry Trade — Systemstress-radar",
                  body:"En av de viktigste, men minst forståtte, risikofaktorene i globale markeder. Japan har i årevis hatt nær null prosent rente, noe som har gjort det billig å låne yen og investere i dollar-aktiva med høyere avkastning — kalt 'carry trade'. Denne seksjonen under I dag › Makro overvåker om denne handelen er i ferd med å reversere.",
                  items:[
                    {dot:"blue",label:"USD/JPY kurs og 10-dagers endring",txt:"USD/JPY viser hvor mange yen én dollar kjøper. Høy kurs (f.eks. 155) = dollar sterk, yen svak = carry trade intakt. Lav kurs (f.eks. 140) = yen styrkes. En rask fall på 3–5% på 10 dager er faresignalet — carry trade avvikles."},
                    {dot:"red",label:"Carry Trade avvikling — mekanismen",txt:"Når yen styrkes brått: 1) Fond som har lånt yen og kjøpt dollar-aksjer taper på valuta. 2) De tvinges til å SELGE US aksjer for å kjøpe tilbake yen og nedbetale lånene. 3) Dette salget presser SPY og tech-aksjer ned — ikke pga. selskapenes resultater, men pga. tvangssalg. Prosessen tar typisk 2–5 dager og kan skje veldig fort."},
                    {dot:"yellow",label:"Fed vs BoJ rentespread",txt:"Spread = US 10Y rente minus japansk styringsrente (0.75%). Jo høyere spread, jo mer lønnsomt er det å låne yen og investere i US obligasjoner. Spread over 3.5%: carry trade svært lønnsom, yen svak. Under 2%: spredningen snevres, carry trade krevende — risiko for unwind."},
                    {dot:"orange",label:"LAV / MODERAT / HØY / KRITISK",txt:"Risikonivåene beregnes fra kombinasjon av 10-dagers USD/JPY-endring og rentespread. HØY eller KRITISK betyr at det er forhøyet risiko for at carry trade-avvikling kan presse US aksjer ned i nærmeste fremtid."},
                  ]
                },
                {cat:"marked",
                  icon:"📈", title:"Makrokorrelasjonskart — med lag-analyse og Pearson",
                  body:"Det store interaktive chartet under I dag › Makro viser SPY og fire makroindikatorer normalisert til prosentvis endring fra startpunktet — slik at du kan sammenligne ting som ellers har helt ulike skalaer (VIX på 20 vs SPY på 580). Hoverfunksjonen viser alle verdier for valgt dato.",
                  items:[
                    {dot:"green",label:"SPY (alltid synlig, grønn)",txt:"Referanselinje. Alt annet sammenlignes mot denne. Opp = bullish periode. Ned = bearish periode i vinduet."},
                    {dot:"red",label:"VIX (rød) — Fryktindeksen",txt:"Stiger når markedet er nervøst. Typisk NEGATIV korrelasjon med SPY — VIX opp mens SPY ned. Brukes som bekreftelsessignal, ikke som ledende indikator alene siden den er reaktiv."},
                    {dot:"orange",label:"HYG/LQD (gul) — Kredittspreads",txt:"Forholdet mellom high-yield obligasjoner (HYG) og investment-grade (LQD). Fall i forholdet = kredittstress, investorer flykter fra risiko. Kreditmarkedet reagerer ofte 1–3 dager FØR aksjemarkedet — et svakt ledende signal."},
                    {dot:"purple",label:"Rentekurve (lilla) — 10Y minus 3M",txt:"Normalt positivt (langsiktig > kortsiktig). Når kurven INVERTERER (negativ) har det varslet alle resesjoner i USA siden 1970 — men typisk 12–18 måneder i forveien. Se etter om den faller mot null eller under."},
                    {dot:"blue",label:"USD/JPY (blå) — Yen Carry Trade",txt:"Fall = yen styrkes = carry trade press. Se lag-analysen nedenfor for å finne om USD/JPY leder SPY med noen dager."},
                    {dot:"yellow",label:"Tidsvindu: 3M / 6M / 1Y",txt:"Bytt vindu øverst til høyre. 3M bruker allerede lastet data. 6M og 1Y henter ny data automatisk. Lengre vindu gir bedre bilde av regimer og resesjonssignaler (rentekurven trenger 1 år for å si noe meningsfullt)."},
                    {dot:"green",label:"Lag-analyse — Pearson-korrelasjonsprofilbarchart",txt:"Velg en indikator (f.eks. USD/JPY) i lag-analyse-seksjonen. Du ser da 31 søyler som viser Pearson-korrelasjonskoeffisienten (r) mellom indikatoren og SPY for alle lag fra −15 til +15 dager. Positiv søyle = korrelerer i samme retning. Negativ = invers. STØRST absolutt r = optimalt lag. Klikk 'Beste: +Xd'-knappen for å hoppe dit automatisk og se det i chartet."},
                    {dot:"yellow",label:"Hva betyr r (Pearson-korrelasjon)?",txt:"r = +1.0: perfekt positiv korrelasjon (beveger seg alltid likt). r = −1.0: perfekt invers korrelasjon (alltid motsatt). r = 0: ingen lineær sammenheng. I praksis er r over 0.5 eller under −0.5 meningsfull. USD/JPY ved lag +3 viser ofte r rundt 0.4–0.7 mot SPY — brukbar prediktiv kraft for 2–5 dagers perspektiv."},
                  ]
                },
                {cat:"marked",
                  icon:"📊", title:"SPY Teknisk Analyse — daglige signaler",
                  body:"Panelet under I dag › Makro beregner fem tekniske signaler fra ett år med SPY dagslukninger. Formålet er å raskt identifisere om du er i et bullish eller bearish regime, og om momentum er positivt eller negativt.",
                  items:[
                    {dot:"green",label:"Regime (200 MA)",txt:"Det viktigste enkeltlevel i markedet. SPY OVER 200 MA = bullish regime, institutionelle investorer er generelt long. Under = bearish regime. En lukking UNDER 200 MA i 2–3 påfølgende dager er et sterkt sell-signal."},
                    {dot:"green",label:"MA Kryss 50/200 — Golden / Death Cross",txt:"Golden Cross (50 MA krysser over 200 MA) = langsiktig bullish skift — brukes av pensjonsfond og ETF-rebalanseringer. Death Cross = motsatt. Disse signalene er sent (lagging) men pålitelige for trenden over uker til måneder."},
                    {dot:"yellow",label:"RSI 14 — Overkjøpt / Oversolgt",txt:"RSI over 70 = overkjøpt, se etter bearish divergens (kurs ny high men RSI lavere high). RSI under 30 = oversolgt, potensielt kjøpssignal. RSI alene er ikke nok — markedet kan forbli overkjøpt i uker. Divergens er nøkkelen."},
                    {dot:"yellow",label:"MACD Histogram",txt:"Beregnet fra EMA(12) − EMA(26), deretter signal = EMA(9) av MACD. Histogrammet (MACD minus signal) viser momentum-endring. Histogram vokser = momentum bygges. Histogram krymper = momentum avtar selv om prisen fortsatt stiger — tidlig advarsel."},
                    {dot:"blue",label:"Kortsiktig (20 MA)",txt:"Pris over 20 MA = kortsiktig bullish. Under = kortsiktig svakhet. Brukes for timing av kortsiktige entries og exits i en ellers bullish trend."},
                  ]
                },
                {cat:"fundamental",
                  icon:"📉", title:"Short Float & Days to Cover — Intel-fanen",
                  body:"Short interest-data vises i Intel-fanen (Posisjonering-seksjonen) i Deep Dive. Data hentes fra Finviz og oppdateres typisk to ganger per måned. Viser hvor mye av selskapets tilgjengelige aksjer (float) som er shortet og hvor lang tid det tar å dekke dem.",
                  items:[
                    {dot:"red",label:"Short Float over 20%",txt:"Ekstremt shortet — over 1 av 5 tilgjengelige aksjer er shortet. Dobbel risiko: enten bekrefter markedet bears og aksjen faller, eller en positiv nyhet utløser et massivt short squeeze der shorts tvinges til å kjøpe tilbake."},
                    {dot:"orange",label:"Short Float 10–20%",txt:"Høyt bearish sentiment. Markedet er generelt skeptisk. Kan gi større svingninger begge veier."},
                    {dot:"yellow",label:"Short Float 5–10%",txt:"Moderat short-press. Normalt for mange vekstaksjer."},
                    {dot:"green",label:"Short Float under 5%",txt:"Lavt short-press. Få satser mot aksjen."},
                    {dot:"yellow",label:"Days to Cover (Short Ratio)",txt:"Beregnes som: antall shortede aksjer delt på gjennomsnittlig daglig handelsvolum. Viser hvor mange handelsdager det ville ta for alle shorts å dekke seg inn på normalt volum. Over 7 dager = shorts sitter fast, høy squeeze-risiko. Under 3 dager = shorts kan raskt ut, lavt squeeze-potensial."},
                  ]
                },
            ];
            const q=infoSearch.toLowerCase();
            const filtered=sections.filter(s=>{
              if(infoCat!=="alle"&&s.cat!==infoCat)return false;
              if(!q)return true;
              return s.title.toLowerCase().includes(q)||(s.body||"").toLowerCase().includes(q)||s.items.some(i=>i.label.toLowerCase().includes(q)||i.txt.toLowerCase().includes(q));
            });
            return (
              <div style={{maxWidth:780}}>
                <div style={{fontSize:13,fontWeight:700,color:"#f8fafc",letterSpacing:1,marginBottom:4}}>ORDBOK & FORKLARINGER</div>
                <div style={{fontSize:10,color:"#475569",marginBottom:12}}>Alt du trenger å vite for å lese denne siden</div>
                <input value={infoSearch} onChange={e=>setInfoSearch(e.target.value)} placeholder="🔍 Søk etter begrep..." style={{width:"100%",padding:"8px 12px",background:"#0a1628",border:"1px solid #1e293b",borderRadius:5,color:"#e2e8f0",fontSize:12,fontFamily:"inherit",boxSizing:"border-box",marginBottom:10,outline:"none"}}/>
                <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:14}}>
                  {INFO_CATS.map(cat=>(
                    <button key={cat.id} onClick={()=>setInfoCat(cat.id)}
                      style={{padding:"4px 13px",border:"1px solid",borderColor:infoCat===cat.id?"#3b82f6":"#1e293b",background:infoCat===cat.id?"#3b82f620":"transparent",color:infoCat===cat.id?"#3b82f6":"#475569",borderRadius:4,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
                      {cat.label}
                    </button>
                  ))}
                </div>
                {filtered.length===0&&<div style={{padding:"32px",textAlign:"center",color:"#334155",fontSize:12}}>Ingen treff på «{infoSearch}»</div>}
                {filtered.map((section,si)=>(
                  <div key={si} style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px 16px",marginBottom:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:section.body?8:0}}>
                      <span style={{fontSize:18}}>{section.icon}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:700,color:"#e2e8f0",letterSpacing:0.5}}>{section.title}</div>
                      </div>
                      {section.cat&&<div style={{fontSize:9,color:"#334155",background:"#0a1628",border:"1px solid #1e293b",borderRadius:3,padding:"2px 7px",flexShrink:0,whiteSpace:"nowrap"}}>{INFO_CATS.find(c=>c.id===section.cat)?.label}</div>}
                    </div>
                    {section.body&&<div style={{fontSize:11,color:"#64748b",lineHeight:1.6,marginBottom:section.items.length?10:0}}>{section.body}</div>}
                    {section.items.map((item,ii)=>{
                      const c=DOT_COLORS[item.dot]||"#94a3b8";
                      return (
                        <div key={ii} style={{display:"flex",gap:10,padding:"7px 0",borderTop:"1px solid #0d1e30",alignItems:"flex-start"}}>
                          <div style={{width:8,height:8,borderRadius:"50%",background:c,flexShrink:0,marginTop:3}}/>
                          <div>
                            <div style={{fontSize:10,fontWeight:700,color:c,marginBottom:3}}>{item.label}</div>
                            <div style={{fontSize:10,color:"#475569",lineHeight:1.6}}>{item.txt}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div style={{padding:"9px 13px",background:"#060f1c",border:"1px solid #1e293b",borderRadius:6,marginTop:4}}>
                  <div style={{fontSize:9,color:"#334155",letterSpacing:1,marginBottom:3}}>⚠️ DISCLAIMER</div>
                  <div style={{fontSize:9,color:"#334155",lineHeight:1.5}}>Denne siden er et lærings- og informasjonsverktøy, ikke finansiell rådgivning. All handel innebærer risiko. Gjør alltid eigen research og vurder din eigen risikotoleranse før du investerer.</div>
                </div>
              </div>
            );
          })()}

          {/* ── DEEP DIVE ── */}
          {view==="deepdive"&&(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
                <div style={{fontSize:10,color:"#475569",letterSpacing:1}}>AKSJE:</div>
                <select value={deepTicker} onChange={e=>setDeepTicker(e.target.value)}
                  style={{padding:"6px 10px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:12,fontFamily:"inherit",fontWeight:700}}>
                  <option disabled>── ETF / Indeks ──</option>
                  {[{ticker:"SPX",name:"S&P 500 Index"},{ticker:"XSP",name:"Mini-SPX Index"},{ticker:"SPY",name:"S&P 500 ETF"},{ticker:"QQQ",name:"Nasdaq 100 ETF"},{ticker:"IWM",name:"Russell 2000 ETF"},{ticker:"DIA",name:"Dow Jones ETF"}].map(s=>(
                    <option key={s.ticker} value={s.ticker}>{s.ticker} — {s.name}</option>
                  ))}
                  <option disabled>── Aksjer ──</option>
                  {[...allStockList].sort((a,b)=>a.ticker.localeCompare(b.ticker)).map(s=><option key={s.ticker} value={s.ticker}>{s.ticker} — {s.name}</option>)}
                </select>
                <button onClick={() => { setOpxMode("kjede"); setDeepTab("opsjoner"); }}
                  style={{padding:"5px 11px",fontSize:10,background:"#0a1f38",border:"1px solid #3b82f6",
                    borderRadius:4,color:"#93c5fd",cursor:"pointer",fontFamily:"inherit"}}>
                  🎯 Opsjonskjede {deepTicker}
                </button>
                {ivInfo&&(
                  <span title="IV30 = implisitt volatilitet for 30 dager. IV-rang 0 = laveste IV siste år (billige opsjoner), 100 = høyeste (dyre opsjoner)."
                    style={{fontSize:10,padding:"5px 10px",border:"1px solid #1e293b",borderRadius:4,color:"#94a3b8",whiteSpace:"nowrap"}}>
                    IV30 <b style={{color:"#e2e8f0"}}>{(ivInfo.cur*100).toFixed(0)}%</b>
                    {ivInfo.rank!=null
                      ?<> · IV-rang <b style={{color:ivInfo.rank<30?"#10b981":ivInfo.rank>70?"#f43f5e":"#f59e0b"}}>{ivInfo.rank}</b> <span style={{color:"#475569"}}>({ivInfo.rank<30?"billige opsjoner":ivInfo.rank>70?"dyre opsjoner":"normalt"}, {ivInfo.n} d)</span></>
                      :<span style={{color:"#475569"}}> · IV-rang om {20-ivInfo.n} handelsdager ({ivInfo.n}/20 logget)</span>}
                  </span>
                )}
              </div>

              {/* Priskart øverst — alltid synlig */}
              <LWDeepChart closes={techData[deepTicker]?.closes} timestamps={techData[deepTicker]?.timestamps} volumes={techData[deepTicker]?.volumes} opens={techData[deepTicker]?.opens} highs={techData[deepTicker]?.highs} lows={techData[deepTicker]?.lows} ticker={deepTicker} isMobile={isMobile} trades={journal.filter(t=>t.ticker===deepTicker)} prediction={deepPrediction} predStats={predStats}              />

              {/* Tab-navigasjon under grafen */}
              <div style={{display:"flex",gap:4,marginTop:10,marginBottom:4}}>
                {[["intel","🔬 Intelligence"],["nivaer","📏 Nivåer og risiko"],["teknisk","📡 Opsjons Signal"],["opsjoner","🎯 Opsjoner"],["compare","⚖️ Sammenlign"]].map(([t,l])=>(
                  <button key={t} onClick={()=>setDeepTab(t)}
                    style={{flex:1,padding:"8px 4px",border:"1px solid",borderColor:deepTab===t?"#a78bfa":"#1e293b",background:deepTab===t?"#a78bfa15":"transparent",color:deepTab===t?"#a78bfa":"#64748b",borderRadius:4,cursor:"pointer",fontSize:isMobile?10:11,fontFamily:"inherit",fontWeight:deepTab===t?700:400}}>
                    {l}
                  </button>
                ))}
              </div>

              {deepTab==="nivaer"&&(
                <Levels ticker={deepTicker} td={techData[deepTicker]} opt={optionsData[deepTicker]}
                  onPlan={p=>{
                    const f=v=>`$${v<10?v.toFixed(2):v.toFixed(1)}`;
                    setPlanForm({ticker:p.ticker,signal:"Leder (følg bølgen)",checks:{},status:"watching",
                      notes:`Inngang ~${f(p.entry)} · Tapsgrense ${f(p.stop)} (${p.stopWhy}) · Mål ${f(p.target)} · Gevinst/risiko ${p.rr.toFixed(1)}:1${p.shares!=null?` · ${p.shares} aksjer, maks tap $${p.maxLoss.toFixed(0)}`:""}`});
                    setJournalTab("planer"); setView("journal");
                  }}/>
              )}

              {deepTab==="opsjoner"&&(
                <div style={{marginTop:8}}>
                  <div style={{display:"flex",gap:4,marginBottom:4}}>
                    {[["kjede",`Opsjonskjede ${deepTicker}`],["utlop","Utløpsoversikt (alle aksjer)"]].map(([k,l])=>(
                      <button key={k} onClick={()=>setOpxMode(k)}
                        style={{padding:"5px 12px",fontSize:11,fontFamily:"inherit",cursor:"pointer",borderRadius:4,border:"1px solid",
                          borderColor:opxMode===k?"#3b82f6":"#1e293b",background:opxMode===k?"#3b82f620":"transparent",color:opxMode===k?"#93c5fd":"#64748b"}}>{l}</button>
                    ))}
                  </div>
                  {opxMode==="kjede"
                    ? <OptionsFlow key={deepTicker+(opxNav?.ticker===deepTicker?opxNav.exp||"":"")} initialTicker={deepTicker}
                        initialExp={opxNav?.ticker===deepTicker?opxNav.exp:null}
                        onDeepDive={t=>{setDeepTicker(t);setDeepTab("intel");}}/>
                    : <OpexGrid onSelect={(t,exp)=>{setDeepTicker(t);setOpxNav({ticker:t,exp:exp??null});setOpxMode("kjede");}}/>}
                </div>
              )}

              {deepTab==="intel"&&(
                <div>
                  <div style={{background:"#060f1c",border:"1px solid #1e293b",borderLeft:"3px solid #a78bfa",borderRadius:"0 6px 6px 0",padding:"10px 14px",marginBottom:14,fontSize:13,color:"#94a3b8",lineHeight:1.6}}>
                    {deepIntel.desc}
                    <div style={{fontSize:10,color:"#475569",marginTop:6}}>
                      Vekst, marginer, P/E og beskrivelse er registrert manuelt og kan være utdaterte.
                      {" "}Gamma og call/put er {deepIntel.gammaLive?"live fra opsjonskjeden":"faste tall (opsjonsdata ikke lastet)"}, IV-rang er {deepIntel.ivLive?"live":"et fast tall til 20 dager er logget"}.
                    </div>
                  </div>
                  <SixCards ticker={deepTicker} intel={deepIntel} liveD={deepLive}
                    liveGammaFlip={calcGammaFlip(optionsData[deepTicker]).gammaFlip}
                    liveGammaRegime={calcGammaFlip(optionsData[deepTicker]).gammaRegime}/>

                  {/* Institusjonell Trigger-sjekkliste */}
                  {(()=>{
                    // 1. Sektor P/E
                    const sectorName = allStockList.find(s=>s.ticker===deepTicker)?.cat;
                    const sectorPEs = allStockList
                      .filter(s=>s.cat===sectorName)
                      .map(s=>getIntel(s.ticker).forwardPE)
                      .filter(pe=>pe&&pe>0&&pe<500)
                      .sort((a,b)=>a-b);
                    const sectorMedianPE = sectorPEs.length ? sectorPEs[Math.floor(sectorPEs.length/2)] : null;
                    const tickerPE = deepIntel.forwardPE;
                    const peDiscount = sectorMedianPE&&tickerPE ? Math.round((1-tickerPE/sectorMedianPE)*100) : null;
                    const peOk = peDiscount!==null && peDiscount>=25;

                    // 2. MA-posisjon
                    const techCloses = techData[deepTicker]?.closes;
                    const livePrice = deepLive?.price;
                    let maOk = null, ema9val = null, ema20val = null;
                    if (techCloses?.length && livePrice) {
                      const e9 = calcEMALine(techCloses,9); const e20 = calcEMALine(techCloses,20);
                      ema9val = e9[e9.length-1]; ema20val = e20[e20.length-1];
                      if (ema9val&&ema20val) maOk = livePrice>ema9val && livePrice>ema20val;
                    }

                    // 3. Bollinger Squeeze
                    let squeezeOk = null, bbWidth = null;
                    if (techCloses?.length>=20) {
                      const rec = techCloses.slice(-20);
                      const mean = rec.reduce((a,b)=>a+b,0)/20;
                      const std = Math.sqrt(rec.reduce((s,v)=>s+(v-mean)**2,0)/20);
                      bbWidth = mean>0 ? +((std*4/mean)*100).toFixed(1) : null;
                      squeezeOk = bbWidth!==null && bbWidth<10;
                    }

                    // 4. Options C/P bias
                    const od = optionsData[deepTicker];
                    let cpOk = null, cpRatioVal = null;
                    if (od?.ok) {
                      const cVol=(od.calls||[]).reduce((s,c)=>s+(c.volume||0),0);
                      const pVol=(od.puts||[]).reduce((s,p)=>s+(p.volume||0),0);
                      cpRatioVal = pVol>0 ? +(cVol/pVol).toFixed(2) : null;
                      cpOk = cpRatioVal!==null && cpRatioVal>1.2;
                    }

                    // 5. Gamma flip — live først, fallback til hardkodet Intel-verdi
                    const liveGammaResult = calcGammaFlip(od);
                    const gammaFlip = liveGammaResult.gammaFlip ?? deepIntel.pf.gammaFlip ?? null;
                    const gammaRegime = liveGammaResult.gammaRegime ?? deepIntel.pf.gammaRegime ?? null;
                    const isLiveGamma = !!liveGammaResult.gammaFlip;
                    let gammaOk = null;
                    if (gammaFlip&&livePrice) gammaOk = livePrice>gammaFlip;

                    const criteria = [
                      {
                        label:"P/E vs sektorrabatt",
                        status: peDiscount===null?"unknown":peOk?"green":"red",
                        detail: peDiscount!==null
                          ? `${tickerPE}x vs ${sectorMedianPE}x sektormedian — ${peDiscount>0?peDiscount+"% rabatt":Math.abs(peDiscount)+"% premium"}`
                          : "Mangler P/E data",
                        trigger:"Kjøp-signal: ≥25% rabatt mot sektoren",
                      },
                      {
                        label:"Pris over EMA9 + EMA20",
                        status: maOk===null?"unknown":maOk?"green":"yellow",
                        detail: maOk===null
                          ? (techCloses?"Beregner…":"Henter teknisk data…")
                          : maOk
                            ? `$${livePrice?.toFixed(2)} over EMA9 $${ema9val?.toFixed(2)} og EMA20 $${ema20val?.toFixed(2)}`
                            : `Pris under EMA — vent på bekreftelse over $${Math.max(ema9val||0,ema20val||0).toFixed(2)}`,
                        trigger:"Trigger: stenger over begge EMA-er med volum",
                      },
                      {
                        label:"Bollinger Squeeze",
                        status: squeezeOk===null?"unknown":squeezeOk?"green":"yellow",
                        detail: bbWidth!==null
                          ? squeezeOk
                            ? `BB-bredde ${bbWidth}% — volatilitet på lavpunkt, stor bevegelse nær`
                            : `BB-bredde ${bbWidth}% — normal volatilitet, ingen squeeze ennå`
                          : "Henter teknisk data…",
                        trigger:"Setup: kjøp billig volatilitet (call/straddle) under squeeze",
                      },
                      {
                        label:"Options Flow (C/P ratio)",
                        status: cpOk===null?"unknown":cpOk?"green":"yellow",
                        detail: !od
                          ? "Henter opsjonsdata…"
                          : !od.ok
                            ? "Opsjonsdata ikke tilgjengelig"
                            : cpRatioVal!==null
                              ? `C/P ratio ${cpRatioVal} — ${cpOk?"call-dominans (bullish posisjonering)":"nøytral eller bearish flow"}`
                              : "Ikke nok volum",
                        trigger:"Signal: C/P > 1.2 med uvanlig call-aktivitet nær ATM",
                      },
                      {
                        label:"Pris over gamma flip",
                        status: gammaOk===null?"unknown":gammaOk?"green":"red",
                        detail: gammaFlip&&livePrice
                          ? `Flip $${gammaFlip}${isLiveGamma?" ↻":" (est.)"} — pris er ${gammaOk?"OVER (MMs stabiliserer)":"UNDER (fall forsterkes)"}`
                          : !od?"Henter opsjonsdata…":"Henter opsjonsdata — åpne OPTIONS FLOW-fanen for å trigge",
                        trigger:"Kritisk: over flip = dealer-hedging dempende, ikke forsterkende",
                      },
                    ];

                    const metCount = criteria.filter(c=>c.status==="green").length;
                    const overallColor = metCount>=4?"#10b981":metCount>=2?"#f59e0b":"#f43f5e";
                    const overallLabel = metCount>=4?"A-LIST":metCount>=3?"WATCHLIST":metCount>=2?"TIDLIG":metCount===1?"AVVENT":"NEI";

                    // P/E bar scale
                    const peBarMax = sectorMedianPE ? sectorMedianPE*1.6 : (tickerPE||50)*1.6;
                    const tickerBarPct = Math.min(100, ((tickerPE||0)/peBarMax)*100);
                    const medianBarPct = Math.min(100, ((sectorMedianPE||0)/peBarMax)*100);

                    return (
                      <div style={{marginBottom:14,padding:"14px",background:"#060f1c",border:"1px solid #1e293b",borderRadius:8}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                          <div style={{fontSize:11,color:"#334155",letterSpacing:1}}>🏛️ INSTITUSJONELL TRIGGER-SJEKKLISTE</div>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:10,color:"#475569"}}>{metCount}/5 kriterier</span>
                            <span style={{fontSize:11,fontWeight:700,color:overallColor,padding:"2px 10px",background:overallColor+"18",borderRadius:4}}>{overallLabel}</span>
                          </div>
                        </div>

                        <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:12}}>
                          {criteria.map((c,i)=>{
                            const dc = c.status==="green"?"#10b981":c.status==="yellow"?"#f59e0b":c.status==="red"?"#f43f5e":"#334155";
                            const ch = c.status==="green"?"✓":c.status==="red"?"✗":c.status==="yellow"?"◐":"·";
                            return(
                              <div key={i} style={{padding:"8px 10px",background:"#0a1628",borderRadius:5,borderLeft:`2px solid ${dc}40`}}>
                                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                                  <span style={{fontSize:13,color:dc,fontWeight:700,width:16,flexShrink:0}}>{ch}</span>
                                  <span style={{fontSize:11,color:"#cbd5e1",fontWeight:600,flex:1}}>{c.label}</span>
                                  <span style={{fontSize:9,padding:"1px 6px",background:dc+"20",color:dc,borderRadius:3,flexShrink:0}}>
                                    {c.status==="green"?"OK":c.status==="red"?"NEI":c.status==="yellow"?"NØYTRAL":"—"}
                                  </span>
                                </div>
                                <div style={{fontSize:10,color:"#64748b",marginLeft:24,marginBottom:2}}>{c.detail}</div>
                                <div style={{fontSize:9,color:"#334155",marginLeft:24}}>{c.trigger}</div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Sektor P/E sammenligning */}
                        {sectorMedianPE&&tickerPE&&(
                          <div style={{padding:"10px 12px",background:"#0a1628",borderRadius:5}}>
                            <div style={{fontSize:9,color:"#334155",letterSpacing:1,marginBottom:8}}>P/E SAMMENLIGNING — {sectorName?.toUpperCase()}</div>
                            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                              <span style={{fontSize:11,color:"#f59e0b",fontWeight:700,minWidth:100,fontFamily:"monospace"}}>{deepTicker}: {tickerPE}x</span>
                              <div style={{flex:1,position:"relative",height:8,background:"#1e293b",borderRadius:4}}>
                                <div style={{position:"absolute",left:0,top:0,height:"100%",borderRadius:4,width:`${tickerBarPct}%`,background:peOk?"#10b981":"#f59e0b"}}/>
                                <div style={{position:"absolute",top:-4,width:2,height:16,background:"#94a3b8",borderRadius:1,left:`${medianBarPct}%`}}/>
                              </div>
                              <span style={{fontSize:10,color:"#64748b",minWidth:80,textAlign:"right",fontFamily:"monospace"}}>Median: {sectorMedianPE}x</span>
                            </div>
                            <div style={{display:"flex",justifyContent:"space-between"}}>
                              <span style={{fontSize:10,color:peOk?"#10b981":"#f59e0b"}}>
                                {peDiscount>0?`${peDiscount}% billigere enn sektoren`:`${Math.abs(peDiscount)}% dyrere enn sektoren`}
                              </span>
                              <span style={{fontSize:9,color:"#334155"}}>{sectorPEs.length} selskaper i gruppen</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
                    <div>
                      <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:7}}>📊 FUNDAMENTALE NØKKELTALL</div>
                      <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden",marginBottom:12}}>
                        <MRow label="Revenue Growth" value={`${deepIntel.revenueGrowth}%`} score={scoreRevGrowth(deepIntel.revenueGrowth)} hint={scoreRevGrowth(deepIntel.revenueGrowth).txt}/>
                        <MRow label="Gross Margin" value={`${deepIntel.grossMargin}%`} score={scoreMargin(deepIntel.grossMargin)} hint={scoreMargin(deepIntel.grossMargin).txt}/>
                        <MRow label="Forward P/E" value={`${deepIntel.forwardPE}x`} score={scorePE(deepIntel.forwardPE)} hint={scorePE(deepIntel.forwardPE).txt}/>
                        <MRow label="PEG Ratio" value={deepIntel.peg !== null ? `${deepIntel.peg}x` : "N/A"} score={scorePEG(deepIntel.peg)} hint={`PEG = Forward P/E (${deepIntel.forwardPE}) ÷ EPS-vekst (${deepIntel.epsGrowth ?? "N/A"}%). ${scorePEG(deepIntel.peg).txt}`}/>
                        <MRow label="AI Eksponering" value={deepIntel.aiExposure} score={{dot:deepIntel.aiExposure==="Meget høy"?"green":deepIntel.aiExposure==="Høy"?"green":"yellow",label:deepIntel.aiExposure}} hint="Hvor direkte aksjen er eksponert mot AI-vekst"/>
                      </div>
                      <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:7}}>📅 EARNINGS INTELLIGENCE</div>
                      <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden"}}>
                        <MRow label="Neste Earnings" value={deepIntel.nextEarnings} score={{dot:"yellow",label:"Planlagt"}} hint="Dato for neste kvartalsrapport"/>
                        <MRow label="Expected Move (IV)" value={`±${deepIntel.expectedMove}%`} score={{dot:deepIntel.expectedMove<=5?"green":deepIntel.expectedMove<=9?"yellow":"red",label:deepIntel.expectedMove<=5?"Rolig":deepIntel.expectedMove<=9?"Moderat":"Stor"}} hint="Hva opsjonsmarkedet priser inn av kursbevegelse"/>
                        <MRow label="Historisk snitt" value={`±${deepIntel.avgMove}%`} score={{dot:deepIntel.avgMove<=6?"green":deepIntel.avgMove<=10?"yellow":"red",label:"Historisk"}} hint="Gjennomsnittlig kursreaksjon på earnings"/>
                        <MRow label="Beat Rate" value={`${deepIntel.beatRate}%`} score={scoreBeat(deepIntel.beatRate)} hint={scoreBeat(deepIntel.beatRate).txt}/>
                        <MRow label="Earnings Surprise" value={`+${deepIntel.earningsSurprise}%`} score={{dot:deepIntel.earningsSurprise>=8?"green":deepIntel.earningsSurprise>=4?"yellow":"red",label:deepIntel.earningsSurprise>=8?"Sterk":"OK"}} hint="Hvor mye selskapet typisk slår estimatene"/>
                      </div>
                    </div>
                    <div>
                      <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:7}}>🎯 POSISJONERING — OPSJONSMARKEDET</div>
                      <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden",marginBottom:12}}>
                        {(()=>{
                          const lgd = calcGammaFlip(optionsData[deepTicker]);
                          const gFlip = lgd.gammaFlip ?? deepIntel.pf.gammaFlip;
                          const gPrice = deepLive?.price;
                          const effRegime = (gFlip && gPrice)
                            ? (gPrice > gFlip ? "POSITIV" : "NEGATIV")
                            : (lgd.gammaRegime || deepIntel.pf.gammaRegime);
                          const flipLive = !!lgd.gammaFlip;
                          const rel = gFlip && gPrice ? (gPrice > gFlip ? "Over" : "Under") : null;
                          return (<>
                            <MRow label="Gamma Regime" value={effRegime} score={scoreGamma(effRegime)} hint={`${rel?rel+" flip $"+gFlip+" — ":""} ${scoreGamma(effRegime).txt}${flipLive?" (live ↻)":""}`}/>
                            <MRow label="Gamma Flip" value={gFlip?`$${gFlip}${flipLive?" ↻":""}` :"—"} score={{dot:"yellow",label:flipLive?"Live":"Estimert"}} hint={`${rel||"Kritisk"} nivå — over flip stabiliserer MMs kursen, under forsterker de bevegelser`}/>
                          </>);
                        })()}
                        <MRow label={deepIntel.ivLive?"IV-rang (live)":"IV-rang (fast tall, logges ennå)"} value={`${deepIntel.pf.ivPct}/100`} score={scoreIV(deepIntel.pf.ivPct)} hint={scoreIV(deepIntel.pf.ivPct).txt}/>
                        <MRow label="Call/Put Ratio" value={`${deepIntel.pf.callPutRatio}x`} score={scoreCPR(deepIntel.pf.callPutRatio)} hint={scoreCPR(deepIntel.pf.callPutRatio).txt}/>
                        <MRow label="Options Aktivitet" value={deepIntel.pf.optionsActivity} score={scoreActivity(deepIntel.pf.optionsActivity)} hint={scoreActivity(deepIntel.pf.optionsActivity).txt}/>
                        <MRow label="Earnings Risiko" value={deepIntel.pf.earningsRisk} score={scoreERisk(deepIntel.pf.earningsRisk)} hint={scoreERisk(deepIntel.pf.earningsRisk).txt}/>
                        {(()=>{
                          const sd = shortData[deepTicker];
                          const loading = !sd && deepTab === "intel";
                          const sf = sd?.ok ? sd.shortFloat : null;
                          const sr = sd?.ok ? sd.shortRatio : null;
                          const sfScore = scoreShortFloat(sf);
                          const srScore = scoreShortRatio(sr);
                          const dateHint = sd?.shortDate ? ` (per ${sd.shortDate})` : "";
                          return (<>
                            <MRow label="Short Float" value={loading ? "…" : sfScore.label} score={sfScore} hint={sfScore.txt + dateHint}/>
                            <MRow label="Days to Cover" value={loading ? "…" : srScore.label} score={srScore} hint={srScore.txt}/>
                          </>);
                        })()}
                      </div>
                      <PressureGauge score={calcPressure(deepIntel)}/>
                      <div style={{marginTop:10,padding:"9px 12px",background:"#060f1c",border:"1px solid #1e293b",borderRadius:6}}>
                        <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:4}}>⚠️ DISCLAIMER</div>
                        <div style={{fontSize:11,color:"#334155",lineHeight:1.5}}>Ikke finansiell rådgivning. Tall er informasjon og læringsverktøy. Gamma og IV-data er estimater. Gjør alltid egen research.</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {deepTab==="teknisk"&&(()=>{
                const dailyHistory = techData[deepTicker];
                if (!dailyHistory && !techLoading) fetchTechData(deepTicker);
                const techLiveD = dailyHistory
                  ? { ...deepLive, history: dailyHistory.closes || dailyHistory, volumes: dailyHistory.volumes || [] }
                  : deepLive;
                const sig = getOptionsStrategy(deepIntel, techLiveD);
                const price = deepLive?.price;
                const rsiColor = sig.rsi === null ? "#475569" : sig.rsi < 30 ? "#10b981" : sig.rsi > 70 ? "#f43f5e" : "#f59e0b";
                const bbPos = sig.bb?.position ?? 50;
                return (
                  <div>
                    {techLoading && <div style={{textAlign:"center",color:"#334155",padding:"10px 0",fontSize:11,marginBottom:8}}>⟳ Henter 1-års daglig data for teknisk analyse…</div>}
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                      <span style={{fontSize:9,color:"#10b981",background:"#10b98118",padding:"2px 8px",borderRadius:3}}>✓ Basert på 1Å daglig kursdata — uavhengig av valgt periode</span>
                      {dailyHistory && <span style={{fontSize:9,color:"#475569"}}>{(dailyHistory.closes||dailyHistory).length} datapunkter</span>}
                    </div>
                    {/* Strategi-kort */}
                    <div style={{background:"#060f1c",border:`2px solid ${sig.strategyColor}`,borderRadius:10,padding:"16px 18px",marginBottom:16,display:"flex",gap:14,alignItems:"flex-start",flexWrap:"wrap"}}>
                      <div>
                        <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:6}}>🎯 ANBEFALT OPSJONSSTRATEGI</div>
                        <div style={{fontSize:22,fontWeight:700,color:sig.strategyColor,marginBottom:6}}>{sig.strategy}</div>
                        <div style={{fontSize:12,color:"#64748b",lineHeight:1.6,maxWidth:480}}>{sig.strategyDesc}</div>
                      </div>
                      <div style={{marginLeft:"auto",display:"flex",flexWrap:"wrap",gap:5,maxWidth:260}}>
                        {sig.signals.map((s,i)=>(
                          <span key={i} style={{fontSize:9,color:s.c,background:s.c+"18",padding:"3px 8px",borderRadius:3}}>{s.text}</span>
                        ))}
                      </div>
                    </div>

                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
                      {/* RSI */}
                      <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px"}}>
                        <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:10}}>📊 RSI (14)</div>
                        {sig.rsi === null ? <div style={{color:"#334155",fontSize:12}}>Trenger mer data (min 1M periode)</div> : (<>
                          <div style={{fontSize:36,fontWeight:700,color:rsiColor,marginBottom:6}}>{sig.rsi}</div>
                          <div style={{height:8,background:"#0d1e30",borderRadius:4,marginBottom:6,position:"relative"}}>
                            <div style={{position:"absolute",left:"30%",top:0,width:2,height:"100%",background:"#1e293b"}}/>
                            <div style={{position:"absolute",left:"70%",top:0,width:2,height:"100%",background:"#1e293b"}}/>
                            <div style={{position:"absolute",left:`${Math.min(sig.rsi,99)}%`,top:-1,width:10,height:10,borderRadius:"50%",background:rsiColor,transform:"translateX(-50%)",boxShadow:`0 0 6px ${rsiColor}`}}/>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"#334155"}}>
                            <span>0 — Oversold</span><span>50 — Nøytral</span><span>100 — Overbought</span>
                          </div>
                          <div style={{marginTop:8,fontSize:12,color:rsiColor}}>
                            {sig.rsi < 30 ? "Oversold — mulig reversal oppover" : sig.rsi > 70 ? "Overbought — mulig reversal nedover" : "Nøytralt territorium"}
                          </div>
                        </>)}
                      </div>

                      {/* Bollinger */}
                      <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px"}}>
                        <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:10}}>📉 BOLLINGER BANDS (20)</div>
                        {!sig.bb ? <div style={{color:"#334155",fontSize:12}}>Trenger mer data (min 1M periode)</div> : (<>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                            <div style={{fontSize:20,fontWeight:700,color:sig.bb.squeeze?"#a78bfa":"#94a3b8"}}>{sig.bb.bandWidth}%</div>
                            {sig.bb.squeeze && <span style={{fontSize:11,color:"#a78bfa",background:"#a78bfa20",padding:"2px 8px",borderRadius:3,fontWeight:700}}>⚡ SQUEEZE</span>}
                          </div>
                          <div style={{height:20,background:"#0d1e30",borderRadius:4,marginBottom:6,position:"relative",overflow:"hidden"}}>
                            <div style={{position:"absolute",left:0,right:0,top:"50%",height:1,background:"#1e293b"}}/>
                            <div style={{position:"absolute",left:`${Math.max(0,bbPos-2)}%`,top:"20%",height:"60%",width:4,background:sig.bb.squeeze?"#a78bfa":"#3b82f6",borderRadius:2}}/>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#475569"}}>
                            <span>Lower ${sig.bb.lower.toFixed(1)}</span>
                            <span>MA20 ${sig.bb.avg}</span>
                            <span>Upper ${sig.bb.upper.toFixed(1)}</span>
                          </div>
                          <div style={{marginTop:8,fontSize:12,color:sig.bb.squeeze?"#a78bfa":"#64748b"}}>
                            {sig.bb.squeeze ? "Squeeze aktiv — stor bevegelse kan komme snart. Vurder straddle." : `Bands normale. Pris på ${bbPos}% av bandet.`}
                          </div>
                        </>)}
                      </div>

                      {/* Moving Averages */}
                      <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px"}}>
                        <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:10}}>📈 MOVING AVERAGES</div>
                        {(()=>{
                          const h=techLiveD?.history||[];
                          const ema9a=calcEMALine(h,9); const ema9v=ema9a[ema9a.length-1]??null;
                          const ema20a=calcEMALine(h,20); const ema20v=ema20a[ema20a.length-1]??null;
                          const rows=[
                            {label:"EMA 9", val:ema9v,  hint:"Eksponensielt glidende snitt (raskere signal)"},
                            {label:"EMA 20",val:ema20v, hint:"Kortsiktig trend (EMA)"},
                            {label:"MA 50", val:sig.ma50, hint:"Mellomlang trend"},
                            {label:"MA 200",val:sig.ma200,hint:"Langsiktig trend"},
                          ];
                          return(<>
                            {rows.map((ma,i)=>{
                              if(!ma.val) return <div key={i} style={{fontSize:12,color:"#64748b",marginBottom:6}}>{ma.label}: trenger mer data</div>;
                              const above=price&&price>ma.val;
                              return(
                                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:"1px solid #0d1e30"}} title={ma.hint}>
                                  <span style={{fontSize:12,color:"#475569"}}>{ma.label}</span>
                                  <span style={{fontSize:12,fontWeight:700,color:"#e2e8f0"}}>${ma.val.toFixed(1)}</span>
                                  <span style={{fontSize:11,color:above?"#10b981":"#f43f5e"}}>{price?`Pris ${above?"over":"under"}`:""}</span>
                                </div>
                              );
                            })}
                            {ema9v&&ema20v&&(
                              <div style={{marginTop:8,fontSize:12,color:ema9v>ema20v?"#10b981":"#f43f5e",fontWeight:700}}>
                                {ema9v>ema20v?"✓ EMA9 > EMA20 — kortsiktig momentum bullish":"✗ EMA9 < EMA20 — kortsiktig momentum bearish"}
                              </div>
                            )}
                            {sig.ma50&&sig.ma200&&(
                              <div style={{marginTop:4,fontSize:12,color:sig.ma50>sig.ma200?"#10b981":"#f43f5e",fontWeight:700}}>
                                {sig.ma50>sig.ma200?"✓ Golden Cross — langsiktig bullish trend":"✗ Death Cross — langsiktig bearish trend"}
                              </div>
                            )}
                          </>);
                        })()}
                      </div>

                      {/* Earnings + IV kombinasjon */}
                      <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px"}}>
                        <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:10}}>⚡ EARNINGS + IV SETUP</div>
                        <div style={{marginBottom:8}}>
                          <div style={{fontSize:11,color:"#475569",marginBottom:2}}>Neste earnings</div>
                          <div style={{fontSize:14,fontWeight:700,color:"#f59e0b"}}>{deepIntel.nextEarnings}</div>
                          {sig.daysToEarnings !== null && sig.daysToEarnings >= 0 && (
                            <div style={{fontSize:12,color:sig.daysToEarnings<7?"#f43f5e":sig.daysToEarnings<21?"#f59e0b":"#10b981"}}>
                              {sig.daysToEarnings}d til earnings
                            </div>
                          )}
                        </div>
                        <div style={{marginBottom:8}}>
                          <div style={{fontSize:11,color:"#475569",marginBottom:2}}>IV Percentile</div>
                          <div style={{height:6,background:"#0d1e30",borderRadius:3,marginBottom:3}}>
                            <div style={{height:"100%",width:`${deepIntel.pf.ivPct}%`,background:deepIntel.pf.ivPct>70?"#f43f5e":deepIntel.pf.ivPct>40?"#f59e0b":"#10b981",borderRadius:3}}/>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}>
                            <span style={{color:"#10b981"}}>Lav (kjøp)</span>
                            <span style={{color:"#f59e0b",fontWeight:700}}>{deepIntel.pf.ivPct}/100</span>
                            <span style={{color:"#f43f5e"}}>Høy (selg)</span>
                          </div>
                        </div>
                        <div style={{fontSize:12,color:"#64748b",lineHeight:1.5}}>
                          {deepIntel.pf.ivPct < 40 && sig.daysToEarnings !== null && sig.daysToEarnings <= 21 && sig.daysToEarnings >= 0
                            ? "✓ Ideelt: Lav IV før earnings = kjøp opsjoner nå, selg etter (IV crush)"
                            : deepIntel.pf.ivPct > 70 && (sig.daysToEarnings === null || sig.daysToEarnings < 0 || sig.daysToEarnings > 21)
                            ? "⚠️ Høy IV uten nær earnings = dyre opsjoner, vurder å selge premium"
                            : "Normalt IV-miljø"}
                        </div>
                      </div>
                    </div>
                    {/* OPTIONS FLOW */}
                    {(()=>{
                      const od = optionsData[deepTicker];
                      const fetching = optionsLoading || !(deepTicker in optionsData);
                      if (fetching) return (
                        <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px",marginTop:10}}>
                          <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:8}}>📊 OPTIONS FLOW</div>
                          <div style={{fontSize:12,color:"#475569"}}>Henter live opsjonsdata…</div>
                        </div>
                      );
                      if (!od || !od.ok) return (
                        <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px",marginTop:10}}>
                          <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:6}}>📊 OPTIONS FLOW</div>
                          <div style={{fontSize:12,color:"#475569",marginBottom:8}}>Opsjonsdata ikke tilgjengelig for {deepTicker} akkurat nå. CBOE begrenser tilgang periodisk.</div>
                          <button onClick={()=>setOptionsRetry(n=>n+1)} style={{fontSize:11,padding:"4px 12px",background:"#0d1e30",border:"1px solid #1e3a5f",borderRadius:4,color:"#64748b",cursor:"pointer",fontFamily:"inherit"}}>↻ Prøv igjen</button>
                        </div>
                      );
                      const calls = od.calls || [];
                      const puts = od.puts || [];
                      const cp = od.currentPrice;

                      const totalCallVol = calls.reduce((s,c)=>s+(c.volume||0),0);
                      const totalPutVol = puts.reduce((s,p)=>s+(p.volume||0),0);
                      const totalVol = totalCallVol + totalPutVol;
                      const cpRatio = totalPutVol > 0 ? (totalCallVol / totalPutVol).toFixed(2) : null;
                      const cpSentiment = cpRatio === null ? null : parseFloat(cpRatio) > 1.5 ? {label:"Sterkt bullish", color:"#10b981"} : parseFloat(cpRatio) > 0.8 ? {label:"Nøytral", color:"#f59e0b"} : {label:"Bearish / put-kjøp", color:"#f43f5e"};

                      const unusualCalls = calls.filter(c=>(c.volume||0)>0&&(c.openInterest||0)>0&&c.volume>c.openInterest*1.5).sort((a,b)=>b.volume-a.volume).slice(0,3);
                      const unusualPuts = puts.filter(p=>(p.volume||0)>0&&(p.openInterest||0)>0&&p.volume>p.openInterest*1.5).sort((a,b)=>b.volume-a.volume).slice(0,3);

                      // Max pain: strike where total loss for option buyers is maximized
                      const allStrikes = [...new Set([...calls.map(c=>c.strike),...puts.map(p=>p.strike)])].sort((a,b)=>a-b);
                      let maxPainStrike = null;
                      let maxPainVal = 0;
                      if (allStrikes.length > 0) {
                        const callMap = {};
                        const putMap = {};
                        calls.forEach(c=>{ callMap[c.strike] = c.openInterest||0; });
                        puts.forEach(p=>{ putMap[p.strike] = p.openInterest||0; });
                        let minLoss = Infinity;
                        allStrikes.forEach(expiry=>{
                          let loss = 0;
                          allStrikes.forEach(s=>{
                            if (expiry > s) loss += (expiry - s) * (putMap[s]||0);
                            if (expiry < s) loss += (s - expiry) * (callMap[s]||0);
                          });
                          if (loss < minLoss) { minLoss = loss; maxPainStrike = expiry; }
                        });
                      }

                      // Live GEX + gamma flip via Black-Scholes
                      const { gammaFlip: gammaFlipLive, gammaRegime: gammaRegimeLive } = calcGammaFlip(od);
                      const gammaSqueeze = detectGammaSqueeze(od);

                      // Top OI strikes (calls + puts combined)
                      const oiByStrike = {};
                      calls.forEach(c=>{ oiByStrike[c.strike] = (oiByStrike[c.strike]||{calls:0,puts:0}); oiByStrike[c.strike].calls += c.openInterest||0; });
                      puts.forEach(p=>{ oiByStrike[p.strike] = (oiByStrike[p.strike]||{calls:0,puts:0}); oiByStrike[p.strike].puts += p.openInterest||0; });
                      const topOI = Object.entries(oiByStrike).map(([k,v])=>({strike:parseFloat(k),calls:v.calls,puts:v.puts,total:v.calls+v.puts})).sort((a,b)=>b.total-a.total).slice(0,5);

                      return (
                        <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px",marginTop:10}}>
                          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                            <div style={{fontSize:11,color:"#334155",letterSpacing:1}}>📊 OPTIONS FLOW — LIVE</div>
                            {od.expirationDate&&(()=>{
                              const d = new Date(od.expirationDate + "T12:00:00");
                              const today = new Date().toISOString().slice(0,10);
                              const isToday = od.expirationDate === today;
                              const daysLeft = Math.round((d - new Date()) / (1000*60*60*24));
                              const formatted = d.toLocaleDateString("nb-NO",{day:"numeric",month:"long",year:"numeric"});
                              return isToday ? (
                                <span style={{fontSize:10,padding:"2px 8px",borderRadius:3,background:"#f43f5e18",color:"#f43f5e",fontWeight:700}} title="Kontrakter som utløper i dag gir ikke nyttig fremoverrettet informasjon — max pain og delta-hedging har ingen tid igjen å virke.">
                                  ⚠️ Forfall i dag — max pain ikke relevant
                                </span>
                              ) : (
                                <span style={{fontSize:10,padding:"2px 8px",borderRadius:3,background:"#a78bfa18",color:"#a78bfa",fontWeight:700}}>
                                  Forfall: {formatted} ({daysLeft}d)
                                </span>
                              );
                            })()}
                          </div>

                          {/* Gamma squeeze alert */}
                          {gammaSqueeze&&(
                            <div style={{marginBottom:12,padding:"10px 14px",background: gammaSqueeze.strength==="STERK"?"#f59e0b18":"#a78bfa12",border:`1px solid ${gammaSqueeze.strength==="STERK"?"#f59e0b":"#a78bfa"}60`,borderRadius:6}}>
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                                <span style={{fontSize:16}}>⚡</span>
                                <span style={{fontSize:12,fontWeight:700,color: gammaSqueeze.strength==="STERK"?"#f59e0b":"#a78bfa",letterSpacing:0.5}}>
                                  GAMMA SQUEEZE SETUP — {gammaSqueeze.strength}
                                </span>
                              </div>
                              <div style={{fontSize:11,color:"#94a3b8",marginBottom:6,lineHeight:1.5}}>
                                Opsjonspresset kan tvinge market makers til å kjøpe aksjer mekanisk — en selvforsterkende oppgangsspiral.
                              </div>
                              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                                {gammaSqueeze.conditions.map((c,i)=>(
                                  <span key={i} style={{fontSize:10,padding:"2px 7px",borderRadius:3,background:"#0d1e30",color:"#64748b"}}>✓ {c}</span>
                                ))}
                              </div>
                              {gammaSqueeze.topCall&&(
                                <div style={{marginTop:7,fontSize:11,color:"#94a3b8"}}>
                                  Sterkeste signal: <span style={{color:"#10b981",fontWeight:700}}>CALL ${gammaSqueeze.topCall.strike}</span>
                                  {" "}— {(gammaSqueeze.topCall.volume||0).toLocaleString()} vol vs {gammaSqueeze.topCall.openInterest} OI
                                  {" "}({Math.round(gammaSqueeze.topCall.volume/gammaSqueeze.topCall.openInterest)}× OI)
                                </div>
                              )}
                            </div>
                          )}

                          {/* Flow summary */}
                          {(()=>{
                            const expDate2 = od.expirationDate ? new Date(od.expirationDate+"T12:00:00") : null;
                            const daysLeft2 = expDate2 ? Math.round((expDate2 - new Date())/(1000*60*60*24)) : null;
                            const isToday2 = od.expirationDate === new Date().toISOString().slice(0,10);
                            const topUC = unusualCalls[0], topUP = unusualPuts[0];

                            // Bias score: positive = bullish, negative = bearish
                            let score = 0;
                            const bullets = [];

                            if (cpRatio !== null) {
                              const r = parseFloat(cpRatio);
                              if (r > 1.8) { score += 2; bullets.push({text:`Call/Put ${cpRatio} — sterkt bullish sentiment`, color:"#10b981"}); }
                              else if (r > 1.2) { score += 1; bullets.push({text:`Call/Put ${cpRatio} — forsiktig bullish`, color:"#10b981"}); }
                              else if (r < 0.7) { score -= 2; bullets.push({text:`Call/Put ${cpRatio} — bearish / put-dominans`, color:"#f43f5e"}); }
                              else { bullets.push({text:`Call/Put ${cpRatio} — nøytral`, color:"#94a3b8"}); }
                            }

                            if (maxPainStrike && cp && !isToday2) {
                              const gp = Math.abs((maxPainStrike-cp)/cp*100);
                              const dir = maxPainStrike > cp ? "over" : "under";
                              const dirScore = maxPainStrike > cp ? 1 : -1;
                              if (daysLeft2 !== null && gp < 3 && daysLeft2 >= 3) {
                                score += dirScore; bullets.push({text:`Max pain $${maxPainStrike} — ${gp.toFixed(1)}% ${dir} kurs, sterk magnet (${daysLeft2}d)`, color:"#a78bfa"});
                              } else if (daysLeft2 !== null && gp < 7 && daysLeft2 >= 3) {
                                score += dirScore*0.5; bullets.push({text:`Max pain $${maxPainStrike} — ${gp.toFixed(1)}% ${dir} kurs, mulig magnet (${daysLeft2}d)`, color:"#a78bfa"});
                              } else {
                                bullets.push({text:`Max pain $${maxPainStrike} — ${gp.toFixed(1)}% ${dir} kurs (for langt for sterk tiltrekning)`, color:"#475569"});
                              }
                            }

                            if (gammaRegimeLive) {
                              const gScore = gammaRegimeLive === "POSITIV" ? 1 : -1;
                              score += gScore;
                              const flipText = gammaFlipLive ? ` (flip $${gammaFlipLive})` : "";
                              const aboveBelow = gammaFlipLive && cp
                                ? (cp > gammaFlipLive ? "over flip" : "under flip") + " — " + (gammaRegimeLive === "POSITIV" ? "stabiliserende" : "forsterkende")
                                : "";
                              bullets.push({
                                text: `Gamma regime: ${gammaRegimeLive}${flipText}${aboveBelow ? " — " + aboveBelow : ""}`,
                                color: gammaRegimeLive === "POSITIV" ? "#10b981" : "#f43f5e"
                              });
                            }

                            if (topUC && topUP) {
                              const ratio = topUC.volume / topUP.volume;
                              if (ratio > 2) { score += 1; bullets.push({text:`Uvanlig CALL dominerer: $${topUC.strike} (${topUC.volume.toLocaleString()} vol)`, color:"#10b981"}); }
                              else if (ratio < 0.5) { score -= 1; bullets.push({text:`Uvanlig PUT dominerer: $${topUP.strike} (${topUP.volume.toLocaleString()} vol)`, color:"#f43f5e"}); }
                              else { bullets.push({text:`Blandet uvanlig aktivitet — CALL $${topUC.strike} + PUT $${topUP.strike}`, color:"#f59e0b"}); }
                            } else if (topUC) {
                              score += 1; bullets.push({text:`Uvanlig CALL-kjøp: $${topUC.strike} — ${topUC.volume.toLocaleString()} vol vs ${topUC.openInterest} OI`, color:"#10b981"});
                            } else if (topUP) {
                              score -= 1; bullets.push({text:`Uvanlig PUT-kjøp: $${topUP.strike} — ${topUP.volume.toLocaleString()} vol vs ${topUP.openInterest} OI`, color:"#f43f5e"});
                            }

                            const biasLabel = score >= 2 ? "STERKT BULLISH" : score >= 1 ? "FORSIKTIG BULLISH" : score <= -2 ? "STERKT BEARISH" : score <= -1 ? "FORSIKTIG BEARISH" : "NØYTRAL";
                            const biasColor = score >= 1 ? "#10b981" : score <= -1 ? "#f43f5e" : "#94a3b8";

                            if (!bullets.length) return null;
                            return (
                              <div style={{marginBottom:12,padding:"10px 12px",background:"#040d18",borderRadius:6,border:`1px solid ${biasColor}30`}}>
                                <div style={{fontSize:9,color:"#334155",letterSpacing:1,marginBottom:7}}>HVA SIER FLOW NÅ?</div>
                                <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:8}}>
                                  {bullets.map((b,i)=>(
                                    <div key={i} style={{fontSize:11,color:b.color,display:"flex",alignItems:"baseline",gap:6}}>
                                      <span style={{fontSize:8,opacity:0.7,flexShrink:0}}>●</span>{b.text}
                                    </div>
                                  ))}
                                </div>
                                <div style={{display:"flex",alignItems:"center",gap:8}}>
                                  <div style={{fontSize:9,color:"#334155"}}>TOTAL BIAS:</div>
                                  <div style={{fontSize:10,fontWeight:700,color:biasColor,padding:"2px 8px",borderRadius:3,background:biasColor+"18"}}>{biasLabel}</div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* C/P ratio + key metrics */}
                          {(()=>{
                            const totalOI = calls.reduce((s,c)=>s+(c.openInterest||0),0) + puts.reduce((s,p)=>s+(p.openInterest||0),0);
                            const afterHours = totalVol === 0;
                            return (
                              <div style={{marginBottom:12}}>
                                {afterHours && (
                                  <div style={{fontSize:11,color:"#475569",background:"#0d1e30",borderRadius:4,padding:"5px 10px",marginBottom:8}}>
                                    🕐 Volum er 0 — børsen er stengt. OI og Max Pain er tilgjengelig hele døgnet.
                                  </div>
                                )}
                                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                                  {cpRatio&&(
                                    <div style={{flex:1,minWidth:100,background:"#0d1e30",borderRadius:6,padding:"10px 12px"}}>
                                      <div style={{fontSize:10,color:"#475569",marginBottom:4}}>CALL/PUT RATIO</div>
                                      <div style={{fontSize:22,fontWeight:700,color:cpSentiment.color}}>{cpRatio}</div>
                                      <div style={{fontSize:11,color:cpSentiment.color,marginTop:2}}>{cpSentiment.label}</div>
                                    </div>
                                  )}
                                  {!afterHours&&(
                                    <>
                                      <div style={{flex:1,minWidth:100,background:"#0d1e30",borderRadius:6,padding:"10px 12px"}}>
                                        <div style={{fontSize:10,color:"#475569",marginBottom:4}}>CALL VOLUM</div>
                                        <div style={{fontSize:16,fontWeight:700,color:"#10b981"}}>{totalCallVol.toLocaleString()}</div>
                                      </div>
                                      <div style={{flex:1,minWidth:100,background:"#0d1e30",borderRadius:6,padding:"10px 12px"}}>
                                        <div style={{fontSize:10,color:"#475569",marginBottom:4}}>PUT VOLUM</div>
                                        <div style={{fontSize:16,fontWeight:700,color:"#f43f5e"}}>{totalPutVol.toLocaleString()}</div>
                                      </div>
                                    </>
                                  )}
                                  {maxPainStrike&&(
                                    <div style={{flex:1,minWidth:100,background:"#0d1e30",borderRadius:6,padding:"10px 12px"}}>
                                      <div style={{fontSize:10,color:"#475569",marginBottom:4}}>MAX PAIN</div>
                                      <div style={{fontSize:16,fontWeight:700,color:"#a78bfa"}}>${maxPainStrike}</div>
                                      {cp&&(()=>{
                                        const gapPct = Math.abs((maxPainStrike - cp) / cp * 100);
                                        const expDate = od.expirationDate ? new Date(od.expirationDate + "T12:00:00") : null;
                                        const daysLeft = expDate ? Math.round((expDate - new Date()) / (1000*60*60*24)) : null;
                                        const isToday = od.expirationDate === new Date().toISOString().slice(0,10);
                                        const direction = maxPainStrike > cp ? "bullish" : "bearish";
                                        const reachable = !isToday && daysLeft !== null && gapPct < 5 && daysLeft >= 3;
                                        const sentiment = !isToday && gapPct < 3 && daysLeft >= 5 ? {label:"Prismål", color:"#10b981"} :
                                                          !isToday && gapPct < 7 && daysLeft >= 5 ? {label:"Mulig prismål", color:"#f59e0b"} :
                                                          {label:direction==="bullish"?"Bullish signal":"Bearish signal", color:direction==="bullish"?"#10b981":"#f43f5e"};
                                        return (
                                          <>
                                            <div style={{fontSize:10,color:direction==="bullish"?"#10b981":"#f43f5e",marginTop:2,fontWeight:700}}>
                                              {direction==="bullish"?"▲":"▼"} {gapPct.toFixed(1)}% {direction==="bullish"?"under":"over"} max pain
                                            </div>
                                            <div style={{fontSize:9,marginTop:3,padding:"2px 5px",borderRadius:3,background:sentiment.color+"18",color:sentiment.color,fontWeight:700,display:"inline-block"}}>
                                              {sentiment.label}
                                            </div>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  )}
                                  {totalOI>0&&(
                                    <div style={{flex:1,minWidth:100,background:"#0d1e30",borderRadius:6,padding:"10px 12px"}}>
                                      <div style={{fontSize:10,color:"#475569",marginBottom:4}}>TOTAL OI</div>
                                      <div style={{fontSize:16,fontWeight:700,color:"#94a3b8"}}>{totalOI.toLocaleString()}</div>
                                    </div>
                                  )}
                                  {gammaRegimeLive&&(
                                    <div style={{flex:1,minWidth:120,background:"#0d1e30",borderRadius:6,padding:"10px 12px"}}>
                                      <div style={{fontSize:10,color:"#475569",marginBottom:4}}>GAMMA REGIME</div>
                                      <div style={{fontSize:14,fontWeight:700,color:gammaRegimeLive==="POSITIV"?"#10b981":"#f43f5e"}}>{gammaRegimeLive}</div>
                                      {gammaFlipLive&&cp&&(()=>{
                                        const above = cp > gammaFlipLive;
                                        const gapPct = Math.abs((cp - gammaFlipLive) / gammaFlipLive * 100);
                                        return (
                                          <>
                                            <div style={{fontSize:10,color:"#64748b",marginTop:3}}>Flip: <span style={{color:"#e2e8f0",fontWeight:600}}>${gammaFlipLive}</span></div>
                                            <div style={{fontSize:10,marginTop:2,color:above?"#10b981":"#f43f5e",fontWeight:700}}>
                                              {above?"▲":"▼"} {gapPct.toFixed(1)}% {above?"over":"under"} flip
                                            </div>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Top OI by strike */}
                          {topOI.length>0&&(
                            <div style={{marginBottom:12}}>
                              <div style={{fontSize:10,color:"#475569",letterSpacing:0.5,marginBottom:6}}>TOPP OI PER STRIKE (calls / puts)</div>
                              {topOI.map((row,i)=>{
                                const maxTotal = topOI[0].total;
                                const callPct = row.total>0?Math.round(row.calls/row.total*100):0;
                                const isCP = cp && Math.abs(row.strike - cp)/cp < 0.03;
                                return(
                                  <div key={i} style={{marginBottom:5}}>
                                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:2}}>
                                      <span style={{color:isCP?"#f59e0b":"#94a3b8",fontWeight:isCP?700:400}}>${row.strike}{isCP?" ◀ ATM":""}</span>
                                      <span style={{color:"#64748b"}}>{row.total.toLocaleString()} OI</span>
                                      <span style={{fontSize:10}}>
                                        <span style={{color:"#10b981"}}>{row.calls.toLocaleString()}C</span>
                                        <span style={{color:"#64748b"}}> / </span>
                                        <span style={{color:"#f43f5e"}}>{row.puts.toLocaleString()}P</span>
                                      </span>
                                    </div>
                                    <div style={{height:4,background:"#0a1628",borderRadius:2,overflow:"hidden"}}>
                                      <div style={{height:"100%",width:`${Math.round(row.total/maxTotal*100)}%`,background:`linear-gradient(90deg,#10b981 ${callPct}%,#f43f5e ${callPct}%)`,borderRadius:2}}/>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Unusual activity */}
                          {(unusualCalls.length>0||unusualPuts.length>0)&&(
                            <div>
                              <div style={{fontSize:10,color:"#475569",letterSpacing:0.5,marginBottom:6}}>🔥 UVANLIG AKTIVITET (volum &gt; OI × 1.5)</div>
                              {unusualCalls.slice(0,2).map((c,i)=>(
                                <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 8px",background:"#10b98118",borderRadius:4,marginBottom:3,fontSize:11}}>
                                  <span style={{color:"#10b981",fontWeight:700}}>CALL ${c.strike}</span>
                                  <span style={{color:"#94a3b8"}}>Vol {(c.volume||0).toLocaleString()}</span>
                                  <span style={{color:"#64748b"}}>OI {(c.openInterest||0).toLocaleString()}</span>
                                  {c.impliedVolatility&&<span style={{color:"#a78bfa"}}>IV {(c.impliedVolatility*100).toFixed(0)}%</span>}
                                </div>
                              ))}
                              {unusualPuts.slice(0,2).map((p,i)=>(
                                <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 8px",background:"#f43f5e18",borderRadius:4,marginBottom:3,fontSize:11}}>
                                  <span style={{color:"#f43f5e",fontWeight:700}}>PUT ${p.strike}</span>
                                  <span style={{color:"#94a3b8"}}>Vol {(p.volume||0).toLocaleString()}</span>
                                  <span style={{color:"#64748b"}}>OI {(p.openInterest||0).toLocaleString()}</span>
                                  {p.impliedVolatility&&<span style={{color:"#a78bfa"}}>IV {(p.impliedVolatility*100).toFixed(0)}%</span>}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Nøkkelnivåer & Handling */}
                          {cp&&(()=>{
                            const levels = [];

                            // Unusual calls at or above price → squeeze targets
                            unusualCalls.filter(c=>c.strike>=cp*0.99).forEach(c=>{
                              levels.push({ price:c.strike, dir:"bull", tag:"magnet",
                                label:`Call magnet — ${Math.round(c.volume/(c.openInterest||1))}× OI (${c.volume.toLocaleString()} vol)`,
                                outcome:"Squeeze-mål. MMs kjøper aksjer når prisen nærmer seg." });
                            });

                            // Max pain
                            if (maxPainStrike && Math.abs(maxPainStrike-cp)/cp>0.005) {
                              const mpDir = maxPainStrike>cp?"bull":"bear";
                              levels.push({ price:maxPainStrike, dir:mpDir, tag:"maxpain",
                                label:"Max pain — gravity for opsjonsforfall",
                                outcome:"Gravitasjonspunkt til forfall. Kurs dras hit av delta-hedging." });
                            }

                            // Top call-OI wall strikes above (not already listed)
                            const topCallWall = topOI.filter(t=>t.strike>cp&&t.calls>t.puts*2&&!levels.find(l=>l.price===t.strike)).slice(0,2);
                            topCallWall.forEach((t,i)=>{
                              const isHighestOI = i===0&&t.total===topOI[0]?.total;
                              levels.push({ price:t.strike, dir:"wall", tag:"wall",
                                label:`${t.calls.toLocaleString()}C / ${t.puts}P OI — call-vegg`,
                                outcome: isHighestOI
                                  ? "Sterk vegg. ≈70% sjanse for stall/reversering. Brytes → akselerert stigning (short gamma squeeze fra call-selgere)."
                                  : "Call-vegg. Call-selgere selger aktivt for å holde prisen under." });
                            });

                            // Gamma flip
                            if (gammaFlipLive && Math.abs(gammaFlipLive-cp)/cp>0.003) {
                              const flipBelow = gammaFlipLive < cp;
                              levels.push({ price:gammaFlipLive, dir:"key", tag:"flip",
                                label:`Gamma flip $${gammaFlipLive}`,
                                outcome: flipBelow
                                  ? "Kritisk støtte. Faller kurs under dette: gamma-regimet skifter og fall forsterkes automatisk av MM-hedging."
                                  : "Kritisk motstand. Brytes oppover: gamma-regimet stabiliserer og skvisen dempes." });
                            }

                            // Unusual puts below price
                            unusualPuts.filter(p=>p.strike<cp).forEach(p=>{
                              const mult = Math.round(p.volume/(p.openInterest||1));
                              levels.push({ price:p.strike, dir:"support", tag:"put",
                                label:`Put støtte (${mult}× OI, ${p.volume.toLocaleString()} vol)`,
                                outcome: mult>5
                                  ? "Svært stor put-aktivitet. Kan være institusjonell sikring av long-posisjon (bullish for aksjen) eller direkte bearish sats."
                                  : "Put-aktivitet. Støttenivå om pris faller hit — put-selgere (MMs) kjøper aksjer for å hedget." });
                            });

                            // Put-OI floor below
                            topOI.filter(t=>t.strike<cp&&t.puts>t.calls*2&&!levels.find(l=>l.price===t.strike)).slice(0,1).forEach(t=>{
                              levels.push({ price:t.strike, dir:"support", tag:"floor",
                                label:`${t.puts.toLocaleString()}P OI — put-gulv`,
                                outcome:"Put-gulv. ≈70% sjanse for bounce her. Brytes → forsterket fall (MMs selger aksjer for å hedget puts som går ITM)." });
                            });

                            const above = levels.filter(l=>l.price>cp).sort((a,b)=>a.price-b.price);
                            const below = levels.filter(l=>l.price<=cp).sort((a,b)=>b.price-a.price);
                            if (!above.length && !below.length) return null;

                            // Action text
                            const stopLevel = below.find(l=>l.tag==="flip")||below[0];
                            const firstTarget = above[0];
                            const isToday2 = od.expirationDate===new Date().toISOString().slice(0,10);
                            let actionText;
                            if (gammaSqueeze?.strength==="STERK"&&firstTarget) {
                              const targets = above.filter(l=>l.tag!=="wall"||above.indexOf(l)<3).slice(0,3).map(l=>`$${l.price}`).join(" → ");
                              actionText = `Gamma squeeze STERK: Entry under $${firstTarget.price} med stop under $${stopLevel?.price?.toFixed(2)||"—"}. Mål i rekkefølge: ${targets}.`;
                            } else if (gammaSqueeze?.strength==="MULIG"&&firstTarget) {
                              actionText = `Mulig gamma squeeze: Vent på bekreftelse over $${firstTarget.price} (volum + grønn stang) før entry. Stop under $${stopLevel?.price?.toFixed(2)||"—"}.`;
                            } else if (cpRatio!==null&&parseFloat(cpRatio)<0.7&&below.length) {
                              actionText = `Bearish put-dominans: Vurder puts eller reduser eksponering. Første støtte: $${below[0]?.price?.toFixed(2)||"—"}.`;
                            } else if (maxPainStrike&&!isToday2) {
                              const dir = maxPainStrike>cp?"opp mot":"ned mot";
                              actionText = `Nøytral: Max pain-gravitasjon trekker kurs ${dir} $${maxPainStrike} til forfall. Vent på retningsbekreftelse.`;
                            } else {
                              actionText = `Blandet bilde — ingen klar kant. Observer volum og vent på retningsbekreftelse.`;
                            }

                            // Stop factors depending on scenario
                            const stopFactors = [];
                            if (gammaSqueeze) {
                              stopFactors.push("IV-stigning gjør nye calls dyrere → færre kjøpere → motor stopper");
                              stopFactors.push("Profit-taking: call-kjøpere selger tilbake → MMs unwinder hedge → salgspress");
                              stopFactors.push("Makro/nyheter trumfer opsjonsmekanikk (SPY-fall, Fed, earnings)");
                              if (above.some(l=>l.tag==="wall")) stopFactors.push(`Call-veggen ved $${above.find(l=>l.tag==="wall")?.price} — call-selgere selger aktivt for å holde prisen under`);
                            } else if (cpRatio!==null&&parseFloat(cpRatio)<0.7) {
                              stopFactors.push("Sterk makro-oppgang kan overstyre put-signalet");
                              stopFactors.push("Put-kjøp kan være institusjonell sikring, ikke retningssats");
                            }

                            const dirColor = { bull:"#10b981", bear:"#f43f5e", key:"#a78bfa", support:"#38bdf8", wall:"#f97316" };

                            return (
                              <div style={{marginBottom:12,padding:"12px",background:"#040d18",borderRadius:6,border:"1px solid #1e293b30"}}>
                                <div style={{fontSize:10,color:"#334155",letterSpacing:1,marginBottom:10}}>🎯 NØKKELNIVÅER & HANDLING</div>

                                {/* Level list */}
                                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
                                  {above.map((l,i)=>(
                                    <div key={i} style={{borderLeft:`2px solid ${dirColor[l.dir]||"#10b981"}20`,paddingLeft:8}}>
                                      <div style={{display:"flex",gap:10,alignItems:"baseline",fontFamily:"monospace"}}>
                                        <span style={{color:dirColor[l.dir]||"#10b981",minWidth:64,fontSize:11,fontWeight:700}}>↑ ${l.price}</span>
                                        <span style={{fontSize:10,color:"#64748b"}}>{l.label}</span>
                                      </div>
                                      {l.outcome&&<div style={{fontSize:10,color:"#334155",marginTop:2,paddingLeft:74,lineHeight:1.4}}>{l.outcome}</div>}
                                    </div>
                                  ))}
                                  <div style={{display:"flex",gap:10,alignItems:"baseline",fontFamily:"monospace",padding:"4px 0"}}>
                                    <span style={{color:"#f59e0b",minWidth:64,fontSize:12,fontWeight:700}}>► ${cp.toFixed(2)}</span>
                                    <span style={{fontSize:10,color:"#f59e0b",fontWeight:700}}>Nåværende kurs</span>
                                  </div>
                                  {below.map((l,i)=>(
                                    <div key={i} style={{borderLeft:`2px solid ${dirColor[l.dir]||"#f43f5e"}20`,paddingLeft:8}}>
                                      <div style={{display:"flex",gap:10,alignItems:"baseline",fontFamily:"monospace"}}>
                                        <span style={{color:dirColor[l.dir]||"#f43f5e",minWidth:64,fontSize:11,fontWeight:700}}>↓ ${typeof l.price==="number"?l.price.toFixed(2):l.price}</span>
                                        <span style={{fontSize:10,color:"#64748b"}}>{l.label}</span>
                                      </div>
                                      {l.outcome&&<div style={{fontSize:10,color:"#334155",marginTop:2,paddingLeft:74,lineHeight:1.4}}>{l.outcome}</div>}
                                    </div>
                                  ))}
                                </div>

                                {/* Action */}
                                <div style={{padding:"8px 10px",background:"#0a1628",borderRadius:5,borderLeft:"2px solid #a78bfa",marginBottom:stopFactors.length?8:0}}>
                                  <div style={{fontSize:9,color:"#334155",letterSpacing:1,marginBottom:4}}>MULIG HANDLING</div>
                                  <div style={{fontSize:11,color:"#e2e8f0",lineHeight:1.6}}>{actionText}</div>
                                </div>

                                {/* Stop factors */}
                                {stopFactors.length>0&&(
                                  <div style={{padding:"8px 10px",background:"#0a1628",borderRadius:5,borderLeft:"2px solid #f43f5e30"}}>
                                    <div style={{fontSize:9,color:"#334155",letterSpacing:1,marginBottom:5}}>HVA KAN STOPPE DETTE</div>
                                    {stopFactors.map((f,i)=>(
                                      <div key={i} style={{display:"flex",gap:6,fontSize:10,color:"#475569",marginBottom:i<stopFactors.length-1?4:0,lineHeight:1.4}}>
                                        <span style={{color:"#f43f5e",flexShrink:0}}>●</span>{f}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div style={{marginTop:6,fontSize:9,color:"#1e3a5f"}}>Ikke finansiell rådgivning. Bruk som ett av flere analyseverktøy.</div>
                              </div>
                            );
                          })()}

                          <details style={{marginTop:12}}>
                            <summary style={{fontSize:11,color:"#475569",cursor:"pointer",listStyle:"none",display:"flex",alignItems:"center",gap:6,userSelect:"none"}}>
                              <span style={{fontSize:12}}>📖</span> <span>Forstå dataene</span> <span style={{fontSize:9,color:"#334155"}}>▼</span>
                            </summary>
                            <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:8}}>
                              {[
                                { term:"Open Interest (OI)", color:"#94a3b8", text:"Antall opsjonskontrakter som er åpne akkurat nå — ikke handlet i dag, men holdt av noen. Høy OI på en strike betyr at mange aktører har posisjonert seg der. Det er et kart over hvor pengene ligger." },
                                { term:"Call/Put Ratio", color:"#10b981", text:"For hver put kjøpt, hvor mange calls kjøpes. Over 1.5 = bullish sentiment. Under 0.7 = frykt/bearish. Vær obs: institusjonelle kjøper puts som sikring på long-posisjoner — ikke alltid bearish." },
                                { term:"Max Pain", color:"#a78bfa", text:"Striken der flest opsjoner utløper verdiløse — der opsjons-selgerne (market makers) tjener mest. Aksjekursen graviterer ofte mot max pain i uken før forfall fordi market makers delta-hedger posisjonene sine." },
                                { term:"Uvanlig aktivitet 🔥", color:"#f59e0b", text:"Volum > OI × 1.5 betyr at nye store posisjoner åpnes i dag, ikke at gamle handles rundt. Dette er det sterkeste signalet — noen kjøper en stor ny posisjon akkurat nå. Typisk institusjonelt." },
                                { term:"OI per strike", color:"#38bdf8", text:"Striken med høyest OI er et magnetnivå — prisen trekkes dit fordi market makers kontinuerlig hedger sine posisjoner der. Calls >> Puts på en strike = markedet tror prisen vil nå dit. ATM = nærmeste nåværende kurs." },
                                { term:"Delta-hedging — hvorfor OI beveger prisen", color:"#38bdf8", text:"Når du kjøper en call, selger en market maker den til deg. De vil ikke ha retningsrisiko, så de kjøper aksjer proporsjonalt med opsjonens delta (0–1). Når prisen nærmer seg en høy OI-strike øker delta automatisk → de kjøper MER aksjer → prisen trekkes dit. Faller prisen fra striken synker delta → de selger aksjer → prisen presses ned. Eksempel: META nærmer seg $600 (høyest OI). Callene går fra delta 0.3 → 0.7 → market makers kjøper kontinuerlig META-aksjer. Det er ikke magi — det er algoritmer som delta-hedger som skaper magneten." },
                                { term:"OI-veggen som tak — hvorfor høy-OI strikes er motstand", color:"#f97316", text:"Tre lag med motstand ved en høy call-OI strike: (1) Market makers slutter å kjøpe aksjer når de er fullt hedget over striken — motoren slukner. (2) De som solgte calls der selger aktivt aksjen for å holde prisen under — de taper penger om den går over. (3) Alle andre ser OI-veggen og selger der. Samme logikk omvendt for høy put-OI under prisen — det er støtte fordi mange har investert i å holde prisen oppe." },
                                { term:"Gamma squeeze — når options tar kontroll", color:"#f472b6", text:"Noen ganger forsterker options en bevegelse langt utover det fundamentals alene skulle tilsi. Aksjen stiger 5% på nyheter → delta-hedging krever kjøp → prisen stiger 3% til → krever enda mer kjøp. En selvforsterkende spiral. NVDA har gjort dette flere ganger. GME i 2021 var ekstremt: retail kjøpte billige OTM calls, MMs måtte kjøpe aksjer, som drev prisen opp, som krevde mer kjøp. Options-markedet tok fullstendig kontroll." },
                                { term:"Hvor mye påvirker options aksjeprisen?", color:"#94a3b8", text:"Normalt 5–15% av bevegelsen — fundamentals og makro er den egentlige motoren. Men på OpEx-fredager (forfall) kan options stå for 30–50% av intradag-bevegelsen. 0DTE-opsjoner (null dager til forfall) er nå 50%+ av S&P 500-volum daglig og skaper intense kortsiktige gamma-effekter. Beste tilnærming: bruk fundamentals for retning, options flow for bekreftelse og sentiment, teknisk analyse for timing og entry." },
                                { term:"Gamma Flip (Zero Gamma Level)", color:"#10b981", text:"Prisen der market makers' samlede gammaeksponering er nøyaktig null — grensen mellom positiv og negativ gammaregime. Beregnet fra Black-Scholes på faktisk OI og IV. Over flip: MMs kjøper dips og selger rally → markedet stabiliseres. Under flip: MMs selger ved fall og kjøper ved stigning → bevegelsen forsterkes. Gamma flip er ofte en sterkere støtte/motstand enn tekniske nivåer fordi den er forankret i faktisk derivatposisjonering." },
                                { term:"Implied Volatility (IV %)", color:"#c084fc", text:"Markedets forventning til fremtidig kursbevegelse. Høy IV = dyre opsjoner og stor forventet svingning. Lav IV = billige opsjoner. IV stiger ofte før earnings og faller etter (IV crush)." },
                              ].map(({term,color,text},i) => (
                                <div key={i} style={{padding:"9px 12px",background:"#0a1628",borderRadius:6,borderLeft:`2px solid ${color}40`}}>
                                  <div style={{fontSize:11,fontWeight:700,color:color,marginBottom:4}}>{term}</div>
                                  <div style={{fontSize:11,color:"#64748b",lineHeight:1.6}}>{text}</div>
                                </div>
                              ))}
                            </div>
                          </details>
                          <div style={{marginTop:10,fontSize:10,color:"#1e3a5f"}}>Kilde: CBOE — nærmeste opsjonsforfall. Oppdateres ved bytte av aksje.</div>
                        </div>
                      );
                    })()}

                    <div style={{marginTop:10,padding:"9px 13px",background:"#060f1c",border:"1px solid #1e293b",borderRadius:6}}>
                      <div style={{fontSize:11,color:"#334155",lineHeight:1.5}}>⚠️ Tekniske indikatorer er basert på historisk kursdata og er IKKE finansiell rådgivning. RSI og Bollinger er mer pålitelige med lengre tidsperioder (3M+). Beregnet fra tilgjengelig kurshistorikk.</div>
                    </div>
                  </div>
                );
              })()}

              {deepTab==="compare"&&(
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                    <select value={compareA} onChange={e=>setCompareA(e.target.value)}
                      style={{padding:"6px 10px",background:"#0a1628",border:"1px solid #f59e0b50",borderRadius:4,color:"#f59e0b",fontSize:12,fontFamily:"inherit",fontWeight:700}}>
                      {allStockList.map(s=><option key={s.ticker} value={s.ticker}>{s.ticker}</option>)}
                    </select>
                    <div style={{fontSize:12,color:"#334155",fontWeight:700}}>vs</div>
                    <select value={compareB} onChange={e=>setCompareB(e.target.value)}
                      style={{padding:"6px 10px",background:"#0a1628",border:"1px solid #3b82f650",borderRadius:4,color:"#3b82f6",fontSize:12,fontFamily:"inherit",fontWeight:700}}>
                      {allStockList.map(s=><option key={s.ticker} value={s.ticker}>{s.ticker}</option>)}
                    </select>
                  </div>
                  <CompareTable tickerA={compareA} tickerB={compareB} allStockList={allStockList}/>
                </div>
              )}

              {false&&(()=>{
                const DEEP_PERIODS = [
                  { label:"1U", days:5 },{ label:"1M", days:21 },{ label:"3M", days:63 },
                  { label:"6M", days:126 },{ label:"1Å", days:252 },
                ];
                const techCloses = techData[deepTicker]?.closes;
                const allPrices = techCloses || deepLive?.history || [];
                if (allPrices.length < 2) return null;
                const dp = DEEP_PERIODS.find(p => p.label === deepPeriod) || DEEP_PERIODS[2];
                const prices = allPrices.slice(-dp.days);
                if (prices.length < 2) return null;
                const startIdx = Math.max(0, allPrices.length - dp.days);
                const ma9Slice   = calcMALine(allPrices, 9).slice(startIdx);
                const ema20Slice = calcEMALine(allPrices, 20).slice(startIdx);
                const ma50Slice  = calcMALine(allPrices, 50).slice(startIdx);
                const ma200Slice = calcMALine(allPrices, 200).slice(startIdx);
                const activeMA = [
                  ...(showMA.ma9   ? ma9Slice   : []),
                  ...(showMA.ema20 ? ema20Slice : []),
                  ...(showMA.ma50  ? ma50Slice  : []),
                  ...(showMA.ma200 ? ma200Slice : []),
                ].filter(v => v !== null);
                const W=660, H=160, pad={t:10,r:16,b:32,l:58};
                const cW=W-pad.l-pad.r, cH=H-pad.t-pad.b;
                const allVals = [...prices, ...activeMA];
                const minP=Math.min(...allVals), maxP=Math.max(...allVals), rP=maxP-minP||1;
                const toX = i => (i/Math.max(prices.length-1,1))*cW;
                const toY = p => cH-((p-minP)/rP)*cH;
                const maToSegs = (vals) => vals.reduce((segs, v, i) => {
                  if (v === null) return segs;
                  const pt = `${toX(i)},${toY(v)}`;
                  if (!segs.length || vals[i-1] === null) segs.push([]);
                  segs[segs.length-1].push(pt);
                  return segs;
                }, []);
                const pts = prices.map((p,i)=>`${toX(i)},${toY(p)}`).join(" ");
                const pct = (prices[prices.length-1]/prices[0]-1)*100;
                const lineColor = pct >= 0 ? "#10b981" : "#f43f5e";
                const approxDate = (i) => {
                  const daysAgo = (prices.length - 1 - i);
                  const d = new Date();
                  d.setDate(d.getDate() - daysAgo);
                  return d.toLocaleDateString("nb-NO",{day:"numeric",month:"short"});
                };
                const numLabels=5;
                const labelIdxs = Array.from({length:numLabels},(_,i)=>Math.round(i*(prices.length-1)/(numLabels-1)));
                const yLabels = [minP, minP+(maxP-minP)/2, maxP];
                const handleMouseMove = (e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const svgX = ((e.clientX - rect.left) / rect.width) * W - pad.l;
                  if (svgX < 0 || svgX > cW) { setChartHover(null); return; }
                  const idx = Math.round((svgX / cW) * (prices.length - 1));
                  const ci = Math.max(0, Math.min(prices.length - 1, idx));
                  setChartHover({ x: toX(ci), y: toY(prices[ci]), price: prices[ci], label: approxDate(ci) });
                };
                const handleTouchMove = (e) => {
                  const touch = e.touches[0];
                  const rect = e.currentTarget.getBoundingClientRect();
                  const svgX = ((touch.clientX - rect.left) / rect.width) * W - pad.l;
                  if (svgX < 0 || svgX > cW) { setChartHover(null); return; }
                  const idx = Math.round((svgX / cW) * (prices.length - 1));
                  const ci = Math.max(0, Math.min(prices.length - 1, idx));
                  setChartHover({ x: toX(ci), y: toY(prices[ci]), price: prices[ci], label: approxDate(ci) });
                };
                const hv = chartHover;
                const tooltipX = hv ? (hv.x + pad.l + 74 > W ? hv.x - 70 : hv.x + 6) : 0;
                return (
                  <div style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px 14px 6px",marginTop:14}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:6}}>
                      <div style={{fontSize:9,color:"#334155",letterSpacing:1}}>KURSUTVIKLING{techCloses?" — DAGLIG DATA":""}</div>
                      <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
                        <span style={{fontSize:11,fontWeight:700,color:lineColor,marginRight:6}}>{pct>=0?"+":""}{pct.toFixed(2)}%</span>
                        {DEEP_PERIODS.map(p=>(
                          <button key={p.label} onClick={()=>{setDeepPeriod(p.label);setChartHover(null);}}
                            style={{padding:"2px 8px",border:"1px solid",borderColor:deepPeriod===p.label?lineColor:"#1e293b",background:deepPeriod===p.label?lineColor+"20":"transparent",color:deepPeriod===p.label?lineColor:"#475569",borderRadius:3,cursor:"pointer",fontSize:9,fontFamily:"inherit"}}>
                            {p.label}
                          </button>
                        ))}
                        <div style={{width:1,height:12,background:"#1e293b",margin:"0 2px"}}/>
                        {[["ma9","MA 9","#f97316"],["ema20","EMA 20","#38bdf8"],["ma50","MA 50","#f59e0b"],["ma200","MA 200","#a78bfa"]].map(([k,lbl,c])=>(
                          <button key={k} onClick={()=>setShowMA(p=>({...p,[k]:!p[k]}))}
                            style={{padding:"2px 7px",border:`1px solid ${showMA[k]?c+"80":"#1e293b"}`,background:showMA[k]?c+"18":"transparent",color:showMA[k]?c:"#334155",borderRadius:3,cursor:"pointer",fontSize:9,fontFamily:"inherit",fontWeight:showMA[k]?700:400}}>
                            {lbl}
                          </button>
                        ))}
                      </div>
                    </div>
                    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{overflow:"visible",cursor:"crosshair"}}
                      onMouseMove={handleMouseMove} onMouseLeave={()=>setChartHover(null)}
                      onTouchMove={handleTouchMove} onTouchEnd={()=>setChartHover(null)}>
                      <defs>
                        <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={lineColor} stopOpacity="0.12"/>
                          <stop offset="100%" stopColor={lineColor} stopOpacity="0"/>
                        </linearGradient>
                      </defs>
                      <g transform={`translate(${pad.l},${pad.t})`}>
                        {yLabels.map((v,i)=>(
                          <g key={i}>
                            <line x1={0} x2={cW} y1={toY(v)} y2={toY(v)} stroke="#1e293b" strokeWidth="0.5"/>
                            <text x={-6} y={toY(v)} fill="#475569" fontSize="10" textAnchor="end" dominantBaseline="middle">${v<10?v.toFixed(2):v.toFixed(0)}</text>
                          </g>
                        ))}
                        <polygon points={`${pts} ${toX(prices.length-1)},${cH} 0,${cH}`} fill="url(#chartFill)"/>
                        {showMA.ma200 && maToSegs(ma200Slice).map((seg,i)=><polyline key={i} points={seg.join(" ")} fill="none" stroke="#a78bfa" strokeWidth="1.2" opacity="0.75" strokeLinecap="round"/>)}
                        {showMA.ma50  && maToSegs(ma50Slice).map((seg,i) =><polyline key={i} points={seg.join(" ")} fill="none" stroke="#f59e0b" strokeWidth="1.2" opacity="0.75" strokeLinecap="round"/>)}
                        {showMA.ema20 && maToSegs(ema20Slice).map((seg,i)=><polyline key={i} points={seg.join(" ")} fill="none" stroke="#38bdf8" strokeWidth="1.2" opacity="0.75" strokeLinecap="round"/>)}
                        {showMA.ma9   && maToSegs(ma9Slice).map((seg,i)  =><polyline key={i} points={seg.join(" ")} fill="none" stroke="#f97316" strokeWidth="1.2" opacity="0.85" strokeLinecap="round"/>)}
                        <polyline points={pts} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        {!hv && <circle cx={toX(prices.length-1)} cy={toY(prices[prices.length-1])} r="3" fill={lineColor}/>}
                        {labelIdxs.map((idx,i)=>(
                          <g key={i}>
                            <line x1={toX(idx)} x2={toX(idx)} y1={cH} y2={cH+4} stroke="#334155" strokeWidth="0.5"/>
                            <text x={toX(idx)} y={cH+14} fill="#475569" fontSize="9" textAnchor="middle">{approxDate(idx)}</text>
                          </g>
                        ))}
                        {hv && (
                          <g>
                            <line x1={hv.x} x2={hv.x} y1={0} y2={cH} stroke="#475569" strokeWidth="1" strokeDasharray="3,3"/>
                            <circle cx={hv.x} cy={hv.y} r="4" fill={lineColor} stroke="#060f1c" strokeWidth="2"/>
                            <rect x={tooltipX} y={hv.y-24} width={68} height={36} rx={4} fill="#0a1628" stroke="#1e3a5f" strokeWidth="1"/>
                            <text x={tooltipX+34} y={hv.y-12} fill="#e2e8f0" fontSize="12" fontWeight="700" textAnchor="middle">${hv.price<10?hv.price.toFixed(2):hv.price.toFixed(1)}</text>
                            <text x={tooltipX+34} y={hv.y+4} fill="#475569" fontSize="9" textAnchor="middle">{hv.label}</text>
                          </g>
                        )}
                      </g>
                    </svg>
                  </div>
                );
              })()}
              {isMobile&&<div style={{height:80}}/>}
            </div>
          )}

          {/* ── JOURNAL ── */}
          {view==="journal"&&(()=>{
            // Sjekkliste bygget på backtesten: velg sterke aksjer i opptrend, unngå de testede advarslene, og styr risikoen
            const CHECKLIST = [
              { id:"regime", label:"Markedslyset grønt eller gult?", desc:"I dag › Oversikt. Ingen nye kjøp når lyset er rødt." },
              { id:"trend",  label:"Aksjen i opptrend?",             desc:"Kurs over 50- og 200-dagers snitt (Watchlist: ↗ opp)" },
              { id:"rs",     label:"Blant de sterkeste?",            desc:"Sterkere enn S&P 500 siste 3 mnd, helst en «leder» i varslene" },
              { id:"warn",   label:"Ingen testet advarsel?",         desc:"Ingen Bull Trap eller Shooting Star siste 5 dager" },
              { id:"earn",   label:"Rapport håndtert?",              desc:"Ingen rapport før utløp — eller et bevisst valg om å holde gjennom" },
              { id:"iv",     label:"Riktig type for opsjonsprisen?", desc:"IV-rang under 50: calls eller debit spread. Over 50: spread eller aksjen." },
              { id:"risk",   label:"Risiko og tapsgrense satt?",     desc:"Maks 2–5 % av kontoen kan tapes, og tapsgrensen er skrevet ned før kjøp" },
            ];
            const planScore = (checks) => CHECKLIST.filter(q => checks?.[q.id]).length;
            const scoreColor = (n) => n >= 6 ? "#10b981" : n >= 4 ? "#f59e0b" : "#f43f5e";
            const scoreLabel = (n) => n >= 6 ? "Sterk setup — gå for det" : n >= 4 ? "OK — vurder halvposisjon" : "Svak — ikke ta traden";
            const REASONS = [
              "── Manuelt ──",
              "MA-brudd","Lav RSI","Post-earnings dip","Gode earnings","Momentum","Nyhet","Rykte","Teknisk breakout","Annet",
              "── Testede signaler ──",
              "📡 Følg bølgen (opptrend + styrke)","📡 RSI-divergens","⚠ Bull Trap (put/short)","⚠ Shooting Star (put/short)",
              "── Eldre app-signaler (ikke testet) ──",
              "📡 EARNINGS PLAY","📡 SQUEEZE PLAY","📡 VOLUME BREAKOUT","📡 OVERSOLD BOUNCE",
              "📡 EARNINGS SETUP","📡 MOMENTUM","📡 DEEP VALUE","📡 REVERSAL WATCH",
            ];
            const openTrades  = journal.filter(t=>!t.exitDate);
            const closedTrades = journal.filter(t=>t.exitDate).sort((a,b)=>b.exitDate.localeCompare(a.exitDate));

            const exportJournal = () => {
              const blob = new Blob([JSON.stringify(journal, null, 2)], { type:"application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = "trading-journal.json"; a.click();
              URL.revokeObjectURL(url);
            };
            const importJournal = (e) => {
              const file = e.target.files[0]; if (!file) return;
              const reader = new FileReader();
              reader.onload = ev => {
                try { const data = JSON.parse(ev.target.result); if (Array.isArray(data)) setJournal(data); } catch {}
              };
              reader.readAsText(file);
              e.target.value = "";
            };

            const saveEntry = (form) => {
              if (!form.ticker || !form.date || !form.price) return;
              const isOpsjon = form.tradeType === "opsjon";
              const entry = {
                id: Date.now()+""+Math.random(),
                ticker: form.ticker.toUpperCase(),
                date: form.date,
                price: parseFloat(form.price),
                qty: parseFloat(form.qty)||1,
                reason: form.reason||"Annet",
                notes: form.notes||"",
                tradeType: form.tradeType||"aksje",
                ...(isOpsjon && {
                  optionType: form.optionType||"call",
                  optionStrike: form.optionStrike ? parseFloat(form.optionStrike) : null,
                  optionExpiry: form.optionExpiry||null,
                }),
                exitDate:null,
                exitPrice:null
              };
              setJournal(prev=>[...prev, entry]);
              setJournalForm(null);
            };
            const closePosition = (form) => {
              if (!form.exitDate || !form.exitPrice) return;
              setJournal(prev=>prev.map(t=>t.id===form.id ? {...t, exitDate:form.exitDate, exitPrice:parseFloat(form.exitPrice)} : t));
              setCloseForm(null);
            };
            const deleteEntry = (id) => setJournal(prev=>prev.filter(t=>t.id!==id));

            const btnBase = { padding:"6px 14px", border:"1px solid #1e293b", borderRadius:4, cursor:"pointer", fontSize:11, fontFamily:"inherit", background:"transparent" };

            return (
              <div>
                {/* Header */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
                  <div style={{display:"flex",gap:2}}>
                    {[["journal","📓 Journal"],["planer","📋 Planer"],["statistikk","📊 Statistikk"],["backtest","🧪 Backtest"]].map(([t,lbl])=>(
                      <button key={t} onClick={()=>setJournalTab(t)}
                        style={{padding:"6px 16px",border:`1px solid ${journalTab===t?"#3b82f6":"#1e293b"}`,borderRadius:4,cursor:"pointer",fontSize:12,fontFamily:"inherit",background:journalTab===t?"#3b82f620":"transparent",color:journalTab===t?"#3b82f6":"#475569",fontWeight:journalTab===t?700:400}}>
                        {lbl} {t==="planer"&&plans.filter(p=>p.status==="watching").length>0?`(${plans.filter(p=>p.status==="watching").length})`:""}
                      </button>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                    {journalTab==="statistikk"||journalTab==="backtest" ? null : journalTab==="journal" ? (<>
                      <button onClick={()=>setJournalForm({ticker:"",date:new Date().toISOString().slice(0,10),price:"",qty:"",reason:"MA-brudd",notes:"",tradeType:"aksje",optionType:"call",optionStrike:"",optionExpiry:""})}
                        style={{...btnBase, background:"#10b98120", border:"1px solid #10b98160", color:"#10b981", fontWeight:700}}>
                        + Ny handel
                      </button>
                      <button onClick={exportJournal} style={{...btnBase, color:"#64748b"}}>⬇ Eksport</button>
                      <label style={{...btnBase, color:"#64748b", display:"inline-block"}}>
                        ⬆ Importer
                        <input type="file" accept=".json" onChange={importJournal} style={{display:"none"}}/>
                      </label>
                    </>) : (
                      <button onClick={()=>setPlanForm({ticker:"",signal:"Leder (følg bølgen)",notes:"",checks:{},status:"watching"})}
                        style={{...btnBase, background:"#a78bfa20", border:"1px solid #a78bfa60", color:"#a78bfa", fontWeight:700}}>
                        + Ny plan
                      </button>
                    )}
                  </div>
                </div>

                {journalTab==="statistikk" && <JournalStats journal={journal} predLog={predLog} techData={techData} onOpen={t=>{setDeepTicker(t);setView("deepdive");}}/>}
                {journalTab==="backtest" && <BacktestView favorites={favorites} />}

                {/* PLANER TAB */}
                {journalTab==="planer" && (<>
                  {/* New plan form */}
                  {planForm && (
                    <div style={{background:"#060f1c",border:"1px solid #a78bfa40",borderRadius:10,padding:"16px 18px",marginBottom:18}}>
                      <div style={{fontSize:11,color:"#a78bfa",letterSpacing:1,marginBottom:14}}>NY PRE-TRADE PLAN</div>
                      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"1fr 1fr 2fr",gap:10,marginBottom:14}}>
                        <div>
                          <div style={{fontSize:10,color:"#475569",marginBottom:4}}>Ticker</div>
                          <input value={planForm.ticker} onChange={e=>setPlanForm(p=>({...p,ticker:e.target.value.toUpperCase()}))}
                            placeholder="MSFT"
                            style={{width:"100%",padding:"7px 9px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                        </div>
                        <div>
                          <div style={{fontSize:10,color:"#475569",marginBottom:4}}>Primærsignal</div>
                          <select value={planForm.signal} onChange={e=>setPlanForm(p=>({...p,signal:e.target.value}))}
                            style={{width:"100%",padding:"7px 9px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}>
                            {["Leder (følg bølgen)","RSI-divergens","Bull Trap (put/short)","Shooting Star (put/short)","Earnings","B20","E20","M50","RVOL↑","RVOL↓","Confluence","Annet"].map(s=><option key={s}>{s}</option>)}
                          </select>
                        </div>
                        <div>
                          <div style={{fontSize:10,color:"#475569",marginBottom:4}}>Plan / Hva venter du på?</div>
                          <input value={planForm.notes} onChange={e=>setPlanForm(p=>({...p,notes:e.target.value}))}
                            placeholder="f.eks. Venter på pullback til EMA20 ~$500 med lavt volum"
                            style={{width:"100%",padding:"7px 9px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                        </div>
                      </div>
                      <div style={{marginBottom:14}}>
                        <div style={{fontSize:10,color:"#475569",letterSpacing:1,marginBottom:10}}>SJEKKLISTE</div>
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          {CHECKLIST.map(q=>(
                            <label key={q.id} style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",padding:"8px 10px",borderRadius:6,background:planForm.checks?.[q.id]?"#10b98110":"#0a1628",border:`1px solid ${planForm.checks?.[q.id]?"#10b98140":"#1e293b"}`,transition:"all 0.15s"}}>
                              <input type="checkbox" checked={!!planForm.checks?.[q.id]} onChange={e=>setPlanForm(p=>({...p,checks:{...p.checks,[q.id]:e.target.checked}}))}
                                style={{marginTop:2,accentColor:"#10b981",width:14,height:14,flexShrink:0}}/>
                              <div>
                                <div style={{fontSize:12,color:planForm.checks?.[q.id]?"#10b981":"#94a3b8",fontWeight:planForm.checks?.[q.id]?700:400}}>{q.label}</div>
                                <div style={{fontSize:10,color:"#475569",marginTop:1}}>{q.desc}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                      {(() => {
                        const n = planScore(planForm.checks);
                        return (
                          <div style={{marginBottom:14,padding:"10px 14px",borderRadius:6,background:scoreColor(n)+"18",border:`1px solid ${scoreColor(n)}40`,display:"flex",alignItems:"center",gap:14}}>
                            <div style={{fontSize:22,fontWeight:700,color:scoreColor(n)}}>{n}/7</div>
                            <div style={{fontSize:12,color:scoreColor(n),fontWeight:700}}>{scoreLabel(n)}</div>
                          </div>
                        );
                      })()}
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>{
                          if (!planForm.ticker) return;
                          setPlans(prev=>[{...planForm, id:Date.now()+"", date:new Date().toISOString().slice(0,10), score:planScore(planForm.checks)}, ...prev]);
                          setPlanForm(null);
                        }} style={{...btnBase, background:"#a78bfa20",border:"1px solid #a78bfa60",color:"#a78bfa",fontWeight:700}}>Lagre plan</button>
                        <button onClick={()=>setPlanForm(null)} style={{...btnBase, color:"#64748b"}}>Avbryt</button>
                      </div>
                    </div>
                  )}

                  {/* Plan list */}
                  {plans.length === 0 ? (
                    <div style={{textAlign:"center",color:"#334155",padding:"60px 0"}}>
                      <div style={{fontSize:32,marginBottom:10}}>📋</div>
                      <div style={{fontSize:13,marginBottom:6}}>Ingen planer ennå</div>
                      <div style={{fontSize:11}}>Bruk sjekklisten til å vurdere et setup før du handler</div>
                    </div>
                  ) : (
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {plans.map(p => {
                        const sc = scoreColor(p.score);
                        const statusColors = { watching:"#f59e0b", entered:"#10b981", passed:"#475569" };
                        const statusLabels = { watching:"👁 Observerer", entered:"✅ Gikk inn", passed:"⏭ Passet" };
                        return (
                          <div key={p.id} style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"14px 16px"}}>
                            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                              <div style={{display:"flex",alignItems:"center",gap:12,flex:1,flexWrap:"wrap"}}>
                                <div>
                                  <span style={{fontSize:15,fontWeight:700,color:"#60a5fa",cursor:"pointer"}} onClick={()=>{setDeepTicker(p.ticker);setView("deepdive");}}>{p.ticker}</span>
                                  <span style={{fontSize:10,color:"#475569",marginLeft:8}}>{p.date}</span>
                                </div>
                                <span style={{fontSize:10,padding:"2px 8px",borderRadius:3,background:"#1e293b",color:"#94a3b8"}}>{p.signal}</span>
                                <div style={{display:"flex",alignItems:"center",gap:6,padding:"3px 10px",borderRadius:4,background:sc+"18",border:`1px solid ${sc}40`}}>
                                  <span style={{fontSize:14,fontWeight:700,color:sc}}>{p.score}/7</span>
                                  <span style={{fontSize:10,color:sc}}>{scoreLabel(p.score)}</span>
                                </div>
                              </div>
                              <div style={{display:"flex",gap:4,alignItems:"center"}}>
                                {["watching","entered","passed"].map(s=>(
                                  <button key={s} onClick={()=>setPlans(prev=>prev.map(x=>x.id===p.id?{...x,status:s}:x))}
                                    style={{padding:"3px 8px",border:`1px solid ${p.status===s?statusColors[s]:"#1e293b"}`,borderRadius:3,cursor:"pointer",fontSize:10,fontFamily:"inherit",background:p.status===s?statusColors[s]+"20":"transparent",color:p.status===s?statusColors[s]:"#475569"}}>
                                    {statusLabels[s]}
                                  </button>
                                ))}
                                <button onClick={()=>setPlans(prev=>prev.filter(x=>x.id!==p.id))} style={{padding:"3px 8px",border:"1px solid #1e293b",borderRadius:3,cursor:"pointer",fontSize:10,fontFamily:"inherit",background:"transparent",color:"#475569"}}>✕</button>
                              </div>
                            </div>
                            {p.notes && <div style={{marginTop:8,fontSize:11,color:"#64748b",fontStyle:"italic"}}>→ {p.notes}</div>}
                            <div style={{marginTop:10,display:"flex",flexWrap:"wrap",gap:4}}>
                              {CHECKLIST.map(q=>(
                                <span key={q.id} style={{fontSize:10,padding:"2px 7px",borderRadius:3,background:p.checks?.[q.id]?"#10b98120":"#1e293b",color:p.checks?.[q.id]?"#10b981":"#334155",border:`1px solid ${p.checks?.[q.id]?"#10b98140":"transparent"}`}}>
                                  {p.checks?.[q.id]?"✓":""} {q.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>)}

                {journalTab==="journal" && (<>

                {/* Add form */}
                {journalForm && !journalForm.id && (
                  <div style={{background:"#060f1c",border:"1px solid #10b98140",borderRadius:10,padding:"16px 18px",marginBottom:18}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                      <div style={{fontSize:11,color:"#10b981",letterSpacing:1}}>NY HANDEL</div>
                      <div style={{display:"flex",gap:4}}>
                        {[["aksje","📈 Aksje","#3b82f6"],["opsjon","🎯 Opsjon","#c084fc"]].map(([type,label,col])=>(
                          <button key={type} onClick={()=>setJournalForm(p=>({...p,tradeType:type}))}
                            style={{padding:"4px 12px",border:`1px solid ${journalForm.tradeType===type?col:"#1e293b"}`,borderRadius:4,cursor:"pointer",fontSize:11,fontFamily:"inherit",background:journalForm.tradeType===type?col+"20":"transparent",color:journalForm.tradeType===type?col:"#475569",fontWeight:journalForm.tradeType===type?700:400}}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(5,1fr)",gap:10,marginBottom:10}}>
                      {[["Ticker","ticker","text"],["Dato","date","date"],["Kjøpspris $","price","number"],["Antall","qty","number"]].map(([lbl,key,type])=>(
                        <div key={key}>
                          <div style={{fontSize:10,color:"#475569",marginBottom:4}}>{lbl}</div>
                          <input type={type} value={journalForm[key]||""} onChange={e=>setJournalForm(p=>({...p,[key]:e.target.value}))}
                            style={{width:"100%",padding:"7px 9px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                        </div>
                      ))}
                      <div>
                        <div style={{fontSize:10,color:"#475569",marginBottom:4}}>Årsak</div>
                        <select value={journalForm.reason||"MA-brudd"} onChange={e=>setJournalForm(p=>({...p,reason:e.target.value}))}
                          style={{width:"100%",padding:"7px 9px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}>
                          {REASONS.map(r=>r.startsWith("──")
                            ? <option key={r} disabled style={{color:"#334155"}}>{r}</option>
                            : <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    </div>
                    {journalForm.tradeType==="opsjon" && (
                      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(3,1fr)",gap:10,marginBottom:10,padding:"12px 14px",background:"#c084fc08",border:"1px solid #c084fc20",borderRadius:6}}>
                        <div>
                          <div style={{fontSize:10,color:"#c084fc",marginBottom:6}}>OPSJON — CALL / PUT</div>
                          <div style={{display:"flex",gap:4}}>
                            {[["call","▲ Call","#10b981"],["put","▼ Put","#f43f5e"]].map(([cp,label,col])=>(
                              <button key={cp} onClick={()=>setJournalForm(p=>({...p,optionType:cp}))}
                                style={{flex:1,padding:"7px 0",border:`1px solid ${journalForm.optionType===cp?col:"#1e3a5f"}`,borderRadius:4,cursor:"pointer",fontSize:12,fontFamily:"inherit",background:journalForm.optionType===cp?col+"20":"#0a1628",color:journalForm.optionType===cp?col:"#64748b"}}>
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div style={{fontSize:10,color:"#475569",marginBottom:4}}>Strike $</div>
                          <input type="number" value={journalForm.optionStrike||""} onChange={e=>setJournalForm(p=>({...p,optionStrike:e.target.value}))}
                            placeholder="f.eks. 200"
                            style={{width:"100%",padding:"7px 9px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                        </div>
                        <div>
                          <div style={{fontSize:10,color:"#475569",marginBottom:4}}>Utløpsdato</div>
                          <input type="date" value={journalForm.optionExpiry||""} onChange={e=>setJournalForm(p=>({...p,optionExpiry:e.target.value}))}
                            style={{width:"100%",padding:"7px 9px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                        </div>
                      </div>
                    )}
                    <div style={{marginBottom:10}}>
                      <div style={{fontSize:10,color:"#475569",marginBottom:4}}>Notater (valgfritt)</div>
                      <input value={journalForm.notes||""} onChange={e=>setJournalForm(p=>({...p,notes:e.target.value}))}
                        placeholder="f.eks. kursen gikk under MA9 og MA20 med høyt volum"
                        style={{width:"100%",padding:"7px 9px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>saveEntry(journalForm)} style={{...btnBase, background:"#10b98120",border:"1px solid #10b98160",color:"#10b981",fontWeight:700}}>Lagre kjøp</button>
                      <button onClick={()=>setJournalForm(null)} style={{...btnBase, color:"#64748b"}}>Avbryt</button>
                    </div>
                  </div>
                )}

                {/* Close position form */}
                {closeForm && (
                  <div style={{background:"#060f1c",border:"1px solid #f43f5e40",borderRadius:10,padding:"16px 18px",marginBottom:18}}>
                    <div style={{fontSize:11,color:"#f43f5e",letterSpacing:1,marginBottom:12}}>LUKK POSISJON — {closeForm.ticker}</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                      {[["Salgsdato","exitDate","date"],["Salgspris $","exitPrice","number"]].map(([lbl,key,type])=>(
                        <div key={key}>
                          <div style={{fontSize:10,color:"#475569",marginBottom:4}}>{lbl}</div>
                          <input type={type} value={closeForm[key]||""} onChange={e=>setCloseForm(p=>({...p,[key]:e.target.value}))}
                            style={{width:"100%",padding:"7px 9px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                        </div>
                      ))}
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>closePosition(closeForm)} style={{...btnBase, background:"#f43f5e20",border:"1px solid #f43f5e60",color:"#f43f5e",fontWeight:700}}>Bekreft salg</button>
                      <button onClick={()=>setCloseForm(null)} style={{...btnBase, color:"#64748b"}}>Avbryt</button>
                    </div>
                  </div>
                )}

                {/* Open positions */}
                {openTrades.length > 0 && (
                  <div style={{marginBottom:20}}>
                    <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:8}}>📈 ÅPNE POSISJONER ({openTrades.length})</div>
                    <div style={{display:"grid",gap:6}}>
                      {openTrades.map(t=>{
                        const live = liveData[t.ticker];
                        const curPrice = live?.price;
                        const pct = curPrice ? ((curPrice - t.price) / t.price * 100) : null;
                        const pctColor = pct == null ? "#475569" : pct >= 0 ? "#10b981" : "#f43f5e";
                        return (
                          <div key={t.id} style={{background:"#060f1c",border:"1px solid #1e293b",borderRadius:8,padding:"12px 14px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                            <div style={{minWidth:60}}>
                              <div style={{display:"flex",alignItems:"center",gap:6}}>
                                <div style={{fontSize:14,fontWeight:700,color:"#3b82f6",cursor:"pointer"}} onClick={()=>{setDeepTicker(t.ticker);setView("deepdive");}}>{t.ticker}</div>
                                {t.tradeType==="opsjon" && <span style={{fontSize:9,color:"#c084fc",background:"#c084fc18",padding:"1px 5px",borderRadius:3,fontWeight:700}}>OPT</span>}
                              </div>
                              <div style={{fontSize:10,color:"#334155"}}>{t.date}</div>
                            </div>
                            <div style={{minWidth:70}}>
                              <div style={{fontSize:10,color:"#475569",marginBottom:2}}>KJØPT</div>
                              <div style={{fontSize:13,fontWeight:700,color:"#e2e8f0"}}>${t.price.toFixed(2)}</div>
                            </div>
                            <div style={{minWidth:50}}>
                              <div style={{fontSize:10,color:"#475569",marginBottom:2}}>ANTALL</div>
                              <div style={{fontSize:13,color:"#94a3b8"}}>{t.qty}</div>
                            </div>
                            {t.tradeType==="opsjon" && (t.optionStrike||t.optionExpiry) && (
                              <div style={{minWidth:80}}>
                                <div style={{fontSize:10,color:"#c084fc",marginBottom:2}}>{t.optionType==="put"?"▼ PUT":"▲ CALL"}</div>
                                <div style={{fontSize:11,color:"#94a3b8"}}>{t.optionStrike?`$${t.optionStrike}`:""}{t.optionStrike&&t.optionExpiry?" · ":""}{t.optionExpiry||""}</div>
                              </div>
                            )}
                            {curPrice && (
                              <div style={{minWidth:90}}>
                                <div style={{fontSize:10,color:"#475569",marginBottom:2}}>NÅ</div>
                                <div style={{fontSize:13,fontWeight:700,color:pctColor}}>${curPrice.toFixed(2)} <span style={{fontSize:11}}>{pct>=0?"+":""}{pct.toFixed(1)}%</span></div>
                              </div>
                            )}
                            <div style={{flex:1,minWidth:80}}>
                              <span style={{fontSize:10,color:"#f59e0b",background:"#f59e0b15",padding:"2px 7px",borderRadius:3}}>{t.reason}</span>
                              {t.notes && <div style={{fontSize:10,color:"#334155",marginTop:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.notes}</div>}
                            </div>
                            <div style={{display:"flex",gap:5,flexShrink:0}}>
                              <button onClick={()=>setCloseForm({id:t.id,ticker:t.ticker,exitDate:new Date().toISOString().slice(0,10),exitPrice:""})}
                                style={{...btnBase, color:"#f43f5e",border:"1px solid #f43f5e40",fontSize:10}}>Lukk</button>
                              <button onClick={()=>deleteEntry(t.id)} style={{...btnBase, color:"#334155",fontSize:10}}>×</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Closed trades */}
                {closedTrades.length > 0 && (
                  <div style={{marginBottom:20}}>
                    <div style={{fontSize:11,color:"#334155",letterSpacing:1,marginBottom:8}}>✅ LUKKEDE HANDLER ({closedTrades.length})</div>
                    <div style={{display:"grid",gap:6}}>
                      {closedTrades.map(t=>{
                        const pct = ((t.exitPrice - t.price) / t.price * 100);
                        const win = pct > 0;
                        return (
                          <div key={t.id} style={{background:"#060f1c",border:`1px solid ${win?"#10b98130":"#f43f5e30"}`,borderLeft:`3px solid ${win?"#10b981":"#f43f5e"}`,borderRadius:"0 8px 8px 0",padding:"10px 14px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                            <div style={{minWidth:60}}>
                              <div style={{display:"flex",alignItems:"center",gap:6}}>
                                <div style={{fontSize:13,fontWeight:700,color:"#94a3b8",cursor:"pointer"}} onClick={()=>{setDeepTicker(t.ticker);setView("deepdive");}}>{t.ticker}</div>
                                {t.tradeType==="opsjon" && <span style={{fontSize:9,color:"#c084fc",background:"#c084fc18",padding:"1px 5px",borderRadius:3,fontWeight:700}}>{t.optionType==="put"?"PUT":"CALL"}</span>}
                              </div>
                              <div style={{fontSize:10,color:"#334155"}}>{t.date} → {t.exitDate}</div>
                            </div>
                            <div style={{minWidth:120}}>
                              <div style={{fontSize:10,color:"#475569",marginBottom:1}}>Kjøpt → Solgt</div>
                              <div style={{fontSize:12,color:"#64748b"}}>${t.price.toFixed(2)} → ${t.exitPrice.toFixed(2)}</div>
                            </div>
                            <div style={{minWidth:70}}>
                              <div style={{fontSize:15,fontWeight:700,color:win?"#10b981":"#f43f5e"}}>{win?"+":""}{pct.toFixed(1)}%</div>
                              <div style={{fontSize:10,color:"#475569"}}>{t.qty} stk</div>
                            </div>
                            <div style={{flex:1}}>
                              <span style={{fontSize:10,color:win?"#10b981":"#f43f5e",background:win?"#10b98115":"#f43f5e15",padding:"2px 7px",borderRadius:3}}>{t.reason}</span>
                              {t.notes && <div style={{fontSize:10,color:"#334155",marginTop:3}}>{t.notes}</div>}
                            </div>
                            <button onClick={()=>deleteEntry(t.id)} style={{...btnBase, color:"#334155",fontSize:10}}>×</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {journal.length === 0 && (
                  <div style={{textAlign:"center",color:"#334155",padding:"60px 0"}}>
                    <div style={{fontSize:32,marginBottom:10}}>📓</div>
                    <div style={{fontSize:13,marginBottom:6}}>Ingen handler ennå</div>
                    <div style={{fontSize:11}}>Trykk "+ Ny handel" for å logge ditt første kjøp</div>
                  </div>
                )}
                </>)}
              </div>
            );
          })()}

          {/* ── KALENDER ── */}
          {view==="idag"&&idagMode==="kalender"&&(
            <div>
              {/* Earnings Radar — neste 45 dager */}
              {(()=>{
                const in45=new Date(todayD);in45.setDate(todayD.getDate()+45);
                const radar=allCalEvents.filter(e=>e.type==="EARN"&&e.date>=todayD&&e.date<=in45).sort((a,b)=>a.date-b.date);
                if(!radar.length)return null;
                return(
                  <div style={{marginBottom:16,padding:"12px 14px",background:"#060f1c",borderRadius:6,border:"1px solid #1e293b"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                      <div style={{fontSize:10,color:"#475569",letterSpacing:1}}>📅 EARNINGS RADAR — NESTE 45 DAGER</div>
                      <div style={{fontSize:10,color:"#334155"}}>{radar.filter(e=>e.isFav).length} favoritter · {radar.length} totalt</div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:3}}>
                      {radar.map((e,i)=>{
                        const dte=Math.round((e.date-todayD)/86400000);
                        const intel=getIntel(e.ticker);
                        const livePrice=liveData[e.ticker]?.price;
                        const urgency=dte<=3?"#f43f5e":dte<=7?"#f97316":dte<=14?"#f59e0b":"#475569";
                        return(
                          <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",background:e.isFav?"#1a2d4a":"#060f1c",borderRadius:4,border:`1px solid ${e.isFav?"#3b82f620":"#0d1e30"}`}}>
                            <span style={{color:"#f59e0b",fontWeight:700,minWidth:52,fontSize:11,fontFamily:"monospace"}}>{e.ticker}</span>
                            {e.isFav&&<span style={{fontSize:9,color:"#3b82f6"}}>★</span>}
                            <span style={{color:"#64748b",fontSize:10,minWidth:72}}>{e.date.toLocaleDateString("nb-NO",{weekday:"short",day:"numeric",month:"short"})}</span>
                            <span style={{color:urgency,fontSize:10,minWidth:36,fontFamily:"monospace"}}>om {dte}d</span>
                            <span style={{color:"#10b981",fontSize:10}}>±{intel.expectedMove}%</span>
                            {livePrice&&<span style={{color:"#94a3b8",fontSize:10,marginLeft:"auto"}}>${livePrice.toFixed(2)}</span>}
                            {e.live&&<span style={{fontSize:9,color:"#3b82f660",marginLeft:e.isFav?0:"auto"}}>↻live</span>}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{marginTop:8,fontSize:9,color:"#1e3a5f"}}>Datoer fra Yahoo Finance (live). ± expected move er historisk snitt — IV kan endre dette.</div>
                  </div>
                );
              })()}
              <div style={{display:"flex",flexDirection:isMobile?"column":"row",gap:14}}>
                <div style={{width:isMobile?"100%":195,flexShrink:0}}>
                  <div style={{fontSize:12,color:"#475569",letterSpacing:1,marginBottom:8}}>FILTRER HENDELSER</div>
                  {ALL_CAL_CATS.map(cat=>(
                    <label key={cat} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",cursor:"pointer"}}>
                      <div onClick={()=>setCalFilters(p=>({...p,[cat]:!p[cat]}))}
                        style={{width:12,height:12,borderRadius:2,background:calFilters[cat]?(CAL_COLORS[cat]||"#94a3b8"):"transparent",border:`1px solid ${CAL_COLORS[cat]||"#475569"}`,cursor:"pointer",flexShrink:0}}/>
                      <span style={{fontSize:12,color:calFilters[cat]?"#e2e8f0":"#475569"}}>{cat}</span>
                    </label>
                  ))}
                  <div style={{marginTop:14,borderTop:"1px solid #1e293b",paddingTop:12}}>
                    <div style={{fontSize:12,color:"#475569",letterSpacing:1,marginBottom:8}}>NESTE 14 DAGER</div>
                    {upcoming.length===0&&<div style={{fontSize:12,color:"#334155"}}>Ingen hendelser</div>}
                    {upcoming.map((e,i)=>(
                      <div key={i} style={{marginBottom:7,padding:"7px 9px",borderRadius:5,background:e.isFav?"#1a2d4a":"#060f1c",borderLeft:`3px solid ${e.color}`}}>
                        <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:2}}>
                          <span style={{fontSize:11,color:e.color}}>{e.date.toLocaleDateString("nb-NO",{day:"numeric",month:"short"})}</span>
                          {e.live&&<span style={{fontSize:9,color:"#3b82f660"}}>↻</span>}
                          {e.isFav&&<span style={{fontSize:9,color:"#3b82f6",marginLeft:"auto"}}>★ fav</span>}
                        </div>
                        <div style={{fontSize:11,color:"#94a3b8",lineHeight:1.4}}>{e.title}</div>
                        {e.type==="EARN"&&e.expectedMove&&<div style={{fontSize:10,color:"#10b98180",marginTop:2}}>exp. ±{e.expectedMove}%</div>}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                    <button onClick={()=>setCalMonth(new Date(calMonth.getFullYear(),calMonth.getMonth()-1,1))}
                      style={{padding:"6px 14px",background:"transparent",border:"1px solid #1e293b",borderRadius:4,color:"#64748b",cursor:"pointer",fontFamily:"inherit",fontSize:14}}>←</button>
                    <div style={{fontSize:14,fontWeight:700,color:"#e2e8f0",letterSpacing:1}}>{calMonth.toLocaleDateString("nb-NO",{month:"long",year:"numeric"}).toUpperCase()}</div>
                    <button onClick={()=>setCalMonth(new Date(calMonth.getFullYear(),calMonth.getMonth()+1,1))}
                      style={{padding:"6px 14px",background:"transparent",border:"1px solid #1e293b",borderRadius:4,color:"#64748b",cursor:"pointer",fontFamily:"inherit",fontSize:14}}>→</button>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:2}}>
                    {["Man","Tir","Ons","Tor","Fre","Lør","Søn"].map(d=>(
                      <div key={d} style={{padding:"6px 4px",textAlign:"center",fontSize:12,color:"#475569",letterSpacing:1}}>{d}</div>
                    ))}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
                    {Array.from({length:firstDow}).map((_,i)=><div key={`e${i}`} style={{background:"#050c18",minHeight:isMobile?56:82,borderRadius:4}}/>)}
                    {Array.from({length:daysInMonth}).map((_,i)=>{
                      const day=i+1;
                      const evs=eventsByDay[day]||[];
                      const isToday=new Date().toDateString()===new Date(calMonth.getFullYear(),calMonth.getMonth(),day).toDateString();
                      return (
                        <div key={day} style={{background:"#060f1c",border:isToday?"1px solid #f59e0b60":"1px solid #0d1e30",borderRadius:4,minHeight:isMobile?56:82,padding:"4px 5px"}}>
                          <div style={{fontSize:12,color:isToday?"#f59e0b":"#475569",marginBottom:3,fontWeight:isToday?700:400}}>{day}</div>
                          {evs.slice(0,2).map((e,ei)=>(
                            <div key={ei} onMouseEnter={()=>setHoveredEvent({...e,day})} onMouseLeave={()=>setHoveredEvent(null)}
                              style={{fontSize:10,color:e.color,background:e.color+"22",padding:"2px 4px",borderRadius:3,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",cursor:"default"}}>
                              {e.type}: {e.title.split(" ").slice(0,3).join(" ")}
                            </div>
                          ))}
                          {evs.length>2&&<div style={{fontSize:10,color:"#475569"}}>+{evs.length-2} til</div>}
                        </div>
                      );
                    })}
                  </div>
                  {hoveredEvent&&(
                    <div style={{marginTop:10,padding:"11px 15px",background:"#060f1c",border:`1px solid ${hoveredEvent.color}40`,borderLeft:`3px solid ${hoveredEvent.color}`,borderRadius:5}}>
                      <div style={{fontSize:12,color:hoveredEvent.color,marginBottom:4}}>{hoveredEvent.cat} — {hoveredEvent.date.toLocaleDateString("nb-NO",{day:"numeric",month:"long",year:"numeric"})}</div>
                      <div style={{fontSize:14,color:"#e2e8f0",fontWeight:700,marginBottom:4}}>{hoveredEvent.title}</div>
                      <div style={{fontSize:12,color:"#64748b",lineHeight:1.5}}>{hoveredEvent.desc}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* PIN / SYNC MODAL */}
      {showPinModal&&(
        <div style={{position:"fixed",inset:0,background:"#000b",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={e=>e.target===e.currentTarget&&setShowPinModal(false)}>
          <div style={{background:"#060f1c",border:"1px solid #1e3a5f",borderRadius:12,padding:28,width:300,boxShadow:"0 8px 40px #000a"}}>
            <div style={{fontSize:14,fontWeight:700,color:"#f8fafc",letterSpacing:1,marginBottom:4}}>☁️ SKY-SYNKRONISERING</div>
            <div style={{fontSize:11,color:"#475569",marginBottom:20,lineHeight:1.5}}>
              {syncPin ? "Du er koblet til. Journal og favoritter synkroniseres automatisk på tvers av enheter." : "Skriv inn PIN-koden for å koble til skylagring. Journal og favoritter vil synkroniseres på alle enheter."}
            </div>
            {syncPin ? (
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",background:"#10b98110",border:"1px solid #10b98130",borderRadius:6,marginBottom:16}}>
                  <span style={{fontSize:16}}>✓</span>
                  <span style={{fontSize:12,color:"#10b981"}}>Tilkoblet — synkroniserer automatisk</span>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{syncFromCloud(syncPin);setShowPinModal(false);}}
                    style={{flex:1,padding:"9px",background:"#1e3a5f",border:"1px solid #3b82f6",borderRadius:4,color:"#3b82f6",cursor:"pointer",fontSize:11,fontFamily:"inherit",fontWeight:700}}>
                    ↓ Hent fra sky nå
                  </button>
                  <button onClick={()=>{setSyncPin("");localStorage.removeItem("ai-aksjer-pin");setSyncStatus("idle");setShowPinModal(false);}}
                    style={{padding:"9px 14px",background:"transparent",border:"1px solid #3f1a1a",borderRadius:4,color:"#f43f5e",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
                    Koble fra
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{fontSize:10,color:"#475569",letterSpacing:1,marginBottom:6}}>PIN-KODE</div>
                <input type="password" value={pinInput} onChange={e=>setPinInput(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&handlePinSubmit()}
                  placeholder="Skriv inn PIN…" autoFocus
                  style={{width:"100%",padding:"10px 12px",background:"#0a1628",border:`1px solid ${pinError?"#f43f5e":"#1e3a5f"}`,borderRadius:4,color:"#e2e8f0",fontSize:14,fontFamily:"inherit",boxSizing:"border-box",marginBottom:6}}/>
                {pinError&&<div style={{fontSize:10,color:"#f43f5e",marginBottom:10}}>{pinError}</div>}
                {!pinError&&<div style={{height:16,marginBottom:4}}/>}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={handlePinSubmit} disabled={!pinInput||syncStatus==="syncing"}
                    style={{flex:1,padding:"9px",background:"#1e3a5f",border:"1px solid #3b82f6",borderRadius:4,color:"#3b82f6",cursor:"pointer",fontSize:11,fontFamily:"inherit",fontWeight:700}}>
                    {syncStatus==="syncing"?"Kobler til…":"Koble til"}
                  </button>
                  <button onClick={()=>{setShowPinModal(false);setPinInput("");setPinError("");}}
                    style={{padding:"9px 14px",background:"transparent",border:"1px solid #1e293b",borderRadius:4,color:"#64748b",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
                    Avbryt
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ADD STOCK MODAL */}
      {addModal&&(
        <div style={{position:"fixed",inset:0,background:"#000a",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={e=>e.target===e.currentTarget&&setAddModal(false)}>
          <div style={{background:"#060f1c",border:"1px solid #1e3a5f",borderRadius:10,padding:22,width:310}}>
            <div style={{fontSize:13,fontWeight:700,color:"#e2e8f0",letterSpacing:1,marginBottom:14}}>LEGG TIL AKSJE</div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:9,color:"#475569",letterSpacing:1,marginBottom:4}}>TICKER (f.eks. TSLA, AAPL)</div>
              <input value={addTicker} onChange={e=>setAddTicker(e.target.value.toUpperCase())} onKeyDown={e=>e.key==="Enter"&&addStock()}
                placeholder="NVDA" style={{width:"100%",padding:"8px 10px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:9,color:"#475569",letterSpacing:1,marginBottom:4}}>KATEGORI</div>
              <select value={addCat} onChange={e=>setAddCat(e.target.value)}
                style={{width:"100%",padding:"8px 10px",background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:4,color:"#e2e8f0",fontSize:11,fontFamily:"inherit",boxSizing:"border-box"}}>
                {[...Object.keys(stocks),"Egendefinert"].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {addError&&<div style={{fontSize:10,color:"#f43f5e",marginBottom:9}}>{addError}</div>}
            <div style={{display:"flex",gap:8}}>
              <button onClick={addStock} disabled={addLoading}
                style={{flex:1,padding:"9px",background:"#1e3a5f",border:"1px solid #3b82f6",borderRadius:4,color:"#3b82f6",cursor:"pointer",fontSize:11,fontFamily:"inherit",fontWeight:700}}>
                {addLoading?"Henter…":"Legg til"}
              </button>
              <button onClick={()=>{setAddModal(false);setAddError("");setAddTicker("");}}
                style={{padding:"9px 14px",background:"transparent",border:"1px solid #1e293b",borderRadius:4,color:"#64748b",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── BacktestView ──────────────────────────────────────────────────────────────
function BacktestView({ favorites = [] }) {
  const ALL_TYPES = [
    { type:"MA_BOUNCE_EMA20", label:"E20 Bounce",    bias:"bullish" },
    { type:"MA_BOUNCE_MA50",  label:"M50 Bounce",    bias:"bullish" },
    { type:"BREAKOUT_20D",    label:"B20 Breakout",  bias:"bullish" },
    { type:"HIGH_RVOL_UP",    label:"RVOL↑",         bias:"bullish" },
    { type:"HIGH_RVOL_DOWN",  label:"RVOL↓",         bias:"bearish" },
    { type:"LIQUIDITY_SWEEP", label:"Liq. Sweep",    bias:"bullish" },
    { type:"HAMMER",          label:"Hammer",         bias:"bullish" },
    { type:"SHOOTING_STAR",   label:"Shooting Star", bias:"bearish" },
    { type:"BULL_TRAP",       label:"Bull Trap",     bias:"bearish" },
    { type:"RSI_DIV_BULL",    label:"RSI Div↗",      bias:"bullish" },
  ];
  const DEFAULT_TYPES = new Set(["MA_BOUNCE_EMA20","BREAKOUT_20D","HIGH_RVOL_UP","LIQUIDITY_SWEEP","HAMMER"]);

  const [tickerInput, setTickerInput] = useState(() => favorites.slice(0,15).join(", "));
  const [selTypes, setSelTypes]     = useState(DEFAULT_TYPES);
  const [avwapFilter, setAvwapFilter] = useState(false);
  const [confluenceFilter, setConfluenceFilter] = useState(false);
  const [status, setStatus]         = useState("");
  const [btData, setBtData]         = useState({});
  const [results, setResults]       = useState(null);
  const [baseline, setBaseline]     = useState(null);
  const [sortCol, setSortCol]       = useState({ h:"edge", stat:"edge10" });
  const [costPct, setCostPct]       = useState(0.15);   // tur/retur kurtasje + spread i prosent

  const toggleType = t => setSelTypes(prev => {
    const next = new Set(prev);
    next.has(t) ? next.delete(t) : next.add(t);
    return next;
  });

  const runBacktest = async () => {
    const tickers = tickerInput.split(/[,\s]+/).map(t => t.trim().toUpperCase()).filter(Boolean);
    if (!tickers.length || !selTypes.size) return;

    setStatus("Henter 5-årig historikk…");
    const isProxy = window.location.hostname !== "localhost";
    const newData = { ...btData };

    await Promise.all([...new Set([...tickers, "SPY"])].map(async ticker => {
      if (newData[ticker]?.closes?.length > 200) return; // already loaded
      const url = `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(ticker)}?range=5y&interval=1d`;
      const proxy = isProxy ? `/yahoo?url=${encodeURIComponent(url)}` : `https://corsproxy.io/?url=${encodeURIComponent(url)}`;
      try {
        const res = await fetch(proxy, { signal: AbortSignal.timeout(15000) });
        if (!res.ok) return;
        const json = await res.json();
        const r = json?.chart?.result?.[0];
        if (!r) return;
        const q = r.indicators?.quote?.[0] || {};
        newData[ticker] = {
          closes: q.close?.map(v => v ?? null) || [],
          opens:  q.open?.map(v => v ?? null)  || [],
          highs:  q.high?.map(v => v ?? null)  || [],
          lows:   q.low?.map(v => v ?? null)   || [],
          volumes: q.volume || [],
          timestamps: r.timestamp || [],
        };
      } catch {}
    }));

    setBtData(newData);
    setStatus("Beregner signaler og avkastning…");

    const bias = Object.fromEntries(ALL_TYPES.map(t => [t.type, t.bias]));
    const bt = calcBacktest(newData, tickers, [...selTypes], { avwapFilter, confluenceFilter, costPct, bias, spy: newData.SPY });
    setResults(bt.results);
    setBaseline(bt.baseline);
    setStatus("");
  };

  const fmt = (v, suffix="") => v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}${suffix}`;
  const fmtWin = v => v == null ? "—" : `${v.toFixed(0)}%`;

  const HORIZONS = [
    { key:"h5",  label:"5 dager" },
    { key:"h10", label:"10 dager" },
    { key:"h20", label:"20 dager" },
  ];

  const sortedTypes = ALL_TYPES.filter(t => selTypes.has(t.type) && results?.[t.type]);
  const sortVal = (type) => sortCol.h === "edge" ? results[type]?.[sortCol.stat] : results[type]?.[sortCol.h]?.[sortCol.stat];
  sortedTypes.sort((a, b) => {
    const va = sortVal(a.type);
    const vb = sortVal(b.type);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    return vb - va;
  });

  const thBtn = (h, stat, label) => {
    const active = sortCol.h === h && sortCol.stat === stat;
    return (
      <th key={`${h}-${stat}`} onClick={() => setSortCol({ h, stat })}
        style={{padding:"7px 8px",cursor:"pointer",fontWeight:active?700:400,color:active?"#93c5fd":"#475569",fontSize:10,whiteSpace:"nowrap",borderRight:"1px solid #0d1f35"}}>
        {label}{active?" ↓":""}
      </th>
    );
  };

  const retCell = (val, baseline_val, count) => {
    if (val == null) return <td style={{padding:"6px 8px",color:"#334155",textAlign:"center",fontSize:11}}>—</td>;
    const diff = baseline_val != null ? val - baseline_val : null;
    const good = val > 0;
    return (
      <td style={{padding:"6px 8px",textAlign:"center",fontSize:11,color:good?"#10b981":"#f43f5e",fontWeight:700}}>
        {fmt(val,"%")}
        {diff != null && <span style={{fontSize:9,color:diff>0?"#22c55e80":"#f43f5e80",marginLeft:3}}>{diff>0?"+":""}{diff.toFixed(1)}</span>}
      </td>
    );
  };

  return (
    <div style={{padding:"18px 16px",maxWidth:1200,margin:"0 auto"}}>
      <div style={{marginBottom:18}}>
        <div style={{fontSize:9,color:"#334155",letterSpacing:2,marginBottom:10}}>BACKTEST — HISTORISKE SIGNALER</div>

        {/* Forklaringsboks */}
        <div style={{background:"#0a1628",border:"1px solid #1e293b",borderRadius:8,padding:"14px 16px",marginBottom:16}}>
          <div style={{fontSize:11,color:"#f59e0b",fontWeight:700,marginBottom:10,letterSpacing:0.5}}>HVORDAN LESE TABELLEN</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:12}}>
            <div>
              <div style={{fontSize:11,color:"#94a3b8",fontWeight:700,marginBottom:5}}>Kolonnene</div>
              <div style={{fontSize:11,color:"#64748b",lineHeight:1.7}}>
                <b style={{color:"#94a3b8"}}>Win%</b> — andel handler som endte positivt.<br/>
                <b style={{color:"#94a3b8"}}>Snitt</b> — gjennomsnittlig avkastning. Kan dras opp av noen store vinnere.<br/>
                <b style={{color:"#94a3b8"}}>Median</b> — midtverdien. Mer robust — halvparten tjente mer, halvparten mindre.<br/>
                <b style={{color:"#94a3b8"}}>Edge</b> — ekstra avkastning på 10 dager mot tilfeldig inngang, etter kostnad. For bearish signaler: hvor mye mer det falt. Under står hvor pålitelig tallet er.<br/>
                <b style={{color:"#94a3b8"}}>Opptrend / Nedtrend</b> — samme edge, delt etter om S&P 500 var over eller under 200-dagers snitt.<br/>
                <b style={{color:"#94a3b8"}}>≥+5% 20d</b> — hvor ofte kursen var minst 5 % høyere etter 20 dager. Det er omtrent det en bull call spread med 5 % bredde trenger for full gevinst.
              </div>
            </div>
            <div>
              <div style={{fontSize:11,color:"#94a3b8",fontWeight:700,marginBottom:5}}>Baseline — den viktigste referansen</div>
              <div style={{fontSize:11,color:"#64748b",lineHeight:1.7}}>
                Baseline viser hva som skjer om du kjøper en tilfeldig dag på de samme aksjene. I en bullperiode er baseline positiv (markedet stiger uansett). Et signal som gir +3% snitt når baseline er +2.3% gir bare +0.7% <i>ekstra</i> verdi. <b style={{color:"#f59e0b"}}>Bruk "vs baseline 10d"-kolonnen — det er den som avslører om signalet faktisk hjelper.</b>
              </div>
            </div>
            <div>
              <div style={{fontSize:11,color:"#94a3b8",fontWeight:700,marginBottom:5}}>Filtre og begrensninger</div>
              <div style={{fontSize:11,color:"#64748b",lineHeight:1.7}}>
                <b style={{color:"#a78bfa"}}>Confluence</b>: krever at en annen signaltype treffer samme aksje innen ±3 trading-dager. Færre signaler, men høyere sannsynlig kvalitet.<br/>
                <b style={{color:"#fb923c"}}>AVWAP</b>: kun signaler der kurs er over forankret VWAP — bekrefter bullish struktur.<br/>
                <span style={{color:"#f59e0b"}}>Survivorship bias</span>: kun aksjer som finnes i dag er testet.<br/>
                <span style={{color:"#f59e0b"}}>⚠ lav N</span>: under 30 forekomster er statistisk usikkert.
              </div>
            </div>
          </div>
        </div>

        {/* Ticker input */}
        <div style={{marginBottom:12}}>
          <div style={{fontSize:10,color:"#64748b",marginBottom:5}}>AKSJER (kommaseparert)</div>
          <textarea value={tickerInput} onChange={e => setTickerInput(e.target.value)} rows={2}
            style={{width:"100%",background:"#0a1628",border:"1px solid #1e293b",borderRadius:5,color:"#e2e8f0",padding:"8px 10px",fontSize:12,fontFamily:"inherit",resize:"vertical",boxSizing:"border-box"}}/>
        </div>

        {/* Signal types */}
        <div style={{marginBottom:12}}>
          <div style={{fontSize:10,color:"#64748b",marginBottom:7}}>SIGNAL-TYPER</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {ALL_TYPES.map(({type, label, bias}) => {
              const on = selTypes.has(type);
              const c = bias === "bullish" ? "#10b981" : "#f43f5e";
              return (
                <button key={type} onClick={() => toggleType(type)}
                  style={{padding:"4px 10px",border:`1px solid ${on ? c+"80" : "#1e293b"}`,background:on?c+"15":"transparent",color:on?c:"#475569",borderRadius:4,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Options row */}
        <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:14,flexWrap:"wrap"}}>
          <button onClick={() => setAvwapFilter(v => !v)}
            style={{padding:"5px 12px",border:`1px solid ${avwapFilter?"#fb923c80":"#1e293b"}`,background:avwapFilter?"#fb923c15":"transparent",color:avwapFilter?"#fb923c":"#475569",borderRadius:4,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
            {avwapFilter ? "AVWAP: PÅ" : "AVWAP: AV"}{avwapFilter && <span style={{fontSize:9,color:"#64748b",marginLeft:5}}>pris &gt; AVWAP</span>}
          </button>
          <button onClick={() => setConfluenceFilter(v => !v)}
            title="Kun signaler der en annen signaltype treffer samme aksje innen ±3 trading-dager"
            style={{padding:"5px 12px",border:`1px solid ${confluenceFilter?"#a78bfa80":"#1e293b"}`,background:confluenceFilter?"#a78bfa15":"transparent",color:confluenceFilter?"#a78bfa":"#475569",borderRadius:4,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
            {confluenceFilter ? "Confluence: PÅ" : "Confluence: AV"}{confluenceFilter && <span style={{fontSize:9,color:"#64748b",marginLeft:5}}>2+ signaler ±3d</span>}
          </button>
          <label style={{fontSize:11,color:"#64748b",display:"flex",alignItems:"center",gap:5}} title="Kurtasje og spread tur/retur, trekkes fra hver handel">
            Kostnad
            <input type="number" step="0.05" min="0" value={costPct} onChange={e => setCostPct(Math.max(0, parseFloat(e.target.value) || 0))}
              style={{width:54,background:"#0a1628",border:"1px solid #1e293b",borderRadius:4,color:"#e2e8f0",padding:"4px 6px",fontSize:11,fontFamily:"inherit"}}/> %
          </label>
          <button onClick={() => setTickerInput("XLK, XLF, XLE, XLV, XLI, XLY, XLP, XLU, AAPL, JPM, XOM, JNJ, PG, KO, WMT, HD, CAT, UNH, PFE, INTC, BA, DIS, NKE, F, T, VZ, C, MMM, WBA, PYPL")}
            title="Bredt utvalg på tvers av sektorer, også aksjer som har gått dårlig. Viser om signalet virker generelt, ikke bare på AI-vinnere."
            style={{padding:"5px 12px",border:"1px solid #1e293b",background:"transparent",color:"#94a3b8",borderRadius:4,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>
            Bredt utvalg (test mot hindsight)
          </button>
          <button onClick={runBacktest} disabled={!!status}
            style={{padding:"6px 18px",background:status?"#1e293b":"#3b82f6",color:status?"#475569":"white",border:"none",borderRadius:5,cursor:status?"default":"pointer",fontSize:12,fontFamily:"inherit",fontWeight:700}}>
            {status || "Kjør backtest"}
          </button>
          {status && <span style={{fontSize:11,color:"#64748b"}}>{status}</span>}
        </div>
      </div>

      {/* Results table */}
      {results && (
        <div style={{overflowX:"auto"}}>
          <div style={{fontSize:9,color:"#334155",letterSpacing:2,marginBottom:8}}>RESULTATER — klikk kolonneoverskrift for å sortere</div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
            <thead>
              <tr style={{background:"#0a1628",borderBottom:"1px solid #1e293b"}}>
                <th style={{padding:"7px 10px",textAlign:"left",color:"#475569",fontSize:10,whiteSpace:"nowrap"}}>Signal</th>
                <th style={{padding:"7px 8px",color:"#475569",fontSize:10,borderRight:"1px solid #1e293b"}}>#</th>
                {HORIZONS.map(({key, label}) => (
                  <Fragment key={key}>
                    <th colSpan={3} style={{padding:"5px 8px",textAlign:"center",color:"#64748b",fontSize:9,letterSpacing:1,borderRight:"1px solid #1e293b",background:"#060f1c"}}>{label.toUpperCase()}</th>
                  </Fragment>
                ))}
                <th colSpan={4} style={{padding:"5px 8px",textAlign:"center",color:"#f59e0b",fontSize:9,letterSpacing:1,background:"#060f1c"}}>FORDEL MOT TILFELDIG INNGANG (10 DAGER)</th>
              </tr>
              <tr style={{background:"#060f1c",borderBottom:"2px solid #1e293b"}}>
                <th style={{padding:"4px 10px"}}/>
                <th style={{padding:"4px 8px",borderRight:"1px solid #0d1f35"}}/>
                {HORIZONS.map(({key}) => (
                  <Fragment key={key}>
                    {thBtn(key,"winRate","Win%")}
                    {thBtn(key,"avg","Snitt")}
                    {thBtn(key,"median","Median")}
                  </Fragment>
                ))}
                {thBtn("edge","edge10","Edge")}
                {thBtn("edge","edgeUp","Opptrend")}
                {thBtn("edge","edgeDown","Nedtrend")}
                {thBtn("edge","hit","≥+5% 20d")}
              </tr>
            </thead>
            <tbody>
              {sortedTypes.map(({type, label, bias}) => {
                const r = results[type];
                const lowN = Math.min(r.h5.count, r.h10.count, r.h20.count) < 30;
                const bc = bias === "bullish" ? "#10b981" : "#f43f5e";
                return (
                  <tr key={type} style={{borderBottom:"1px solid #0d1f35",background:"transparent"}}
                    onMouseEnter={e => e.currentTarget.style.background="#0a1628"}
                    onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                    <td style={{padding:"8px 10px",color:bc,fontWeight:700,fontSize:12,whiteSpace:"nowrap"}}>
                      {label}
                      {lowN && <span title="Under 30 forekomster — statistisk usikkert" style={{marginLeft:5,fontSize:9,color:"#f59e0b",background:"#f59e0b15",borderRadius:3,padding:"1px 4px"}}>⚠ lav N</span>}
                    </td>
                    <td style={{padding:"8px",textAlign:"center",color:lowN?"#f59e0b":"#64748b",fontWeight:lowN?700:400,fontSize:11,borderRight:"1px solid #0d1f35"}}>
                      {Math.min(r.h5.count, r.h10.count, r.h20.count)||r.h5.count}
                    </td>
                    {[r.h5, r.h10, r.h20].map((h, hi) => (
                      <Fragment key={hi}>
                        <td style={{padding:"8px",textAlign:"center",fontSize:11,color:h.winRate>=50?"#10b981":"#f43f5e"}}>{fmtWin(h.winRate)}</td>
                        {retCell(h.avg, baseline?.[HORIZONS[hi].key]?.avg, h.count)}
                        <td style={{padding:"8px",textAlign:"center",fontSize:11,color:h.median>=0?"#10b981":"#f43f5e",borderRight:"1px solid #0d1f35"}}>{fmt(h.median,"%")}</td>
                      </Fragment>
                    ))}
                    {(() => {
                      const rel = reliability(r);
                      const ec = v => v == null ? "#334155" : v > 0 ? "#10b981" : "#f43f5e";
                      return (
                        <>
                          <td style={{padding:"8px",textAlign:"center",fontSize:11,whiteSpace:"nowrap"}}>
                            <span style={{color:ec(r.edge10),fontWeight:700}}>{fmt(r.edge10,"%")}</span>
                            <div style={{fontSize:9,color:rel.color}} title={`t-verdi ${r.t10?.toFixed(1) ?? "–"} (over 2 = neppe tilfeldig)`}>{rel.label}</div>
                          </td>
                          <td style={{padding:"8px",textAlign:"center",fontSize:11,color:ec(r.edgeUp)}} title={`${r.nUp} treff mens S&P 500 var over 200-dagers snitt`}>{fmt(r.edgeUp,"%")}<div style={{fontSize:9,color:"#334155"}}>n={r.nUp}</div></td>
                          <td style={{padding:"8px",textAlign:"center",fontSize:11,color:ec(r.edgeDown)}} title={`${r.nDown} treff mens S&P 500 var under 200-dagers snitt`}>{fmt(r.edgeDown,"%")}<div style={{fontSize:9,color:"#334155"}}>n={r.nDown}</div></td>
                          <td style={{padding:"8px",textAlign:"center",fontSize:11,color:r.hit!=null&&baseline?.hit!=null&&r.hit>baseline.hit?"#10b981":"#94a3b8"}}>{r.hit!=null?`${(r.hit*100).toFixed(0)}%`:"—"}</td>
                        </>
                      );
                    })()}
                  </tr>
                );
              })}
              {/* Baseline row */}
              {baseline && (
                <tr style={{borderTop:"2px solid #1e293b",background:"#060f1c"}}>
                  <td style={{padding:"8px 10px",color:"#475569",fontSize:11,fontStyle:"italic"}}>Baseline (tilfeldig)</td>
                  <td style={{padding:"8px",textAlign:"center",color:"#334155",borderRight:"1px solid #0d1f35"}}>—</td>
                  {[baseline.h5, baseline.h10, baseline.h20].map((h, hi) => (
                    <Fragment key={hi}>
                      <td style={{padding:"8px",textAlign:"center",fontSize:11,color:"#475569"}}>{fmtWin(h.winRate)}</td>
                      <td style={{padding:"8px",textAlign:"center",fontSize:11,color:"#475569"}}>{fmt(h.avg,"%")}</td>
                      <td style={{padding:"8px",textAlign:"center",fontSize:11,color:"#475569",borderRight:"1px solid #0d1f35"}}>{fmt(h.median,"%")}</td>
                    </Fragment>
                  ))}
                  <td colSpan={3} style={{padding:"8px",textAlign:"center",fontSize:10,color:"#334155"}}>referanse</td>
                  <td style={{padding:"8px",textAlign:"center",fontSize:11,color:"#475569"}}>{baseline.hit!=null?`${(baseline.hit*100).toFixed(0)}%`:"—"}</td>
                </tr>
              )}
            </tbody>
          </table>
          {/* Funn-oppsummering */}
          {(() => {
            const ALL_LABELS = Object.fromEntries(ALL_TYPES.map(t => [t.type, t.label]));
            const groups = [
              ["pålitelig", "#10b981", "Pålitelig fordel (bruk disse)"],
              ["lovende", "#f59e0b", "Lovende, men trenger flere treff"],
              ["støy", "#64748b", "Ingen målbar fordel (støy)"],
              ["virker motsatt", "#f43f5e", "Virker motsatt av det navnet sier"],
              ["for få treff", "#475569", "For få treff til å si noe"],
            ].map(([key, color, title]) => ({ color, title, items: sortedTypes.filter(({type}) => reliability(results[type]).label === key) }))
             .filter(g => g.items.length);
            return (
              <div style={{marginTop:14,padding:"12px 14px",background:"#060f1c",border:"1px solid #1e293b",borderRadius:6}}>
                <div style={{fontSize:10,color:"#334155",letterSpacing:1,marginBottom:8}}>TOLKNING AV RESULTATER</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:14,fontSize:11,lineHeight:1.7}}>
                  {groups.map(g => (
                    <div key={g.title} style={{flex:1,minWidth:200}}>
                      <span style={{color:g.color,fontWeight:700}}>{g.title}:</span>
                      {g.items.map(({type}) => {
                        const r = results[type];
                        const regimeNote = r.edgeUp != null && r.edgeDown != null && Math.sign(r.edgeUp) !== Math.sign(r.edgeDown)
                          ? ` · virker bare i ${r.edgeUp > 0 ? "opptrend" : "nedtrend"}` : "";
                        return <div key={type} style={{color:"#64748b"}}>
                          <span style={{color:g.color}}>{ALL_LABELS[type]}</span> — edge {r.edge10 != null ? `${r.edge10 >= 0 ? "+" : ""}${r.edge10.toFixed(2)}%` : "–"}, {r.h10.count} treff{regimeNote}
                        </div>;
                      })}
                    </div>
                  ))}
                </div>
                <div style={{marginTop:10,fontSize:11,color:"#475569",lineHeight:1.7}}>
                  <span style={{color:"#f59e0b",fontWeight:700}}>Husk:</span> «Pålitelig» krever minst 30 treff og t-verdi over 2, som betyr at fordelen neppe er tilfeldig. Selv da er historikk ingen garanti. Ser du «virker bare i opptrend», bruk signalet bare når «I dag» viser grønt.
                </div>
              </div>
            );
          })()}
          <div style={{marginTop:8,fontSize:10,color:"#334155",lineHeight:1.7}}>
            <span style={{color:"#f59e0b"}}>⚠ lav N</span> = under 30 forekomster — statistisk usikkert.{" "}
            Små fargede tall etter Snitt = differanse fra baseline.
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem("ak-auth") === "1");
  if (!authed) return <PasswordGate onAuth={() => {
    sessionStorage.setItem("ak-auth", "1");
    setAuthed(true);
  }} />;
  return <AppContent />;
}
